'use client';

// Мастер-анкета шаблона (SCRIPTS-MODE Ф3.1) — генерный рендерер: шаги и поля берёт из
// данных (TemplateWizardDef из lib/template-wizards), поля рисует штатный ParamsForm
// (канон FORM-ENGINE-PLAN — никакого статичного HTML под шаблон). «Создать» шлёт POST
// drafts { template, wizard } — канон собирает сервер тем же конвейером, что импорт.
// Режим «Настроить» (FORM-WIZARD Ф1, ЖЕЛЕЗНОЕ владельца 04.08 «создал через виджет —
// редактирует через виджет»): та же анкета с сохранёнными ответами, «Сохранить» шлёт
// PUT drafts/[id] { wizard } — сервер пересобирает канон новой ревизией.
//
// Перестройка 05.08 №3 (заказ владельца, скрин формы IG):
//  • Превью-телефон — в ШАПКЕ панели, напротив стрелки закрытия (создание: headerRight/
//    actionIcon хоста через header-проп; правка на странице сценария — своя строка сверху).
//  • Счётчик «Шаг N из M» — серой строкой ПОД заголовком шапки (header.update → subtitle).
//  • «Назад»/«Дальше» — нижней планкой, прибитой к низу (sticky, контент не просвечивает).
//  • Страница — БЛОКАМИ: «Аккаунт» первым пузырём, дальше блок с именем шага
//    (например «Условия запуска»); «Проверить подписку» и его поля — последним пунктом
//    блока, выделены заливкой (wiz-sub).
//  • Превью чата в теле формы не живёт — открывается по кнопке-телефону нижним листом
//    (натив: родной лист, канон §11; веб: лист поверх мастера с той же сценой, что в приложении).
//  • Форма — «пузырями» набора form-kit (FCard/FField), как страница добавления подключения.
//  • Ошибки валидации честные: «Дальше» всегда нажимается, недозаполненные обязательные
//    поля подсвечиваются fieldWarnings ParamsForm (а не молча запертая кнопка).
//  • Семейство анкет (family): «Чат-бот на кодовое слово» — ТГ и инста ОДНИМ мастером,
//    первый вопрос «Аккаунт» собирает подключения обеих сетей, выбор переключает анкету
//    (и шаблон, из которого сервер соберёт сценарий).
import { Clock } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { log as journalLog } from '@/lib/client-journal';
import { WizSaveQueue } from './wizard-save-queue';
import { useLocale, useTranslations } from 'next-intl';
import { AutoGrowTextarea, ParamsForm, SmartSelect, type SmartOption } from '@scriptera/ui';
import {
  KBareButton,
  KButton,
  KInput,
  KListCheck,
  KListGroup,
  KListRow,
  KListSection,
  KPill,
  KWheelDialog,
} from '@scriptera/ui/kit';
import { usePhoneLook, webOnly } from './layout-mode';
import { PlusGlyph } from './plus-glyph';
import {
  ACCOUNT_CRED_TYPE,
  attachFileRef,
  botAccountLabel,
  chatAttachFit,
  chatAttachPick,
  chatAttachLimit,
  chatContentNameKey,
  visibleParams,
  type BotAccountChannel,
  type CatalogParam,
  type CatalogParamOption,
  type ChatAttachFit,
  type ChatAttachKind,
} from '@scriptera/ui/scenario';
import {
  BROADCAST_WIZARD_ID,
  bcastNativeFormOn,
  bcastWhoNative,
  CHAIN_WIZARD_ID,
  CHAIN_DONE_STEP,
  WIZ_CHAIN_RUNNING,
  chainNoOthersWords,
  chainStartAnswers,
  isWizWordsField,
  wizardDefaults,
  wizardSiblings,
  wizardSteps,
  wizStartIdx,
  wizStepOf,
  wizCoveredParams,
  wizMessageCards,
  wizMessageDraft,
  wizMessagePreview,
  wizItemCanMove,
  wizItemTitle,
  wizBotLinkFields,
  wizBotLinkAddKinds,
  wizUsedLinkLabels,
  WIZ_ADD_BOTLINK_PFX,
  wizAddBotLinkEntry,
  wizEntryWords,
  wizLinkReadyFields,
  WIZ_BOT_LINK_PFX,
  WIZ_LINK_READY_FIELD,
  wizAccountOwnLabel,
  whenIsPast,
  WIZ_GIFT_FILE,
  WIZ_GIFT_KIND,
  WIZ_GIFT_TG,
  WIZ_GIFT_TG_BOT,
  WIZ_GIFT_TG_LINK,
  wizGiftTgDerive,
  wizGiftTgFill,
  wizGiftTgFrom,
  WIZ_INTRO_KEY,
  applyToggleTexts,
  wizardAboutTitle,
  type TemplateWizardDef,
  type WizLiveChecks,
  type WizardLocale,
  type WizardItemRow,
  type WizardPreviewMsg,
  type WizardStep,
  type WizardItemAddKind,
  type WizardItemLive,
  type WizardStepItemList,
  type WizardStepMessage,
} from '@/lib/template-wizards';
// Слова шага «Что запускает» (общие у цепочки и у ботов) — из его строителя: подсказка
// про одинаковый хвост ссылки на странице обязана совпадать со словом блокировки шага.
import { trigDupTail, trigWords, trigWordsWarn } from '@/lib/template-wizards/trigger-step';
import { isButtonUrl, normalizeButtonUrl, toTgDraft, type TgDraft } from '@scriptera/db/tg-message';
import { translitRefCode } from '@/lib/bot-ref-core';
import { wizardPreviewHash } from '@/lib/wizard-preview-data';
import { peekPersistent, refreshPersistent, revalidatePersistentPrefix, stalePersistentPrefix, usePersistentData } from '@/lib/use-persistent-data';
import {
  SCENARIO_TITLES_KEY,
  SCENARIO_TITLES_URL,
  nextTitleNumber,
  numberedTitle,
  scenarioTitlesOk,
  type ScenarioTitlesResp,
} from '@/lib/bot-title-number';
import { nextBroadcastName } from '@/lib/broadcast-name';
import { useParamsFormDict } from './params-form-dict';
import { createErrText, useBotExistsAsk, useCreateGo } from './new-scenario-button';
import {
  STARTS_TAKEN_KEY,
  STARTS_TAKEN_URL,
  findTakenStart,
  startsTakenOk,
  wizardWantedStarts,
  type StartsTakenResp,
} from '@/lib/starts-taken';
import { ChipsInput, limitHint, StartLinkFields, StartLinkReadyRow } from './chat-step-settings';
import { useAccountCreds } from './manual-start';
import {
  AudienceRow,
  AudienceSummary,
  openAudienceScreen,
  useAudienceCount,
  useAudienceCountWords,
  type AudienceOpenOpts,
} from './audience-screen';
// РОДНОЙ ЭКРАН «КТО ПОЛУЧИТ» (Г11): в приложении список живых людей рисует натив, а мозг
// экрана живёт скрытым куском веба — здесь, рядом с мозгом окна «Сообщение». Веб-шторки
// тут открыть нельзя: она легла бы ПОД системным листом анкеты (тот же урок, что Г8).
import { AudienceNativeHost } from './audience-native';
import { ProBadge, useProLocked } from './billing-views';
import { ScenePhoneBadge } from '@/components/scene-pro-badge';
// ЖИВЫЕ СПИСКИ КАБИНЕТА (боты и метки) — те же своды, из которых их берёт лист условий:
// второго источника имён у входов цепочки нет (К5).
import {
  useBotOptions,
  useChainOptions,
  useTagOptions,
  useTelegramBotOptions,
} from './audience-filter-sheet';
import { audienceOfRaw, audienceQuery } from './audience-value';
// СОСТАВ ПИСЬМА СЛОВАМИ для строки «Сообщение» (И11.3) — чистая функция, слова даёт
// словарь кабинета: своего набора слов у страницы нет.
import { messageSummary, type MessageSummaryDict } from './bcast-summary';
import { BcastNativeForm } from './bcast-native';
import { DateTimePick } from './date-range';
import {
  FAddBubble,
  FBlock,
  FCard,
  type FNotifyItem,
  FChoiceBlock,
  FError,
  FField,
  FFormError,
  FHint,
  FNotifyList,
  FPane,
  FThumb,
} from './form-kit';
import { WizUsageCard } from './wiz-usage';
import { WizOverSheet } from './wiz-over-sheet';
import { ConfirmInline } from './confirm-inline';
import { CopyBtn } from './copy-btn';
import '@/components/onboarding/tg-token-how.css';
import { useWizardConnectFlow } from './wizard-connect-flow';
import {
  chanFailKey,
  chanItemHint,
  chanKey,
  chanOptionsOf,
  chanStepsText,
  tgHandleUrl,
  useWizChannelCheck,
  WIZ_CHANNEL_FIELD,
  type WizChannelItem,
} from './wizard-channel-check';
import { useMessageComposer } from './message-composer/message-composer';
// РОДНОЕ ОКНО «СООБЩЕНИЕ» (Г6): в приложении письмо собирает натив, а мозг окна живёт
// скрытым куском веба. Родной мастер шторок веба открыть не может — они легли бы ПОД
// системным листом анкеты, поэтому окно поднимается прямо отсюда (ТЗ Д2, стопка).
import { MessageNativeHost } from './message-native';
import { useSlidePanel } from './native-layer-host';
import type { DrawerPayload } from './drawer';
import { nativeBridge, nativeMethod, useNativeMethod } from '@/lib/native-app';
import { isNewAppBuild } from '@/lib/build-caps';
// ВОПРОС ПРО ТЯЖЁЛЫЙ ФАЙЛ — ТОТ ЖЕ, ЧТО В ОКНЕ «СООБЩЕНИЕ» (ТЗ 20.09.2026 F3): лист со
// словами и капсулами действий (MediaAskSheet) и та же подготовка файла раннером
// (useMediaConvert, операция `fit-size`). Второй механики «файл не влезает» не заводим.
import { MediaAskSheet } from './message-composer/media-fix-sheet';
import { useMsgConflictWords } from './message-composer/use-msg-dict';
import {
  applyConflict,
  conflictAsk,
  doneConflict,
  type MsgConflict,
  type MsgConflictAnswer,
} from './message-conflict';
// КАРТИНКУ НА САЙТЕ УЖИМАЕТ САМ БРАУЗЕР (ТЗ 20.09.2026 V5): раннеру её возить незачем —
// файл уже в руках. Ролик по-прежнему готовит раннер (useMediaConvert).
import { imageNeedsSqueeze, imageSide, squeezeImage } from './message-composer/image-squeeze';
// Форматы словом человека («mp3, m4a») — общий словарь кабинета, не свой список.
import { mimeWord, mimeWords } from '@/lib/mime-words';
import { withExtension } from '@/lib/media-limits';
import { useMediaConvert } from './message-composer/use-media-convert';
import type {
  NativeWizDict,
  NativeWizItem,
  NativeWizItemRows,
  NativeWizPreviewMsg,
  NativeWizStep,
} from '@/lib/wiz-native';
// Жалоба в центр ошибок: «натив перестал брать открытую анкету» — наша поломка, клиенту
// её не показываем (CLAUDE.md запрет №7), но разбирать её надо по журналу (Н15).
import { reportUiError } from '@/lib/ui-error-report';
import {
  nativeWizAnswers,
  nativeWizFields,
  nativeWizIsPostsIds,
  nativeWizRows,
  nativeWizSteps,
  nativeWizValueToAnswer,
  nativeWizWebOwned,
  nativeWordsPlain,
  nativeLinesPageItem,
  nativeAddKinds,
  LINES_ROW_FIELD,
  useNativeDraft,
  useNativeWizard,
  STEP_ROW_FIELD,
  WIZ_PHONE_HINT_STEP,
  type WizFileStatus,
} from './wizard-native';
// СЦЕНА С ПРИМЕРОВ РАБОТ на первом экране анкеты (ТЗ-3 Т1): тот же компонент, что на
// продающей странице и в мини-туре приложения. Второй копии сцен не заводим.
import { Scene, useScenes } from '@/components/auth/app-onboarding';
import { SCENE_SLOTS, type SceneTexts } from '@/lib/wizard-scene-texts';
// ПРИМЕР НА ЕГО ДАННЫХ (портрет клиента, бэклог 31.08): его аватарка и имя как
// отправитель, его публикация, слово и обещание — по его нише.
import { nichePhrases, nicheScene, sceneAvatarUrl } from '@/lib/niche-scene';
import { hasOwnLetters, withoutSeries } from '@/lib/scene-series';
import {
  buildDemoSceneUrl,
  demoPersona,
  demoThemeNow,
  mergedSceneTexts,
  wizardAccountCreds,
  wizardAccountId,
  wizardDefForCred,
  PRO_ONLY_WIZARDS,
} from '@/lib/wizard-demo-url';
import { seededNiche } from '@/lib/app-pages/wizard-demo';
import {
  useNichePersonal,
  pickAccountForChannel,
  accountByAnyCred,
} from '@/lib/use-niche-personal';
import { BottomSheet } from './bottom-sheet';
import { IgMediaChosen, IgMediaPicker, type IgMediaCard } from './ig-media-picker';
// Хиро и нижняя планка — ОБЩАЯ оболочка мастеров (wizard-shell): той же разметкой живёт
// мастер настройки запуска в конструкторе (StartEditor). Своей копии тут нет.
import { WizFoot, WizHero } from './wizard-shell';
import { QS_ROOT_COPY, useOpenQuickDone, useQuickFlow, WizOffer } from './quick-start';
import { quickErrStep } from '@/lib/quick-start/errors';
import { QS_HAS_WEBINAR, QS_SUMMARY_ACCOUNT_ROWS, qsBuyLoginFill } from '@/lib/template-wizards/quick-start';
import type { OfferSlot } from '@/lib/quick-start/offer';
import { wizLetterCommit, wizLetterValues, type WizLetterDraft } from './wizard-letter-draft';
import type { DraftSnapshot } from './chat';

/** Один экран рассылки на телефоне в браузере (Р2). Выключен 12.10.2026: рассылка — мастер по шагам, как в iOS. */
const BCAST_ONE_SCREEN: boolean = false;
/** Названия рассылок кабинета, включая удалённые — подсказка «Сообщение N» поля «Название» (APP-CLAIMS-1020 ред. 8, Р17). */
const BCAST_TITLES_KEY = 'cabinet:broadcast-titles';
const BCAST_TITLES_URL = `${SCENARIO_TITLES_URL}?kind=broadcast`;

function isEmptyAnswer(v: unknown): boolean {
  if (v === undefined || v === null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/** Черновик нового триггера: он ещё не в ответах анкеты (в них его уносит «Готово»). */
interface WizDraftAdd {
  name: string;
  kind: string;
  value?: string;
  /** Ответы анкеты С этим триггером (его номер — `i`); правки полей копятся сюда. */
  next: Record<string, unknown>;
  i: number;
}

/**
 * «ПЫТАЛИСЬ ОТПРАВИТЬ» У СТРАНИЦЫ В БРАУЗЕРЕ (К24 п.1, владелец 07.09.2026: «подсказка
 * показывается ДО того, как человек хоть раз нажал „Готово“ — куча подобных мест»).
 * Тело шторки собирается ОДИН раз, а «Готово» живёт в её ШАПКЕ — поэтому признак ездит
 * сигналом: шапка на отказе бьёт `fire()`, страница взводит свой признак и с этой
 * секунды считает подсказки по живым значениям (исправил поле — подсказка гаснет,
 * испортил снова — появляется). Страницу закрыли — сигнал ушёл вместе с ней, и
 * следующее открытие снова чистое.
 */
interface WizTriedSignal {
  /** Отказ «Готово»: страница показывает подсказки-ошибки с этой секунды. */
  fire: () => void;
  /** Подписчик — сама страница; `null` снимает подписку при уходе. */
  bind: (fn: (() => void) | null) => void;
}

function wizTriedSignal(): WizTriedSignal {
  let hit: (() => void) | null = null;
  return {
    fire: () => hit?.(),
    bind: (fn) => {
      hit = fn;
    },
  };
}

/**
 * ПОДСКАЗКА-ОШИБКА ЗАНЯТОГО ХВОСТА рядом с обычными «заполните это поле» (К18.3 этап 1
 * п.2 г): нативу она уезжает ключом `invalid` того же поля, что и в браузере.
 */
function linkWarnCtx(
  base: Record<string, string> | undefined,
  extra: Record<string, string> | undefined,
): { warnings?: Record<string, string> } {
  return extra ? { warnings: { ...(base ?? {}), ...extra } } : {};
}

/**
 * СЛОВА СОСТАВА ПИСЬМА («Фото, подпись, кнопки (2)») — ОДИН словарь на все три места, где
 * строка «Сообщение» показывает состав: веб-карточка шага, родной мастер и родная форма
 * рассылки (И11.3, владелец 04.09.2026). Складывает строку чистая `messageSummary`.
 */
function useMsgSummaryDict(): MessageSummaryDict {
  const t = useTranslations('cabinet.broadcasts');
  return useMemo<MessageSummaryDict>(
    () => ({
      empty: t('msgNotFilled'),
      media: {
        photo: t('msgPhoto'),
        video: t('msgVideo'),
        animation: t('msgAnimation'),
        audio: t('msgAudio'),
        voice: t('msgVoice'),
        round: t('msgRound'),
        document: t('msgDocument'),
      },
      album: (n) => t('msgAlbum', { n }),
      text: t('msgText'),
      caption: t('msgCaption'),
      buttons: (n) => t('msgButtons', { n }),
    }),
    [t],
  );
}

/**
 * КУСКИ ФОРМЫ ШАГА (сегменты). Часть полей анкеты рисуется СВОИМИ контролами кабинета
 * (кодовые слова — кружками, публикации — сеткой снимков, картинка — заливкой файла,
 * отбор аудитории, день и час), остальные — кусками общего рендерера ParamsForm;
 * порядок полей авторский (keepOrder).
 *
 * INVARIANT(scriptera): рендерер полей анкеты ОДИН на все её дома — страницу шага и
 * страницу элемента шага-списка (ТЗ 03.09.2026 В1). Причина: скопировать раскладку «для
 * страницы входа» значило бы получить второй вид тех же полей — кодовые слова снова
 * стали бы строкой «через запятую», которую владелец забраковал (2026-08-06).
 * Канон: docs/MESSAGE-COMPOSER-PLAN.md В, docs/concepts/FORM-ENGINE-PLAN.md §0.
 */
type WizSeg =
  | { k: 'params'; list: CatalogParam[] }
  | { k: 'channel'; p: CatalogParam }
  | { k: 'linkLabel'; p: CatalogParam }
  | { k: 'igPosts'; p: CatalogParam }
  | { k: 'words'; p: CatalogParam }
  | { k: 'image'; p: CatalogParam }
  | { k: 'gift'; p: CatalogParam }
  /** «Ссылка в Telegram-бот» (UT 14.10 п.6): выбор Telegram-бота с «Добавить бота» и
   *  готовая ссылка с копированием. */
  | { k: 'tgBot'; p: CatalogParam }
  | { k: 'tgLink'; p: CatalogParam }
  | { k: 'replies'; p: CatalogParam }
  | { k: 'repliesRow'; p: CatalogParam }
  // Контролы мастера РАССЫЛКИ (ТЗ 16): набор фильтров аудитории, день+час старта,
  // картинка и кнопки сообщения. Все они уже существуют в кабинете — мастер берёт
  // их как есть, своих копий не рисует.
  | { k: 'audience'; p: CatalogParam }
  | { k: 'runAt'; p: CatalogParam }
  /** «Отправить» рассылки — ОДНА строка со значением (И10 п.6): лист вида и родные окна
   *  дня и часа за ней; отдельного поля «День и час» на экране больше нет. */
  | { k: 'when'; p: CatalogParam }
  /** Абзац-инструкция анкеты (`type: 'notice'`, «Добавьте бота администратором канала…»):
   *  серым текстом между карточками, как в iOS (ТЗ WEB-PARITY-3 М67, кадр W32). Рендерер
   *  полей такие плашки прячет (сырые notice n8n — не интерфейс), у анкеты они свои. */
  | { k: 'notice'; p: CatalogParam }
  | { k: 'divider'; p: CatalogParam; title: string; show?: Record<string, unknown[]> }
  /** Конструктор фразы «Оффер» (быстрый запуск D8, §2.6): предложение с пропусками-пилюлями. */
  | { k: 'offer'; p: CatalogParam };

function buildWizSegs(
  list: CatalogParam[],
  groupBefore: WizardStep['groupBefore'],
  channel?: BotAccountChannel,
): WizSeg[] {
  const segs: WizSeg[] = [];
  for (const p of list) {
    // Черта с подписью блока перед полем (step.groupBefore): ветки «если нет
    // подписки» и «если есть подписка» иначе читаются одной кашей.
    const gt = groupBefore?.[p.name];
    if (gt !== undefined) {
      if (typeof gt === 'string') segs.push({ k: 'divider', p, title: gt });
      else segs.push({ k: 'divider', p, title: gt.title, show: gt.show });
    }
    if (p.type === 'offer') {
      segs.push({ k: 'offer', p });
    } else if (p.name === WIZ_GIFT_TG_LINK) {
      // Готовая ссылка — строкой с копированием (не серой плашкой notice).
      segs.push({ k: 'tgLink', p });
    } else if (p.name === WIZ_GIFT_TG_BOT) {
      segs.push({ k: 'tgBot', p });
    } else if ((p.type ?? 'string') === 'notice') {
      segs.push({ k: 'notice', p });
    } else if (channel === 'telegram' && p.name === 'channelLink') {
      segs.push({ k: 'channel', p });
    } else if (channel === 'telegram' && p.name === 'linkLabel') {
      segs.push({ k: 'linkLabel', p });
    } else if (isWizWordsField(p.name)) {
      // Кодовые слова — ЧИПАМИ, как в «Старте» (владелец 2026-08-06: «через запятую —
      // отстой»): каждое слово плашкой, добавление по Enter. Наружу уходит та же
      // строка через запятую — формат ответов анкеты не меняется. Полей слов у анкеты
      // бывает несколько (свои у каждого входа, §5 плана входов) — контрол один.
      segs.push({ k: 'words', p });
    } else if (channel === 'instagram' && p.name === 'mediaIds') {
      // Выбор публикаций (05.08) — своим контролом: превью выбранных + лист с фото.
      segs.push({ k: 'igPosts', p });
    } else if (p.name === 'welcomeImage' || p.name.startsWith('msgPhoto')) {
      // Картинка — ЗАГРУЗКОЙ файла, а не адресом (владелец 2026-08-06).
      segs.push({ k: 'image', p });
    } else if ((p.typeOptions as { wizControl?: unknown } | undefined)?.wizControl === 'when') {
      // «Когда отправлять» рассылки — своя строка (И10 п.6). Помечен ПАРАМЕТР, а не имя:
      // ответ `startMode` есть и у мастера запуска Инстаграма, там он про другое.
      segs.push({ k: 'when', p });
    } else if (p.name === 'audience') {
      // Условия «кому» — ОДНА строка «Кто получит», за ней общий экран живых людей
      // (И3). У письма цепочки та же строка стоит на его странице (kind 'audience'),
      // поля отбора в форме не рисуются вовсе.
      segs.push({ k: 'audience', p });
    } else if (p.name === 'runAt') {
      segs.push({ k: 'runAt', p });
    } else if (p.name.startsWith('msgBtns')) {
      // Кнопки письма — тем же списком полей, что варианты ответа: по строке на
      // кнопку, «Добавить» снизу. Второго списка-контрола у мастеров нет.
      segs.push({ k: 'replies', p });
    } else if (p.name === WIZ_GIFT_FILE) {
      // Файл «вкусняшки» — своим контролом: заливка тем же путём, что у редактора
      // схемы, с пределами и форматами канала.
      segs.push({ k: 'gift', p });
    } else if (p.name === 'commentTexts') {
      // Варианты ответа — СТРОКОЙ СО СЧЁТОМ, сам список — в шторке (ТЗ USERTEST-1014 п.7, владелец 14.10.2026;
      // прежде список полей стоял прямо на шаге, владелец 2026-08-06).
      segs.push({ k: 'repliesRow', p });
    } else {
      const last = segs[segs.length - 1];
      if (last?.k === 'params') last.list.push(p);
      else segs.push({ k: 'params', list: [p] });
    }
  }
  return segs;
}

/** Отрисовка кусков формы. Значения и запись наружу — пропами: тем же куском живут и
 *  страница шага (ответы анкеты), и страница элемента шага-списка (свои значения). */
function WizSegs({
  segs,
  params,
  keyPfx,
  values,
  warnings,
  dict,
  channel,
  botHandle,
  chain,
  entry,
  wiz,
  stepKey,
  tgBot,
  giftChannel,
  onChange,
  onSet,
}: {
  /** Канал файла подарка, если он не канал аккаунта (`TemplateWizardDef.giftChannel`). */
  giftChannel?: 'telegram' | 'instagram';
  segs: WizSeg[];
  /** Все поля этого куска — по ним считается видимость (те же правила displayOptions). */
  params: CatalogParam[];
  keyPfx: string;
  values: Record<string, unknown>;
  warnings?: Record<string, string>;
  dict: ReturnType<typeof useParamsFormDict>;
  channel?: BotAccountChannel;
  /** @логин бота — для «готовой ссылки» поля «Подпись источника». */
  botHandle: string | null;
  /** Анкета цепочки: ссылка ведёт в цепочку, а не в бота (одно слово подводки, К17.4 п.6). */
  chain?: boolean;
  /** Страница элемента шага-списка (вход/триггер), а не сам шаг: у поля слов там своё
   *  пояснение — описание параметра (кадр iOS W33-tg-trigger-keyword «Можно указать несколько
   *  слов…»), на шаге — wizWordsHint (кадр W13-igkw-step4-trigger «Введите слово и нажмите
   *  Enter»; ТЗ WEB-CAUSES О10). */
  entry?: boolean;
  /** Где мы — для журнала клиента (DIAGNOSTICS-CANON): id анкеты и ключ шага. */
  wiz?: string;
  stepKey?: string;
  /** «Ссылка в Telegram-бот» (UT 14.10 п.6): @логин выбранного Telegram-бота для готовой
   *  ссылки и строка «Добавить бота» (та же стопка подключения, сразу форма Telegram). */
  tgBot?: { handle: string | null; addLabel: string; onAdd: () => void };
  /** Полный набор значений после правки поля рендерером. */
  onChange: (values: Record<string, unknown>) => void;
  /** Правка ОДНОГО поля своим контролом мастера. */
  onSet: (name: string, value: unknown) => void;
}) {
  const ta = useTranslations('cabinet.automations');
  const ts = useTranslations('cabinet.scenario');
  const visibleNames = useMemo(
    () => new Set(visibleParams(params, values).map((p) => p.name)),
    [params, values],
  );
  const giftRaw = values[WIZ_GIFT_KIND];
  const giftKind: ChatAttachKind =
    giftRaw === 'video' || giftRaw === 'audio' || giftRaw === 'file' ? giftRaw : 'image';
  const loc: WizardLocale = useLocale() === 'en' ? 'en' : 'ru';
  return (
    <>
      {segs.map((s, i) =>
        s.k === 'offer' ? (
          visibleNames.has(s.p.name) ? (
            <Fragment key={`${keyPfx}offer`}>
              <WizOffer
                slots={((s.p.typeOptions as { slots?: OfferSlot[] } | undefined)?.slots ?? [])}
                values={values}
                locale={loc}
                bad={!!warnings?.[s.p.name]}
                onSet={(field, v) => onChange({ ...values, [field]: v })}
              />
              {typeof s.p.description === 'string' && s.p.description ? <FHint>{s.p.description}</FHint> : null}
            </Fragment>
          ) : null
        ) : s.k === 'params' ? (
          // Кусок, у которого ВСЕ поля сейчас скрыты, не рисуем вовсе (владелец 2026-08-06,
          // Ч9): рендерер на пустом наборе печатает «У этого шага нет настроек» — фраза
          // ниоткуда посреди формы.
          s.list.every((p) => !visibleNames.has(p.name)) ? null : (
            <ParamsForm
              key={`${keyPfx}${i}`}
              params={s.list}
              values={values}
              dict={dict}
              keepOrder
              toggleCaps
              fieldWarnings={warnings}
              onChange={onChange}
            />
          )
        ) : !visibleNames.has(s.p.name) ? null : s.k === 'divider' ? (
          // Условная подпись блока: правило то же, что у displayOptions.show полей.
          Object.entries(s.show ?? {}).every(([n, vals]) => vals.includes(values[n])) ? (
            <div className="wiz-div" key={`wiz-div-${s.p.name}`}>
              {s.title ? <span>{s.title}</span> : null}
            </div>
          ) : null
        ) : s.k === 'notice' ? (
          <FHint className="wiz-notice" key={`wiz-notice-${s.p.name}`}>
            {s.p.displayName ?? ''}
          </FHint>
        ) : s.k === 'igPosts' ? (
          <WizIgPosts
            key="wiz-ig-posts"
            credId={typeof values.accountId === 'string' ? values.accountId : null}
            value={typeof values.mediaIds === 'string' ? values.mediaIds : ''}
            onChange={(v) => onSet('mediaIds', v)}
          />
        ) : s.k === 'words' ? (
          // Подпись — над пузырём, как у всех полей (владелец 11.08.2026): FField cap.
          // Имя поля берём у самого параметра: слова бывают свои у каждого входа
          // (messageWords, commentWords…), а контрол и подсказка у них одни.
          <FField
            cap
            key={`wiz-words-${s.p.name}`}
            label={s.p.displayName ?? ''}
            bad={!!warnings?.[s.p.name]}
            hint={entry && s.p.description ? s.p.description : ta('wizWordsHint')}
          >
            <ChipsInput
              values={String(values[s.p.name] ?? '')
                .split(',')
                .map((w) => w.trim())
                .filter(Boolean)}
              onChange={(list) => onSet(s.p.name, list.join(', '))}
              placeholder={s.p.placeholder ?? ''}
              ariaLabel={s.p.displayName ?? ''}
              removeLabel={ts('chatStartWordRemove')}
              addLabel={ts('chatStartWordAdd')}
              invalid={!!warnings?.[s.p.name]}
            />
          </FField>
        ) : s.k === 'replies' ? (
          // Имя поля берём у самого параметра: этим контролом живут и варианты ответа
          // (commentTexts), и кнопки письма рассылки (msgBtns…) — второго списка не заводим.
          <WizReplies
            key={`wiz-replies-${s.p.name}`}
            label={s.p.displayName ?? ''}
            hint={typeof s.p.description === 'string' ? s.p.description : ''}
            value={typeof values[s.p.name] === 'string' ? (values[s.p.name] as string) : ''}
            onChange={(v) => onSet(s.p.name, v)}
          />
        ) : s.k === 'repliesRow' ? (
          <WizRepliesRow
            key={`wiz-replies-row-${s.p.name}`}
            label={s.p.displayName ?? ''}
            hint={typeof s.p.description === 'string' ? s.p.description : ''}
            value={typeof values[s.p.name] === 'string' ? (values[s.p.name] as string) : ''}
            onChange={(v) => onSet(s.p.name, v)}
          />
        ) : s.k === 'audience' ? (
          <WizAudience
            key={`wiz-audience-${s.p.name}`}
            name={s.p.name}
            label={s.p.displayName ?? ''}
            value={typeof values[s.p.name] === 'string' ? (values[s.p.name] as string) : '{}'}
            cred={typeof values.accountId === 'string' ? values.accountId : ''}
            onChange={(v) => onSet(s.p.name, v)}
          />
        ) : s.k === 'when' ? (
          <WizWhen
            key="wiz-when"
            label={s.p.displayName ?? ''}
            mode={values.startMode === 'date' ? 'date' : 'manual'}
            runAt={typeof values.runAt === 'string' ? values.runAt : ''}
            options={((s.p.options as CatalogParamOption[] | undefined) ?? []).map((o) => ({
              value: String(o.value),
              label: o.name,
              ...(o.description ? { hint: o.description } : {}),
            }))}
            bad={!!warnings?.startMode || !!warnings?.runAt}
            onManual={() => onChange({ ...values, startMode: 'manual' })}
            onDate={(runAt, tz) => onChange({ ...values, startMode: 'date', runAt, tz })}
          />
        ) : s.k === 'runAt' ? (
          <WizRunAt
            key="wiz-run-at"
            label={s.p.displayName ?? ''}
            value={typeof values.runAt === 'string' ? values.runAt : ''}
            bad={!!warnings?.runAt}
            onChange={(v, tz) => onChange({ ...values, runAt: v, tz })}
          />
        ) : s.k === 'tgBot' ? (
          // Выбор Telegram-бота — тем же рендерером полей (варианты подставила страница
          // мастера), под ним «Добавить бота» — как «Добавить аккаунт» под пузырём аккаунта.
          <Fragment key="wiz-tg-bot">
            <ParamsForm
              params={[s.p]}
              values={values}
              dict={dict}
              keepOrder
              toggleCaps
              fieldWarnings={warnings}
              onChange={onChange}
            />
            {tgBot ? <FAddBubble label={tgBot.addLabel} onClick={tgBot.onAdd} /> : null}
          </Fragment>
        ) : s.k === 'tgLink' ? (
          // Готовая ссылка — ТОЙ ЖЕ строкой с копированием, что у входа «Переход по ссылке».
          <StartLinkReadyRow
            key="wiz-tg-link"
            botHandle={tgBot?.handle ?? null}
            tail={translitRefCode(wizGiftTgFrom(values))}
            block
            hint=""
          />
        ) : s.k === 'gift' ? (
          <WizFile
            key="wiz-gift"
            channel={giftChannel ?? (channel === 'telegram' ? 'telegram' : 'instagram')}
            kind={giftKind}
            value={
              typeof values[WIZ_GIFT_FILE] === 'string' ? (values[WIZ_GIFT_FILE] as string) : ''
            }
            onChange={(v) => onSet(WIZ_GIFT_FILE, v)}
            warn={warnings?.[WIZ_GIFT_FILE]}
          />
        ) : s.k === 'image' ? (
          // Тем же контролом живут картинка приветствия (welcomeImage) и картинка письма
          // рассылки (msgPhoto…): имя поля берём у параметра.
          <WizImage
            key={`wiz-image-${s.p.name}`}
            channel={channel === 'telegram' ? 'telegram' : 'instagram'}
            label={s.p.displayName ?? ''}
            hint={typeof s.p.description === 'string' ? s.p.description : ''}
            value={typeof values[s.p.name] === 'string' ? (values[s.p.name] as string) : ''}
            onChange={(v) => onSet(s.p.name, v)}
          />
        ) : s.k === 'channel' ? (
          <TgChannelPicker
            key="wiz-channel"
            credId={typeof values.accountId === 'string' ? values.accountId : ''}
            value={typeof values.channelLink === 'string' ? values.channelLink : ''}
            onChange={(v) => onSet('channelLink', v)}
            warn={warnings?.channelLink}
            hint={typeof s.p.description === 'string' ? s.p.description : ''}
            wiz={wiz ?? ''}
            stepKey={stepKey ?? ''}
          />
        ) : (
          <StartLinkFields
            key="wiz-link-label"
            value={typeof values.linkLabel === 'string' ? values.linkLabel : ''}
            onChange={(v) => onSet('linkLabel', v)}
            botHandle={botHandle}
            warn={warnings?.linkLabel}
            // Ссылки из других ботов собираются ПО ЭТОМУ ЖЕ доступу (К17.4 п.6 в).
            credId={typeof values.accountId === 'string' ? values.accountId : ''}
            {...(chain ? { chain: true } : {})}
          />
        ),
      )}
    </>
  );
}

/**
 * СТРАНИЦА ОДНОГО ПИСЬМА ЦЕПОЧКИ — ТРИ СТРОКИ (ТЗ 03.09.2026 В2, В4, карта окон Д2):
 * «Сколько ждать», «Кому слать», «Сообщение». Каждая строка открывает СВОЮ страницу
 * поверх этой (стопка слоёв, NATIVE-SLIDE-PLAN §11 п.8): поля — тем же рендерером, что
 * у всей анкеты, «Сообщение» — ОБЩИМ окном (useMessageComposer), своего редактора
 * письма тут нет.
 *
 * Значения живут СВОИ, пока страница открыта: тело шторки собирается один раз, и читать
 * «живые» ответы панели ему неоткуда — каждая правка тут же уезжает наверх (onPatch).
 */
/**
 * КОГДА ПИСЬМО УЙДЁТ — для часов в углу пузыря (ТЗ И11.7). Назначенное время есть только
 * у рассылки («в назначенный день и час»); у всего остального в углу стоит текущее время.
 * Имена ответов — те же, что читает сама анкета рассылки (`startMode`, `runAt`).
 */
/** «<шаблон> N» из КЭША списка названий (П202): пусто — кэша нет или у анкеты нет «О боте». */
function cachedNumberedTitle(def: TemplateWizardDef, locale: WizardLocale): string {
  const base = wizardAboutTitle(def, locale);
  if (!base) return '';
  const cached = peekPersistent<ScenarioTitlesResp>(SCENARIO_TITLES_KEY);
  if (!cached || !scenarioTitlesOk(cached) || !cached.titles) return '';
  return numberedTitle(base, nextTitleNumber(cached.titles, base));
}

function wizClockAt(values: Record<string, unknown>): string {
  if (values.startMode !== 'date') return '';
  const raw = typeof values.runAt === 'string' ? values.runAt : '';
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) ? raw : '';
}

function WizItemPage({
  list,
  index,
  pageTitle,
  initial,
  dict,
  channel,
  botHandle,
  editDraftId,
  moveUpLabel,
  moveDownLabel,
  onPatch,
  onRemove,
  onMove,
  onLeave,
  removeAsk,
  tried,
  foot,
  phoneWebTitle = false,
  chainButtons = false,
}: {
  list: WizardStepItemList;
  /** Номер письма (1…). */
  index: number;
  /** Заголовок ЭТОЙ страницы («Сообщение 2») — он же подзаголовок окна письма (И11.8). */
  pageTitle: string;
  initial: Record<string, unknown>;
  dict: ReturnType<typeof useParamsFormDict>;
  channel?: BotAccountChannel;
  botHandle: string | null;
  /** Черновик ЖИВОЙ цепочки (правка): с ним экран «Кто получит» считает не по всем
   *  карточкам кабинета, а «среди тех, кто идёт и этого письма ещё не получал» (И2). */
  editDraftId?: string;
  /** ПИСЬМО ЦЕПОЧКИ (BACKLOG п.19 (2)): у кнопки-метки есть «и завершить эту цепочку». */
  chainButtons?: boolean;
  moveUpLabel: string;
  moveDownLabel: string;
  /**
   * Правка страницы уехала наверх. `save` — человек нажал КРУГЛУЮ ГАЛОЧКУ окна
   * «Сообщение» (ТЗ 21.09.2026 раздел 6 п.1). С П132 (модель А) хозяин страницы кладёт
   * в ответы любую правку сразу, и ключ ничего не меняет — оставлен ради сигнатуры.
   */
  onPatch: (patch: Record<string, unknown>, save?: boolean) => void;
  onRemove: () => void;
  /** Переставить письмо на шаг выше (-1) или ниже (+1). */
  onMove: (dir: -1 | 1) => void;
  /** Страницу сняли любым путём («Готово», «×», «‹», клик мимо) — П132 п.3. */
  onLeave?: () => void;
  /** Слова вопроса перед «Удалить» (П132 п.5); null — удалять без вопроса. */
  removeAsk?: () => { title: string; text: string; ok: string } | null;
  /** «Готово» отбито (П132 п.2, К24 п.1): с этой секунды негодная строка подсвечена. */
  tried?: WizTriedSignal;
  /** Широкая «Готово» внизу (телефон в браузере, М35) вместо галочки в шапке. */
  foot?: WizPageFoot;
  /** Заголовок страницы крупно в теле — лист без шапки (М107). */
  phoneWebTitle?: boolean;
}) {
  const tc = useTranslations('cabinet.common');
  const panel = useSlidePanel();
  const openMessage = useMessageComposer();
  // «КОГДА» — БАРАБАНОМ (М111, кадр W56-chain-msg-when): число · единица, «Готово».
  const [wheel, setWheel] = useState<{
    title: string;
    amount: CatalogParam;
    unit: CatalogParam;
  } | null>(null);
  // Телефон в браузере — страницы строк с «Готово» внизу (М35), как у страниц входов.
  const phoneWeb = usePhoneLook();
  const [vals, setVals] = useState(initial);
  const live = useRef(vals);
  live.current = vals;
  // ДО ПЕРВОЙ ПОПЫТКИ «ГОТОВО» СТРАНИЦА ЧИСТАЯ (К24 п.1) — та же механика, что у входов.
  const [triedOn, setTriedOn] = useState(false);
  useEffect(() => {
    if (!tried) return;
    tried.bind(() => setTriedOn(true));
    return () => tried.bind(null);
  }, [tried]);
  // УХОД СО СТРАНИЦЫ — ЕЁ СНЯТИЕ (П132 п.3): уровень шторки снимается любым путём, и
  // тело размонтируется. Двойной прогон эффектов dev-режима (mount → unmount → mount)
  // уходом не считается: решение откладывается на тик и сверяется с признаком «жива».
  const leaveRef = useRef(onLeave);
  useEffect(() => {
    leaveRef.current = onLeave;
  });
  const alive = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      setTimeout(() => {
        if (!alive.current) leaveRef.current?.();
      });
    };
  }, []);
  // «УДАЛИТЬ» С ВОПРОСОМ (П132 п.5): вопрос задаёт штатный ConfirmInline (родное окно в
  // приложении, окошко по центру шторки на сайте); его триггер спрятан, спрашивает
  // красная кнопка страницы через `registerAsk`.
  const askRemove = useRef<(() => void) | null>(null);
  const [ask, setAsk] = useState<{ title: string; text: string; ok: string } | null>(null);
  const tapRemove = (): void => {
    const words = removeAsk?.() ?? null;
    if (!words) {
      onRemove();
      return;
    }
    setAsk(words);
  };
  useEffect(() => {
    if (ask) askRemove.current?.();
  }, [ask]);

  const push = (patch: Record<string, unknown>, save?: boolean): void => {
    setVals((v) => ({ ...v, ...patch }));
    onPatch(patch, save);
  };

  const rows = list.rows?.(vals, index) ?? [];
  const count = list.count(vals);
  const canMove = wizItemCanMove(list);

  /** Строка «Сообщение» — общее окно, «Кому» — общий экран живых людей; остальные
   *  строки — своя страница с полями. */
  const openRow = (row: WizardItemRow): void => {
    if ((row.kind === 'audience' || row.kind === 'conditions') && row.param) {
      const param = row.param;
      openAudienceScreen({
        panel,
        title: row.title,
        value: audienceOfRaw(live.current[param]),
        inherited: row.inherited ?? [],
        // БОТ-ОТПРАВИТЕЛЬ (К27.2 п.5): у цепочки он выбран первым шагом анкеты, и «кому»
        // считается только по его собеседникам — карточка человека одна на весь кабинет.
        ...(typeof live.current.accountId === 'string' && live.current.accountId
          ? { cred: live.current.accountId }
          : {}),
        footer: row.footer ?? [],
        // ЛИСТ УСЛОВИЙ БЕЗ ЭКРАНА ЛЮДЕЙ (И10 п.10): у сообщений цепочки «кому»
        // проверяется в момент отправки, и список подходящих сейчас был бы враньём.
        ...(row.kind === 'conditions'
          ? {
              conditions: true,
              runningNote: !!editDraftId,
              ...(row.only ? { only: row.only } : {}),
            }
          : {}),
        ...(editDraftId && row.setKey ? { set: `chain:${editDraftId}:${row.setKey}` } : {}),
        ...(phoneWeb ? { phoneSheet: true } : {}),
        onChange: (v) => push({ [param]: JSON.stringify(v) }),
      });
      return;
    }
    if (row.kind === 'message' && row.param) {
      const raw = live.current[row.param];
      let value: TgDraft | undefined;
      if (typeof raw === 'string' && raw !== '') {
        try {
          value = toTgDraft(JSON.parse(raw)) ?? undefined;
        } catch {
          value = undefined;
        }
      }
      openMessage({
        ...(value ? { value } : {}),
        title: row.title,
        // ПОДЗАГОЛОВОК — ОТКУДА ПРИШЛИ (И11.8): «Сообщение 2», а не имя самой строки.
        subtitle: pageTitle,
        ...(chainButtons ? { chainButtons: true } : {}),
        // Быстрый запуск D17a: подсказка медиа под письмом, пока вложения нет (`WizardItemRow.hint`).
        ...(row.hint ? { hint: row.hint } : {}),
        // «ОТПРАВИТЬ СЕБЕ» (ТЗ SEND-SELF З-5): письмо цепочки — черновик анкеты и поле письма; бот — из ответов.
        ...(chainButtons
          ? { testSend: { draftId: editDraftId ?? 'new-chain', param: row.param, area: () => live.current } }
          : {}),
        // QUICK-START §11.35 ч.2: каталог сообщений у письма цепочки (подстановки — из ответов быстрого запуска бота).
        ...(chainButtons ? { catalog: { draftId: editDraftId ?? '' } } : {}),
        // ПИСЬМО СОХРАНЯЕТСЯ ПРИ ЛЮБОМ УХОДЕ (ТЗ М2.2, владелец 16.09.2026): черновик
        // ложится в ответ на КАЖДУЮ правку, поэтому галочка, «‹» и свайп дают один
        // результат — набранное остаётся на странице письма.
        //
        // КЛАДЁТ ЖЕ ПИСЬМО В АНКЕТУ КРУГЛАЯ ГАЛОЧКА (ТЗ 21.09.2026 раздел 6 п.1) — её и
        // означает второй ключ `push`. Хозяин страницы (openItemPage) решает, копить
        // правку или подтвердить её сейчас.
        onChange: (draft) => push({ [row.param!]: JSON.stringify(draft) }),
        onSave: (draft) => push({ [row.param!]: JSON.stringify(draft) }, true),
      });
      return;
    }
    const fields = row.fields ?? [];
    if (fields.length === 0) return;
    // «КОГДА» (число + единица) на телефоне — барабан (М111), не два поля.
    if (
      phoneWeb &&
      fields.length === 2 &&
      fields[0]!.type === 'number' &&
      fields[1]!.type === 'options'
    ) {
      setWheel({ title: row.title, amount: fields[0]!, unit: fields[1]! });
      return;
    }
    // Одно текстовое поле («Название», М110): лист-стопка с «×», без подписи над полем.
    const single = phoneWeb && fields.length === 1 && (fields[0]!.type ?? 'string') === 'string';
    panel.open({
      title: row.title,
      sheet: true,
      // «×» у одного поля («Название», М110) — новый лист поверх (ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2). Полушторкой снизу
      // (владелец 30.10: «должна быть полушторка снизу в обеих версиях в обоих местах», «как и название») — как «Когда».
      ...(single ? { lead: 'close' as const, sheetDetent: 0.5 } : {}),
      // Свой ключ окна — значит НОВЫЙ уровень поверх страницы письма (стопка, а не
      // подмена): «‹» возвращает ровно на уровень выше.
      winKey: 'wiz-item-row',
      // Телефон в браузере — «Готово» широкой кнопкой внизу (М35), галочки в шапке нет.
      ...(phoneWeb ? {} : { actionLabel: tc('done'), onAction: () => panel.close() }),
      body: (
        <WizEntryItem
          {...(phoneWeb ? { foot: { label: tc('done'), onClick: () => panel.close() } } : {})}
          {...(single ? { noCap: true } : {})}
          params={fields}
          initial={live.current}
          dict={dict}
          {...(channel ? { channel } : {})}
          botHandle={botHandle}
          onPatch={push}
        />
      ),
    });
  };

  const wheelUnits = wheel ? ((wheel.unit.options as CatalogParamOption[] | undefined) ?? []) : [];
  const wheelMax = wheel
    ? Number((wheel.amount.typeOptions as { maxValue?: unknown } | undefined)?.maxValue ?? 60)
    : 60;
  return (
    <FPane className="wiz-page">
      {phoneWebTitle ? <h2 className="wiz-h1 wiz-item-h1">{pageTitle}</h2> : null}
      {wheel ? (
        <KWheelDialog
          open
          // Телефон в браузере — листом «Когда» (кадр iOS W56-chain-msg-when); иначе окно по центру.
          sheet={phoneWebTitle}
          closeLabel={tc('close')}
          // Заголовок листа — имя строки («Когда», кадр iOS W56-chain-msg-when), не имя письма.
          title={phoneWebTitle ? wheel.title : (wheel.amount.displayName ?? '')}
          doneLabel={tc('done')}
          columns={[
            {
              key: 'n',
              value: String(vals[wheel.amount.name] ?? wheel.amount.default ?? 0),
              items: Array.from({ length: Math.min(wheelMax, 365) + 1 }, (_, i) => ({
                value: String(i),
                label: String(i),
              })),
            },
            {
              key: 'u',
              value: String(vals[wheel.unit.name] ?? wheel.unit.default ?? ''),
              items: wheelUnits.map((o) => ({ value: String(o.value), label: o.name })),
            },
          ]}
          onDone={([n, u]) => {
            const patch: Record<string, unknown> = {};
            if (n !== undefined) patch[wheel.amount.name] = Number(n);
            if (u !== undefined) patch[wheel.unit.name] = u;
            push(patch);
            setWheel(null);
          }}
          onClose={() => setWheel(null)}
        />
      ) : null}
      <FNotifyList
        items={rows.map((row) => ({
          key: row.key,
          label: row.title,
          // У строки «Кому» сводка ЖИВАЯ: условия словами с именами ботов и писем плюс
          // счётчик — те же, что видит человек на экране «Кто получит» (И3).
          summary:
            row.kind === 'audience' && row.param ? (
              <AudienceSummary
                value={audienceOfRaw(vals[row.param])}
                emptyWords={row.empty ?? row.value}
                inherited={row.inherited ?? []}
                {...(editDraftId && row.setKey
                  ? { set: `chain:${editDraftId}:${row.setKey}` }
                  : {})}
                {...(typeof vals.accountId === 'string' && vals.accountId
                  ? { cred: vals.accountId }
                  : {})}
                countKey={row.param}
              />
            ) : (
              row.value
            ),
          // У первого письма паузы не бывает: строка стоит, но открывать нечего.
          chevron: !(row.kind === 'fields' && (row.fields ?? []).length === 0),
          onOpen: () => openRow(row),
        }))}
      />
      {/* «ГОТОВО» ОТБИТО (П132 п.2): причина под строками — той же красной строкой, что
          подсказка у полей входа. До первой попытки страница чистая (К24 п.1). */}
      {triedOn
        ? rows
            .filter((row) => row.invalid)
            .map((row) => <FError key={`bad-${row.key}`}>{`${row.title}: ${row.value}`}</FError>)
        : null}
      {/* ПОРЯДОК ПИСЕМ — ПОРЯДОК ОТПРАВКИ, и меняется он отсюда: письмо переезжает на
          шаг выше или ниже, а его СТАБИЛЬНЫЙ ключ едет вместе с ним (журнал «уже
          получал» перестановку переживает). Кнопки — из набора. У входов цепочки
          перестановки НЕТ вовсе (canMove:false): порядок входов ничего не значит. */}
      {phoneWebTitle ? (
        // ТЕЛЕФОН (М107, кадр W56): «Удалить» — красной текстовой ссылкой по центру; кнопок
        // перестановки нет (порядок — в режиме «Изм.» списка, вопрос владельцу В-М7).
        count > 1 || list.fields ? (
          <KBareButton className="wiz-item-del" onClick={tapRemove}>
            {list.itemRemove}
          </KBareButton>
        ) : null
      ) : (
        <div className="wiz-reps-acts">
          {canMove && index > 1 && (
            <KButton size="sm" onClick={() => onMove(-1)}>
              {moveUpLabel}
            </KButton>
          )}
          {canMove && index < count && (
            <KButton size="sm" onClick={() => onMove(1)}>
              {moveDownLabel}
            </KButton>
          )}
          {(count > 1 || list.fields) && (
            <KButton size="sm" tone="danger" onClick={tapRemove}>
              {list.itemRemove}
            </KButton>
          )}
        </div>
      )}
      {ask ? (
        <span style={{ display: 'none' }}>
          <ConfirmInline
            label=""
            question={ask.title}
            note={ask.text}
            confirmLabel={ask.ok}
            windowOnPage
            onConfirm={() => {
              setAsk(null);
              onRemove();
            }}
            onCancel={() => setAsk(null)}
            registerAsk={(fn) => {
              askRemove.current = fn;
            }}
          />
        </span>
      ) : null}
      {foot ? <WizPageFootBar foot={foot} /> : null}
    </FPane>
  );
}

/**
 * СТРАНИЦА ОДНОГО ЭЛЕМЕНТА ШАГА-СПИСКА (ТЗ 03.09.2026 В1, Д2): те же поля тем же
 * рендерером, «Готово» в шапке шторки, «Удалить вход» внизу красным. Значения живут
 * СВОИ, пока страница открыта: тело шторки собирается один раз, и читать «живые» ответы
 * панели ему неоткуда — каждая правка тут же уезжает наверх (onPatch).
 */
function WizEntryItem({
  params,
  initial,
  dict,
  channel,
  botHandle,
  chain,
  removeLabel,
  warnFor,
  tried,
  onPatch,
  onRemove,
  foot,
  noCap = false,
  noFocus = false,
}: {
  params: CatalogParam[];
  initial: Record<string, unknown>;
  dict: ReturnType<typeof useParamsFormDict>;
  channel?: BotAccountChannel;
  botHandle: string | null;
  /** Анкета цепочки (К17.4 п.6): подводка ссылки говорит «попадёт в эту цепочку». */
  chain?: boolean;
  /** Кнопки удаления нет вовсе, когда страница не про элемент списка (строка письма). */
  removeLabel?: string;
  /** Подсказки-ошибки ПО ЖИВЫМ значениям страницы (занятый хвост ссылки, К18.3 этап 1
   *  п.2 г): страница считает их сама — значения полей живут здесь. */
  warnFor?: (values: Record<string, unknown>) => Record<string, string> | undefined;
  /** Сигнал «жали „Готово“, и оно отбито» (К24 п.1): до него подсказок-ошибок нет. */
  tried?: WizTriedSignal;
  onPatch: (patch: Record<string, unknown>) => void;
  onRemove?: () => void;
  /** Широкая «Готово» внизу страницы (телефон в браузере, М35/М72) вместо галочки в шапке. */
  foot?: WizPageFoot;
  /** Без подписи над единственным полем (М110, кадр W56-chain-msg-name): имя поля уже в шапке. */
  noCap?: boolean;
  /** Курсор сам не ставится (UT 14.10 п.28, `pageNoFocus` списка): страница открывается целиком. */
  noFocus?: boolean;
}) {
  const [vals, setVals] = useState(initial);
  // КУРСОР СРАЗУ В ПЕРВОМ ПОЛЕ (М38, кадр iOS W33-tg-trigger-keyword): после выезда листа.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!foot || noFocus) return;
    const t = window.setTimeout(() => {
      const el = rootRef.current?.querySelector<HTMLElement>('input:not([type=hidden]), textarea');
      el?.focus();
    }, 380);
    return () => window.clearTimeout(t);
  }, [foot, noFocus]);
  // ДО ПЕРВОЙ ПОПЫТКИ «ГОТОВО» СТРАНИЦА ЧИСТАЯ (К24 п.1): красных рамок и подсказок нет,
  // хотя `warnFor` считается и решает, пускать ли «Готово». После отказа признак стоит до
  // ухода со страницы, а сами подсказки пересчитываются по живым значениям.
  const [triedOn, setTriedOn] = useState(false);
  useEffect(() => {
    if (!tried) return;
    tried.bind(() => setTriedOn(true));
    return () => tried.bind(null);
  }, [tried]);
  // ПОЛЕ «ГОТОВАЯ ССЫЛКА» — ТОЛЬКО ДЛЯ РОДНОГО ЭКРАНА (К16.0 п.6): в браузере ту же
  // ссылку с копированием рисует StartLinkFields, и вторая серая строка под ним была бы
  // повтором. Фильтруем по имени в ОДНОМ месте — здесь строится любая страница входа.
  const own = useMemo(() => params.filter((p) => p.name !== WIZ_LINK_READY_FIELD), [params]);
  const segs = useMemo(() => buildWizSegs(own, undefined, channel), [own, channel]);
  // Наверх уезжают ТОЛЬКО поля этого входа: страница анкеты живёт своей жизнью, и
  // отдавать ей целиком свой снимок ответов нельзя — он затёр бы чужие правки.
  const push = (next: Record<string, unknown>) => {
    setVals(next);
    onPatch(Object.fromEntries(own.map((p) => [p.name, next[p.name]])));
  };
  return (
    <FPane>
      <div ref={rootRef} className={`wiz-entry${noCap ? ' wiz-entry--nocap' : ''}`}>
        <FCard className="wiz-card">
          <WizSegs
            segs={segs}
            params={own}
            keyPfx="wiz-item"
            entry
            values={vals}
            {...(triedOn && warnFor?.(vals) ? { warnings: warnFor(vals)! } : {})}
            dict={dict}
            channel={channel}
            botHandle={botHandle}
            {...(chain ? { chain: true } : {})}
            onChange={push}
            onSet={(name, value) => push({ ...vals, [name]: value })}
          />
        </FCard>
        {/* «Удалить вход» — внизу страницы красным (канон родных экранов): удаление входа
          это про всю страницу, а не про одно поле. Кнопка из набора, своей разметки нет. */}
        {removeLabel && onRemove ? (
          <div className="wiz-reps-acts">
            <KButton size="sm" tone="danger" onClick={onRemove}>
              {removeLabel}
            </KButton>
          </div>
        ) : null}
      </div>
      {foot ? <WizPageFootBar foot={foot} /> : null}
    </FPane>
  );
}

/** Низ страницы элемента на телефоне (М35): одна широкая «Готово», как в iOS. */
type WizPageFoot = { label: string; onClick: () => void };
function WizPageFootBar({ foot }: { foot: WizPageFoot }) {
  return (
    <div className="wiz-page-foot">
      <KButton block tone="primary" onClick={foot.onClick}>
        {foot.label}
      </KButton>
    </div>
  );
}

// Брендовые имена сетей в общем списке аккаунтов семейства — не переводятся.
const CHANNEL_BRAND: Record<string, string> = { telegram: 'Telegram', instagram: 'Instagram' };

/** ПОЛУЛИСТ ЦЕПОЧКИ НА ТЕЛЕФОНЕ (круг 3, П7): меню триггеров, список ботов, страница
 *  триггера, «Условия» — кромка листа на 421 из 874 (кадры iOS W53-chain-*, W56-chain-msg-
 *  conditions), т.е. 52 % экрана снизу. Одно число на все листы шага. */
const WIZ_HALF_SHEET = 0.52;

/** ПОТОК «ДОБАВИТЬ ТРИГГЕР» — ТРИ УРОВНЯ СТОПКОЙ, У КАЖДОГО СВОЁ ИМЯ (ТЗ «Причины» П1, 05.10.2026;
 *  кадры iOS W53-chain-trigger-menu → -passed-pick → -passed-result): меню видов, список ботов и
 *  страница триггера ложатся друг на друга, «‹» возвращает на уровень ниже. Прежде у всех троих
 *  было одно имя `wiz-item`, и каждый следующий подменял предыдущий на месте — без хода, а «‹»
 *  уносил весь полулист (М23). Имена нужны и для правки на месте (update по имени), и чтобы
 *  «Готово» снимало поток целиком (`closeFrom`), как `wizFlowClose` родной шторки потока. */
const WIZ_KINDS = 'wiz-kinds';
const WIZ_PICK = 'wiz-pick';
const WIZ_ITEM = 'wiz-item';
const WIZ_FLOW = [WIZ_KINDS, WIZ_PICK, WIZ_ITEM] as const;

/** «Ничего не оставляем в списке принудительно» — пустой `keep` сводов ботов и меток.
 *  Константа, а не литерал: новый массив на каждый рендер перезапускал бы их памятки. */
const NO_KEEP_OPTS: readonly { value: string; label: string }[] = [];
const NO_KEEP: string[] = [];

/** Приставка ключа строки на странице ВЫБОРА шага-списка (боты входа цепочки, К5.1):
 *  по ней тап `wizItemRowTap` отличается от строки страницы письма. */
const ITEM_PICK_PFX = 'pick:';

/**
 * КНОПКА «ПРАВИТЬ» У ШАГА-СПИСКА МАСТЕРА (К16.1 П6, владелец 06.09.2026: «справа сверху
 * сделать ещё стеклянную кнопку „изменить“, чтобы кто не прошарил про смахивание —
 * нажал, и у всех появились кнопки удалить»).
 *
 * Вид — тот же стеклянный кружок, что у крестика закрытия и кнопки-телефона мастера
 * (`.dr-act.wiz-sec`), только со СЛОВОМ внутри: «Править» в покое, «Готово» в режиме
 * правки. Своей разметки кнопки тут нет — нажимаемое место со своим видом это
 * KBareButton (CLAUDE.md, «сам контрол — тоже компонент»).
 *
 * Место: справа НАД списком. Заголовок шага в браузере живёт в шапке панели-хозяина, и
 * своей строки заголовка у тела мастера нет — поэтому «справа сверху» это правый край
 * первой строки списка, а не чужая шапка.
 */
function WizListEdit({
  on,
  onToggle,
  editLabel,
  doneLabel,
  disabled,
}: {
  on: boolean;
  onToggle: () => void;
  editLabel: string;
  doneLabel: string;
  /** Список пуст — кнопка на месте, но серая и неактивная (П10 п.1, канон «Часов»). */
  disabled?: boolean;
}) {
  return (
    <div className="wiz-list-edit">
      <KBareButton
        className="dr-act wiz-sec wiz-list-edit-btn"
        onClick={onToggle}
        disabled={!!disabled}
      >
        {on ? doneLabel : editLabel}
      </KBareButton>
    </div>
  );
}

/** Кнопка-телефон превью — один силуэт в шапке хоста (создание) и в строке правки. */
export function WizPhoneIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      {/* Знак системного «iphone» (WizardScreen.previewBtn, кадры iOS W11–W18): корпус и
          островок сверху, без стрелки. */}
      <rect x="6.5" y="1.5" width="11" height="21" rx="2.8" />
      <path d="M10.5 4.5h3" />
    </svg>
  );
}

/** Ряд сетки публикаций для родного листа: только то, что рисует натив. */
interface NativePost {
  id: string;
  thumb: string;
  video: boolean;
}

export function TemplateWizardPanel({
  def,
  templates,
  title,
  onDone,
  onCreated,
  onSaved,
  edit,
  header,
  menu,
  hosted,
  onNativeMode,
  dirtyRef,
  preset,
  quickResume,
}: {
  def: TemplateWizardDef;
  /** id шаблонов bot_templates по слагу анкеты (режим СОЗДАНИЯ): у семейства «один
   *  мастер на обе сети» здесь карта всех доступных сестёр — выбор аккаунта решает,
   *  из какого шаблона собирать. Не нужна при edit. */
  templates?: Record<string, string>;
  /** Имя шаблона для заголовка шага «Знакомство» в теле (6.0 п.5, кадр iOS W10: имя
   *  шаблона 28 над подводкой); нет — имя шага. */
  title?: string;
  onDone: () => void;
  /** Создание удалось — чем закрыть поверхность (нет — тем же onDone). Рассылка в браузере
   *  снимает ВСЮ стопку (лист «Рассылки» под формой тоже): после «Создать» человек видит
   *  «Боты», как в приложении (кадр iOS R05-broadcast-created). «×» формы по-прежнему
   *  снимает один слой — onDone. */
  onCreated?: () => void | Promise<void>;
  /** Правка записана финальным ✓ — чем закрыть поверхность (нет — тем же onDone). Рассылка в браузере уходит в список
   *  «Рассылки» (APP-CLAIMS-1020 ред. 6, П16/П26.2); «×» правки по-прежнему — onDone. */
  onSaved?: () => void;
  /** ГОТОВЫЕ ОТВЕТЫ СОЗДАНИЯ поверх умолчаний (UT 14.10.2026 п.10: «Настроить
   *  Telegram-бота» из предупреждения карточки — аккаунт и запуск «Переход по ссылке» на
   *  метку уже стоят). В правке не читается. */
  preset?: Record<string, unknown>;
  /** ПРОДОЛЖЕНИЕ НЕЗАКОНЧЕННОЙ ФОРМЫ БЫСТРОГО ЗАПУСКА (QUICK-START FIX-1 §5.3): id заполнения и шаг, где человек
   *  остановился; ответы черновика приходят `preset`. Нет — новая анкета со своим id. */
  quickResume?: { qsId: string; step: string | null };
  /** Режим «Настроить» (FORM-WIZARD Ф1): правка сценария, рождённого мастером. */
  edit?: {
    draftId: string;
    baseVersion: number;
    initial: Record<string, unknown>;
    /** Язык, на котором бота СОЗДАЛИ (запечён в черновике): им берутся заготовки реплик
     *  и превью чата. Смена языка кабинета переводит подписи анкеты, но НЕ тексты уже
     *  созданного бота (ЖЕЛЕЗНОЕ владельца 2026-08-06) — их пересобирает сервер тем же
     *  языком. Нет записи (черновик до этой правки) — считаем русским. */
    locale?: WizardLocale;
    /** Название черновика (APP-CLAIMS-1020 ред. 8, Р17): у рассылки — значение поля «Название». */
    title?: string;
    /** Цепочка: сколько людей в ней сейчас идут или ждут (MSG-VERIFY-FIX В) — ответ `GET drafts/<id>`. */
    chainRunning?: number;
    onSaved: (draft: DraftSnapshot) => void;
  };
  /** Шапка панели-хоста (режим создания, владелец 05.08 №3): update ведёт серую строку
   *  «Шаг N из M» под заголовком (кнопка-телефон живёт в нижней планке самого мастера)
   *  шапки (каждый рендер — превью зависит от текущих ответов). Нет пропа (правка на
   *  странице сценария) — счётчик и телефон рисуются своей строкой сверху. */
  header?: {
    /** `progress` — доля пройденных шагов для кольца у «Шаг N из M» (М3); `headerRight` —
     *  правый слот шапки («Изм./Готово» шага-списка на телефоне, М33). */
    update: (patch: { subtitle?: string; progress?: number; headerRight?: ReactNode }) => void;
    /** Живой обработчик кнопки-телефона шапки (превью строится из текущих ответов).
     *  `null` — НА ЭТОМ ШАГЕ КНОПКИ НЕТ ВОВСЕ (К19.1 п.9, шаг «Что запускает»): хозяин
     *  шапки её убирает, а на следующем шаге ставит обратно. */
    setPreview: (fn: (() => void) | null) => void;
  };
  /** Кнопки хозяина В СТРОКУ с телефоном (правая панель быстрой настройки, владелец
   *  05.08: «три точки рядом с перевёрнутым экраном») — рисуются в .wiz-btns. */
  menu?: ReactNode;
  /** Тело анкеты живёт СКРЫТЫМ на странице, а не отдельным слоем (родной мастер, П-23):
   *  хозяин — обычный кусок страницы, поэтому закрывать его после создания можно и в
   *  приложении (нативных слоёв это не трогает). Канон: docs/NATIVE-WIZARD-PLAN.md. */
  hosted?: boolean;
  /** Кто рисует анкету: `on` — родной экран приложения (своей формы на странице нет),
   *  `ready` — сборку уже опросили (до этого `on` ещё ничего не значит: наличие методов
   *  моста известно только со второго рендера). Хозяину это нужно, чтобы на запасном
   *  пути открыть прежний веб-слой. */
  /** Родной путь взять анкету НЕ МОЖЕТ, потому что у канала НЕТ подключённого аккаунта
   *  (accNone): вместе с `on:false` уезжает готовая строка веба — та же, что стоит в
   *  плашке «Подключите аккаунт» веб-анкеты. Хозяин показывает её РОДНЫМ окном и ведёт
   *  человека в «Подключения», а не открывает вместо родной анкеты веб-страницу
   *  (docs/NATIVE-TABS-PLAN.md, волна 3: веб-ветки мастера в приложении нет). */
  onNativeMode?: (on: boolean, ready: boolean, blocked?: { text: string }) => void;
  /** Признак «есть несохранённый ввод» для хозяина слоя (ТЗ «Причины» П5): по нему «×»,
   *  смахивание и Escape спрашивают «Отменить создание?» только при вводе. Только создание. */
  dirtyRef?: { current: boolean };
}) {
  const ta = useTranslations('cabinet.automations');
  const tBill = useTranslations('cabinet.billing');
  const proLock = useProLocked();
  const ts = useTranslations('cabinet.scenario');
  const tBc = useTranslations('cabinet.broadcasts');
  // Слова для родного экрана мастера: в Swift переводимых строк не бывает.
  const tc = useTranslations('cabinet');
  const tOpt = useTranslations('cabinet.ai');
  const locale = useLocale();
  const loc: WizardLocale = locale === 'en' ? 'en' : 'ru';
  // Язык ТЕКСТОВ БОТА: при создании — язык кабинета сейчас, при правке — запечённый при
  // создании. Подписи и подсказки самой анкеты живут по loc и меняются языком кабинета
  // живьём; реплики бота от этого не зависят.
  const botLoc: WizardLocale = edit ? (edit.locale ?? 'ru') : loc;
  // Семейство «один мастер на обе сети»: при создании — все сёстры, чей шаблон есть в
  // каталоге; при правке анкета фиксирована (сервер пересобирает по сохранённому слагу,
  // сменить сеть у живого сценария нельзя).
  // Анкета открытой карточки — ПЕРВОЙ: автовыбор аккаунта (первый в списке) обязан
  // попасть в сеть карточки, а не в сестру по семейству.
  const siblings = useMemo(() => {
    if (edit) return [def];
    const fam = wizardSiblings(def).filter((d) => !!templates?.[d.id]);
    return [def, ...fam.filter((d) => d.id !== def.id)];
  }, [def, edit, templates]);
  const merged = siblings.length > 1;
  // Правка — поверх дефолтов (новое поле анкеты у старого сценария получает дефолт), а
  // черновик со СТАРЫМ видом ответов чинит анкета своей дверью (`adoptAnswers`, К23.3
  // этап 1 п.1): человек видит свои тексты в полях, назад пишется нынешний вид.
  const [answers, setAnswers] = useState<Record<string, unknown>>(() => {
    // НОВАЯ ЦЕПОЧКА: у первого сообщения название «Сообщение 1» стоит значением (П132
    // п.7) — как у любого добавленного. Правку не трогаем: у старого черновика с пустым
    // названием остаётся прежняя подстановка.
    const start = edit
      ? { ...wizardDefaults(def, botLoc), ...edit.initial }
      : {
          ...wizardDefaults(def, botLoc),
          ...(def.id === CHAIN_WIZARD_ID ? chainStartAnswers(botLoc) : {}),
          // НАЗВАНИЕ С НОМЕРОМ (П189): «<шаблон> N» стоит СРАЗУ, если список названий уже в
          // кэше (его греет «+» и тап по карточке шаблона, П202); иначе поле пустое с
          // подсказкой-именем шаблона, и номер подставит эффект ниже, когда список приедет.
          // Почему сразу (П202, владелец 29.09.2026): в приложении номер, пришедший патчем
          // ПОСЛЕ показа шага, в поле с кареткой не попадает — родное поле с первым
          // ответчиком патч не принимает; wizOpen обязан уехать уже с номером.
          ...(wizardAboutTitle(def, botLoc) ? { botTitle: cachedNumberedTitle(def, botLoc) } : {}),
          ...(preset ?? {}),
        };
    return def.adoptAnswers ? def.adoptAnswers(start) : start;
  });
  // Версия для оптимистической блокировки живёт состоянием: после удачного «Сохранить»
  // панель остаётся открытой (инлайн-режим страницы сценария) — второе сохранение обязано
  // ехать уже с новой версией, не со стартовой из пропсов.
  const [baseVersion, setBaseVersion] = useState(edit?.baseVersion ?? 0);
  // Словарь формы без приписки «обязательное» и без подсказки про {{ … }} (владелец
  // 05.08: «не пиши везде обязательно», «подсказка — удали»): пустоту обязательных
  // подсвечивает честная валидация, выражения в анкете не нужны.
  const baseDict = useParamsFormDict();
  const dict = useMemo(() => ({ ...baseDict, required: '', exprHint: '' }), [baseDict]);
  /**
   * ЗАНЯТЫЕ ХВОСТЫ ССЫЛОК (ответ 409 `code_taken`, К18.3 этап 1 п.2 г; с 13.09.2026 их
   * считает СЕРВЕР при сохранении входов, BACKLOG п.22): тот же хвост уже стоит у ДРУГОЙ
   * ссылки этого телеграм-бота. Вина клиента — показываем подсказкой-ошибкой под полем
   * «Откуда придут люди» (веб — FError, натив — `invalid` поля) и вспышкой.
   */
  const [takenTails, setTakenTails] = useState<readonly string[]>([]);
  // РУЧНАЯ ПРИЧИНА ОТКАЗА (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128): то, чего
  // не выразить через `blocked`/`missing` — кривая ссылка, занятый хвост, отбой рассылки.
  // Стоит красной строкой в теле шага, пока причину не устранили (правка ответов её гасит,
  // applyAnswers); вспышек по таймеру в планке больше нет.
  const [formErr, setFormErr] = useState<string | null>(null);
  // «ПОМЕНЯТЬ ЗАПУСК» / «×» ШТОРКИ «ЗАНЯТО» (П160 п.4) — вернуть человека на шаг запуска,
  // где бы он ни был (при правке запись идёт в фоне, и отказ приходит уже на следующем
  // шаге). Шаги считаются ниже, поэтому рука — через ссылку.
  const backToStartRef = useRef<() => void>(() => undefined);
  const backToStart = useCallback(() => backToStartRef.current(), []);
  const {
    go,
    busy: creating,
    err,
    dupSheet,
  } = useCreateGo(
    onCreated ?? onDone,
    hosted,
    (tails) => {
      setTakenTails(tails);
      setFormErr(ts('chatStartLinkTakenHere'));
    },
    backToStart,
  );
  // ПЕРЕСОХРАНЕНИЕ АНКЕТЫ ТОЖЕ ОТБИВАЕТСЯ ЗАПРЕТОМ (раздел Л правило 4, п.3): человек мог
  // вписать слово, которое на этом аккаунте уже занято. Сервер отвечает тем же 409
  // bot_exists, и человеку показывают ту же шторку, что при создании. Ею же говорит
  // сверка на «Далее» шага запуска (П160 п.2).
  const saveDup = useBotExistsAsk();
  const [saving, setSaving] = useState(false);
  /** Создание формой быстрого запуска идёт (ответ ручки ждём). */
  const [quickBusy, setQuickBusy] = useState(false);
  const [saveErr, setSaveErr] = useState<'conflict' | 'generic' | null>(null);
  // Честная валидация (владелец 05.08): «Дальше» всегда нажимается; после попытки
  // недозаполненные поля шага подсвечиваются (fieldWarnings ParamsForm).
  const [tried, setTried] = useState(false);
  // Тряска незаполненных полей и красная строка вместо счётчика шага (владелец
  // 2026-08-06: «нужно вибрировать, трясти, и под шагом вместо серенького на время
  // показывать красным текст ошибки, потом снова писать номер шага»).
  // Трясётся ТОЛЬКО поле с ошибкой, не весь пузырь (владелец 2026-08-07): счётчик
  // перемонтирует карточку (key), а анимацию несёт сам красный контрол — свои
  // контролы мастера метим классом `wiz-bad`, поля ParamsForm — его pfFieldBad.
  const [shake, setShake] = useState(0);
  /** Снимок из родного выбора фото уезжает в наш бакет — на это время мастер занят. */
  const [photoBusy, setPhotoBusy] = useState(false);
  /** Файл подарка едет в нашу ручку (заливает сам натив, F4) — ряд говорит «Загружаем…». */
  const [fileUp, setFileUp] = useState(false);
  /** Ролик готовится НА ТЕЛЕФОНЕ (ТЗ V2): ключ слова ряда — «Сжимаем…» под вес,
   *  «Обрабатываем…» под формат (те же слова, что у раннера на сайте). Пусто — не занят. */
  const [fileFit, setFileFit] = useState<'' | 'attachCompressing' | 'attachProcessing'>('');
  /**
   * ТЯЖЁЛЫЙ ФАЙЛ В ПРИЛОЖЕНИИ — РОДНОЙ ЛИСТ (F4): та же механика, что на сайте
   * (useWizFileFix), но вопрос показывает натив — веб-шторка легла бы ПОД системной
   * шторкой мастера (канон §11). Сборка метода не знает (старое приложение) — вопрос не
   * показываем вовсе, а говорим ту же правду одной строкой: поле остаётся пустым, и
   * человек выбирает файл заново сам (запрет №8 — старая сборка работает по-старому).
   */
  const nativeFix = useWizFileFix((q) => {
    if (!nativeMethod('wizAsk')) {
      setFormErr(q.text);
      return true;
    }
    void nativeBridge()?.wizAsk?.({
      title: q.title,
      text: q.text,
      actions: q.actions,
      cancel: q.cancel,
      theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
    });
    return true;
  });
  // Подсветка незаполненного НА РОДНОЙ ФОРМЕ рассылки: она стоит одной страницей, и
  // проверять на «Создать» приходится ВСЕ шаги разом — подсветка шага (warnings) для
  // неё мала. Гаснет от любой правки ответов.
  const [bcastWarn, setBcastWarn] = useState<Record<string, string>>({});
  const panel = useSlidePanel();
  const busy = creating || saving || quickBusy;
  // Телефон в браузере — вид шага как в iOS (ТЗ WEB-PARITY-3): строка аккаунта с «›» и лист
  // выбора вместо списка с галочками; на широком экране — прежняя форма (этап 5).
  const wizPhoneWeb = usePhoneLook();

  // Аккаунт первым вопросом (Ф2): варианты поля `accountId` — подключённые доступы
  // каналов семейства (тот же список, что «Подключения» и manual-start). У семейства
  // из двух сетей каждый вариант подписан своей сетью.
  const { creds, ready: credsReady } = useAccountCreds();
  // ПОРЯДОК ДОСТУПОВ — ОБЩЕЕ ПРАВИЛО (lib/wizard-demo-url.ts, О5.10 П2): тем же порядком
  // каталог заранее считает, какая анкета семейства станет активной и чьё лицо встанет в
  // примере. Своего перебора здесь больше нет — разъехались бы прогрев и мастер.
  const accCredsOrdered = useMemo(() => wizardAccountCreds(siblings, creds), [siblings, creds]);
  const accOptions = useMemo(
    () =>
      accCredsOrdered.map((c) => {
        const name = botAccountLabel(c);
        const own = siblings.find(
          (d) => d.accountChannel && ACCOUNT_CRED_TYPE[d.accountChannel] === c.type,
        );
        return {
          value: c.id,
          label:
            merged && own?.accountChannel ? `${CHANNEL_BRAND[own.accountChannel]} · ${name}` : name,
        };
      }),
    [accCredsOrdered, siblings, merged],
  );
  // «ССЫЛКА В TELEGRAM-БОТ» (UT 14.10.2026 п.6): подключённые Telegram-боты — варианты
  // поля «Telegram-бот» у Instagram-мастеров (тот же список доступов, что у «Аккаунта»).
  const tgCreds = useMemo(
    () => creds.filter((c) => c.type === ACCOUNT_CRED_TYPE.telegram),
    [creds],
  );
  const tgOptions = useMemo(
    () => tgCreds.map((c) => ({ value: c.id, label: botAccountLabel(c) })),
    [tgCreds],
  );
  // ЗНАЧКА СЕТИ У «ГЕРОЯ» ПЕРВОГО ШАГА БОЛЬШЕ НЕТ (владелец 02.09.2026, Н12
  // docs/NATIVE-DESIGN-FIXLIST.md: «убрать героя — картинку/шапку Инстаграма сверху:
  // место нужно нашему анимированному примеру-превью шаблона»). Осталась подводка
  // словами, а освободившееся место занимает сцена с примеров работ (wiz-stage ниже).
  const hasAccountField = siblings.some((d) => !!d.accountChannel);
  const accId = typeof answers.accountId === 'string' ? answers.accountId : '';
  // Кред-двойник той же учётки (переподключение) в списке спрятан — но это ТОТ ЖЕ
  // аккаунт, а не удалённый: мастер — полный перепроход анкеты, поэтому двойник молча
  // считается каноническим кредом своей учётки, и человек видит аккаунт выбранным
  // (канон: lib/account-twins.ts). Выбранный аккаунт удалили из подключений — молча
  // подставить другой нельзя (сменить лицо сценария незаметно): поле считается пустым.
  // Обе оговорки живут внутри общего правила wizardAccountId.
  // Рабочие значения формы (они же уезжают на сервер): пока аккаунт не выбран — молча
  // подставлен ПЕРВЫЙ из подключённых (владелец 05.08: «везде, где просят выбрать
  // аккаунт, ставь что-то сразу выбранным»); удалённый из подключений — тоже заменяется
  // первым живым, человек волен сменить.
  // ПРАВКА ИЛИ СОЗДАНИЕ — обычным ответом `wizEditing`. Им поля, которые бывают только
  // у уже живущего бота (переключатель «кого касается правка» у цепочки рассылок),
  // прячутся на создании штатными displayOptions, а не своим кодом панели. На сервер
  // этот ответ не едет — он снимается в wizAnswers перед отправкой.
  const editingAnswers = useMemo(
    // «В ЦЕПОЧКЕ ЕСТЬ ЛЮДИ» — тем же скрытым ответом (MSG-VERIFY-FIX В): переключатель «Кого касается правка»
    // показывается, только когда есть кого переключать. Число приносит черновик (`chainRunning`).
    () => (edit ? { ...answers, wizEditing: true, [WIZ_CHAIN_RUNNING]: (edit.chainRunning ?? 0) > 0 } : answers),
    [answers, edit],
  );
  // РЕЕСТР ССЫЛОК (bot_ref_links) — для метки «Ссылки в Telegram-бот» по умолчанию (UT 14.10 итерация 2, п.23а:
  // start1, занята у этого бота — start2…). Ходим только когда выбран этот вариант.
  const wantsRefLinks = editingAnswers[WIZ_GIFT_KIND] === WIZ_GIFT_TG;
  const { data: refLinks } = usePersistentData<{ ok: boolean; items: Array<{ credId: string; code: string }> }>(
    'cabinet:ref-links',
    wantsRefLinks ? '/api/cabinet/ref-links' : '',
    { accept: (j) => Array.isArray((j as { items?: unknown } | null)?.items) },
  );
  const giftTgTaken = useCallback(
    (credId: string): readonly string[] | null =>
      refLinks?.items ? refLinks.items.filter((r) => r.credId === credId).map((r) => r.code) : null,
    [refLinks],
  );
  const formValues = useMemo(() => {
    // «Ссылка в Telegram-бот» (UT 14.10 п.6): выбранный бот (один — по умолчанию) и готовая
    // ссылка в ответе `giftTgUrl` — её читает сборка (анкета @логина не знает).
    const base = wizGiftTgDerive(editingAnswers, tgCreds, giftTgTaken);
    if (!hasAccountField) return base;
    // Само правило подстановки — общее (wizardAccountId): им же список ботов и каталог
    // считают адрес сцены заранее.
    const chosen = wizardAccountId({
      creds,
      credsReady,
      options: accCredsOrdered,
      hasAccountField,
      answerId: accId,
    });
    return chosen === accId ? base : { ...base, accountId: chosen };
  }, [editingAnswers, tgCreds, giftTgTaken, hasAccountField, credsReady, creds, accCredsOrdered, accId]);

  /** @логин выбранного Telegram-бота «Ссылки в Telegram-бот» (UT 14.10 п.6) — для
   *  готовой ссылки на странице; нет бота — пусто. */
  const giftTgHandle = useMemo(() => {
    const id = formValues[WIZ_GIFT_TG_BOT];
    const c = tgCreds.find((x) => x.id === id);
    return c?.handle ? c.handle.replace(/^@/, '') : null;
  }, [formValues, tgCreds]);

  /** Ответы, которые уезжают на сервер: служебные пометки панели тут не нужны. */
  const wizAnswers = useMemo(() => {
    const { wizEditing: _drop, [WIZ_CHAIN_RUNNING]: _dropRun, ...rest } = formValues as Record<string, unknown>;
    return rest;
  }, [formValues]);

  // Активная анкета семейства — по сети выбранного аккаунта (тип его доступа). Правило
  // общее с предзагрузкой каталога (wizardDefForCred): каталог обязан греть сцену ТОЙ
  // анкеты, которая тут и встанет (П2 О5.10).
  const defFor = (id: unknown): TemplateWizardDef => wizardDefForCred(siblings, creds, id, def);
  const activeDef = defFor(formValues.accountId);
  // Шаг, у которого при текущих ответах не осталось ни одного видимого поля, ПРОПУСКАЕМ
  // (владелец 2026-08-06: «ответы в комментарии сделай отдельным шагом» — а при запуске
  // из директа комментариев нет вовсе, и пустой экран «Шаг 3 из 4» был бы враньём).
  // Страницы берём ОДНОЙ дверью (wizardSteps): у анкеты со страницами-повторами (мастер
  // рассылки — по странице на письмо) их число зависит от ответов, у остальных нет.
  // Шаг «Знакомство» (ТЗ-2 З1) полей не имеет вовсе — фильтр его щадит: он и не должен
  // ничего спрашивать, на нём человек читает, какого бота собирает.
  // ЦЕПОЧКИ КАБИНЕТА — для «Остановить выбранные» (шаг «Другие цепочки», М1.1): анкета
  // чистая и кабинета не видит, список подставляет панель в поле с typeOptions.liveChains.
  // ВАРИАНТ «выбранные» ВИДЕН ВСЕГДА (владелец 16.09.2026: «где вариант выбрать, какие
  // завершать? Его нет»): прежний фильтр, прятавший его при пустом списке, отменён. Нет
  // других цепочек — вместо мёртвого набора галочек встаёт строка-заметка теми же
  // словами анкеты (`chainNoOthers`), а ответ `endChainIds` остаётся пустым.
  const liveChains = useChainOptions(edit?.draftId ?? '');
  // §11.13: личный логин Telegram человека для «Ваш Telegram» кнопки «Купить» (та же ручка ниши, данные из кэша).
  const selfTg = useNichePersonal().data?.selfTg ?? null;
  const steps = useMemo(
    () =>
      wizardSteps(activeDef, loc, formValues)
        .map((s) => ({
          ...s,
          // «Ссылка в Telegram-бот» (UT 14.10 п.6): варианты ботов и сама ссылка — живые
          // данные кабинета; одним ходом для браузера и приложения. «Ваш Telegram» (§11.13) — та же функция, что у
          // страницы приложения (`qsBuyTgFill`).
          params: qsBuyLoginFill(wizGiftTgFill(s.params, loc, tgOptions, giftTgHandle, formValues), selfTg, loc).flatMap((p) => {
            const live =
              (p.typeOptions as { liveChains?: boolean } | undefined)?.liveChains === true;
            if (!live) return [p];
            if (liveChains.length > 0) {
              return [
                { ...p, options: liveChains.map((o) => ({ name: o.label, value: o.value })) },
              ];
            }
            return [
              {
                name: `${p.name}Empty`,
                type: 'notice',
                default: '',
                displayName: chainNoOthersWords(loc),
                ...(p.displayOptions ? { displayOptions: p.displayOptions } : {}),
              },
            ];
          }),
        }))
        // Шаг-СПИСОК ОБЪЕКТОВ (письма цепочки) и шаг со строкой-карточкой «Сообщение»
        // своих полей на экране не имеют вовсе — фильтр «шаг без видимых полей
        // пропускаем» обязан их щадить, как и знакомство. Карточка со своим условием
        // (`show`) гаснет вместе с шагом: «Если нет подписки» без проверки подписки —
        // это шаг ни о чём.
        .filter(
          (s) =>
            s.key === WIZ_INTRO_KEY ||
            // Шаг «Готово» цепочки — всегда (MSG-VERIFY-FIX Е): «Создать» живёт на нём, а не на «Сообщениях».
            (activeDef.id === CHAIN_WIZARD_ID && s.key === CHAIN_DONE_STEP) ||
            s.itemList ||
            // Шаг из одних строк (сводка «Готово» быстрого запуска) — не пустой.
            (s.rows ?? []).length > 0 ||
            wizMessageCards(s, formValues).length > 0 ||
            visibleParams(s.params, formValues).filter((p) => !wizCoveredParams(s).has(p.name))
              .length > 0,
        ),
    [activeDef, loc, formValues, liveChains, tgOptions, giftTgHandle, selfTg],
  );

  // ── ЗАНЯТЫЕ ЗАПУСКИ — ЗАРАНЕЕ (П160 п.1–2, владелец 26.09.2026: «нужно заранее список
  // триггеров загружать, чтобы отобразить ошибку до перехода») ─────────────────────────
  // Список грузится при открытии мастера (кэш кабинета) и освежается при входе на шаг
  // запуска; сверяет ТА ЖЕ чистая функция, что сервер при записи (findTakenStart). Своего
  // бота при правке не считаем. Анкете без шага запуска (рассылка) список не нужен.
  const startIdx = steps.findIndex((s) => s.starts === true);
  const { data: startsTakenData, reload: reloadStartsTaken } = usePersistentData<StartsTakenResp>(
    STARTS_TAKEN_KEY,
    startIdx >= 0 ? STARTS_TAKEN_URL : '',
    { accept: startsTakenOk },
  );

  /** ПРАВКА ОТКРЫВАЕТСЯ СРАЗУ ПОЛЯМИ (П86, владелец 13.09.2026): сцена-превью в правке
   *  не показывается вовсе — ни шагом, ни в счётчике, а «Назад» с первого поля закрывает
   *  мастер. Считает это одна дверь (`wizStartIdx`), тестом закрыт стартовый шаг. */
  const skipScene =
    wizStartIdx({ edit: !!edit, demoScene: activeDef.demoScene, steps: steps.length }) > 0;
  const [idx, setIdx] = useState(() => {
    // FIX-1 §5.3: продолжение формы быстрого запуска — тот шаг, где человек остановился.
    const resumeAt = quickResume?.step ? steps.findIndex((s) => s.key === quickResume.step) : -1;
    if (resumeAt >= 0) return resumeAt;
    return wizStartIdx({ edit: !!edit, demoScene: def.demoScene, steps: steps.length });
  });
  // «Поменять запуск» шторки «занято» — на шаг запуска (П160 п.4).
  useEffect(() => {
    backToStartRef.current = () => {
      if (startIdx >= 0) setIdx(startIdx);
    };
  }, [startIdx]);

  // Смена сети в семействе: недостающим полям новой анкеты — её дефолты, а выбор
  // «условия запуска» доводим до допустимого (у инсты нет запуска «по ссылке»).
  const applyAnswers = (raw: Record<string, unknown>) => {
    // Правят ответ — ручная причина отказа больше не актуальна и гаснет сама
    // (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128: строка стоит, пока причина
    // не устранена, а не по таймеру).
    setFormErr(null);
    // Сменили вид «вкусняшки» (ссылка → картинка → видео…) — прежний файл больше не
    // годится: картинка видео не станет, а площадка проверяет тип. Чистим сразу, чтобы
    // на сервер не уехало вложение чужого вида.
    const v0 =
      raw[WIZ_GIFT_KIND] !== formValues[WIZ_GIFT_KIND] ? { ...raw, [WIZ_GIFT_FILE]: '' } : raw;
    const nextDef = defFor(v0.accountId);
    // ФРАЗА ПО ГАЛОЧКЕ (владелец 20.09.2026): единственная точка приёма ответов — сюда
    // приходят и веб-поля, и правки родного мастера (wizChange), поэтому механика одна.
    const v = applyToggleTexts(nextDef, botLoc, formValues, v0);
    if (nextDef === defFor(formValues.accountId)) {
      setAnswers(v);
      noteAnswers(v);
      return;
    }
    const next = { ...wizardDefaults(nextDef, botLoc), ...v };
    setAnswers(next);
    noteAnswers(next);
  };
  /** Те же ответы — сразу в `answersRef` (П188): запись, вызванная в том же обработчике,
   *  что и правка, не должна ждать рендера. Служебная пометка панели снимается, как в
   *  `wizAnswers`. */
  const noteAnswers = (v: Record<string, unknown>): void => {
    const { wizEditing: _drop, [WIZ_CHAIN_RUNNING]: _dropRun, ...rest } = v;
    answersRef.current = rest;
  };

  // QUICK-START §11.11: бот Telegram «Продажи» по умолчанию — единственный/первый подключённый (как аккаунт Instagram,
  // та же логика, что у страницы приложения `wizView`); «Добавить бота» — только когда ботов нет.
  useEffect(() => {
    if (activeDef.quickStart !== 'sales' || tgOptions.length === 0) return;
    const cur = formValues[WIZ_GIFT_TG_BOT];
    if (typeof cur === 'string' && cur && tgOptions.some((o) => o.value === cur)) return;
    applyAnswers({ ...formValues, [WIZ_GIFT_TG_BOT]: tgOptions[0]!.value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDef.quickStart, tgOptions, formValues[WIZ_GIFT_TG_BOT]]);

  // «Сохранить» (режим правки): сервер пересобирает канон по новым ответам новой
  // ревизией; переключение на запуск «по ссылке» доводим как при создании — если у
  // сценария ещё нет ссылки в реестре, заводим (best-effort, доберёт в карточке).
  /** `stay` — «ДАЛЕЕ» НА ШАГЕ В РЕЖИМЕ ПРАВКИ (владелец 26.09.2026: «нажал „Далее“ — ты
   *  должен сохранять; делаешь валидацию, всё окей — сохраняешь; речь о шагах мастеров
   *  ботов»): те же проверки, та же ручка, но мастер не закрывается — идём на следующий
   *  шаг. Возвращает, записалось ли. Без «Далее» (просто поменял поле) в базу не едет. */
  // ЗАПИСИ ШАГОВ — ПО ОЧЕРЕДИ, ЧЕЛОВЕК НЕ ЖДЁТ (владелец 26.09.2026: «должно сохраниться,
  // но это не значит, что я должен ждать ответ»): «Далее» переходит на следующий шаг сразу,
  // PUT уходит в фоне.
  // ОЧЕРЕДЬ И ТОЧКИ ЗАПИСИ (ТЗ П188, владелец 27.09.2026: «добавил 6 сообщений — в базе
  // одно»; правило: «Готово» страницы сообщения — запись, «Далее» — запись, «Сохранить» на
  // последнем шаге — запись всего; не избыточно, но логично»):
  //   • очередь `WizSaveQueue`: один PUT в полёте, просьбы во время полёта схлопываются в одну
  //     ожидающую, снимок ответов берётся В МОМЕНТ ОТПРАВКИ (`answersRef`) — раньше уезжал
  //     снимок замыкания, без только что добавленных сообщений; без изменений — PUT не уходит
  //     (пять быстрых «Далее» больше не плодят пять версий);
  //   • запись зовут: «Далее» (`advance`), «Готово» страницы сообщения после валидации
  //     (`onItemRowsDone`), удаление и перестановка элемента списка, финальное «Сохранить»;
  //     добавление без «Готово» не пишется (пустой черновик и так снимается `dropFreshItem`);
  //     невалидное не пишется никогда (урок автозаписи f0cad5b99);
  //   • крестик/свайп в правке дожидается очереди и только потом закрывает мастер.
  const baseVersionRef = useRef(baseVersion);
  useEffect(() => {
    baseVersionRef.current = baseVersion;
  }, [baseVersion]);
  /** Актуальные ответы для записи: обновляются каждым рендером И сразу в `applyAnswers`
   *  (запись, вызванная в том же обработчике, что и правка, обязана увезти уже новые). */
  const answersRef = useRef(wizAnswers);
  answersRef.current = wizAnswers;

  // ── НАЗВАНИЕ РАССЫЛКИ (APP-CLAIMS-1020 ред. 8, Р17) ───────────────────────────────────
  // Поле «Название» — первым на первом шаге формы рассылки, подпись «Видно только вам» (те же слова, что у формы
  // приложения: cabinet.broadcasts nameLabel/nameHint). Пустое поле — сервер ставит «Сообщение N» (наибольший номер
  // среди рассылок кабинета, включая удалённые, + 1); подсказка пустого поля — то же число той же функцией
  // (lib/broadcast-name.ts) по названиям рассылок из ручки `scenarios/titles?kind=broadcast`. Уходит полем `name`
  // создания (POST drafts) и правки (PUT drafts/<id>); при правке поле открывается текущим названием.
  const isBcastWiz = activeDef.id === BROADCAST_WIZARD_ID;
  const [bcastName, setBcastName] = useState(() => (edit?.title ?? '').trim());
  // Снимок для очереди записи — обновляется в самом обработчике поля (как answersRef в applyAnswers).
  const bcastNameRef = useRef(bcastName);
  const { data: bcastTitlesData } = usePersistentData<ScenarioTitlesResp>(
    BCAST_TITLES_KEY,
    isBcastWiz ? BCAST_TITLES_URL : '',
    { accept: scenarioTitlesOk },
  );
  const bcastNamePh = isBcastWiz && bcastTitlesData?.titles ? nextBroadcastName(bcastTitlesData.titles, botLoc) : '';

  // ── НАЗВАНИЕ НОВОГО БОТА С НОМЕРОМ (владелец 27.09.2026, П189) ───────────────────────
  // «<название шаблона> N»: N — следующий номер среди ВСЕХ ботов кабинета с тем же именем
  // шаблона, включая помеченные удалёнными (номера не переиспользуются). Список названий
  // грузится при открытии создания; подставляем, только пока человек поле не трогал
  // (пустое или наша же прошлая подстановка — свежий ответ после кэша её поправит).
  const aboutBase = edit ? '' : wizardAboutTitle(activeDef, botLoc);
  const { data: titlesData, mutate: mutateTitles } = usePersistentData<ScenarioTitlesResp>(
    SCENARIO_TITLES_KEY,
    aboutBase ? SCENARIO_TITLES_URL : '',
    { accept: scenarioTitlesOk },
  );
  // Что подставили сами (из кэша при открытии либо эффектом): по этому слову эффект
  // отличает свою подстановку от набранного человеком.
  const autoTitleRef = useRef(edit ? '' : cachedNumberedTitle(def, botLoc));
  useEffect(() => {
    if (!aboutBase || !titlesData?.titles) return;
    const next = numberedTitle(aboutBase, nextTitleNumber(titlesData.titles, aboutBase));
    const prevAuto = autoTitleRef.current;
    autoTitleRef.current = next;
    setAnswers((prev) => {
      const cur = typeof prev.botTitle === 'string' ? prev.botTitle : '';
      if (cur === next || (cur !== '' && cur !== prevAuto)) return prev;
      return { ...prev, botTitle: next };
    });
  }, [aboutBase, titlesData]);
  const putRef = useRef<(answers: Record<string, unknown>, reason: string) => Promise<boolean>>(
    async () => false,
  );
  const saveReasonRef = useRef('next');
  // Очередь живёт один раз на мастер (ленивый инициализатор состояния — без пересоздания).
  const [saveQueue] = useState(
    () =>
      new WizSaveQueue<{ answers: Record<string, unknown>; reason: string; name: string }>(
        () => ({ answers: answersRef.current, reason: saveReasonRef.current, name: bcastNameRef.current }),
        // Название рассылки (Р17) — часть снимка: правка одного названия тоже уходит записью.
        (s) => JSON.stringify([s.answers, s.name]),
        (s) => putRef.current(s.answers, s.reason),
      ),
  );
  const saveEdit = async (stay = false, reason = stay ? 'next' : 'final'): Promise<boolean> => {
    if (!edit) return false;
    const q = saveQueue;
    saveReasonRef.current = reason;
    if (stay) {
      // Фоновая запись: очередь сама схлопнет лишнее и увезёт актуальные ответы.
      const out = await q.request(reason);
      return out !== 'failed';
    }
    if (saving) return false;
    setSaving(true);
    // Финал: дождаться фоновых записей, затем записать всё (без изменений — пропуск).
    await q.flush();
    const out = await q.request(reason);
    setSaving(false);
    if (out === 'failed') return false;
    (onSaved ?? onDone)();
    return true;
  };
  putRef.current = async (answers: Record<string, unknown>, reason: string): Promise<boolean> => {
    if (!edit) return false;
    const stay = reason !== 'final';
    setSaveErr(null);
    const startedAt = Date.now();
    const r = await fetch(`/api/cabinet/scenarios/drafts/${edit.draftId}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        baseVersion: baseVersionRef.current,
        wizard: { answers },
        // Р17 ред. 8: название рассылки из поля формы (пусто — сервер даёт «Сообщение N»).
        ...(isBcastWiz ? { name: bcastNameRef.current.trim() } : {}),
      }),
    })
      .then(async (res) => ({
        status: res.status,
        j: (await res.json()) as {
          ok?: boolean;
          draft?: DraftSnapshot;
          error?: string;
          names?: unknown;
          existingId?: unknown;
          tails?: unknown;
        },
      }))
      .catch(() => null);
    // Журнал записи (П188): по нему читается следующая претензия «не сохранилось».
    journalLog('wiz.save', {
      reason,
      status: r?.status ?? 0,
      ms: Date.now() - startedAt,
      keys: Object.keys(answers).length,
      letters:
        typeof answers.letters === 'number' ? answers.letters : Number(answers.letters ?? 0) || 0,
    });
    // ЗАНЯТ ТАКОЙ ЖЕ ЗАПУСК (409 bot_exists): не ошибка сохранения, а запрет — анкета
    // остаётся открытой, а шторка уводит в бота, которому слово принадлежит.
    // ЗАПИСЬ В ФОНЕ (П159) — отказ приходит, когда человек уже на следующем шаге: шторка
    // встаёт поверх любого шага (П160 п.3); «Выключить» повторяет эту же запись, «Поменять
    // запуск» возвращает на шаг запуска (п.4).
    if (r?.j.error === 'bot_exists' && typeof r.j.existingId === 'string') {
      const names = Array.isArray(r.j.names)
        ? r.j.names.filter((n: unknown) => typeof n === 'string')
        : [];
      saveDup.show(
        { names: names as string[], existingId: r.j.existingId },
        { retry: () => void saveEdit(stay), change: backToStart },
      );
      return false;
    }
    // ХВОСТ ССЫЛКИ ЗАНЯТ ДРУГИМ БОТОМ (409 `code_taken`, BACKLOG п.22): входы сохранены,
    // ссылка не заведена — мастер остаётся открытым и показывает подсказку под полем
    // «Откуда придут люди» (К18.3 этап 1 п.2 г). Ссылки заводит сервер, не кабинет.
    if (r?.j.error === 'code_taken') {
      const tails = Array.isArray(r.j.tails)
        ? r.j.tails.filter((x: unknown): x is string => typeof x === 'string')
        : [];
      setTakenTails(tails);
      setFormErr(ts('chatStartLinkTakenHere'));
      return false;
    }
    if (r?.j.ok && r.j.draft) {
      setTakenTails([]);
      baseVersionRef.current = r.j.draft.version;
      setBaseVersion(r.j.draft.version);
      edit.onSaved(r.j.draft);
      return true;
    }
    setSaveErr(r?.status === 409 ? 'conflict' : 'generic');
    return false;
  };

  const step = useMemo(
    () => steps[Math.min(idx, steps.length - 1)] ?? { key: 'empty', title: '', params: [] },
    [steps, idx],
  );
  // Вошли на шаг запуска — список занятых освежаем: пока человек выбирает, чужой бот мог
  // появиться или выключиться (П160 п.1).
  // ФОРМЫ БЫСТРОГО ЗАПУСКА (QUICK-START §2.11–2.14): qsId заполнения, предзаполнение, ИИ-подгонка, создание.
  const quick = useQuickFlow({
    def: activeDef,
    botLoc,
    values: formValues,
    apply: applyAnswers,
    stepKey: step.key,
    ...(quickResume ? { qsId: quickResume.qsId } : {}),
  });
  const openQuickDone = useOpenQuickDone();
  const onStartStep = step.starts === true;
  useEffect(() => {
    if (onStartStep) reloadStartsTaken();
  }, [onStartStep, reloadStartsTaken]);
  /** Занят ли выбранный запуск — по заранее загруженному списку (null — свободно или
   *  списка ещё нет: тогда ловит сервер при записи, П160 п.2–3). */
  const startTakenNow = () => {
    const list = startsTakenData?.starts;
    if (!list) return null;
    return findTakenStart(list, wizardWantedStarts(activeDef, wizAnswers, botLoc, edit?.draftId));
  };
  const last = idx >= steps.length - 1;
  // Обязательные поля ШАГА, оставшиеся пустыми (дефолт — тоже ответ). Считаем по
  // formValues: удалённый аккаунт = пустое поле, пока не выбран заново.
  // Поля, которые забрала себе строка-карточка «Сообщение» (ТЗ З7): в форме шага их нет,
  // и «заполните обязательное поле» про них не говорим — человек не найдёт поля на экране.
  const coveredParams = useMemo(() => wizCoveredParams(step), [step]);
  const missing = useMemo(
    () =>
      visibleParams(step.params, formValues).filter(
        (p) =>
          p.required &&
          !coveredParams.has(p.name) &&
          isEmptyAnswer(formValues[p.name] ?? p.default),
      ),
    [step, formValues, coveredParams],
  );
  // ── ЖИВАЯ ПРОВЕРКА КАНАЛА (ТЗ 19.09.2026 Р3) ──────────────────────────────────────
  // Один модуль на браузер и приложение: список каналов бота и состояние проверки пары
  // «бот + адрес». Автопроверки заводит ИМЕННО этот хук (`auto`) — браузерный пикер
  // читает тот же склад и спрашивает площадку только по действию человека.
  const chanValue = typeof formValues.channelLink === 'string' ? formValues.channelLink : '';
  const hasChanField = useMemo(
    () => steps.some((s) => s.params.some((p) => p.name === WIZ_CHANNEL_FIELD)),
    [steps],
  );
  const chanOn = hasChanField && formValues.checkSub !== false;
  const chanCheck = useWizChannelCheck({
    credId: typeof formValues.accountId === 'string' ? formValues.accountId : '',
    value: chanValue,
    active: chanOn,
    auto: true,
    wiz: activeDef.id,
    step: step.key,
  });
  // АВТОЗАПОЛНЕНИЕ КАНАЛА (владелец 19.09.2026: «если он один — автозаполнить; даже если
  // не один — первый или последний выбранный»). Поле пусто, тумблер включён, реестр
  // ответил и в нём есть каналы — подставляем первый: сервер отдаёт их «сначала те, где
  // бот администратор, потом по свежести» (listBotChannels), то есть первый и есть
  // последний проверенный. Один раз на бота: сменил бот — подставится его канал; стёр
  // сам — не возвращаем.
  const chanAutoRef = useRef<string>('');
  /** Адрес канала, подставленный автозаполнением, — не ввод человека (П5, признак «грязный»). */
  const chanAutoValRef = useRef<string>('');
  useEffect(() => {
    if (!chanOn || chanValue.trim() !== '' || !chanCheck.listReady) return;
    const credId = typeof formValues.accountId === 'string' ? formValues.accountId : '';
    if (!credId || chanAutoRef.current === credId) return;
    const first = chanCheck.items[0];
    if (!first) return;
    chanAutoRef.current = credId;
    chanAutoValRef.current = first.address;
    applyAnswers({ ...formValues, channelLink: first.address });
    chanCheck.note({ pick: 1, src: 'acc' });
    // applyAnswers/formValues меняются каждый рендер; ход один — по готовности реестра.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chanOn, chanValue, chanCheck.listReady, chanCheck.items, formValues.accountId]);

  // ── ПРИЗНАК «ГРЯЗНЫЙ» ДЛЯ ВОПРОСА ПРИ ЗАКРЫТИИ (ТЗ «Причины» П5, владелец 05.10.2026:
  // «мастер, ничего не вводил → „×“ → „Отменить создание?“ — этого нет в iOS», кадр iOS
  // W29-iggift-cancel-ask: без ввода лист уходит молча). Грязный = ответы отличаются от
  // исходных. Подстановки самого мастера вводом НЕ считаются: название с номером
  // (autoTitleRef), первый подключённый аккаунт (wizardAccountId), первый канал
  // (chanAutoValRef). Смена шага и полулисты ответов не трогают; черновик сообщения без
  // «Готово» снимается dropFreshItem — ответы возвращаются к исходным, вопроса нет.
  const autoAccId = wizardAccountId({
    creds,
    credsReady,
    options: accCredsOrdered,
    hasAccountField,
    answerId: '',
  });
  const pristineKey = (v: Record<string, unknown>): string => {
    const { wizEditing: _drop, [WIZ_CHAIN_RUNNING]: _dropRun, ...rest } = v;
    const o: Record<string, unknown> = { ...rest };
    if (o.botTitle === '' || o.botTitle === autoTitleRef.current) delete o.botTitle;
    if (!o.accountId || o.accountId === autoAccId) delete o.accountId;
    if (o.channelLink === '' || o.channelLink === chanAutoValRef.current) delete o.channelLink;
    return JSON.stringify(Object.keys(o).sort().map((k) => [k, o[k]]));
  };
  const baseKeyRef = useRef<string | null>(null);
  if (baseKeyRef.current === null) baseKeyRef.current = pristineKey(answers);
  const isDirty = !edit && pristineKey(answers) !== baseKeyRef.current;
  useEffect(() => {
    if (dirtyRef) dirtyRef.current = isDirty;
  }, [dirtyRef, isDirty]);

  const chanBotName = chanCheck.botName || ts('chatSubYourBot');
  /** Готовый текст отказа живой проверки; пусто — отказа нет. */
  const chanFail =
    chanCheck.state?.phase === 'fail'
      ? ts(chanFailKey(chanCheck.state.reason) as 'chatSubErrNetwork', { bot: chanBotName })
      : '';
  /** Вторая строка ВЫБРАННОГО канала: пока идёт проверка — «Проверяем…», прошла —
   *  «@логин · бот — администратор» (после ручного добавления реестр ещё не перечитан,
   *  и подпись собирается из самого ответа площадки). */
  const chanStatus =
    chanCheck.state?.phase === 'checking'
      ? ts('chatSubChecking')
      : chanCheck.state?.phase === 'ok'
        ? `${chanCheck.state.address.startsWith('@') ? `${chanCheck.state.address} · ` : ''}${ts('chatSubIsAdmin')}`
        : '';
  /** Варианты поля «Канал» — та же кладка, что у браузерного пикера (Р2). */
  const chanNativeOptions = useMemo(
    () =>
      chanOptionsOf(chanCheck.items, {
        isAdmin: ts('chatSubIsAdmin'),
        notAdmin: ts('chatSubNotAdmin'),
        closed: ts('chatSubClosed'),
      }),
    [chanCheck.items, ts],
  );
  /** Состояние проверки для замка «Дальше» — НЕ ответ анкеты (инвариант в core.ts). */
  const chanLive: WizLiveChecks | undefined = chanCheck.state
    ? {
        channel: {
          phase: chanCheck.state.phase,
          ...(chanCheck.state.phase === 'fail' ? { reason: chanCheck.state.reason } : {}),
          text: chanCheck.state.phase === 'checking' ? ts('chatSubChecking') : chanFail,
        },
      }
    : undefined;
  // Свой инвариант шага (Ф4, напр. «отмечен хотя бы один вход») — required не выразит.
  // Подсветка «заполните это поле» — только после попытки уйти дальше (владелец 05.08:
  // раньше форма молча не пускала, «сиди гадай почему»).
  const warnMissing = useMemo(() => {
    if (!tried || missing.length === 0) return undefined;
    return Object.fromEntries(missing.map((p) => [p.name, ta('wizFieldRequired')]));
  }, [tried, missing, ta]);
  // ── ШАГ-СПИСОК: входы строками (ТЗ 03.09.2026 В1, Д2) ─────────────────────────────
  // Список — ВИД поверх прежних ответов: набор галочек и поля каждого входа те же, шаг
  // только рисует их иначе. Страница элемента живёт вертикальной шторкой поверх анкеты
  // (в приложении — стопкой), «Готово» в шапке, «Удалить вход» внизу красным.
  // ОДНО окно «Сообщение» на весь продукт: своего редактора письма у мастера нет.
  const openStepMessage = useMessageComposer();
  // ШАГ-СПИСОК ОБЪЕКТОВ (письма цепочки): строки считает сама анкета — она одна знает,
  // из каких ответов состоит её элемент.
  // ШАГ-СООБЩЕНИЕ (рассылка): весь шаг — одна строка-карточка, письмо собирает общее окно.
  // КАРТОЧКИ «СООБЩЕНИЕ» шага (ТЗ Б, В2, З7): у рассылки и у сообщений шаблона карточка
  // одна и полей рядом нет, у «Дополнительных настроек» их две — рядом со своими
  // тумблерами. Поля, которые карточка забрала себе (`covers`), в форме не рисуются и в
  // «заполните обязательное» не участвуют: поля, которого на экране нет, человек не
  // заполнит (запрет №7).
  const msgCards = useMemo(() => wizMessageCards(step, formValues), [step, formValues]);
  // ЧТО СТОИТ СПРАВА В СТРОКЕ «СООБЩЕНИЕ» (И11.3): у рассылки — СОСТАВ письма («Фото,
  // подпись, кнопки (2)»), у остальных анкет — первая строка текста, как было. Решает
  // сама карточка (`summary`), а не экран: у шаблонов бота превью читается лучше.
  const msgSumDict = useMsgSummaryDict();
  const cardValue = useCallback(
    (cfg: WizardStepMessage, vals: Record<string, unknown>): string =>
      cfg.summary === 'compose'
        ? messageSummary(wizMessageDraft(cfg, vals), msgSumDict)
        : wizMessagePreview(cfg, vals),
    [msgSumDict],
  );
  const itemCfg = step.itemList;
  // ЖИВЫЕ СПИСКИ КАБИНЕТА для шага-списка (К5): анкета — чистый модуль, ботов и меток
  // она не знает, а строка входа без имени бота не пишется. Своды те же, что у листа
  // условий (общий кэш «Ботов» и меток) — второго источника имён в продукте нет.
  const liveBotsAll = useBotOptions(NO_KEEP);
  // «ПРОШЁЛ БОТА» У ЦЕПОЧКИ — ТОЛЬКО ТЕЛЕГРАМ-БОТЫ И ЦЕПОЧКИ (BACKLOG п.20, п.27): остальные боты кабинета
  // едут отдельным списком, чтобы старый вход с таким ботом читался словами.
  const tgBots = useTelegramBotOptions();
  const liveBots = activeDef.id === CHAIN_WIZARD_ID ? tgBots.fit : liveBotsAll;
  const liveBotsUnfit = activeDef.id === CHAIN_WIZARD_ID ? tgBots.unfit : NO_KEEP_OPTS;
  const liveBotsGone = activeDef.id === CHAIN_WIZARD_ID ? tgBots.gone : NO_KEEP_OPTS;
  const liveBotsChains = activeDef.id === CHAIN_WIZARD_ID ? tgBots.chains : NO_KEEP;
  // МЕТКИ ВХОДА ЦЕПОЧКИ — ТЕ ЖЕ, ЧТО ВЕЗДЕ (владелец 13.09.2026, БЭКЛОГ п.13): любая
  // новая метка клиента (карточка, кнопка письма, шаг) запускает вход «Получил метку».
  const liveTags = useTagOptions(NO_KEEP);
  // Выбранный аккаунт нужен и живым спискам (логин бота в строке ссылки), и полям
  // страницы триггера ниже — считаем его ОДИН раз, здесь.
  const accCred = creds.find(
    (c) => c.id === (typeof formValues.accountId === 'string' ? formValues.accountId : ''),
  );
  const itemLive = useMemo<WizardItemLive>(
    // ЛОГИН БОТА — часть живых списков (К19.1 п.5): строка «Переход по ссылке» пишет
    // серой подстрокой саму ссылку целиком, а анкета логина не знает (чистый модуль).
    () => ({
      bots: liveBots,
      botsUnfit: liveBotsUnfit,
      botsGone: liveBotsGone,
      botsChains: liveBotsChains,
      tags: liveTags,
      botHandle: accCred?.handle ?? null,
    }),
    [liveBots, liveBotsUnfit, liveBotsGone, liveBotsChains, liveTags, accCred?.handle],
  );
  // Свой инвариант шага (Ф4, напр. «отмечен хотя бы один вход») — required не выразит.
  // Живые списки кабинета едут замку вместе с проверкой канала (П203): по ним он видит,
  // что бот входа «Прошёл бота» удалён. Стоит ПОСЛЕ itemLive — раньше списков ещё нет.
  const blocked =
    activeDef.stepBlocked?.(formValues, step.key, loc, { ...(chanLive ?? {}), items: itemLive }) ??
    null;
  // QUICK-START §11.10 п.1 (владелец 29.10: «одна ошибка в одном месте»): у анкеты быстрого запуска запрет шага,
  // привязанный к полю (`stepBlockedField`: «Одно слово без пробелов», «Укажите ссылку или @логин»), — подсказкой у
  // ЭТОГО поля, без строки над шагом; пустое обязательное — тоже только у поля.
  const blockedField = activeDef.quickStart && blocked !== null ? (activeDef.stepBlockedField?.(formValues, step.key) ?? null) : null;
  const warnings = useMemo(() => {
    if (!tried || !blockedField || blocked === null) return warnMissing;
    return { ...(warnMissing ?? {}), [blockedField]: blocked };
  }, [tried, blockedField, blocked, warnMissing]);
  const entryWords = useMemo(() => wizEntryWords(loc), [loc]);
  /**
   * БОТЫ КАБИНЕТА ДЛЯ ТРИГГЕРА «ПРОШЁЛ БОТА» (К3): свод тот же, что у листа условий. Саму
   * цепочку и уже добавленных ботов не показываем — второго триггера того же бота не
   * бывает, а «цепочка запускает сама себя» это не триггер, а петля.
   */
  const itemPickBots = useCallback(
    (cfg: WizardStepItemList, values: Record<string, unknown>) => {
      const taken = new Set(cfg.pickTaken?.(values) ?? []);
      return itemLive.bots.filter((b) => b.value !== edit?.draftId && !taken.has(b.value));
    },
    [itemLive, edit?.draftId],
  );
  /**
   * ВИДЫ МЕНЮ «ДОБАВИТЬ ТРИГГЕР» — С ПРИЧИНАМИ НЕДОСТУПНОСТИ (К18.1 п.3, владелец
   * 06.09.2026). Порядок видов прежний, недоступные стоят НА СВОИХ МЕСТАХ: анкета гасит
   * то, что знает сама («Уже добавлено»), а «Прошёл бота» гасит ЭТА СТРАНИЦА — свободных
   * ботов кабинета анкета не видит (чистый модуль). Причина у бота двух видов: боты есть,
   * но все уже стоят триггерами — или ботов нет вовсе.
   */
  /**
   * ССЫЛКИ ИЗ ВАШИХ БОТОВ (К17.4 п.6 в) — для РОДНОЙ страницы входа «Переход по ссылке» и (п.29) для меню
   * «Добавить вариант запуска».
   * В браузере тот же список рисует StartLinkFields своей рукой; ключ кэша и адрес общие,
   * поэтому запрос на двоих один (usePersistentData).
   */
  const linkCred = typeof formValues.accountId === 'string' ? formValues.accountId : '';
  const { data: botLinksData } = usePersistentData<{
    ok?: boolean;
    items?: Array<{ draftId: string; title: string; url: string; code: string; known?: boolean }>;
  }>(
    `cabinet:bot-links:${linkCred}`,
    // И для меню «Добавить вариант запуска» (п.29, браузер и приложение) — только у Телеграма и цепочки.
    linkCred && (activeDef.accountChannel === 'telegram' || activeDef.id === CHAIN_WIZARD_ID)
      ? `/api/cabinet/bot-links?cred=${encodeURIComponent(linkCred)}`
      : '',
    { accept: (raw) => !!(raw as { ok?: boolean })?.ok },
  );
  const botLinks = useMemo(() => botLinksData?.items ?? [], [botLinksData]);
  const itemAddKinds = useCallback(
    (cfg: WizardStepItemList, values: Record<string, unknown>): WizardItemAddKind[] => {
      const own = cfg.addKinds?.(values) ?? [];
      // «ССЫЛКИ ИЗ ВАШИХ БОТОВ» ПУЗЫРЁМ НАД ВИДАМИ (п.29): только у списка, где есть «Переход по ссылке».
      const kinds = own.some((k) => k.key === 'link')
        ? [...wizBotLinkAddKinds(botLinks, wizUsedLinkLabels(values), loc, activeDef.id === CHAIN_WIZARD_ID), ...own]
        : own;
      return kinds.map((k) => {
        if (k.pick !== 'bots' || k.disabled || itemPickBots(cfg, values).length > 0) return k;
        const anyBots = itemLive.bots.some((b) => b.value !== edit?.draftId);
        return {
          ...k,
          disabled: true,
          disabledHint: anyBots ? entryWords.botsTaken : entryWords.botsNone,
        };
      });
    },
    [itemPickBots, itemLive, edit?.draftId, entryWords, botLinks, loc, activeDef.id],
  );
  /** Подсказка-ошибка под «Откуда придут люди», когда хвост занят (см. takenTails выше). */
  const linkTakenWarn = useCallback(
    (values: Record<string, unknown>): Record<string, string> | undefined => {
      const tail = translitRefCode(typeof values.linkLabel === 'string' ? values.linkLabel : '');
      return tail !== '' && takenTails.includes(tail)
        ? { linkLabel: ts('chatStartLinkTakenHere') }
        : undefined;
    },
    [takenTails, ts],
  );
  /**
   * ПОДСКАЗКИ-ОШИБКИ СТРАНИЦЫ ЭЛЕМЕНТА — ОДНОЙ РУКОЙ (К21.3 этап 1 п.2; до 07.09 звалась
   * `linkWarnAt` и знала только про ссылку). Три причины, все — вина КЛИЕНТА (запрет №7):
   *  • ХВОСТ ПОВТОРЯЕТ БОЛЕЕ РАННЮЮ ССЫЛКУ этого же списка (К19.1 п.6, владелец
   *    06.09.2026: «добавил переход по ссылке такой, какой был, — стал серый экран») —
   *    раньше вторую ссылку молча выкидывал разбор списка прямо во время набора;
   *  • ХВОСТ ЗАНЯТ ДРУГОЙ ССЫЛКОЙ этого бота (409 `code_taken`, К18.3 этап 1 п.2 г);
   *  • ТРИГГЕР С КОДОВЫМ СЛОВОМ БЕЗ ЕДИНОГО СЛОВА (К21.1 п.5 а, владелец 07.09.2026:
   *    «страницу можно закрыть по „Готово“ без единого слова, и триггер сохраняется
   *    пустым»). Поле слов узнаётся по имени (`isWizWordsField` — одно понятие на весь
   *    мастер), а нужно ли оно при нынешнем режиме — по его же `displayOptions`
   *    (`paramVisible`): у Телеграма поле слов стоит на странице вида «Определённое
   *    сообщение»/«Содержит слово» всегда, у Инстаграма — только при режиме
   *    `keyword|exact` из живого значения поля «Когда запускать». Второго списка имён и
   *    второго правила режима тут не заводим.
   * Слова — общие с блокировкой шага (`trigWords.dupLink` / `trigWords.needWords`), чтобы
   * человек читал на странице ровно то же, что под кнопкой «Дальше».
   *
   * INVARIANT(scriptera): считать эти подсказки — всегда (ими решается, пускать ли
   * «Готово»), а КЛАСТЬ их в поля (`invalid` нативу, `FError` в браузере) — только когда
   * у страницы стоит признак «пытались отправить» (`draftTried`/`rowTried` в приложении,
   * сигнал `WizTriedSignal` в браузере). Причина: страница триггера открывалась уже
   * красной, хотя «Готово» никто не нажимал (претензия владельца 07.09.2026, сборка
   * 165). Канон: docs/MESSAGE-COMPOSER-PLAN.md К24 п.1.
   */
  const pageWarnAt = useCallback(
    (cfg: WizardStepItemList, answers: Record<string, unknown>, i: number) =>
      (values: Record<string, unknown>): Record<string, string> | undefined => {
        const label = typeof values.linkLabel === 'string' ? values.linkLabel : '';
        const n = cfg.count(answers);
        const earlier: string[] = [];
        for (let k = 1; k < i && k <= n; k++) {
          const raw = (cfg.fieldValues?.(answers, k) ?? {}).linkLabel;
          if (typeof raw === 'string') earlier.push(raw);
        }
        // Правило дубля — ОДНО и живёт у строителя шага (`trigDupTail`): страница
        // говорит ровно то же, что блокировка шага.
        if (trigDupTail(earlier, label)) return { linkLabel: trigWords(loc).dupLink };
        const taken = linkTakenWarn(values);
        if (taken) return taken;
        // Правило пустого слова — тоже ОДНО и у строителя шага (`trigWordsWarn`).
        return trigWordsWarn(cfg.fields?.(answers, i) ?? [], values, loc);
      },
    [linkTakenWarn, loc],
  );
  const itemLines = useMemo(() => {
    if (!step.itemList) return [];
    const cfg = step.itemList;
    const n = cfg.count(formValues);
    return Array.from({ length: n }, (_, idx) => ({
      i: idx + 1,
      ...cfg.line(formValues, idx + 1, itemLive),
    }));
  }, [step, formValues, itemLive]);
  // Тело шторки собирается ОДИН раз, а ответы живут дальше своей жизнью — поэтому запись
  // наверх идёт через ссылки на СВЕЖИЕ значения и свежую руку правки. Иначе вторая правка
  // подряд легла бы поверх снимка, снятого в момент открытия.
  const liveValues = useRef(formValues);
  liveValues.current = formValues;
  const liveApply = useRef(applyAnswers);
  liveApply.current = applyAnswers;

  // ЧЕЙ ЭТО ПУСТОЙ ВОПРОС: поля триггера стоят на СВОЕЙ странице и в «заполните
  // обязательное» шага не участвуют вовсе — про них говорит `stepBlocked` анкеты своими
  // словами (К18.3 этап 2 п.1). Здесь остаётся обычная подпись поля шага.
  const missingWhere = (p?: CatalogParam): string => p?.displayName ?? '';

  // ОШИБКА ВСЕГО ШАГА — ОДНОЙ СТРОКОЙ В ТЕЛЕ (канон: UI-TEMPLATE-CANON «Ошибка всего
  // окна», П128). Причина СЧИТАЕТСЯ ЖИВО из ответов: заполнили поле или сняли запрет —
  // строка гаснет сама; до попытки уйти дальше (`tried`) красного в шаге нет вовсе.
  const stepIssue = activeDef.quickStart
    ? blockedField
      ? null
      : blocked
    : (blocked ?? (missing.length > 0 ? ta('wizFillField', { field: missingWhere(missing[0]) }) : null));
  // `formErr` уже сам по себе итог ПОПЫТКИ (нажали «Дальше», грузили фото, сохраняли) —
  // отдельной проверки `tried` ему не нужно; «заполните поле» и запрет шага показываем
  // только после попытки уйти дальше.
  const windowErr =
    formErr ??
    (tried ? stepIssue : null) ??
    (err ? createErrText(ta, err) : null) ??
    (saveErr ? (saveErr === 'conflict' ? ta('wizEditConflict') : ta('createError')) : null);

  // Шаг «Знакомство» (ТЗ-2 З1): он первый и полей не имеет — на нём человек читает, что
  // соберётся, и видит готовую переписку.
  const onIntro = step.key === WIZ_INTRO_KEY;
  /** Первый шаг формы рассылки (после «Знакомства») — на нём стоит поле «Название» (Р17 ред. 8). */
  const bcastFirstStep = steps.find((s) => s.key !== WIZ_INTRO_KEY)?.key ?? '';
  // Превью показывает РЕПЛИКИ БОТА — по языку сборки, а не по языку кабинета.
  // Номер шага у дефа СВОЙ: знакомство сдвинуло нумерацию на единицу (ставит его
  // wizardSteps), поэтому дефу уходит idx-1. На самом знакомстве показываем ПОЛНУЮ
  // картину — как у последнего шага: это сводка «как будет работать», а не полшага.
  const previewMsgs =
    activeDef.preview?.(
      formValues,
      onIntro ? Math.max(0, steps.length - 2) : Math.max(0, idx - 1),
      botLoc,
    ) ?? null;
  // Есть ли у анкеты предпросмотр переписки: от этого зависят и кнопка-телефон в шапке,
  // и подпись про телефончик на шаге «Аккаунт» (ТЗ-9 Е2). Значение — булево (не сам
  // список рядов): списки пересчитываются каждым рендером, и в зависимостях они рвали бы
  // память шагов натива.
  const hasWizPreview = previewMsgs !== null;
  // ТЕЛЕФОНЧИКА НА ЭТОМ ШАГЕ НЕТ (К19.1 п.9, владелец 06.09.2026: на шаге «Что запускает»
  // в шапке уже стоит «Править», две кнопки лишние). Решает сама анкета ключом шага —
  // одно правило на браузер и на приложение (нативу оно уезжает `noPreview`).
  const stepNoPreview = step.noPreview === true;
  // Аккаунтов каналов семейства нет вовсе: селект без вариантов бесполезен — вместо
  // него строка «Добавить аккаунт» и та же честная подсказка (владелец 11.09.2026:
  // модалки «добавьте аккаунт» и увода в «Подключения» больше нет).
  const accNone = hasAccountField && credsReady && accOptions.length === 0;
  // РАССЫЛКА, ОТКРЫТАЯ БЕЗ АККАУНТА (владелец 12.10.2026, ТЗ BCAST-NOACC-WHO): после
  // подключения аккаунта она остаётся тем же экраном общего родного мастера — в родную
  // форму рассылки не переезжает. Отметка ставится один раз и живёт до закрытия.
  const [bcastNoAccStart, setBcastNoAccStart] = useState(false);
  if (accNone && activeDef.id === BROADCAST_WIZARD_ID && !bcastNoAccStart) setBcastNoAccStart(true);
  // Строка «Кто получит» общего родного мастера: число людей — та же рука, что у родной
  // формы рассылки (useAudienceCountWords). Считаем, только когда аккаунт уже есть.
  const bcastWhoCred = typeof formValues.accountId === 'string' ? formValues.accountId : '';
  const { words: bcastWhoWords } = useAudienceCountWords(
    audienceQuery(
      audienceOfRaw(typeof formValues.audience === 'string' ? formValues.audience : '{}'),
    ),
    {
      key: 'bcast',
      ...(bcastWhoCred ? { cred: bcastWhoCred } : {}),
      enabled: activeDef.id === BROADCAST_WIZARD_ID && bcastNoAccStart && !accNone,
    },
  );
  /** Поля и строки шага для общего родного мастера (у рассылки «Кто получит» — строкой). */
  const nativeStepParts = useCallback(
    (s: (typeof steps)[number]) =>
      activeDef.id === BROADCAST_WIZARD_ID
        ? bcastWhoNative(s, accNone, bcastWhoWords)
        : { params: s.params, rows: s.rows ?? [] },
    [activeDef.id, accNone, bcastWhoWords],
  );
  // ПОДКЛЮЧЕНИЕ ПРЯМО ИЗ МАСТЕРА (владелец 11.09.2026): та же стопка, что в
  // «Настройках → Подключения», встаёт ПОВЕРХ мастера и возвращает на этот же шаг, а
  // новый доступ сам становится ответом поля «Аккаунт».
  const connFlow = useWizardConnectFlow({
    wiz: activeDef.id,
    step: step.key,
    // Сеть шага «Аккаунт» известна всегда (у семейства на две сети — активная сестра):
    // «Добавить аккаунт» открывает сразу форму этой сети (владелец 11.09.2026).
    ...(activeDef.accountChannel ? { credType: ACCOUNT_CRED_TYPE[activeDef.accountChannel] } : {}),
    optionIds: accOptions.map((o) => o.value),
    onPicked: (id) => applyAnswers({ ...formValues, accountId: id }),
    // «ДОБАВИТЬ БОТА» у «Ссылки в Telegram-бот» (UT 14.10 п.6): та же стопка, сразу форма
    // Telegram; подключённый бот сам становится ответом поля.
    alt: {
      field: WIZ_GIFT_TG_BOT,
      credType: ACCOUNT_CRED_TYPE.telegram,
      optionIds: tgOptions.map((o) => o.value),
      onPicked: (id) => applyAnswers({ ...formValues, [WIZ_GIFT_TG_BOT]: id }),
    },
    // «ДОБАВИТЬ КАНАЛ» (ТЗ 19.09.2026 Р5) — та же стопка и то же событие моста, только
    // страница своя: веб раздаёт по имени поля. Инструкция про администратора на
    // странице НЕ повторяется — она стоит на самом шаге плашкой `subOnboard`
    // (ТЗ 20.09.2026 C1).
    ...(chanOn
      ? {
          channel: {
            check: (address: string) => chanCheck.check(address, 'form'),
            botName: chanCheck.botName,
            onPicked: (address: string) => applyAnswers({ ...formValues, channelLink: address }),
            items: chanCheck.items,
            live: chanCheck.live,
            reload: chanCheck.reload,
          },
        }
      : {}),
  });

  /** ЛИСТ ВЫБОРА АККАУНТА — ОДНА КОПИЯ на шаг мастера и форму рассылки (ТЗ WEB-CAUSES О10: у мастера
   *  было 56 %, у рассылки 52 %). Полулист WIZ_HALF_SHEET — кромка на 421 из 874, как у родного
   *  выбора (кадры iOS W32-tg-step3-account-pick, R02-broadcast-bot-pick). `withAdd` — строка
   *  «Добавить аккаунт» акцентом (у листа «Бот» рассылки её нет, кадр R02). */
  const openAccountPick = (title: string, withAdd: boolean) =>
    panel.open({
      title,
      sheet: true,
      deck: true,
            sheetDetent: WIZ_HALF_SHEET,
      // «×» — полулист, представленный поверх (кадр iOS W32-tg-step3-account-pick, R02-broadcast-bot-pick; ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2).
      lead: 'close',
      winKey: 'wiz-account-pick',
      body: (
        <WizAccountPick
          options={accOptions}
          value={typeof formValues.accountId === 'string' ? formValues.accountId : ''}
          {...(withAdd
            ? {
                addLabel: ta('wizAddAccount'),
                onAdd: () => {
                  panel.back();
                  connFlow.openWeb();
                },
              }
            : {})}
          onPick={(v) => {
            applyAnswers({ ...formValues, accountId: v });
            panel.back();
          }}
        />
      ),
    });
  // Аккаунт — ПЕРВЫМ вопросом шага (заказ владельца 04.08, копия ChatPlace): своим
  // мини-ParamsForm первым пузырём, иначе ярусная сортировка формы (options =
  // «настройка») утопила бы селект в самый низ. Пока доступы грузятся, поле не рисуем.
  const accParamRaw = hasAccountField ? step.params.find((p) => p.name === 'accountId') : undefined;
  // Подпись поля — ВСЕГДА общее «Аккаунт» (варианты уже подписаны сетями). Раньше при
  // правке она была «Аккаунт Instagram», а при создании «Аккаунт» — правка обязана
  // выглядеть один в один как создание (владелец 2026-08-06).
  // Исключение — анкета выбрала слово сама (цепочка: строка «Бот», К15 П4): её подпись
  // не перебиваем (wizAccountOwnLabel).
  const accParam =
    accParamRaw && !wizAccountOwnLabel(accParamRaw)
      ? { ...accParamRaw, displayName: ta('wizAccount') }
      : accParamRaw;
  // «Проверить подписку» живёт В ПУЗЫРЕ АККАУНТА, сразу под самим аккаунтом (владелец
  // 2026-08-06: «вытащи его снизу и перенеси вверх»): это про аккаунт, а не про запуск —
  // «бот отдаст материал только подписчику аккаунта». Так же у ChatPlace на мобиле.
  const subParamRaw = accParam ? step.params.find((p) => p.name === 'checkSub') : undefined;
  // Аккаунт ещё не подключён — пузыря аккаунта нет вовсе, и «Проверить подписку» вместе
  // с ним бы ПРОПАЛ (живая проба 06.08). В этом случае оставляем его в общем блоке.
  // Владелец 2026-08-10: «Проверить подписку» — ОТДЕЛЬНЫМ пузырём, а не внутри пузыря
  // аккаунта: теперь каждый блок шага стоит своим пузырём, и вложенность лишняя.
  const subInAcc = false;
  const subParam = subInAcc ? subParamRaw : undefined;
  const restParams = (
    accParam
      ? step.params.filter((p) => p.name !== 'accountId' && (!subInAcc || p.name !== 'checkSub'))
      : step.params
  )
    .filter((p) => !coveredParams.has(p.name))
    // РАССЫЛКА БЕЗ АККАУНТА — «Кто получит» скрыт, как в iOS-приложении (владелец 12.10.2026): выбирать людей не
    // из кого; аккаунт подключили — строка возвращается на том же шаге.
    .filter((p) => !(activeDef.id === BROADCAST_WIZARD_ID && accNone && p.name === 'audience'))
    // §11.15: поля групп «Сообщений цепочки» рисуют пилюли (шторка группы), не восемь наборов галочек подряд.
    .filter((p) => !(step.groupPills ?? []).some((g) => g.field === p.name || (g.extra ?? []).includes(p.name) || p.name === QS_HAS_WEBINAR));
  // Спец-поля TG-анкеты рисуются СВОИМИ контролами (владелец 05.08): «Канал» — пикером
  // известных каналов бота с живой проверкой прав, «Подпись источника» — с готовой
  // ссылкой, как в ручном создании. Остальные поля между ними — кусками ParamsForm
  // (порядок полей — авторский, keepOrder).
  const segments = useMemo(() => {
    // Сам «Проверить подписку» переехал в пузырь аккаунта (владелец 2026-08-06), а его
    // зависимые поля, если они есть, по-прежнему идут отдельным блоком в конце.
    const subFrom = restParams.findIndex((p) => p.displayOptions?.show?.checkSub !== undefined);
    const main = subFrom === -1 ? restParams : restParams.slice(0, subFrom);
    const sub = subFrom === -1 ? [] : restParams.slice(subFrom);
    return {
      main: buildWizSegs(main, step.groupBefore, activeDef.accountChannel),
      mainParams: main,
      sub: buildWizSegs(sub, step.groupBefore, activeDef.accountChannel),
      subParams: sub,
    };
  }, [restParams, activeDef, step]);
  // Вид вложения «вкусняшки» — из селекта рядом; при выборе «Ссылка» поле файла скрыто
  // (displayOptions), так что сюда попадает только настоящий вид.
  const giftRaw = formValues[WIZ_GIFT_KIND];
  const giftKind: ChatAttachKind =
    giftRaw === 'video' || giftRaw === 'audio' || giftRaw === 'file' ? giftRaw : 'image';
  /** Канал бота для масок и предела файла подарка (свод правил канала). */
  const giftChannel: 'telegram' | 'instagram' =
    activeDef.giftChannel ?? (activeDef.accountChannel === 'telegram' ? 'telegram' : 'instagram');

  // Пара «комментарий → публичный ответ» для предпросмотра (её считает анкета). Прежний
  // веб-макет телефона (`WizardPreviewPhone`) удалён 01.10.2026 по слову владельца: браузер
  // показывает то же, что iOS-приложение (см. openPreview).
  const previewComments = activeDef.previewComments?.(formValues, botLoc) ?? null;

  // ВЫСОТА МЕСТА ПОД СЦЕНУ (ТЗ-3 Т1: «мне нужно, чтобы без скроллинга всё поместилось»):
  // сцене отдаётся ВСЁ, что осталось от экрана под описанием, — от её верха до нижней
  // планки мастера. Сцена в это место вписывается сама (её телефон ограничен высотой
  // места), лишнее обрезается своим overflow, а страница не прокручивается.
  const stageRef = useRef<HTMLDivElement | null>(null);
  const [stageH, setStageH] = useState(0);

  // СЦЕНА ПЕРВОГО ЭКРАНА (ТЗ-3 Т1, владелец 2026-08-30: «на первый экран повесить с нашего
  // прома — там три экрана с разными сценариями, один из них надо было повесить»). Какая
  // сцена у какой анкеты — сказано в самом дефе (demoScene); null — сцены нет, экран
  // остаётся именем и описанием. Своей демонстрации мастер не рисует.
  const scenes = useScenes();
  // ФРАЗЫ ЭТОГО БОТА В ТЕЛЕФОНЕ (ТЗ-7 Ш3, владелец 2026-08-30: «при редактировании…
  // заполнить нужными фразами в телефоне всё»). В правке сцена показывает ответы анкеты,
  // при создании — свою историю. Сама сцена не меняется: строится её копия.
  // ФРАЗЫ БОТА В СЦЕНЕ — ЖИВЫЕ И ПРИ СОЗДАНИИ (владелец 09.09.2026: телефончик на любом
  // шаге показывает ту же сцену, что первый экран, но В ТЕКУЩЕМ СОСТОЯНИИ). В правке —
  // всё сохранённое, как было. В создании — только то, что человек УЖЕ ИЗМЕНИЛ против
  // умолчаний анкеты (прошёл шаг — его слова встали в сцену); нетронутое остаётся
  // примером ниши (mergedTexts), а не сырым дефолтом: до первого ввода первый экран
  // выглядит ровно как раньше. Сравнение с умолчаниями — слот за слотом: анкета сама
  // решает, какие поля куда ложатся (`def.sceneTexts`), второй карты «поле → слот» нет.
  const sceneTexts = useMemo<SceneTexts | null>(() => {
    if (!activeDef.sceneTexts) return null;
    const full = activeDef.sceneTexts(formValues, botLoc);
    if (edit) return full;
    const base = activeDef.sceneTexts(wizardDefaults(activeDef, botLoc), botLoc);
    const out: SceneTexts = {};
    for (const k of SCENE_SLOTS) {
      const v = full[k];
      if (v && v !== base[k]) out[k] = v;
    }
    return Object.keys(out).length > 0 ? out : null;
  }, [edit, activeDef, formValues, botLoc]);
  // ПРИМЕР ПОД ЧЕЛОВЕКА (портрет клиента): ниша даёт кодовое слово и обещание, портрет
  // канала — лицо, ник и публикацию.
  // ЛИЦО И НИША СЛЕДУЮТ АККАУНТУ АНКЕТЫ (владелец 03.09.2026, О5.9 Д1/Д2) — и при
  // создании, и в правке. Раньше при создании пример крутил аккаунты по кругу, а форма
  // тем временем уже подставила ПЕРВЫЙ подключённый: в телефоне стояло одно лицо, в
  // анкете — другой аккаунт, и смена аккаунта на первом шаге ничего не меняла. Теперь
  // истина одна — `formValues.accountId` (это credId), и мемо пересчитываются вместе с
  // ним. Ротации нет вовсе.
  // Аккаунт ищем по ВХОЖДЕНИЮ в список доступов (Д9): тот же бот бывает подключён
  // дважды, и второй его доступ — это он же, а не «неизвестный аккаунт».
  // Не нашли (портрет ещё не доехал, аккаунт сняли) — падаем на главную нишу человека
  // (useNichePersonal), но НЕ на чужой аккаунт. На лендинге без входа этого компонента
  // нет вовсе — там своя ротация случайной ниши (niche-local).
  // САМ РАСЧЁТ — ОБЩИЙ (lib/wizard-demo-url.ts, demoPersona): им же список ботов и
  // каталог считают лицо и нишу для ПРЕДЗАГРУЗКИ сцены. Второй копии запасных вариантов
  // (ниша аккаунта → главная; лицо аккаунта → нейтральное из доступа → главный портрет)
  // тут больше нет — разъехались бы прогретый адрес и адрес мастера.
  // useNichePersonal вызываем ради его же побочного дела — запомнить нишу на устройстве.
  const { data: nicheData } = useNichePersonal();
  const channel = activeDef.accountChannel ?? null;
  const wizAccId = typeof formValues.accountId === 'string' ? formValues.accountId : '';
  const picked = useMemo(
    () =>
      wizAccId
        ? accountByAnyCred(nicheData?.accounts, wizAccId)
        : // Доступ ещё не выбран (список подключений не доехал) — показываем первый
          // аккаунт этой сети: он же встанет в поле, как только список приедет.
          pickAccountForChannel(nicheData?.accounts, channel),
    [nicheData, channel, wizAccId],
  );
  // Строки доступа отдельно: React Compiler не даёт класть в зависимости сам объект
  // доступа (он приходит из списка, который меняется), а строки — можно.
  const accName = accCred?.name ?? null;
  const accHandle = accCred?.handle ?? null;
  // §9.3 п.3: ниша не определена — случайная ниша справочника на эту анкету (свой жребий на её показ; сервер для
  // приложения тянет его по id черновика), стока «без ниши» нет. Одна на все шаги анкеты.
  const [nicheSeed] = useState(() => `${Date.now()}:${Math.random()}`);
  const { niche, profile } = useMemo(() => {
    const p = demoPersona({
      data: nicheData,
      channel,
      accountId: wizAccId,
      accCred: { name: accName, handle: accHandle },
    });
    return { profile: p.profile, niche: p.niche ?? seededNiche(nicheData?.niches, nicheSeed) };
  }, [nicheData, channel, wizAccId, accName, accHandle, nicheSeed]);
  const t9n = useTranslations('auth.onboarding.pers');
  // СЛОВА НИШИ отдельным мемо: их берут и веб-превью (demoScene), и адрес сцены для
  // приложения (demoUrl). Свои фразы бота (правка) СИЛЬНЕЕ слов ниши — тогда nicheTexts
  // не нужны вовсе (null). ПОЛ владельца (О5.4) выбирает форму «отправил(а)» в словаре.
  // СЛОВА НИШИ — ВСЕГДА, когда ниша известна (и в создании, и в ПРАВКЕ). В правке они
  // ОСНОВА, а настроенные фразы бота (`sceneTexts`) ложатся ПОВЕРХ (см. mergedTexts):
  // что клиент задал — показываем его, а НЕзаданные места (напр. бот реагирует на любой
  // комментарий, кодового слова нет) берут пример НИШИ, а не сырой дефолт сцены ФИГУРА
  // (владелец 03.09.2026). ПОЛ владельца (О5.4) выбирает форму «отправил(а)».
  const nicheTexts = useMemo(
    () => (niche ? nichePhrases(t9n, niche, profile?.gender ?? null, activeDef.demoScene) : null),
    [niche, profile, t9n, activeDef.demoScene],
  );
  // ИТОГОВЫЕ СЛОВА СЦЕНЫ: слова ниши как основа, настроенные фразы бота ПОВЕРХ (сильнее),
  // подпись поста с реальным обещанием бота. Само правило — общее с предзагрузкой
  // (lib/wizard-demo-url.ts, mergedSceneTexts): список ботов считает тем же адрес правки.
  const mergedTexts = useMemo<SceneTexts | null>(
    () => mergedSceneTexts({ nicheTexts, sceneTexts, edit: !!edit, niche, t: t9n }),
    [nicheTexts, sceneTexts, edit, niche, t9n],
  );
  const demoScene = useMemo(() => {
    // Сцена ищется по ключу (SceneDef.k): порядок и состав тура на анкеты не влияют.
    const one = activeDef.demoScene
      ? (scenes.find((s) => s.k === activeDef.demoScene) ?? null)
      : null;
    if (!one || !activeDef.demoScene) return one;
    // Слова: ниша — основа, настроенные фразы бота поверх (mergedTexts); лицо — profile.
    // §8.22 / §8.23 п.1: свои сообщения цепочки у бота — сцена показывает их, без выборки набора.
    const own = hasOwnLetters(sceneTexts) ? withoutSeries(one) : one;
    if (!mergedTexts && !profile && !niche) return own;
    return nicheScene(own, mergedTexts, niche?.key ?? null, profile, locale);
  }, [activeDef.demoScene, scenes, mergedTexts, profile, niche, locale, sceneTexts]);

  // Место под сцену меряем ПОСЛЕ отрисовки заголовка и описания: сколько осталось от
  // низа экрана (нижняя планка мастера прибита к полу) — столько сцене и отдаём.
  // Телефон стоит на «Знакомстве» ВСЕГДА (ТЗ-7 Ш3): в правке — с фразами бота, вместо
  // прежней сводки заполненного (она снята целиком, и в вебе, и в приложении).
  const showScene = onIntro && demoScene !== null;
  useEffect(() => {
    if (!showScene) return undefined;
    const calc = () => {
      const el = stageRef.current;
      if (!el) return;
      // INVARIANT(scriptera): место под сцену считаем по offsetTop от ДОМА (шторка/слой),
      // а НЕ по getBoundingClientRect. Причина (владелец 02.09.2026, Н25 «мокап обрезан
      // снизу и пуст, всё скукоживается»): шторка мастера въезжает снизу трансформом, и
      // на первом замере rect показывал сцену за нижним краем экрана — vh − top уходило
      // в минус, сцена получала минимальные 240 и телефон обрезался. offsetTop трансформа
      // не видит. Канон: docs/concepts/UI-TEMPLATE-CANON.md, словарь облика.
      const host = el.closest('.drawer, .native-layer') as HTMLElement | null;
      // Считаем от НИЖНЕГО КРАЯ СОСЕДА СВЕРХУ, а не от самой сцены: её собственное место
      // зависит от высоты, которую мы же и ставим, — замер «сам от себя» не сходился.
      const anchor = (el.previousElementSibling as HTMLElement | null) ?? el;
      let top = 0;
      for (
        let n: HTMLElement | null = anchor;
        n && n !== host;
        n = n.offsetParent as HTMLElement | null
      ) {
        top += n.offsetTop;
      }
      if (anchor !== el) top += anchor.offsetHeight;
      // Дом-шторка сама стоит у низа экрана (fixed, bottom: 0) — её высота и есть место.
      // На СТРАНИЦЕ бота дома нет: там сцена живёт в потоке документа, и её экранное
      // место — высота вьюпорта минус прокрутка документа (планка внизу тоже fixed).
      const vh = host ? host.clientHeight : (window.visualViewport?.height ?? window.innerHeight);
      if (!host) top -= window.scrollY;
      const foot = document.querySelector('.wiz-foot')?.getBoundingClientRect().height ?? 0;
      setStageH(Math.max(240, Math.round(vh - top - foot - 8)));
    };
    calc();
    // Пересчёт на каждую перекладку (планка появилась, шрифты доехали, поворот экрана):
    // одного замера на маунте не хватало — планки внизу тогда ещё не было, и сцена
    // получала её высоту в подарок и вылезала за низ экрана.
    // Пересчёт откладываем на кадр: замер из самого колбэка ResizeObserver Chrome
    // считает «петлёй» и молча гасит следующие уведомления — экран так и оставался с
    // первым, неверным числом.
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(calc);
    });
    const pane = stageRef.current?.parentElement;
    if (pane) ro.observe(pane);
    const footEl = document.querySelector('.wiz-foot');
    if (footEl) ro.observe(footEl);
    void document.fonts?.ready.then(calc).catch(() => {});
    window.addEventListener('resize', calc);
    window.visualViewport?.addEventListener('resize', calc);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', calc);
      window.visualViewport?.removeEventListener('resize', calc);
    };
  }, [showScene, step.title, step.intro]);

  /** Ряды переписки в форме натива: вид вложения и имя файла разбирает веб. */
  const toNativeMsgs = useCallback(
    (list: WizardPreviewMsg[] | null): NativeWizPreviewMsg[] =>
      (list ?? []).map((m) => ({
        from: m.from,
        text: m.text,
        ...(m.buttons?.length ? { buttons: m.buttons } : {}),
        ...(m.imageUrl ? { imageUrl: m.imageUrl } : {}),
        ...(m.attach
          ? {
              attach: {
                label: ts(chatContentNameKey(m.attach.kind) as 'chatContentImage'),
                ...(attachFileRef(m.attach.url).name
                  ? { name: String(attachFileRef(m.attach.url).name) }
                  : {}),
              },
            }
          : {}),
      })),
    [ts],
  );

  // СЦЕНА шага «Знакомство» ДЛЯ ПРИЛОЖЕНИЯ (ТЗ-6 С2, владелец 2026-08-30: «вставить тот
  // мобильный телефон, который уже сделан, чтобы ни на пиксель не отличался»). В вебе
  // первый экран рисует сам компонент Scene, а в приложении её же показывает веб-вью —
  // нативу уезжает ТОЛЬКО адрес нашей публичной страницы с этой сценой. Пузырей натив
  // больше не рисует: копия «похоже, но не то» отменена владельцем.
  const demoUrl = useMemo(() => {
    if (!activeDef.demoScene || demoScene === null) return '';
    if (typeof window === 'undefined') return '';
    // Тема — та же, что у кабинета; натив, открывая адрес, ставит СВОЮ (тему шторки).
    // Фразы бота едут параметрами (ТЗ-7 Ш3); при создании их нет вовсе.
    // ЛИЦО И СЛОВА КЛИЕНТА — В АДРЕС (владелец 03.09.2026): превью в приложении открыто
    // в изолированном веб-вью без куки входа (WizardScreen.swift), сервер иначе не узнает
    // человека. Свои фразы бота (правка) идут как есть; при создании — слова ниши; лицо
    // и пол — всегда. Меняется профиль (портрет доехал позже) — адрес меняется, натив
    // перегружает превью (loadDemoIfNeeded сверяет адрес).
    // СБОРКА АДРЕСА — ОБЩАЯ с предзагрузкой каталога (lib/wizard-demo-url.ts, О5.9):
    // натив греет страницу заранее и отдаёт мастеру готовую, только если адрес совпал
    // до буквы — второй сборки адреса тут быть не должно.
    return buildDemoSceneUrl({
      origin: window.location.origin,
      locale,
      scene: activeDef.demoScene,
      texts: mergedTexts,
      profile,
      theme: demoThemeNow(),
      // Пометка «Доступно только в Pro» на сцене (владелец 12.10.2026) — цепочка и рассылка без Pro.
      pro: proLock && PRO_ONLY_WIZARDS.includes(activeDef.id),
      niche: niche?.key ?? null,
    });
  }, [activeDef.demoScene, activeDef.id, demoScene, locale, mergedTexts, profile, proLock]);

  // ПРЕДПРОСМОТР ДЛЯ ПРИЛОЖЕНИЯ — АДРЕС НАШЕЙ ЖЕ СТРАНИЦЫ (ТЗ-9 Е3/Е4, владелец
  // 2026-08-30: «чтобы чат был так же хорош, как Инстаграм на первом экране»). Приём тот
  // же, что у сцены знакомства: рисует веб, показывает натив. Содержимое едет ХВОСТОМ
  // адреса — реплики бота этого клиента на сервер не уезжают (lib/wizard-preview-data).
  /** Отпечаток содержимого превью: по нему пересчитывается адрес страницы-телефона —
   *  сами ряды пересобираются каждым рендером и памятью быть не могут. */
  const previewSig = JSON.stringify([previewMsgs, previewComments]);
  const previewUrl = useMemo(() => {
    if (previewMsgs === null || typeof window === 'undefined') return '';
    const theme = document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
    // Пара «комментарий человека → публичный ответ бота» — первая: их считает анкета, и
    // на экране показываем один разговор, а не список. Текста публикации в анкете нет
    // (его человек пишет в самом Инстаграме) — подписи у поста не будет.
    const pair = previewComments?.[0] ?? null;
    // АВАТАРКА АККАУНТА В ШАПКЕ ПЕРЕПИСКИ (О5.9 Д11): раньше на её месте стоял пустой
    // кружок. Берём фото ТОГО аккаунта, что выбран в анкете (picked), и только когда
    // байты скачаны — ручка отдаёт адрес лишь в этом случае. Адрес ПУБЛИЧНЫЙ
    // (/api/scene-avatar/<cred>): страница открыта в веб-вью без куки входа.
    const ava = sceneAvatarUrl(picked?.avatarUrl ?? null);
    const tail = wizardPreviewHash({
      skin: activeDef.accountChannel === 'telegram' ? 'telegram' : 'ig-direct',
      account: {
        name: accCred ? botAccountLabel(accCred) : ts('chatStartBot'),
        ...(accCred?.handle ? { handle: accCred.handle } : {}),
        ...(ava ? { avatar: ava } : {}),
      },
      dm: previewMsgs,
      ...(pair ? { comment: { text: pair.user, ...(pair.bot ? { reply: pair.bot } : {}) } } : {}),
      dict: { dm: ta('wphTabDirect'), comment: ta('wphTabComment') },
    });
    return `${window.location.origin}/${locale}/wizard-preview?theme=${theme}${tail}`;
    // Ряды превью пересобираются каждым рендером (новый массив при тех же словах), и в
    // зависимостях они давали бы новый адрес на каждую букву — считаем по СОДЕРЖИМОМУ.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSig, locale, ta, ts, activeDef, accCred, picked]);

  // Поля-ссылки анкеты (владелец 2026-08-11, скрин «Goo»): перед уходом с шага адрес
  // ДОПИСЫВАЕТСЯ до https:// (если схему не написали) и проверяется, что это вообще
  // адрес; мусор не пускаем дальше — то же поведение, что у обязательных полей.
  const linkFields = () =>
    visibleParams(step.params, formValues).filter(
      (p) => p.name === 'linkUrl' && !isEmptyAnswer(formValues[p.name]),
    );
  // ── РОДНОЙ МАСТЕР В ПРИЛОЖЕНИИ (docs/NATIVE-WIZARD-PLAN.md, облик «Стекло») ──
  // Анкету рисует натив, а веб остаётся мозгом: проверка, переходы и создание бота ниже
  // те же самые — второй логики нет. Натив берёт анкету, только если умеет нарисовать ВСЕ
  // её видимые поля (иначе nativeWizSteps вернёт null) и если сборка знает методы wiz*.
  const wizNativeReady = useNativeMethod('wizSupported');
  // ФАЙЛ ПОДАРКА В ПРИЛОЖЕНИИ (владелец 19.09.2026): сборка умеет системное окно файлов
  // под вид (`wizFile`) — поле едет рядом `file`; не умеет — плашкой-подсказкой.
  const wizFileReady = useNativeMethod('wizFile');
  // РОДНОЕ ОКНО ПИСЬМА И СТРАНИЦА ПИСЬМА (Г6/Г8): без них шаг со списком писем и строка
  // «Сообщение» были бы тупиком — тап никуда не ведёт. Сборка их не знает — анкету ведёт
  // веб ЦЕЛИКОМ (штатный запасной путь Д4, CLAUDE.md запрет №8).
  const msgNativeReady = useNativeMethod('msgOpen');
  const rowsNativeReady = useNativeMethod('wizItemRowsOpen');
  // ПОЯСНЕНИЯ ПОЛЕЙ, которые страница пишет САМА, — те же и в приложении (ТЗ-2 З2).
  // Кодовые слова: в каталоге у поля описание «через запятую», а контрол давно кружками —
  // человек видит «введите слово и нажмите Enter», и в приложении обязан видеть то же.
  // Файл «вкусняшки»: форматы и предел канала — их считает свод правил канала, в анкете
  // такого описания нет вовсе.
  // Старая сборка iOS теряет недописанное слово на «Далее» — ей поле слов обычным текстом.
  const wordsPlain = useMemo(() => nativeWordsPlain(isNewAppBuild()), []);
  const nativeHints = useMemo(() => {
    const out: Record<string, string> = {
      [WIZ_GIFT_FILE]: limitHint(
        activeDef.accountChannel === 'telegram' ? 'telegram' : 'instagram',
        giftKind,
        ts,
      ),
    };
    // Полей слов у анкеты бывает несколько — свои у каждого входа (§5 плана входов), и
    // пояснение у них одно на всех: «введите слово и нажмите Enter».
    for (const s of steps) {
      for (const p of s.params)
        if (isWizWordsField(p.name)) out[p.name] = ta(wordsPlain ? 'wizWordsPlainHint' : 'wizWordsHint');
    }
    // РАССЫЛКА БЕЗ PRO (владелец 12.10.2026): под «Аккаунтом» общего родного мастера — тот же
    // факт тарифа, что под «Получателями» родной формы рассылки. Без цены и «подключить».
    if (activeDef.id === BROADCAST_WIZARD_ID && proLock) out.accountId = tBill('proAppBcastNote');
    return out;
  }, [ta, ts, activeDef, giftKind, steps, proLock, tBill, wordsPlain]);
  // ПАМЯТЬ РОДНОГО ПУТИ на всю жизнь панели: взял ли натив анкету хоть раз, последний
  // набор, который он умел нарисовать, первое незнакомое ему поле и уехавшую жалобу.
  // Ссылка (а не состояние): пишет её сам пересчёт шагов, и перерисовки она не требует —
  // тем же приёмом рядом живёт nativeModeRef.
  const nativeMem = useRef<{
    took: boolean;
    steps: NativeWizStep[] | null;
    bad: { step: string; param: string; type: string } | null;
    reported: boolean;
  }>({ took: false, steps: null, bad: null, reported: false });
  /**
   * АДРЕС РУЧКИ ДЛЯ СНИМКА — ПОЛНЫЙ (хвост ТЗ блока V): оболочка заливает его сама, а
   * относительный путь ей не с чем сложить. Вид и канал те же, что у заливки с сайта:
   * второй дороги у картинки мастера нет. Сборке без `wizFileGo` ключ не шлём — она
   * возит снимок по-старому, строкой base64 (запрет №8).
   */
  const photoUpload = (): { url: string; field: string } | undefined => {
    if (typeof window === 'undefined' || !nativeMethod('wizFileGo')) return undefined;
    return {
      url: `${window.location.origin}/api/cabinet/media?kind=image&channel=${giftChannel}`,
      field: 'file',
    };
  };

  // Общая обстановка сборки родных полей: её читают и шаги мастера, и страница элемента
  // шага-списка — вторая копия разъехалась бы подписями и подсказками.
  const nativeCtx = useMemo(
    () => ({
      values: formValues,
      accOptions,
      accLabel: ta('wizAccount'),
      optional: tOpt('optional'),
      postsLabel: (n: number) => ts('chatStartPostsCount', { n }),
      // СНИМОК ЗАЛИВАЕТ САМА ОБОЛОЧКА (хвост ТЗ блока V) — ключ едет только сборке,
      // которая это умеет; прочим поле-картинка прежнее (base64 через мост).
      ...(photoUpload() ? { photoUpload: photoUpload()! } : {}),
      ...(warnings ? { warnings } : {}),
      hints: nativeHints,
      ...(wordsPlain ? { wordsPlain: true } : {}),
      // ВАРИАНТЫ ОТВЕТА НА ШАГЕ — строкой со счётом, правка на странице-шторке (ТЗ USERTEST-1014 п.7).
      linesRow: (n: number) => ta('wizReplyCount', { n }),
      // Подпись про телефончик показываем, только когда сам телефончик есть (ТЗ-9 Е2).
      hasPreview: hasWizPreview,
      // Слово красной кнопки смахивания у строк списка-полей (этап J п.4) — то же
      // «Удалить», что у карточек и строк шага-списка: второго слова не заводим.
      removeLabel: ta('cardDelete'),
      // СТРОКА «ДОБАВИТЬ АККАУНТ» в листе выбора аккаунта (владелец 11.09.2026): слово
      // шлём, только когда сборка умеет стопку подключения, — иначе тап было бы нечем
      // обслужить (запрет №8).
      ...(connFlow.native ? { accAddLabel: ta('wizAddAccount') } : {}),
      // «Добавить бота» у поля «Telegram-бот» (UT 14.10 п.6) — тем же ключом `addLabel`.
      ...(connFlow.native ? { tgAddLabel: ta('wizAddTgBot') } : {}),
      // ПОЛЕ «КАНАЛ» (ТЗ 19.09.2026 Р1–Р3): варианты реестра, строка «Добавить канал» и
      // состояние живой проверки — второй строкой выбранного варианта и красной строкой
      // под капсулой. Слово действия шлём, только когда сборка умеет стопку: иначе тап
      // было бы нечем обслужить (запрет №8).
      chanOptions: chanNativeOptions,
      ...(connFlow.native ? { chanAddLabel: ts('chatSubAddChannel') } : {}),
      ...(chanStatus ? { chanStatus } : {}),
      ...(chanFail ? { chanInvalid: chanFail } : {}),
      // Подарок «аудио/видео/файл» — рядом `file` под маски канала, а не картинкой.
      giftKind,
      giftChannel: giftChannel,
      fileOk: wizFileReady,
      fileNote: ta('wizFileNeedsUpdate'),
      // Ряд файла говорит, чем занят: заливкой (её ведёт натив) или подготовкой ролика.
      fileBusy: fileUp
        ? ts('attachUploading')
        : fileFit !== ''
          ? ts(fileFit)
          : nativeFix.busy
            ? ts(nativeFix.busyWord)
            : '',
    }),
    [
      formValues,
      accOptions,
      ta,
      tOpt,
      ts,
      warnings,
      nativeHints,
      wordsPlain,
      hasWizPreview,
      connFlow.native,
      chanNativeOptions,
      chanStatus,
      chanFail,
      giftKind,
      giftChannel,
      wizFileReady,
      fileUp,
      fileFit,
      nativeFix.busy,
      nativeFix.busyWord,
    ],
  );
  const nativeSteps = useMemo(() => {
    // БЕЗ АККАУНТА АНКЕТА ВСЁ РАВНО ОТКРЫВАЕТСЯ (владелец 11.09.2026): подключение живёт
    // на шаге «Аккаунт» строкой «Добавить аккаунт». Сборка стопки не умеет — прежний
    // запасной ход (родное окно → «Подключения»), запрет №8.
    if (!wizNativeReady || (accNone && !connFlow.native)) return null;
    // Анкета со списком писем или строкой «Сообщение» требует ещё двух родных экранов.
    if (
      steps.some((s) => s.itemList || (s.messages?.length ?? 0) > 0) &&
      !(msgNativeReady && rowsNativeReady)
    ) {
      return null;
    }
    nativeMem.current.bad = null;
    return nativeWizSteps(
      steps.map((s) => ({
        key: s.key,
        title: s.title,
        ...(s.intro ? { intro: s.intro } : {}),
        // ТЕЛЕФОНЧИКА НА ШАГЕ НЕТ (К19.1 п.9): признак шага анкеты едет нативу как есть
        // (аддитивный ключ, wizard-native.ts кладёт его в `NativeWizStep.noPreview`).
        ...(s.noPreview ? { noPreview: true } : {}),
        // ЗНАКОМСТВО (ТЗ-2 З1) уезжает нативу НОВЫМИ ключами: сводка заполненного
        // (правка) и сцена с примеров работ (создание). Старая сборка их не читает и
        // покажет шаг с одним заголовком и подводкой — так и задумано.
        ...(s.key === WIZ_INTRO_KEY
          ? {
              kind: 'intro' as const,
              // Правка — слово «Редактировать» на кнопке первого экрана (ТЗ-7 Ш2).
              ...(edit ? { edit: true } : {}),
              ...(demoUrl ? { demo: { url: demoUrl } } : {}),
            }
          : {}),
        // ПОДПИСИ БЛОКОВ (ТЗ-9 Е6): те же черты с подписью, что видит человек в браузере
        // («Если нет подписки»). Условные гасим ЗДЕСЬ, тем же правилом, что и на
        // странице (displayOptions.show): нативу уезжают только видимые.
        ...(s.groupBefore
          ? {
              groupBefore: Object.fromEntries(
                Object.entries(s.groupBefore)
                  .map(([name, g]) => {
                    const title = typeof g === 'string' ? g : g.title;
                    const show = typeof g === 'string' ? undefined : g.show;
                    const on = Object.entries(show ?? {}).every(([n, vals]) =>
                      vals.includes(formValues[n]),
                    );
                    return on ? ([name, title] as const) : null;
                  })
                  .filter((x): x is readonly [string, string] => x !== null),
              ),
            }
          : {}),
        // ШАГ-СПИСОК ОБЪЕКТОВ (ТЗ 03.09.2026 В2, письма цепочки): нативу уезжают ГОТОВЫЕ
        // строки, а страницу письма он попросит сам (wizItemListTap → wizItemRowsOpen).
        ...(s.itemList
          ? {
              itemList: (() => {
                const cfg = s.itemList;
                const n = cfg.count(formValues);
                const kinds = itemAddKinds(cfg, formValues);
                return {
                  // Своего ответа у списка нет — адресом ему служит ключ шага.
                  name: s.key,
                  items: Array.from({ length: n }, (_, k) => {
                    const line = cfg.line(formValues, k + 1, itemLive);
                    return {
                      key: String(k + 1),
                      title: line.title,
                      sub: line.sub,
                      // Вход без настроек — строка без шеврона и без страницы (К16.0 п.3).
                      ...(line.chevron === false ? { chevron: false } : {}),
                      // Мёртвый вход (бот удалён, П203) — подстрока красным, тот же ключ,
                      // что у строки «Сообщение» (П132 п.2); старая сборка ключ не читает.
                      ...(line.invalid ? { invalid: true } : {}),
                    };
                  }),
                  // Список полон — пунктирной строки нет вовсе.
                  addLabel: n < cfg.max ? cfg.itemAdd : '',
                  empty: cfg.itemEmpty,
                  remove: cfg.itemRemove,
                  max: cfg.max,
                  // Последнее письмо переставлять некуда; у входов перестановки нет вовсе.
                  canMove: wizItemCanMove(cfg) && n > 1,
                  // СПИСОК НАД ПОЛЯМИ ШАГА (этап J п.1): решает анкета, а не экран.
                  ...(cfg.first ? { first: true } : {}),
                  ...(kinds.length > 0
                    ? {
                        addKinds: nativeAddKinds(kinds, entryWords.already),
                        // ВСЕ ВИДЫ ЗАНЯТЫ — «Добавить триггер» неактивна (К20.1 п.15,
                        // владелец 07.09.2026): открывать нечего, меню состояло бы из
                        // одних серых пунктов. Натив рисует строку приглушённой и
                        // отвечает отбоем.
                        ...(kinds.every((k) => k.disabled) ? { addDisabled: true } : {}),
                      }
                    : {}),
                };
              })(),
            }
          : {}),
        // ШАГ-СООБЩЕНИЕ (ТЗ Б, З7): ОДНА строка-карточка с превью письма и ничего рядом —
        // уезжает родным полем. Несколько карточек или карточка рядом с полями (шаг
        // «Дополнительно» шаблона) — родного вида нет, анкета уходит вебу целиком.
        ...(() => {
          const cards = wizMessageCards(s, formValues);
          if (cards.length === 0) return {};
          const covered = wizCoveredParams(s);
          const rest = visibleParams(s.params, formValues).filter((p) => !covered.has(p.name));
          const card = cards[0]!;
          if (cards.length > 1 || rest.length > 0) return { webOnly: true };
          return {
            message: {
              name: card.param,
              label: card.title,
              value: cardValue(card, formValues),
              empty: card.empty,
            },
          };
        })(),
        // Строки самого шага (событие запуска цепочки, И10 п.10) — тем же полем `rows`,
        // что и страница письма: второго вида строки-перехода в анкетах нет.
        ...(() => {
          const parts = nativeStepParts(s);
          return {
            ...(parts.rows.length > 0 ? { rows: nativeWizRows(parts.rows) } : {}),
            params: visibleParams(parts.params, formValues),
          };
        })(),
      })),
      nativeCtx,
      (bad) => {
        nativeMem.current.bad = bad;
      },
    );
  }, [
    wizNativeReady,
    msgNativeReady,
    rowsNativeReady,
    accNone,
    nativeStepParts,
    connFlow.native,
    steps,
    formValues,
    edit,
    demoUrl,
    nativeCtx,
    cardValue,
    itemLive,
    itemAddKinds,
    entryWords,
  ]);
  // ── КТО РИСУЕТ АНКЕТУ, РЕШАЕТСЯ ОДИН РАЗ — НА ОТКРЫТИИ (владелец 02.09.2026, Н15
  // docs/NATIVE-DESIGN-FIXLIST.md: «тап „Проверить подписку“ иногда показывает страницу
  // конструктора») ─────────────────────────────────────────────────────────────────────
  // INVARIANT(scriptera): УЖЕ ОТКРЫТАЯ родная анкета НИКОГДА не уходит на веб посреди
  // работы. Причина: `nativeSteps` пересчитывается на КАЖДЫЙ ответ (тумблер «Проверить
  // подписку» открывает новые поля, ревалидация подключений на миг обнуляет список
  // аккаунтов), и один-единственный null посреди анкеты снимал родную шторку
  // (useNativeWizard active→false ⇒ wizClose) и звал запасной путь хозяина: при правке
  // это СТРАНИЦА бота — та самая «обёртка конструктора» из претензии владельца.
  // Поэтому решение латчится: взял натив — держим натив, а на «не смог» жалуемся в центр
  // ошибок (это наша поломка, клиенту её не показываем — CLAUDE.md запрет №7).
  // Канон: docs/NATIVE-WIZARD-PLAN.md, П-23 + docs/NATIVE-SLIDE-PLAN.md §11.
  if (nativeSteps) {
    nativeMem.current.took = true;
    nativeMem.current.steps = nativeSteps;
  }
  const nativeStepsLive = nativeSteps ?? (nativeMem.current.took ? nativeMem.current.steps : null);
  const nativeOn = nativeStepsLive !== null;
  // ── СТРАНИЦА ПИСЬМА В ПРИЛОЖЕНИИ (шаг-список объектов, ТЗ 03.09.2026 В2, карта Д2) ──
  // Глубина стопки: анкета → письмо → строка письма (поля) → окно «Сообщение». Каждый
  // уровень свой, «Назад» уводит ровно на уровень выше (§11 «стопкой, не подменой»).
  /** Какое письмо какого шага сейчас открыто (номер письма, 1…). */
  const [rowsAt, setRowsAt] = useState<{ name: string; i: number } | null>(null);
  /**
   * ОТКРЫТОЕ «ДОБАВИТЬ …» ШАГА-СПИСКА ОБЪЕКТОВ в приложении (К5.1). Без `kind` — меню
   * ВИДОВ, с `kind` — страница выбора (боты кабинета). Обе рисуются существующим
   * `wizItemRowsOpen` (строки + тап `wizItemRowTap`), поэтому нового метода моста нет и
   * дорожка работает на сборке, которая про `addKinds` ничего не знает (запрет №8).
   */
  const [addAt, setAddAt] = useState<{ name: string; kind?: string } | null>(null);
  /**
   * ЧЕРНОВИК НОВОГО ТРИГГЕРА (владелец 06.09.2026, П10 п.5–7): страница настроек
   * открывается ДО того, как триггер попал в список, шторкой на полэкрана поверх списка
   * ботов; в список он попадает только по «Готово». Закрыли иначе — ничего не добавилось.
   * `next` — ответы анкеты С этим триггером (его номер `i`), правки полей копятся сюда же.
   */
  /**
   * ЧЕРНОВИК ЖИВЁТ ЕЩЁ И В ССЫЛКЕ (К18.1 п.7, владелец 06.09.2026: «набрал слово, нажал
   * „Готово“ без Enter — слово не сохранилось»). Приложение шлёт `wizChange` и
   * `wizItemBack` ОДНИМ ТИКОМ: состояние React к моменту второго события ещё старое, и
   * «Готово» уносило в ответы черновик БЕЗ последней правки. Ответы анкеты держатся
   * синхронной ссылкой ровно по этой причине (`liveValues`) — черновик теперь тоже
   * (`useNativeDraft`: состояние для экрана, `draftRef.read()` для обработчиков).
   */
  const draftRef = useNativeDraft<WizDraftAdd>();
  const draftAdd = draftRef.value;
  const setDraftAdd = draftRef.set;
  /**
   * ЧЕРНОВИК ОТКРЫТОГО ПИСЬМА (ТЗ 21.09.2026 раздел 6, см. `WizLetterDraft`). Держится
   * тем же `useNativeDraft`, что и черновик триггера, и ровно по той же причине:
   * приложение шлёт правку и «Готово» ОДНИМ тиком, и обработчик обязан читать черновик
   * ссылкой (`letterRef.read()`), а не значением прошлого рендера.
   */
  const letterRef = useNativeDraft<WizLetterDraft>();
  const letterDraft = letterRef.value;
  const setLetterDraft = letterRef.set;
  /** Открытая строка страницы письма С ПОЛЯМИ («Сколько ждать», «Кому слать»). */
  const [rowAt, setRowAt] = useState<string | null>(null);
  /**
   * «ОТКРЫТЬ ЗАНОВО» (К16.1 П1, претензия владельца 06.09.2026: страница входа
   * открывалась и сразу закрывалась, а следующие тапы были мертвы). Тап по строке, что
   * уже считается открытой, двигает счётчик — и мост шлёт `wizItemOpen` ещё раз, даже
   * если состояние веба не изменилось. Так «мёртвого» экрана не бывает: страницу мог
   * увести сам натив, не сказав вебу.
   */
  const [reopen, setReopen] = useState(0);
  /** ОТКРЫТАЯ СТРАНИЦА-ШТОРКА «ВАРИАНТЫ ОТВЕТА» в приложении (ТЗ USERTEST-1014 п.7): имя поля списка строк; null —
   *  её нет. Открывает тап по строке со счётом (`wizPick`), закрывает «Готово»/«×» (`wizItemBack`). */
  const [linesAt, setLinesAt] = useState<string | null>(null);
  /**
   * «ГОТОВО» ОТБИТО ПРОВЕРКОЙ (К22.2 этап 1 п.1, претензия владельца 07.09.2026 по
   * сборке 164: «нажал — шторка закрылась, потом сама открылась заново; нажал ещё раз —
   * закрылась и больше не открылась… добавление триггера сломалось»).
   *
   * Закрытие по «Готово» решает ВЕБ: применил — страница исчезла (`wizItemClose`),
   * отказал — она остаётся на месте, а сборке уезжает `wizItemPatch` с теми же полями и
   * подсказкой-ошибкой. Счётчик двигают ровно отказы: содержимое полей при этом не
   * меняется (подсказка стояла и до «Готово»), и без него патч не ушёл бы вовсе —
   * страница молчала бы в ответ на нажатие. Ключ страницы не трогаем: `wizItemOpen`
   * на пути отказа и был дефектом.
   */
  const [warnTick, setWarnTick] = useState(0);
  /**
   * «ПЫТАЛИСЬ ОТПРАВИТЬ» У СТРАНИЦЫ ТРИГГЕРА (К24 п.1, претензия владельца 07.09.2026 по
   * сборке 165: открыл «Сообщение в директ» — у «Кодовых слов» уже красная рамка и
   * подсказка «Впишите хотя бы одно кодовое слово», а «Готово» он не нажимал; «куча
   * подобных мест»).
   *
   * `pageWarnAt` считается ВСЕГДА — им решается, пускать ли «Готово». А в поля
   * (`invalid` нативу, `FError` в браузере) подсказка кладётся только при этом признаке:
   * его ставит ПЕРВЫЙ отказ «Готово», и дальше подсказки живут до ухода со страницы,
   * пересчитываясь по живым значениям (поправка владельца 13:52: «живёт, пока не покинул
   * окно»). Признак у черновика и у правки свой; состояние — для сборки страницы,
   * ссылка (`useNativeDraft`) — для обработчиков моста, они бегут до рендера.
   */
  const draftTriedRef = useNativeDraft<true>();
  const rowTriedRef = useNativeDraft<true>();
  const setDraftTried = draftTriedRef.set;
  const setRowTried = rowTriedRef.set;
  const draftTried = draftTriedRef.value === true;
  const rowTried = rowTriedRef.value === true;
  /**
   * РЕЖИМ ПРАВКИ ШАГА-СПИСКА (К16.1 П6, владелец 06.09.2026: «справа сверху сделать ещё
   * стеклянную кнопку „изменить“, чтобы кто не прошарил про смахивание — нажал, и у всех
   * появились кнопки удалить»). Один на шаг: ушли с шага — режим снят.
   */
  const [listEdit, setListEdit] = useState(false);
  /** §11.16 п.3: открытая полушторка паузы письма «Цепочки сообщений» (номер письма) — в режиме «Изм.». */
  /** §11.34 п.12в: пилюля паузы строки письма цепочки (режим «Изм.») — лист «Когда» (тот же ряд `wait`, что у письма). */
  const [letterWaitAt, setLetterWaitAt] = useState<number | null>(null);
  const pausePhone = usePhoneLook();
  /** Открытое РОДНОЕ окно «Сообщение»: чей ответ правим, чем подписана шапка и какой
   *  час стоит в углу пузыря (у рассылки с назначенным временем — её, И11.7). */
  const [msgAt, setMsgAt] = useState<{
    param: string;
    title: string;
    subtitle: string;
    clockAt?: string;
    /** Окно открыто со СТРАНИЦЫ ПИСЬМА — страницей той же шторки (К23.1 п.3). */
    flow?: boolean;
    /**
     * ОКНО ПИСЬМА ЦЕПОЧКИ (ТЗ 21.09.2026 раздел 6): оно правит ЧЕРНОВИК письма, а в
     * анкету письмо кладёт круглая галочка окна. У прочих окон (карточка шага, рассылка)
     * ключа нет, и черновик ложится в ответы на каждую правку — как было (М2.2).
     */
    letter?: true;
  } | null>(null);
  /** Открытый РОДНОЙ экран «Кто получит» (Г11): чем его открыли. Один на анкету — и на
   *  строку «Кому» письма цепочки, и на строку «Кто получит» родной формы рассылки. */
  const [audAt, setAudAt] = useState<AudienceOpenOpts | null>(null);

  /**
   * ОТКРЫТЬ СТРОКУ САМОГО ШАГА (И10 п.10). Пока такая строка одна — событие запуска
   * цепочки, — и за ней стоит ЛИСТ УСЛОВИЙ без списка живых людей: тех, кто подойдёт,
   * ещё нет. Развилка «родной экран или веб-шторка» живёт, как всегда, внутри
   * `openAudienceScreen`.
   */
  const openStepRow = (row: WizardItemRow): void => {
    // QUICK-START §11.8: строка итога «Всё готово» — шаг ПОВЕРХ с «ОК» (шаги из простых полей; аккаунты, оффер и список
    // писем — прежним переходом на шаг: их контролы живут только на странице шага).
    const over = row.step && activeDef.quickStart ? steps.find((x) => x.key === row.step) : undefined;
    if (over && !over.itemList && !over.params.some((p) => p.type === 'offer' || p.name === 'accountId' || p.name === WIZ_GIFT_TG_BOT || p.name === WIZ_GIFT_FILE)) {
      const params = over.params.filter((p) => (p.type ?? 'string') !== 'hidden' && (p.type ?? 'string') !== 'notice');
      panel.open({
        // Как у страницы шага: на широком — имя шага в шапке, на телефоне — только заголовком в теле (WizHero).
        title: wizPhoneWeb ? '' : over.title,
        sheet: true,
        deck: true,
        lead: 'close',
        winKey: `wiz-over-${over.key}`,
        body: (
          <WizOverSheet
            title={over.title}
            params={params}
            values={liveValues.current}
            dict={dict}
            ok={ts('chatStartPostsOk')}
            required={ta('wizFieldRequired')}
            {...(over.intro ? { intro: over.intro } : {})}
            {...(over.usage ? { usage: over.usage } : {})}
            check={(vals) => {
              const merged = { ...liveValues.current, ...vals };
              const text = activeDef.stepBlocked?.(merged, over.key, loc) ?? null;
              return text ? { field: activeDef.stepBlockedField?.(merged, over.key) ?? null, text } : null;
            }}
            onOk={(vals) => {
              liveApply.current({ ...liveValues.current, ...vals });
              panel.back();
            }}
          />
        ),
      });
      return;
    }
    // Строка сводки «Готово» быстрого запуска — назад на её шаг, тем же ходом, что «‹ Назад».
    if (row.step) {
      const at = steps.findIndex((x) => x.key === row.step);
      if (at >= 0) {
        setTried(false);
        setIdx(at);
      }
      return;
    }
    if (!row.param) return;
    const param = row.param;
    openAudienceScreen({
      panel,
      native: setAudAt,
      title: row.title,
      value: audienceOfRaw(liveValues.current[param]),
      // Бот-отправитель из ответа анкеты (К27.2 п.5) — свои люди только у него.
      ...(typeof liveValues.current.accountId === 'string' && liveValues.current.accountId
        ? { cred: liveValues.current.accountId }
        : {}),
      footer: row.footer ?? [],
      ...(row.kind === 'conditions'
        ? {
            conditions: true,
            ...(row.only ? { only: row.only } : {}),
          }
        : {}),
      onChange: (v) => liveApply.current({ ...liveValues.current, [param]: JSON.stringify(v) }),
    });
  };

  useEffect(() => {
    setRowsAt(null);
    setRowAt(null);
    setAddAt(null);
    setDraftAdd(null);
    // Ушли с шага — черновик письма выброшен вместе со страницей (ТЗ раздел 6 п.3).
    setLetterDraft(null);
    setMsgAt(null);
    setListEdit(false);
  }, [idx, setDraftAdd, setLetterDraft]);
  /**
   * ПРИЗНАК «ПЫТАЛИСЬ ОТПРАВИТЬ» ЖИВЁТ РОВНО ОДНУ СТРАНИЦУ (К24 п.1). Сбрасывать его
   * руками в каждом обработчике (закрытие, `pop`, успешное «Готово», открытие другой
   * страницы) — верный способ где-нибудь забыть, поэтому он привязан к САМОЙ странице:
   * сменился её адрес (или страницы не стало) — признак снят. Отказ «Готово» адрес не
   * меняет, поэтому подсказки после него остаются до ухода.
   */
  const draftPageKey = draftAdd
    ? `${draftAdd.name}:draft:${draftAdd.kind}:${draftAdd.value ?? ''}`
    : '';
  const rowPageKey = rowsAt && rowAt === null ? `${rowsAt.name}:${rowsAt.i}` : '';
  useEffect(() => {
    setDraftTried(null);
  }, [draftPageKey, setDraftTried]);
  useEffect(() => {
    setRowTried(null);
  }, [rowPageKey, setRowTried]);
  /** Шаг-список объектов по его адресу (ключ шага) и строки открытого письма. */
  const stepWithItems = (name: string): WizardStep | undefined =>
    steps.find((s) => s.key === name && !!s.itemList);
  // ── ЧЕРНОВИК ПИСЬМА (ТЗ 21.09.2026 раздел 6) ────────────────────────────────────────
  /** Черновик ОТКРЫТОГО письма: он же и есть признак «правило подтверждения работает». */
  const letterAt = (name: string, i: number): WizLetterDraft | null => {
    const d = letterRef.read();
    return d && d.name === name && d.i === i ? d : null;
  };
  /**
   * «ПРАВИЛО ЗАМЕТОК» В ПРИЛОЖЕНИИ (П132 п.3, владелец 25.09.2026): какое сообщение
   * добавлено ЭТИМ открытием страницы. Уход со страницы любым путём (`wizItemRowsBack` —
   * «Готово», «×», свайп) сверяется с ним в ОДНОМ месте (`dropFreshItem`): нетронутое —
   * убирается молча. Открыли другое, переставили, удалили — признак снят.
   */
  const itemFresh = useRef<{ name: string; i: number } | null>(null);
  /**
   * «ГОТОВО» СТРАНИЦЫ СООБЩЕНИЯ ОТБИТО (П132 п.2, механика К24 п.1): сообщение не готово.
   * С этой секунды строка «Сообщение» этого элемента уезжает нативу с `invalid`; гаснет
   * сама, когда сообщение станет годным (признак строки считает анкета).
   */
  const [itemTried, setItemTried] = useState<{ name: string; i: number } | null>(null);
  /** Ответ на родной вопрос «Удалить сообщение?» (П132 п.5): один вопрос — один ответ. */
  const itemAskDel = useRef<((key: string) => void) | null>(null);
  /**
   * СООБЩЕНИЕ ЭЛЕМЕНТА НЕ УХОДИТ ОДНИМ, НО ЧИНИТСЯ ОТВЕТОМ (ТЗ П163 п.4, владелец
   * 26.09.2026): текст дописан сверх подписи при медиа, старое «кружок + текст» /
   * «альбом + кнопки». «Готово» страницы элемента в таком случае спрашивает («Убрать
   * медиа» / «Отмена»), а не только подсвечивает строку. null — спрашивать не о чем
   * (пусто, файл в пути, прочее) — прежняя подсветка.
   */
  const conflictWords = useMsgConflictWords();
  const itemMsgConflict = (
    rows: readonly WizardItemRow[],
  ): { c: MsgConflict; param: string; draft: TgDraft } | null => {
    for (const row of rows) {
      if (row.kind !== 'message' || !row.param || !row.invalid) continue;
      const raw = liveValues.current[row.param];
      if (typeof raw !== 'string' || raw === '') continue;
      let draft: TgDraft | null = null;
      try {
        draft = toTgDraft(JSON.parse(raw));
      } catch {
        draft = null;
      }
      if (!draft || draft.media.some((m) => m.pending === true || m.failed === true)) continue;
      const c = doneConflict(draft);
      if (c) return { c, param: row.param, draft };
    }
    return null;
  };
  /** Ответ на вопрос: черновик по ответу — в ответы анкеты. */
  const fixItemMsg = (
    clash: { c: MsgConflict; param: string; draft: TgDraft },
    answer: MsgConflictAnswer,
  ): void => {
    journalLog('msg.conflictPick', { kind: clash.c.kind, answer, at: 'item' });
    liveApply.current({
      ...liveValues.current,
      [clash.param]: JSON.stringify(applyConflict(clash.draft, clash.c, answer)),
    });
  };
  /**
   * Уход со страницы элемента: добавленный этим открытием и НЕТРОНУТЫЙ элемент убрать
   * молча (П132 п.3). true — убран. Признак «добавлен этим открытием» снимается всегда.
   */
  const dropFreshItem = (name: string, i: number): boolean => {
    const f = itemFresh.current;
    itemFresh.current = null;
    if (!f || f.name !== name || f.i !== i) return false;
    const cfg = stepWithItems(name)?.itemList;
    if (!cfg?.untouched || !cfg.untouched(liveValues.current, i)) return false;
    liveApply.current(cfg.remove(liveValues.current, i));
    return true;
  };
  /** Правка письма — в черновик, а не в ответы: её подтвердят «Готово» или галочка окна. */
  const letterPatch = (patch: Record<string, unknown>): void => {
    const d = letterRef.read();
    if (!d) return;
    setLetterDraft({ ...d, pend: { ...d.pend, ...patch } });
  };
  /**
   * Положить накопленное в ответы анкеты. `keys` — только эти ответы (галочка окна
   * «Сообщение»); без них — всё («Готово» страницы письма). После подтверждения письмо
   * УЖЕ в анкете, поэтому заготовка снимается: следующий ход считается от живых ответов.
   */
  const letterApply = (keys?: string[]): void => {
    const d = letterRef.read();
    if (!d) return;
    liveApply.current(wizLetterCommit(d, liveValues.current, keys));
    setLetterDraft({ ...d, seed: null, ...(keys ? {} : { pend: {} }) });
  };
  /** Имя ответа принадлежит полям страницы этого письма (их правку копим в черновике)? */
  const letterOwnField = (
    cfg: WizardStepItemList,
    vals: Record<string, unknown>,
    i: number,
    name: string,
  ): boolean =>
    (cfg.rows?.(vals, i) ?? []).some((r) => (r.fields ?? []).some((p) => p.name === name));
  const nativeItem = useMemo<NativeWizItem | null>(() => {
    if (!nativeOn) return null;
    // СТРАНИЦА-ШТОРКА «ВАРИАНТЫ ОТВЕТА» (ТЗ USERTEST-1014 п.7): полушторка отдельным листом поверх мастера
    // (`stack`, «×» слева, «Готово» внизу), внутри — пояснение и список строк. Кнопки удаления страницы нет.
    if (linesAt) {
      const lp = steps.flatMap((st) => st.params).find((x) => x.name === linesAt);
      // «Изм.» в шапке и минусы у строк — сборка 250+ (`edit` + слова пилюли); старая ключи не читает — смахивание.
      const page = lp
        ? nativeLinesPageItem(lp, { ...nativeCtx, values: formValues }, { done: tc('done'), edit: ta('wizListEdit') })
        : null;
      if (page) return page;
    }
    // ЧЕРНОВИК НОВОГО ТРИГГЕРА — полушторка поверх списка ботов (П10 п.5–7): поля те же,
    // что у страницы триггера, но считаются по ответам С ним (`draftAdd.next`), а в
    // настоящие ответы уезжают только по «Готово». Кнопки удаления нет: удаление — через
    // «Править» на списке (П10 п.8).
    if (draftAdd) {
      const cfg = stepWithItems(draftAdd.name)?.itemList;
      if (cfg?.fields) {
        const params = cfg.fields(draftAdd.next, draftAdd.i, itemLive);
        const vals = { ...draftAdd.next, ...(cfg.fieldValues?.(draftAdd.next, draftAdd.i) ?? {}) };
        const fields = nativeWizFields(
          wizBotLinkFields(
            wizLinkReadyFields(visibleParams(params, vals), loc, accCred?.handle ?? null, vals),
            loc,
            botLinks,
            activeDef.id === CHAIN_WIZARD_ID,
          ),
          {
            ...nativeCtx,
            values: vals,
            // ПОДСКАЗКА-ОШИБКА — ТОЛЬКО ПОСЛЕ ПОПЫТКИ «ГОТОВО» (К24 п.1): при открытии
            // страница чистая, признак ставит первый отказ. Предупреждения шага
            // («заполните это поле» по «Дальше») в `nativeCtx` — как были.
            ...linkWarnCtx(
              nativeCtx.warnings,
              draftTried ? pageWarnAt(cfg, draftAdd.next, draftAdd.i)(vals) : undefined,
            ),
          },
        );
        if (!fields) return null;
        // ПОЛНАЯ ШТОРКА У «ПЕРЕХОДА ПО ССЫЛКЕ» (К18.1 п.4 п.1), остальные виды —
        // полушторкой поверх списка (П10 п.5). Правило ОДНО и живёт у анкеты
        // (`pageFull`, К19.3 этап 1 п.1 б): у черновика и у уже добавленного триггера
        // страница открывается одинаково. Нативу про `full` знать нечего — веб просто не
        // шлёт `half` (запрет №8).
        const full = cfg.pageFull
          ? cfg.pageFull(draftAdd.next, draftAdd.i)
          : !!itemAddKinds(cfg, draftAdd.next).find((k) => k.key === draftAdd.kind)?.full;
        return {
          name: draftAdd.name,
          key: `${draftAdd.name}:draft:${draftAdd.kind}:${draftAdd.value ?? ''}`,
          title: wizItemTitle(cfg, draftAdd.next, draftAdd.i, itemLive),
          fields,
          remove: '',
          done: tc('done'),
          // ОДНА ШТОРКА, ШАГИ ВНУТРИ (К20.1 п.1–2, владелец 07.09.2026: «либо
          // горизонтальные, либо не должны все выпадать»): страница ЧЕРНОВИКА
          // принадлежит потоку добавления — натив кладёт её В ТУ ЖЕ шторку (`flow`).
          flow: true,
          // `half` едет ВСЕГДА (как было): шторка потока тянущаяся. `full` говорит, на
          // какой высоте открыть страницу — обе подсказки вместе, старая сборка прочтёт
          // только `half` и покажет полушторку (запрет №8).
          half: true,
          ...(full ? { full: true } : {}),
          // Каретку не ставить самой (п.28, «Переход по ссылке»): страница видна целиком, клавиатура по тапу.
          ...(cfg.pageNoFocus?.(draftAdd.next, draftAdd.i) ? { autoFocus: false } : {}),
        };
      }
    }
    if (rowsAt && !rowAt) {
      const cfg = stepWithItems(rowsAt.name)?.itemList;
      if (cfg?.fields) {
        const params = cfg.fields(formValues, rowsAt.i, itemLive);
        const vals = { ...formValues, ...(cfg.fieldValues?.(formValues, rowsAt.i) ?? {}) };
        const fields = nativeWizFields(
          // ГОТОВАЯ ССЫЛКА подставляется ЗДЕСЬ (К16.0 п.6): @логин бота знает страница, а
          // не анкета. В браузере это поле не рисуется — его заменяет StartLinkFields.
          // Раздел «Ссылки из ваших ботов» — теми же строками-переходами страницы (К17.4
          // п.6 в): в браузере его рисует StartLinkFields, здесь — поля родного экрана.
          wizBotLinkFields(
            wizLinkReadyFields(visibleParams(params, vals), loc, accCred?.handle ?? null, vals),
            loc,
            botLinks,
            activeDef.id === CHAIN_WIZARD_ID,
          ),
          {
            ...nativeCtx,
            values: vals,
            // То же правило у правки уже добавленного триггера (К24 п.1).
            ...linkWarnCtx(
              nativeCtx.warnings,
              rowTried ? pageWarnAt(cfg, formValues, rowsAt.i)(vals) : undefined,
            ),
          },
        );
        if (!fields) return null;
        return {
          name: rowsAt.name,
          key: `${rowsAt.name}:${rowsAt.i}`,
          title: wizItemTitle(cfg, formValues, rowsAt.i, itemLive),
          fields,
          // Кнопки «Удалить» на странице нет (владелец 06.09.2026, П10 п.8): удаление —
          // через «Править» и смахивание на самом списке.
          remove: '',
          done: tc('done'),
          // ПРАВКА УЖЕ ДОБАВЛЕННОГО ТРИГГЕРА — ТОЖЕ ПОЛУШТОРКОЙ (К19.1 п.1–3, владелец
          // 06.09.2026: «редактирование должно тоже на половинку»), кроме «Перехода по
          // ссылке» — у него страница полная (`pageFull` анкеты).
          ...(cfg.pageFull?.(formValues, rowsAt.i) ? {} : { half: true }),
          // ПОЛУШТОРКА РАЗВЁРНУТА НА ВЕСЬ ЭКРАН И КАРЕТКА В ПОЛЕ (этап J п.2): решает
          // анкета по своим ответам. Ключи аддитивные — старая сборка их не читает и
          // оставляет лист на половине (CLAUDE.md запрет №8).
          ...(cfg.pageGrow?.(formValues, rowsAt.i) ? { grow: true } : {}),
          ...(cfg.pageNoFocus?.(formValues, rowsAt.i) ? { autoFocus: false } : {}),
          ...(() => {
            const focus = cfg.pageFocus?.(formValues, rowsAt.i) ?? '';
            return focus ? { focus } : {};
          })(),
        };
      }
    }
    // Строка письма с полями стоит ПОВЕРХ страницы письма — её страница та же, что у
    // входа (ТЗ Д2): второго рендерера полей в приложении нет.
    if (rowsAt && rowAt) {
      const cfg = stepWithItems(rowsAt.name)?.itemList;
      // ЧЕРНОВИК ПИСЬМА (ТЗ раздел 6): «Когда» показывает и правит НЕ подтверждённые
      // ответы, а значения черновика — в анкету они уедут по «Готово» страницы письма.
      const vals = wizLetterValues(
        letterDraft && letterDraft.name === rowsAt.name && letterDraft.i === rowsAt.i
          ? letterDraft
          : null,
        formValues,
      );
      const row = cfg?.rows?.(vals, rowsAt.i).find((r) => r.key === rowAt);
      if (!cfg || !row || row.kind !== 'fields') return null;
      const fields = nativeWizFields(visibleParams(row.fields ?? [], vals), {
        ...nativeCtx,
        values: vals,
      });
      if (!fields) return null;
      return {
        name: rowsAt.name,
        key: `${rowsAt.name}:${rowsAt.i}:${row.key}`,
        title: row.title,
        fields,
        // Удаляют ЦЕЛОЕ письмо на его странице, а не одну строку-вопрос.
        remove: '',
        done: tc('done'),
        // СТРАНИЦА ТОЙ ЖЕ ШТОРКИ, ЧТО И ПИСЬМО (К23.1 п.3): натив кладёт её внутрь
        // (push) со своей маленькой шапкой и стрелкой «назад», а не открывает лист
        // поверх листа. Высота при этом не меняется — `half`/`full` не шлём, как и не
        // слали. «Готово» здесь возвращает к письму (rowAt = null), а не закрывает
        // шторку: это не поток добавления.
        flow: true,
        // ПОЛУШТОРКА ПОВЕРХ КАРТОЧКИ ПИСЬМА (П130 п.13, владелец 24.09.2026): «Название»,
        // «Когда», «Условия» — отдельным листом снизу, не боком; `flow` остаётся старой сборке.
        half: true,
        stack: true,
        // ГАЛОЧКИ В ШАПКЕ НЕТ — «ГОТОВО» ВНИЗУ (П132 п.2, модель А, владелец 25.09.2026):
        // значение применяется сразу (правка → wizChange → ответы), лист закрывает одна
        // кнопка «Готово» дока (слово `done` выше), свайп и «×» равнозначны. Ключ `check`
        // больше не шлём: по нему натив ставил галочку и прятал док. Барабаны у «Когда» — П130.
        ...(cfg.pageWheel?.(vals, rowsAt.i, row.key) ? { wheel: true } : {}),
        // ЕДИНСТВЕННОЕ ТЕКСТОВОЕ ПОЛЕ — КАРЕТКА СРАЗУ (владелец 26.09.2026, лист «Название»
        // карточки: «не хватает автофокуса на единственное поле»): за ним и пришли. Ключ
        // `focus` аддитивный (запрет №8): натив ставит каретку по имени поля, старая сборка
        // не читает. Поле среди прочих (барабан, переключатели) фокус не получает.
        ...(fields.length === 1 &&
        fields[0] &&
        (fields[0].kind === 'text' || fields[0].kind === 'textarea')
          ? { focus: fields[0].name }
          : {}),
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // `warnTick` в зависимостях НАРОЧНО (К22.2 этап 1 п.1): отказ «Готово» обязан
    // пересчитать страницу по свежим ответам — поля с `invalid` уезжают сборке патчем.
    // `draftTried`/`rowTried` — там же (К24 п.1): признак взводится отказом «Готово», и
    // страница обязана пересобраться уже С подсказкой.
  }, [
    linesAt,
    rowsAt,
    rowAt,
    draftAdd,
    letterDraft,
    nativeOn,
    steps,
    formValues,
    nativeCtx,
    itemLive,
    loc,
    accCred?.handle,
    botLinks,
    ta,
    tc,
    activeDef.id,
    itemAddKinds,
    pageWarnAt,
    warnTick,
    draftTried,
    rowTried,
  ]);
  /** Открытая страница письма: три строки-вопроса и «Удалить письмо» внизу. */
  const nativeItemRows = useMemo<NativeWizItemRows | null>(() => {
    if (!nativeOn) return null;
    // «ДОБАВИТЬ …» СТРАНИЦЕЙ СПИСКА (К5.1): сперва меню видов, за ним — выбор бота. Обе
    // рисует тот же `wizItemRowsOpen`; удаления на них нет (`remove` пуст).
    if (addAt) {
      const cfg = stepWithItems(addAt.name)?.itemList;
      if (!cfg?.addKinds) return null;
      if (!addAt.kind) {
        return {
          name: addAt.name,
          key: 'add:kinds',
          title: cfg.itemAdd,
          // ЗАПАСНОЙ ПУТЬ (сборка без `addKinds`): меню видов страницей строк. Правило то
          // же, что в меню (К18.1 п.3) — недоступный вид стоит на своём месте, БЕЗ
          // шеврона и без страницы за ним, причина второй строкой вместо описания.
          rows: itemAddKinds(cfg, formValues).map((k) => ({
            key: k.key,
            title: k.title,
            sub: k.disabled ? (k.disabledHint ?? '') : (k.description ?? ''),
            kind: 'nav' as const,
            chevron: !k.disabled,
          })),
          remove: '',
          done: tc('done'),
        };
      }
      const kind = itemAddKinds(cfg, formValues).find((k) => k.key === addAt.kind);
      const bots = itemPickBots(cfg, formValues);
      return {
        name: addAt.name,
        key: `add:pick:${addAt.kind}`,
        title: kind?.title ?? cfg.itemAdd,
        // СПИСОК БОТОВ — ПОЛУШТОРКОЙ (К19.1 п.1, владелец 06.09.2026: «список ботов я бы
        // открывал шторкой в половину высоты»); настройки выбранного бота встают
        // полушторкой поверх неё. Меню видов (`add:kinds`) и страницы писем цепочки
        // открываются полным листом, как открывались. Ключ аддитивный: старая сборка его
        // не читает и покажет полный лист (запрет №8).
        half: true,
        // СПИСОК БОТОВ — СТРАНИЦА ПОТОКА (К20.1 п.2): едет внутрь той же шторки, что и
        // меню видов, а не отдельным листом поверх него.
        flow: true,
        // Пустой страницы выбора не бывает вовсе (К18.3 этап 1 п.1): вид без свободных
        // ботов гаснет прямо в меню и сюда не ведёт.
        rows: bots.map((b) => ({
          key: `${ITEM_PICK_PFX}${b.value}`,
          title: b.label,
          sub: '',
          kind: 'nav' as const,
          chevron: true,
        })),
        remove: '',
        done: tc('done'),
      };
    }
    if (!rowsAt) return null;
    const cfg = stepWithItems(rowsAt.name)?.itemList;
    if (!cfg || !cfg.rows) return null;
    // ЧЕРНОВИК ПИСЬМА (ТЗ раздел 6): НОВОЕ письмо в ответах анкеты ещё не стоит — его
    // страница живёт заготовкой черновика, иначе номер письма оказался бы «за границей
    // списка» и страницы не было бы вовсе.
    const draft =
      letterDraft && letterDraft.name === rowsAt.name && letterDraft.i === rowsAt.i
        ? letterDraft
        : null;
    const vals = wizLetterValues(draft, formValues);
    const count = cfg.count(vals);
    if (rowsAt.i < 1 || rowsAt.i > count) return null;
    return {
      name: rowsAt.name,
      key: String(rowsAt.i),
      title: wizItemTitle(cfg, vals, rowsAt.i, itemLive),
      // Строки собирает чистая половина (её проверяют юниты): «Кому» уезжает ПЕРЕХОДОМ с
      // готовым значением, а не текстовым полем с JSON внутри (И3).
      // «Готово» уже отбито (П132 п.2) — негодная строка уезжает с подсветкой.
      rows: nativeWizRows(
        cfg.rows(vals, rowsAt.i),
        !!itemTried && itemTried.name === rowsAt.name && itemTried.i === rowsAt.i,
      ),
      // ОДНА ШТОРКА, СТРАНИЦЫ ВНУТРИ (К23.1 п.2–3, владелец 07.09.2026: «тоже в одну
      // сторону открытия работает»): страница письма — КОРЕНЬ шторки, а «Когда»,
      // «Условия» и «Сообщение» едут боком внутри неё, а не отдельными листами поверх.
      // ПОЛУШТОРКА И ГАЛОЧКА (П130, владелец 24.09.2026): письмо открывается на половине
      // экрана, справа от заголовка галочка закрывает его; нижней кнопки нет. Страницы
      // «Название», «Когда», «Условия» едут внутри той же полушторки.
      // КАРТОЧКА ПИСЬМА — ПОЛНАЯ ШТОРКА, галочки в шапке нет, нижнее «Готово» остаётся
      // (П130 п.13, уточнение владельца 24.09.2026); прежние `half`/`check` сняты.
      flow: true,
      // «ГОТОВО» СПРАШИВАЕТ ВЕБ (П132 п.2): негодное сообщение страницу не выпускает и в
      // приложении — натив шлёт `wizItemRowsDone`, ответ — `onItemRowsDone` ниже.
      askDone: true,
      // Последнее сообщение не удаляется — цепочка без сообщений не бывает.
      remove: count > 1 ? cfg.itemRemove : '',
      done: tc('done'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rowsAt,
    addAt,
    letterDraft,
    nativeOn,
    steps,
    formValues,
    itemLive,
    edit,
    tc,
    itemAddKinds,
    itemPickBots,
    itemTried,
  ]);
  // Жалоба уходит ОДИН раз на анкету: пересчёт идёт на каждую букву, штормить незачем.
  useEffect(() => {
    if (nativeSteps !== null || !nativeMem.current.took || nativeMem.current.reported) return;
    nativeMem.current.reported = true;
    reportUiError('wizard_native_drop', 'Родной мастер перестал брать анкету на открытом шаге', {
      wizard: activeDef.id,
      step: step.key,
      accNone,
      ...(nativeMem.current.bad ?? {}),
    });
  }, [nativeSteps, activeDef.id, step.key, accNone]);
  // ── РОДНАЯ ФОРМА РАЗОВОЙ РАССЫЛКИ (волна 2, Опус-В2Б) ──────────────────────────
  // Рассылку родной МАСТЕР не берёт (среди полей отбор аудитории и «день и час» —
  // контролов, которых он не рисует), поэтому в приложении её ведёт своя родная форма:
  // одна страница из трёх пузырей «кому / что / когда». Мозг тот же — эта панель:
  // ответы, проверки и создание ниже общие с вебом, второй логики нет.
  const bcastReady = useNativeMethod('bcastOpen');
  const bcastOn = bcastNativeFormOn({
    edit: !!edit,
    isBcast: activeDef.id === BROADCAST_WIZARD_ID,
    ready: bcastReady,
    accNone,
    startedNoAcc: bcastNoAccStart,
  });
  // ОДИН ЭКРАН «НОВАЯ РАССЫЛКА» НА ТЕЛЕФОНЕ В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 Р2, решение
  // владельца 3.5; кадр iOS R02-broadcast-new): три шага анкеты стоят ТРЕМЯ группами одной
  // страницы — «Получатели» (Бот · Кто получит), «Сообщение», «Когда» — с подсказками под
  // группами и широкой «Создать» внизу, как в родной форме приложения (bcast-native).
  // Мозг тот же: ответы, проверки (bcastSubmit) и создание общие с шагами; второго
  // пути создания нет. На широком экране шаги остаются.
  // ОДИН ЭКРАН РАССЫЛКИ ВЫКЛЮЧЕН (владелец 12.10.2026: «разовая рассылка — такой же мастер, как в iOS-приложении»,
  // по трём шагам «Получатели → Сообщение → Когда»). Код экрана остаётся до решения владельца, вход в него закрыт.
  const bcastOne = BCAST_ONE_SCREEN && activeDef.id === BROADCAST_WIZARD_ID && wizPhoneWeb && !bcastOn;
  // Состояние родной формы рассылки — в журнал клиента (03.09.2026, «Создать рассылку»
  // в приложении молчала): кто рисует и почему.
  useEffect(() => {
    if (activeDef.id !== BROADCAST_WIZARD_ID) return;
    journalLog('bcast.form', {
      on: bcastOn ? 1 : 0,
      ready: bcastReady ? 1 : 0,
      wiz: wizNativeReady ? 1 : 0,
      accNone: accNone ? 1 : 0,
      creds: credsReady ? 1 : 0,
      hosted: hosted ? 1 : 0,
    });
  }, [activeDef.id, bcastOn, bcastReady, wizNativeReady, accNone, credsReady, hosted]);
  /** Шаги формы: те же видимые поля, что у веб-мастера, без экрана «Знакомство». */
  const bcastSteps = useMemo(
    () =>
      steps
        .filter((s) => s.key !== WIZ_INTRO_KEY)
        .map((s) => ({
          key: s.key,
          title: s.title,
          ...(s.intro ? { intro: s.intro } : {}),
          // ШАГ-СООБЩЕНИЕ (ТЗ Б): пузырь «что» — ОДНА строка с превью письма, за ней
          // общее окно «Сообщение». Прежней тройки полей на родной форме больше нет.
          // Сборка без родного окна письма живёт ПРЕЖНЕЙ тройкой полей — так у неё было
          // и так остаётся (запрет №8): нового поведения старым сборкам не подсовываем.
          ...(() => {
            const card = msgNativeReady ? wizMessageCards(s, formValues)[0] : undefined;
            return card
              ? {
                  message: {
                    key: card.param,
                    title: card.title,
                    value: cardValue(card, formValues),
                    empty: card.empty,
                    // Подвал группы — пояснение про письмо (И10 п.3): внутри строки
                    // пояснений не бывает.
                    ...(card.hint ? { hint: card.hint } : {}),
                  },
                }
              : {};
          })(),
          params: visibleParams(s.params, formValues),
        })),
    [steps, formValues, msgNativeReady, cardValue],
  );

  const nativeDict = useMemo<NativeWizDict>(
    () => ({
      next: ta('wizNext'),
      back: ta('wizBack'),
      cancel: tc('cancel'),
      finish: edit ? ta('wizSave') : ta('wizCreate'),
      // Счёт без сцены (владелец 04.10.2026): «Знакомство» — не шаг ни при создании, ни в правке.
      stepOf: ta('wizStepOf', wizStepOf(steps, idx)),
      preview: ta('wizPreviewTitle'),
      optional: tOpt('optional'),
      empty: ta('wizNotChosen'),
      // Подпись ряда картинки: у ЗАПОЛНЕННОГО поля «Заменить», у пустого «Загрузить»
      // (натив читает их именно так: ready ? addImage : fromGallery). Раньше сюда уходила
      // одна и та же строка, и поле со снимком по-прежнему звало «Загрузить картинку» —
      // человек читал это как «ничего не выбрано» (ТЗ-2 З5).
      addImage: ta('wizImageReplace'),
      fromGallery: ta('wizImageAdd'),
      // Ряд файла подарка (`file`, 19.09.2026): «Выбрать файл» у пустого — те же слова,
      // что у поля на странице (cabinet.scenario). Ключи новые, старая сборка их не читает.
      pickFile: ts('attachPickFile'),
      replaceFile: ts('attachReplace'),
      addLine: ta('wizReplyAdd'),
      // Первый экран: «Настроить» при создании, «Редактировать» в правке (ТЗ-7 Ш2).
      setup: ta('wizSetup'),
      editWord: ta('wizEdit'),
      // Подпись над сценой первого экрана СНЯТА (владелец 29.09.2026: место отдано
      // телефону). Пустое слово — натив строку не ставит и отдаёт высоту сцене.
      example: '',
      phoneHint: ta('wizPhoneHint'),
      // Кнопка «Править» у шага-списка (К16.0 п.5 / К16.2): слово покоя и слово режима.
      // Ключи необязательные — сборка без них кнопку просто не рисует (запрет №8).
      // СПИСКУ ИЗ ОДНОГО ЭЛЕМЕНТА «ПРАВИТЬ» НЕ НУЖНА (круг 12, §18.3 п.2): слово не шлём —
      // натив по пустому слову кнопку не ставит, как и веб (`itemCfg.max > 1`).
      edit: (steps[idx]?.itemList?.max ?? 2) > 1 ? ta('wizListEdit') : '',
      done: tc('done'),
      // Слова копирования готовой ссылки (К18, этап 4): пункт долгого нажатия и слово
      // кнопки после тапа. Ключи необязательные — старая сборка их не читает (запрет №8).
      copy: ta('wizCopy'),
      copied: ta('wizCopied'),
    }),
    [ta, tc, tOpt, edit, idx, steps],
  );
  // Хозяин страницы должен знать, кто рисует анкету: у родной её тело живёт в шторке,
  // а на самой странице пусто (П-21). Обработчик держим в ссылке — иначе всякий рендер
  // с новой стрелкой перезапускал бы эффект.
  const nativeModeRef = useRef(onNativeMode);
  nativeModeRef.current = onNativeMode;
  // ПОЧЕМУ родной путь не взял анкету: у канала нет подключённого аккаунта. Слова — те
  // же, что у подсказки без аккаунта ниже по файлу (второго текста не заводим).
  const noAccText = merged
    ? ta('wizNoAccounts')
    : ts(def.accountChannel === 'telegram' ? 'chatStartNoBot' : 'chatStartNoIgAccount');
  useEffect(() => {
    nativeModeRef.current?.(
      nativeOn || bcastOn,
      wizNativeReady || bcastReady,
      // УВОД В «ПОДКЛЮЧЕНИЯ» ОСТАЛСЯ ТОЛЬКО У СТАРЫХ СБОРОК (владелец 11.09.2026): у
      // свежих аккаунт подключается на шаге «Аккаунт», и анкету закрывать незачем.
      accNone && !connFlow.native ? { text: noAccText } : undefined,
    );
    // Родная форма рассылки — такой же «натив рисует», как и мастер: веб-слоя не будет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nativeOn, bcastOn, wizNativeReady, bcastReady, accNone, connFlow.native, noAccText]);
  // «Дальше» с родного экрана: сначала в состояние уезжают ответы шага, и только потом —
  // тот же goNext, которым живёт веб (иначе проверка считала бы по прошлым ответам).
  const [nextAsked, setNextAsked] = useState(0);

  const normalizeLinks = (): string | null => {
    for (const p of linkFields()) {
      const raw = String(formValues[p.name] ?? '');
      // Один разбор адреса на кабинет (normalizeButtonUrl / isButtonUrl): мастер, кнопки
      // окна сообщения и отправка понимают адрес одинаково.
      const withScheme = normalizeButtonUrl(raw);
      if (!isButtonUrl(withScheme)) return p.displayName ?? p.name;
      if (withScheme !== raw) applyAnswers({ ...formValues, [p.name]: withScheme });
    }
    return null;
  };
  const goNext = () => {
    if (busy) return;
    // Ссылка без схемы получает https:// сама; не-адрес — честная красная тряска.
    const badLink = normalizeLinks();
    if (badLink !== null) {
      setTried(true);
      setShake((n) => n + 1);
      if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
      setFormErr(ta('wizBadLink', { field: badLink }));
      return;
    }
    // Честная валидация: недозаполненный шаг не молчит. Слабой красной подписи владельцу
    // мало (2026-08-06) — поля ТРЯСЁТ и телефон вибрирует; причина при этом встаёт
    // КРАСНОЙ СТРОКОЙ В ТЕЛЕ шага под вступлением и стоит, пока её не устранили
    // (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128 — вспышки в планке больше нет).
    if (missing.length > 0 || blocked !== null) {
      setTried(true);
      setShake((n) => n + 1);
      if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
      setFormErr(null);
      return;
    }
    setFormErr(null);
    setTried(false);
    // ЗАНЯТЫЙ ЗАПУСК — ДО ПЕРЕХОДА (П160 п.2, владелец 26.09.2026: «сначала перешёл на
    // соседнюю вкладку, потом показал уведомление»): на «Далее» шага запуска сверяемся со
    // списком, загруженным заранее. Занято — шторка сразу, шаг не меняется, ничего не
    // пишется; «Выключить» гасит хозяина и делает этот же ход дальше (п.4).
    if (step.starts) {
      const taken = startTakenNow();
      if (taken) {
        if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
        saveDup.show(taken, { retry: advance });
        return;
      }
    }
    advance();
  };
  /** Ход «Далее» после всех проверок: следующий шаг (с записью при правке) или финал. */
  const advance = () => {
    if (activeDef.quickStart) quick.leaveStep(step.key);
    if (!last) {
      // Режим правки: «Далее» = шаг проверен → записан (владелец 26.09.2026); не записалось
      // (занято, конфликт, сеть) — остаёмся на шаге, причина показана той же рукой.
      setIdx(idx + 1);
      // Режим правки: «Далее» = шаг проверен → записан (владелец 26.09.2026). Переход —
      // сразу, запись — в фоне; отказ (занято, конфликт, сеть) покажет та же рука.
      if (edit) void saveEdit(true);
      return;
    }
    if (edit) {
      void saveEdit();
      return;
    }
    // ФОРМА БЫСТРОГО ЗАПУСКА: «Создать бота(ов)» — `POST /api/cabinet/quick-start` (ответы штатных мастеров готовит
    // сервер), затем лист «Боты созданы» (E). Отказ — по правилу «чей косяк»: строкой на шаге или «Такой бот уже есть».
    if (activeDef.quickStart) {
      if (quickBusy) return;
      setQuickBusy(true);
      void quick.create().then((r) => {
        setQuickBusy(false);
        if (r.ok) {
          // Список «Ботов» тянем сразу, как после мастера (useCreateGo): иначе в нём висел бы список без новых ботов.
          // Копия списка на устройстве больше не «свежая»: переход или перезагрузка обрывают запрос ниже, и следующее
          // открытие «Ботов» обязано сверить список с сервером (сохранённое — в первый кадр, как всегда).
          stalePersistentPrefix('cabinet:scenarios');
          // Анкета закончена — строка «Незаконченные анкеты: N» корня «+» сверяется с сервером при следующем открытии.
          stalePersistentPrefix(QS_ROOT_COPY);
          revalidatePersistentPrefix('cabinet:scenarios');
          refreshPersistent('cabinet:scenarios', '/api/cabinet/scenarios', { accept: (j) => !!(j as { ok?: boolean } | null)?.ok });
          refreshPersistent(STARTS_TAKEN_KEY, STARTS_TAKEN_URL, { accept: startsTakenOk });
          const closeAll = () => panel.closeAll();
          openQuickDone(r.done, { closeAll });
          return;
        }
        if (r.fail.error === 'bot_exists' && r.fail.existingId) {
          const wordAt = steps.findIndex((x) => x.key === 'word');
          saveDup.show({ names: r.fail.names ?? [], existingId: r.fail.existingId }, { change: () => { if (wordAt >= 0) setIdx(wordAt); } });
          return;
        }
        const back = steps.findIndex((x) => x.key === quickErrStep({ steps }, r.fail.field));
        if (back >= 0) setIdx(back);
        setFormErr(r.fail.text || ta('createError'));
      });
      return;
    }
    // Шаблон нужен всем анкетам, КРОМЕ мастера без шаблона (standalone, рассылка): у него
    // сырья нет — схему целиком собирает сервер из ответов.
    const templateId = templates?.[activeDef.id];
    if (!templateId && !activeDef.standalone) return;
    // ССЫЛКИ ВХОДОВ «ПЕРЕХОД ПО ССЫЛКЕ» ЗАВОДИТ СЕРВЕР (BACKLOG п.22, владелец 13.09.2026):
    // в том же запросе, что сохраняет входы. Занятый хвост сервер отбивает 409
    // `code_taken` ДО создания ряда — мастер показывает подсказку под «Откуда придут люди»
    // (onTaken у useCreateGo). Отдельного запроса из кабинета больше нет.
    // Название нового бота сразу в кэш списка названий (П189): мастер, открытый следом,
    // не должен выдать тот же номер, пока ручка не освежилась.
    const madeTitle = typeof wizAnswers.botTitle === 'string' ? wizAnswers.botTitle.trim() : '';
    if (aboutBase && madeTitle && titlesData?.titles) {
      mutateTitles({ ok: true, titles: [...titlesData.titles, madeTitle] });
    }
    void go(
      {
        ...(templateId ? { template: templateId } : {}),
        wizard: { id: activeDef.id, answers: wizAnswers, locale: botLoc },
        // Р17 ред. 8: название рассылки из поля «Название»; пусто — сервер даёт «Сообщение N».
        ...(isBcastWiz && bcastName.trim() ? { name: bcastName.trim() } : {}),
      },
      // Сценарий из мастера открывается МАСТЕРОМ, не схемой (владелец 05.08) — вид
      // не навязываем, страница сама выберет режим «Настройка» по wizard черновика.
      'wizard',
    );
  };

  useEffect(() => {
    if (nextAsked === 0) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    goNext();
    // Ход делаем ровно один раз на просьбу натива: goNext пересобирается каждым рендером.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextAsked]);

  // Превью «как это будет выглядеть в чате» — по кнопке-телефону (владелец 05.08, из
  // тела формы убрано): натив — родной нижний лист ПОВЕРХ слоя (стопка, канон §11 п.8),
  // веб — лист стопкой поверх мастера (winKey `wizard-preview`), в нём то же, что в приложении.
  // ТЕЛЕФОНЧИК = СЦЕНА ПЕРВОГО ЭКРАНА В ТЕКУЩЕМ СОСТОЯНИИ (владелец 09.09.2026): на любом
  // шаге открывается тот же нарисованный телефон (страница wizard-scene, адрес demoUrl —
  // слова ниши + уже введённые фразы бота + лицо аккаунта), а не отдельная страница
  // переписки. У анкеты без сцены приложение показывает страницу /wizard-preview — её же
  // показывает и браузер. Отдельного веб-макета больше нет (удалён 01.10.2026).
  const openPreview = () => {
    if (nativeOn && nativeMethod('wizPreview') && demoUrl) {
      void nativeBridge()?.wizPreview?.({
        skin: activeDef.accountChannel === 'telegram' ? 'telegram' : 'ig-direct',
        account: { name: accCred ? botAccountLabel(accCred) : '' },
        msgs: [],
        // ЗАГОЛОВОК ТЕЛЕФОНЧИКА — КОРОТКИЙ (П27 обхода владельца 11.09.2026, круг 3;
        // отменяет длинную подпись из П16 круга 2). Слово стоит в шапке листа справа от
        // крестика, одной строкой: «Пример, как это будет выглядеть» там переносилось и
        // лезло в две строки. Подпись ПОД кнопкой в самой анкете остаётся прежней
        // (wizIntroExample) — там места хватает и слова объясняют кнопку.
        dict: {
          title: ta('wizPreviewShort'),
          inputHint: '',
          commentsTitle: '',
          replyHint: '',
          botNote: '',
        },
        url: demoUrl,
      });
      return;
    }
    // Родной мастер — родной и предпросмотр: ряды считает веб, рисует натив (приказ
    // владельца 29.08.2026 «телефончик остаётся»).
    if (nativeOn && nativeMethod('wizPreview')) {
      void nativeBridge()?.wizPreview?.({
        skin: activeDef.accountChannel === 'telegram' ? 'telegram' : 'ig-direct',
        account: {
          name: accCred ? botAccountLabel(accCred) : '',
          ...(accCred?.handle ? { handle: accCred.handle } : {}),
        },
        msgs: toNativeMsgs(previewMsgs),
        ...(previewComments ? { comments: previewComments } : {}),
        dict: {
          // Тот же короткий заголовок, что у сцены выше (П27): телефончик показывает ПРИМЕР.
          title: ta('wizPreviewShort'),
          inputHint: ta('wphInputPh'),
          commentsTitle: ta('wphComments'),
          replyHint: ta('wizReplyPh'),
          botNote: '',
        },
        // НОВЫЙ КЛЮЧ (ТЗ-9 Е4): адрес нашей же страницы с нарисованным телефоном. Новая
        // сборка показывает его веб-вью и своих пузырей не рисует; старая сборка ключа
        // не знает и рисует по-прежнему — поэтому все прежние ключи по-прежнему шлются
        // (контракт моста заморожен, CLAUDE.md запрет №8).
        ...(previewUrl ? { url: previewUrl } : {}),
      });
      return;
    }
    // БРАУЗЕР — ТО ЖЕ, ЧТО ПОКАЗЫВАЕТ iOS-ПРИЛОЖЕНИЕ (владелец 01.10.2026: «сцена должна быть
    // та же, что в iOS-приложении; нынешний макет пережил себя, его нужно удалить»). Раньше
    // ветка со сценой стояла под `panel.available`, а оно истинно только внутри приложения:
    // браузер проваливался в старый серый макет переписки. Теперь лист поверх мастера несёт
    // ту же сцену первого экрана в текущем состоянии (тот же компонент `Scene` и те же слова,
    // что страница /wizard-scene, которую показывает лист приложения), заголовок — тот же
    // короткий. У анкеты без сцены — та же страница /wizard-preview, что в приложении.
    const body = demoScene ? (
      <div className="wiz-stage wiz-stage--sheet">
        <Scene def={demoScene} on active still replay />
      </div>
    ) : previewUrl ? (
      <iframe className="wiz-preview-frame" src={previewUrl} title={ta('wizPreviewShort')} />
    ) : null;
    if (!body) return;
    // ЛИСТ-СТОПКА С КРЕСТИКОМ (круг 3, П5, кадр iOS W31-tg-step2-preview): в приложении превью —
    // отдельный лист поверх мастера (WizPreviewWebSheet) с «×» слева и словом по центру, а не
    // шаг внутри мастера со скобкой «‹». `deck` в браузере вид не меняет, только глиф (drawer).
    // «×» СЛЕВА, КАК У ЛИСТА ПРИЛОЖЕНИЯ (APP-CLAIMS-1020 ред. 11, Д11; WizardPreview.swift — лист стопкой с крестиком): без
    // `lead` уровень поверх мастера получал скобку «‹», словно шаг вбок.
    panel.open({ title: ta('wizPreviewShort'), sheet: true, deck: true, lead: 'close', winKey: 'wizard-preview', body });
  };

  // ПУБЛИКАЦИИ И КАРТИНКА В РОДНОМ МАСТЕРЕ (владелец 29.08.2026). Мастер показан
  // СИСТЕМНОЙ шторкой, а веб-листы живут внутри веб-вью — они лягут ПОД ней и человек их
  // не увидит. Поэтому сетку снимков и выбор фото рисует натив, а веб остаётся мозгом:
  // список публикаций тянет он (ходит в Мету) и отдаёт готовыми рядами, снимок из
  // системного окна тоже заливает он — второго пути заливки у мастера нет.
  //
  // НАЖАЛ — ОТКРЫЛОСЬ МОМЕНТАЛЬНО (владелец 02.09.2026, Н17). Раньше тап по полю
  // «Публикации» уходил в Мету и лист вставал только с ответом — между тапом и
  // открытием висел запрос. Теперь ряды тянутся ЗАРАНЕЕ (поле появилось на шаге и
  // аккаунт выбран — список уже едет), а тап показывает сетку сразу: не доехало —
  // натив дополняет уже открытый лист тем же вызовом (wizPosts).
  const nativePosts = useRef<{ items: NativePost[]; hasMore: boolean; loadedFor: string | null }>({
    items: [],
    hasMore: false,
    loadedFor: null,
  });
  /** Лист открыт (или его только что попросили) — ряды показываем, как только приедут. */
  const postsWant = useRef(false);
  /** Первая страница уже едет: второй запрос за тем же не заводим. */
  const postsBusy = useRef(false);
  const postsCredId = typeof formValues.accountId === 'string' ? formValues.accountId : '';
  /** Отдать сетку нативу: листа нет — он откроется, есть — дополнится (WizPostsSheet.fill).
   *  ПОКА КАТАЛОГ ЕДЕТ, слова про пустоту не шлём: под заголовком молчание, а не
   *  «публикаций нет» (тот же приём, что у родного листа шаблонов). */
  /** ЗНАЧЕНИЯ ОТКРЫТОЙ СТРАНИЦЫ: у страницы триггера (черновик или существующий) поля живут
   *  внутри ответа-списка, у шага — плоские ответы. Лист публикаций подсвечивает выбранное
   *  по ним, а не по плоскому `mediaIds` (К18, приёмка Г29). */
  const pageValues = (): Record<string, unknown> => {
    const draft = draftRef.read();
    if (draft) {
      const cfg = stepWithItems(draft.name)?.itemList;
      return { ...draft.next, ...(cfg?.fieldValues?.(draft.next, draft.i) ?? {}) };
    }
    if (rowsAt && !rowAt) {
      const cfg = stepWithItems(rowsAt.name)?.itemList;
      if (cfg?.fields) return { ...formValues, ...(cfg.fieldValues?.(formValues, rowsAt.i) ?? {}) };
    }
    return formValues;
  };
  const showNativePosts = (): void => {
    void nativeBridge()?.wizPosts?.({
      title: ts('chatStartPosts'),
      items: nativePosts.current.items,
      picked: String(pageValues().mediaIds ?? '')
        .split(',')
        .filter(Boolean),
      hasMore: nativePosts.current.hasMore,
      more: ts('chatStartPostsMore'),
      done: ts('chatStartPostsOk'),
      empty: nativePosts.current.loadedFor === null ? '' : ts('chatStartPostsEmpty'),
      theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
    });
  };
  const loadNativePosts = async (offset: number, show: boolean): Promise<void> => {
    if (show) postsWant.current = true;
    // Прогрев уже едет — его конец сам покажет ряды тому, кто их ждёт.
    if (postsBusy.current && offset === 0) return;
    const credId = postsCredId;
    const q = new URLSearchParams({ offset: String(offset), ...(credId ? { credId } : {}) });
    postsBusy.current = true;
    const ans = (await fetch(`/api/cabinet/ig-media?${q.toString()}`, { cache: 'no-store' })
      .then((r) => r.json())
      .catch(() => ({}))) as { items?: IgMediaCard[]; hasMore?: boolean };
    postsBusy.current = false;
    const fresh: NativePost[] = (ans.items ?? []).map((m) => ({
      id: m.id,
      thumb: m.thumb ?? '',
      video: m.productType === 'REELS' || m.type === 'VIDEO',
    }));
    nativePosts.current = {
      items: offset === 0 ? fresh : [...nativePosts.current.items, ...fresh],
      hasMore: !!ans.hasMore,
      loadedFor: credId,
    };
    if (postsWant.current) showNativePosts();
  };
  // Прогрев: ряды готовим ДО тапа — на каждый выбранный аккаунт свои.
  const postsWarm =
    nativeOn &&
    nativeMethod('wizPosts') &&
    !!postsCredId &&
    steps.some((st) => st.params.some((p) => p.name === 'mediaIds'));
  useEffect(() => {
    if (!postsWarm || nativePosts.current.loadedFor === postsCredId) return;
    void loadNativePosts(0, false);
    // Один прогрев на аккаунт: рядами владеет ref, повторные рендеры их не перекачивают.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postsWarm, postsCredId]);

  /** Снимок из родного выбора фото — ПУТЬ СТАРОЙ СБОРКИ (base64 через мост): заливаем
   *  ТЕМ ЖЕ путём, что и файл со страницы. Сборка с нативной заливкой сюда не приходит. */
  const uploadNativePhoto = async (name: string, dataUrl: string): Promise<void> => {
    // СНИМОК ПОД НЕ-КАРТИНКУ НЕ БЕРЁМ (владелец 19.09.2026): подарок «аудио/видео/файл»
    // в старой сборке ещё мог прийти из окна фото — jpg под sendAudio ронял бота.
    if (name === WIZ_GIFT_FILE && giftKind !== 'image') {
      setFormErr(ts('attachBadType'));
      return;
    }
    setPhotoBusy(true);
    const blob = await fetch(dataUrl)
      .then((r) => r.blob())
      .catch(() => null);
    if (!blob) {
      setPhotoBusy(false);
      return;
    }
    const body = new FormData();
    body.append('file', new File([blob], 'photo.jpg', { type: 'image/jpeg' }));
    // Картинка мастера едет НАШИМ адресом (/api/form/file/<id>), как медиа сообщений:
    // билд перепишет его на /f/<id>/<имя> за Cloudflare, и Telegram скачает (владелец
    // 19.09.2026). Прежний публичный S3 (s3.firstvds.ru) Telegram не достаёт — sendPhoto
    // падал «failed to get HTTP URL content», бот молчал.
    // `kind=image`: ручка сверяет настоящий тип файла (по байтам) с видом — не картинка
    // не пройдёт (владелец 19.09.2026: «проверять и тип, и заголовки, и размер»).
    const j = await fetch(`/api/cabinet/media?kind=image&channel=${giftChannel}`, {
      method: 'POST',
      body,
    })
      .then(
        async (r) =>
          (await r.json().catch(() => ({}))) as { ok?: boolean; url?: string; name?: string },
      )
      .catch(() => null);
    setPhotoBusy(false);
    const url =
      j?.ok && j.url ? (j.name ? `${j.url}?name=${encodeURIComponent(j.name)}` : j.url) : null;
    if (url) applyAnswers({ ...formValues, [name]: url });
    else setFormErr(ta('wizImageFail'));
  };

  /**
   * ПРАВКА ПОЛЯ ОТКРЫТОЙ СТРАНИЦЫ ВХОДА — ОДНОЙ РУКОЙ (родной путь). Поля страницы входа
   * ЦЕПОЧКИ живут не плоским ответом, а внутри своего ответа-списка (К5): правку
   * складывает обратно сама анкета. У входов обычного бота ответ плоский.
   */
  const applyEntryField = (name: string, value: unknown): void => {
    // Черновик нового триггера (П10 п.6): правка копится в нём, а не в ответах анкеты.
    // Черновик читаем ИЗ ССЫЛКИ: правка и «Готово» приходят одним тиком (К18.1 п.7).
    const draft = draftRef.read();
    if (draft) {
      const cfg = stepWithItems(draft.name)?.itemList;
      if (cfg?.applyFields) {
        const next = cfg.applyFields(draft.next, draft.i, { [name]: value });
        setDraftAdd({ ...draft, next });
      }
      return;
    }
    const entryCfg = rowsAt && !rowAt ? stepWithItems(rowsAt.name)?.itemList : undefined;
    if (entryCfg?.fields && entryCfg.applyFields) {
      // ПРАВКА И «ГОТОВО» ПРИХОДЯТ ОДНИМ ТИКОМ (К18.1 п.7): у черновика свежесть держит
      // `draftRef`, у УЖЕ ДОБАВЛЕННОГО триггера — `liveValues`. Без этой строчки проверка
      // «Готово» (`pageWarnAt` в `onItemBack`) читала бы ответы БЕЗ последнего слова и
      // отбивала бы законное «Готово» (К22.2 этап 1 п.1).
      const next = entryCfg.applyFields(liveValues.current, rowsAt!.i, { [name]: value });
      liveValues.current = next;
      applyAnswers(next);
      return;
    }
    applyAnswers({ ...formValues, [name]: value });
  };

  const openNativePick = (name: string) => {
    // ФАЙЛ ПОДАРКА ПОД ВЫБРАННЫЙ ВИД (владелец 19.09.2026): ряд `file` — за файлом натив
    // идёт к вебу, веб просит системное окно под маски канала (аудио — mp3/m4a, видео —
    // библиотека, «файл» — любой). Заливает файл веб, ручка мастера сверяет тип по байтам.
    if (name === WIZ_GIFT_FILE) {
      if (!nativeMethod('wizFile')) return;
      void nativeBridge()?.wizFile?.({
        name,
        // Маски окна — ШИРЕ списка отправки (mov/webm/avi под «Видео»): показать ролик
        // человек может, а в mp4 его переведёт устройство по вопросу «Обработать».
        accept: [...chatAttachPick(giftChannel, giftKind)],
        videos: giftKind === 'video',
        // АДРЕС НАШЕЙ РУЧКИ ЦЕЛИКОМ (F4): натив заливает файл сам, поэтому адрес обязан
        // быть полным — относительный путь оболочке не с чем сложить. Вид и канал те же,
        // что у заливки с сайта: второй дороги у файла мастера нет.
        upload: {
          url: `${window.location.origin}/api/cabinet/scenarios/files?kind=${giftKind}&channel=${giftChannel}`,
          field: 'file',
        },
        theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
      });
      return;
    }
    // СТРОКА «ВАРИАНТЫ ОТВЕТА» СО СЧЁТОМ (ТЗ USERTEST-1014 п.7): за ней страница-шторка со списком строк.
    if (name === LINES_ROW_FIELD) {
      if (linesAt === name) setReopen((n) => n + 1);
      setLinesAt(name);
      return;
    }
    // ССЫЛКА ИЗ ДРУГОГО БОТА (К17.4 п.6 в): имя строки несёт код ссылки — он и становится
    // подписью источника, вход встаёт ровно на эту ссылку. Ссылка без кода нажимаемой не
    // бывает (она приезжает подводкой), поэтому сюда её тап не приходит.
    if (name.startsWith(WIZ_BOT_LINK_PFX)) {
      applyEntryField('linkLabel', name.slice(WIZ_BOT_LINK_PFX.length));
      return;
    }
    // СТРОКА САМОГО ШАГА (запуск цепочки, И10 п.10) приезжает тем же `wizPick`, но с
    // именем `steprow:<ключ>` (nativeWizSteps): у главного экрана мастера в сборках до 117
    // тап по рядам вида `rows` не подключён, и строка шла бы в никуда (приёмка владельца
    // 05.09). Дальше — та же дорога, что у тапа со страницы сообщения.
    if (name.startsWith(STEP_ROW_FIELD)) {
      openNativeRow(name.slice(STEP_ROW_FIELD.length));
      return;
    }
    // СТРОКА-КАРТОЧКА «Сообщение» на шаге (ТЗ Б): за ней родное окно письма. Смысл
    // события `wizPick` тот же, что был, — «за содержимым этой строки натив идёт к
    // вебу», поэтому нового имени тут не заводим (запрет №8).
    for (const st of steps) {
      const card = (st.messages ?? []).find((c) => c.param === name);
      if (card) {
        setMsgAt({
          param: name,
          title: card.title,
          subtitle: st.title,
          // Назначен час отправки — он и стоит в углу пузыря (И11.7).
          ...(wizClockAt(formValues) ? { clockAt: wizClockAt(formValues) } : {}),
        });
        return;
      }
    }
    if (name === 'mediaIds' && nativeMethod('wizPosts')) {
      // Сетка встаёт СРАЗУ (Н17): готовое показываем, недостающее доезжает в открытый
      // лист. Аккаунт сменили — старые снимки не показываем ни кадра.
      if (nativePosts.current.loadedFor !== postsCredId) {
        nativePosts.current = { items: [], hasMore: false, loadedFor: null };
      }
      postsWant.current = true;
      showNativePosts();
      if (nativePosts.current.loadedFor !== postsCredId) void loadNativePosts(0, true);
      return;
    }
    if (name === 'mediaIds') {
      panel.open({
        title: ts('chatStartPosts'),
        sheet: true,
        body: (
          <WizPostsSheetBody
            credId={typeof formValues.accountId === 'string' ? formValues.accountId : null}
            initial={String(pageValues().mediaIds ?? '')
              .split(',')
              .filter(Boolean)}
            // Выбор уходит в открытый триггер (черновик или строку списка), а не в плоский
            // ответ шага; вне страницы триггера — как раньше, плоско.
            onPick={(ids) => applyEntryField('mediaIds', ids.join(','))}
          />
        ),
      });
      return;
    }
    const p = steps.flatMap((st) => st.params).find((x) => x.name === name);
    panel.open({
      title: p?.displayName ?? '',
      sheet: true,
      body: (
        <WizImage
          channel={giftChannel}
          label={p?.displayName ?? ''}
          hint={typeof p?.description === 'string' ? p.description : ''}
          value={typeof formValues[name] === 'string' ? (formValues[name] as string) : ''}
          onChange={(v) => setAnswers({ ...formValues, [name]: v })}
        />
      ),
    });
  };

  /**
   * ФАЙЛ ПОДАРКА ИЗ ПРИЛОЖЕНИЯ — ЗАЛИВАЕТ НАТИВ (ТЗ 20.09.2026 F4, владелец: «приложение
   * обязано брать файл любого допустимого размера»). Мост больше НЕ возит файл строкой
   * base64 и своего предела не держит: натив шлёт его multipart-ом прямо в нашу ручку, а
   * сюда приходит ход дела — поехал, залит (ответ ручки), не доехал.
   *
   * Дальше дорога ОДНА с сайтом (F3): влезает — кладём в ответ, не влезает — вопрос
   * листом «Сжать / Выбрать другой файл», и лист этот РОДНОЙ (веб-шторка легла бы ПОД
   * системной шторкой мастера — канон §11).
   */
  const onNativeFile = (
    name: string,
    file: { fileName: string; mime: string; bytes: number },
    state: { status: WizFileStatus; body?: Record<string, unknown>; code?: string },
  ): void => {
    if (state.status === 'uploading') {
      setFormErr(null);
      setFileUp(true);
      setFileFit('');
      return;
    }
    // ГОТОВИМ НА ТЕЛЕФОНЕ (ТЗ 20.09.2026 V2): слово ряда то же, что у раннера на сайте, —
    // какое именно, решено в момент просьбы (вес или формат), а не здесь.
    if (state.status === 'compressing') {
      setFormErr(null);
      setFileUp(false);
      setFileFit((was) => (was === '' ? 'attachCompressing' : was));
      return;
    }
    setFileUp(false);
    setFileFit('');
    // ФАЙЛ ВЫБРАН, НО В СЕТЬ НЕ УХОДИЛ (ТЗ V.4, владелец 20.09.2026): решает ВЕБ по своду
    // правил канала. Подходит — «вези как есть»; не подходит — вопрос человеку, и только
    // по его слову устройство жмёт ролик. Числа настоящие — их дала оболочка с диска.
    if (state.status === 'picked') {
      const fit = chatAttachFit(giftChannel, giftKind, file.bytes, { mime: file.mime });
      setFormErr(null);
      if (fit.fits) {
        void nativeBridge()?.wizFileGo?.({ name, action: 'upload' });
        return;
      }
      // РОЛИК ГОТОВИТ САМО УСТРОЙСТВО — И ПОД ВЕС, И ПОД ФОРМАТ (ТЗ V.3 + хвост блока V):
      // экспорт AVAssetExportSession всегда отдаёт mp4, поэтому «Сжать» (тяжёлый) и
      // «Обработать» (mov/webm/avi) — одно и то же действие оболочки, разные только
      // слова человеку. Тяжёлый исходник на сервер не идёт ни в том, ни в другом случае.
      const onDevice =
        giftKind === 'video'
          ? (): void => {
              setFileFit(fit.fix === 'convert' ? 'attachProcessing' : 'attachCompressing');
              void nativeBridge()?.wizFileGo?.({
                name,
                action: 'compress',
                maxBytes: fit.maxBytes,
              });
            }
          : undefined;
      nativeFix.askBefore({ kind: giftKind, bytes: file.bytes, mime: file.mime }, fit, {
        ...(fit.fix === 'compress' && onDevice ? { squeeze: onDevice } : {}),
        ...(fit.fix === 'convert' && onDevice ? { convert: onDevice } : {}),
        again: () => {
          void nativeBridge()?.wizFileGo?.({ name, action: 'drop' });
          openNativePick(name);
        },
        drop: () => {
          void nativeBridge()?.wizFileGo?.({ name, action: 'drop' });
        },
      });
      return;
    }
    const body = state.body ?? {};
    const err = typeof body.error === 'string' ? body.error : '';
    if (state.status === 'error' || body.ok !== true || typeof body.url !== 'string') {
      const mb = Math.round(
        (chatAttachLimit(giftChannel, giftKind)?.maxBytes ?? 0) / (1024 * 1024),
      );
      setFormErr(
        // Ужали на телефоне, а всё равно не влезает (или жать нечем) — говорим честно, а
        // не кладём в поле файл, который площадка отобьёт уже у живого бота (ТЗ V2).
        state.code === 'toobig'
          ? ts('attachStillTooBig')
          : state.code === 'compress'
            ? ts('attachCompressFail')
            : state.code === '413'
              ? ts('attachTooLarge', { mb })
              : state.code === '415'
                ? ts(err === 'bad_dims' ? 'attachBadDims' : 'attachBadType')
                : ts('attachUploadError'),
      );
      return;
    }
    setFormErr(null);
    nativeFix.settle(
      {
        url: body.url,
        ...(typeof body.id === 'string' ? { id: body.id } : {}),
        ...(typeof body.name === 'string' ? { name: body.name } : { name: file.fileName }),
        ...(typeof body.bytes === 'number' ? { bytes: body.bytes } : { bytes: file.bytes }),
        ...(typeof body.maxBytes === 'number' ? { maxBytes: body.maxBytes } : {}),
      },
      {
        channel: giftChannel,
        kind: giftKind,
        apply: (url) => applyAnswers({ ...formValues, [name]: url }),
        // «Выбрать другой файл» — то же системное окно, что открыл тап по ряду.
        again: () => openNativePick(name),
        fail: (msg) => setFormErr(msg),
      },
    );
  };

  /**
   * СНИМОК ИЗ ПРИЛОЖЕНИЯ — ТОЖЕ ЗАЛИВАЕТ НАТИВ (хвост ТЗ блока V, правило владельца
   * «подходящий файл не трогаем»). Раньше снимок ехал вебу строкой base64 и БЕЗУСЛОВНО
   * ужимался оболочкой до 1600 px — портился и там, где портить было нечего. Теперь
   * дорога у снимка ровно та же, что у файла (F4 + V.4): оболочка кладёт его на диск,
   * шлёт метаданные, ВЕБ считает по своду правил канала, и только если снимок не влезает
   * в предел, человека спрашивают — «Сжать» (лестница 4096 → 2560 → 1920 → 1280 НА
   * УСТРОЙСТВЕ) или «Выбрать другой файл».
   */
  /** Открыть системное окно фото заново («Выбрать другой файл» в родном вопросе). */
  const openNativePhoto = (name: string): void => {
    void nativeBridge()?.wizPhotoOpen?.({ name, upload: photoUpload() });
  };

  const onNativePhoto = (
    name: string,
    file: { fileName: string; mime: string; bytes: number; width?: number; height?: number },
    state: { status: WizFileStatus; body?: Record<string, unknown>; code?: string },
  ): void => {
    // СНИМОК ПОД НЕ-КАРТИНКУ НЕ БЕРЁМ (владелец 19.09.2026): подарок «аудио/видео/файл»
    // картинкой не бывает — jpg под sendAudio ронял бота.
    if (name === WIZ_GIFT_FILE && giftKind !== 'image') {
      setFormErr(ts('attachBadType'));
      void nativeBridge()?.wizFileGo?.({ name, action: 'drop' });
      return;
    }
    if (state.status === 'uploading' || state.status === 'compressing') {
      setFormErr(null);
      setPhotoBusy(true);
      return;
    }
    setPhotoBusy(false);
    if (state.status === 'picked') {
      const fit = chatAttachFit(giftChannel, 'image', file.bytes, {
        mime: file.mime,
        ...(file.width ? { width: file.width } : {}),
        ...(file.height ? { height: file.height } : {}),
      });
      setFormErr(null);
      // ВЛЕЗАЕТ — ВЕЗЁМ КАК ЕСТЬ, ни о чём не спрашивая и ничего не переделывая.
      if (fit.fits) {
        void nativeBridge()?.wizFileGo?.({ name, action: 'upload' });
        return;
      }
      nativeFix.askBefore({ kind: 'image', bytes: file.bytes, mime: file.mime }, fit, {
        // Лестница уменьшения живёт В ОБОЛОЧКЕ: тяжёлый снимок в сеть не уходит вовсе.
        ...(fit.fix === 'compress'
          ? {
              squeeze: () => {
                void nativeBridge()?.wizFileGo?.({
                  name,
                  action: 'compress',
                  maxBytes: fit.maxBytes,
                });
              },
            }
          : {}),
        again: () => {
          void nativeBridge()?.wizFileGo?.({ name, action: 'drop' });
          openNativePhoto(name);
        },
        drop: () => {
          void nativeBridge()?.wizFileGo?.({ name, action: 'drop' });
        },
      });
      return;
    }
    const body = state.body ?? {};
    if (state.status === 'error' || body.ok !== true || typeof body.url !== 'string') {
      const mb = Math.round((chatAttachLimit(giftChannel, 'image')?.maxBytes ?? 0) / (1024 * 1024));
      setFormErr(
        state.code === 'toobig'
          ? ts('attachStillTooBig')
          : state.code === 'compress'
            ? ts('attachCompressFail')
            : state.code === '413'
              ? ts('attachTooLarge', { mb })
              : state.code === '415'
                ? ts(body.error === 'bad_dims' ? 'attachBadDims' : 'attachBadType')
                : ta('wizImageFail'),
      );
      return;
    }
    setFormErr(null);
    // Имя файла — параметром адреса: сборка деплоя перепишет его в публичный путь
    // /f/<id>/<имя>, и площадка узнает вид по расширению (как у заливки с сайта).
    const url =
      typeof body.name === 'string' && body.name !== ''
        ? `${body.url}?name=${encodeURIComponent(body.name)}`
        : body.url;
    applyAnswers({ ...formValues, [name]: url });
  };

  /**
   * «СОЗДАТЬ» С РОДНОЙ ФОРМЫ: проверяем ВСЕ шаги сразу (страница одна — «шага, на
   * котором ошибка», у неё нет), и только потом отдаём создание общей руке — той же
   * `goNext` на последнем шаге, которой живёт веб-мастер.
   */
  const bcastSubmit = () => {
    if (busy) return;
    const bad: Record<string, string> = {};
    let why = '';
    for (const s of steps) {
      if (s.key === WIZ_INTRO_KEY) continue;
      const miss = visibleParams(s.params, formValues).filter(
        (p) => p.required && isEmptyAnswer(formValues[p.name] ?? p.default),
      );
      for (const p of miss) bad[p.name] = ta('wizFieldRequired');
      const stop =
        activeDef.stepBlocked?.(formValues, s.key, loc, { ...(chanLive ?? {}), items: itemLive }) ??
        null;
      if (why === '' && (stop !== null || miss.length > 0)) {
        why = stop ?? ta('wizFillField', { field: miss[0]?.displayName ?? '' });
      }
    }
    if (why !== '') {
      setBcastWarn(bad);
      if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
      setTried(true);
      setFormErr(why);
      return;
    }
    setBcastWarn({});
    setFormErr(null);
    setTried(false);
    // Создание ведёт общая рука: встаём на последний шаг и просим её ход (тот же путь,
    // которым «Создать» жмут в вебе и в родном мастере).
    setIdx(Math.max(0, steps.length - 1));
    setNextAsked((n) => n + 1);
  };

  useNativeWizard({
    // Рассылку в приложении ведёт своя родная форма — мастер тогда молчит: два экрана
    // на одну анкету означали бы два слушателя одних и тех же событий.
    active: nativeOn && !bcastOn,
    caption: ta('botTplWizMark'),
    steps: nativeStepsLive ?? [],
    at: Math.min(idx, Math.max(0, steps.length - 1)),
    dict: nativeDict,
    dark: typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark',
    busy: busy || photoBusy || fileUp || fileFit !== '' || nativeFix.busy,
    // КЛЮЧ `error` НЕ МЕНЯЕТСЯ (мост заморожен, CLAUDE.md запрет №8) — меняются только
    // правила текста: он уезжает после попытки и стоит, пока причина не устранена
    // (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128).
    error: windowErr ?? '',
    hasPreview: hasWizPreview,
    // Полями публикаций владеет ВЕБ: нативу они уезжают ПОДПИСЬЮ, и принять её обратно
    // значило бы затереть список id (ТЗ-3 Т4, инвариант у nativeWizWebOwned). А вот СПИСОК
    // id от родного листа публикаций приходит тем же событием и принимается — без этого
    // выбранная публикация до ответов не доезжала вовсе (ТЗ-5 К5).
    onChange: (name, value) => {
      // ГОТОВАЯ ССЫЛКА ОТВЕТА НЕ ИМЕЕТ (К17.2 п.2): её считает веб. Сборка, которая вида
      // `copy` ещё не знает, рисует его обычным полем ввода со ссылкой внутри и шлёт это
      // значение назад — принять его значило бы записать ссылку вместо подписи источника.
      if (name === WIZ_LINK_READY_FIELD) return;
      // ЧЕРНОВИК НОВОГО ТРИГГЕРА (П10 п.6): правки полей копятся в нём до «Готово».
      if (draftAdd) {
        applyEntryField(name, value);
        return;
      }
      // ЧЕРНОВИК ПИСЬМА (ТЗ 21.09.2026 раздел 6): правка «Когда» копится в нём и уезжает
      // в анкету только по «Готово» страницы письма. Берём черновик ССЫЛКОЙ: правка и
      // «Готово» приходят одним тиком (К18.1 п.7).
      const lcur = rowsAt ? letterAt(rowsAt.name, rowsAt.i) : null;
      if (lcur) {
        const lcfg = stepWithItems(lcur.name)?.itemList;
        if (lcfg && letterOwnField(lcfg, wizLetterValues(lcur, liveValues.current), lcur.i, name)) {
          letterPatch({ [name]: nativeWizValueToAnswer(name, value) });
          return;
        }
      }
      // ПОЛЯ СТРАНИЦЫ ВХОДА живут не плоским ответом, а внутри своего ответа-списка (К5):
      // правку складывает обратно сама анкета, иначе в ответах завёлся бы мусорный ключ.
      const entryCfg = rowsAt && !rowAt ? stepWithItems(rowsAt.name)?.itemList : undefined;
      if (entryCfg?.fields && entryCfg.applyFields) {
        const own = entryCfg.fields(formValues, rowsAt!.i, itemLive).some((p) => p.name === name);
        if (own) {
          applyEntryField(name, value);
          return;
        }
      }
      // ПРАВКА СТРОК НА СТРАНИЦЕ «ВАРИАНТЫ ОТВЕТА» (ТЗ USERTEST-1014 п.7): на шаге поле едет строкой `message`
      // (ответом «владеет веб»), но со страницы-шторки значение принимается — это и есть правка списка.
      if (linesAt && name === linesAt) {
        applyAnswers({ ...formValues, [name]: nativeWizValueToAnswer(name, value) });
        return;
      }
      // КАНАЛ ВЫБРАН В РОДНОМ ЛИСТЕ — ПРОВЕРКА ЗАНОВО КАЖДЫЙ РАЗ (ТЗ USERTEST-1014 п.13, владелец 14.10.2026): права
      // бота могли снять; автопроверка шага помнит уже проверенные адреса и второй раз их не спрашивает.
      if (name === WIZ_CHANNEL_FIELD && typeof value === 'string' && value.trim() !== '') {
        applyAnswers({ ...formValues, [name]: value });
        void chanCheck.check(value, 'list');
        return;
      }
      if (nativeWizWebOwned(nativeStepsLive ?? []).has(name) && !nativeWizIsPostsIds(value)) return;
      applyAnswers({ ...formValues, [name]: nativeWizValueToAnswer(name, value) });
    },
    onNext: (vals: Record<string, string | boolean | string[]>) => {
      // Только поля ТЕКУЩЕГО шага: словарь сборки копит и чужие, застаревшие (20.09.2026).
      const live = nativeStepsLive ?? [];
      const at = Math.min(idx, Math.max(0, live.length - 1));
      applyAnswers({ ...formValues, ...nativeWizAnswers(vals, live, at) });
      setNextAsked((n) => n + 1);
    },
    // «НАЗАД» С ПЕРВОГО ШАГА ПРАВКИ ЗАКРЫВАЕТ МАСТЕР (П86): шага «Знакомство» в правке
    // нет, возвращаться некуда.
    onBack: () => (skipScene && idx <= 1 ? onDone() : setIdx((n) => Math.max(0, n - 1))),
    // КРЕСТИК/СВАЙВ В ПРАВКЕ — ДОЖДАТЬСЯ НАЧАТЫХ ЗАПИСЕЙ (ТЗ П188): новых записей крестик не
    // делает, но начатые («Готово» сообщения, «Далее») обязаны долететь до закрытия.
    onCancel: () => {
      if (edit && saveQueue.busy) {
        void saveQueue.flush().then(onDone);
        return;
      }
      onDone();
    },
    onPick: openNativePick,
    onPostsMore: () => void loadNativePosts(nativePosts.current.items.length, true),
    onPhoto: (name, data) => void uploadNativePhoto(name, data),
    onPhotoFile: onNativePhoto,
    onFile: onNativeFile,
    // Ответ родного листа «файл не влезает» (F4): ключ действия, пустой — «Отмена».
    onAskPick: (key) => {
      // ВОПРОС «УДАЛИТЬ СООБЩЕНИЕ?» (П132 п.5): ответ забирает тот, кто спрашивал, — и
      // ровно один раз. Иначе — вопрос про тяжёлый файл (F4), как было.
      const del = itemAskDel.current;
      if (del) {
        itemAskDel.current = null;
        del(key);
        return;
      }
      nativeFix.pick(key);
    },
    onPreview: openPreview,
    // ШАГ-СПИСОК: строки рисует натив, а что за ними — знает веб (ТЗ 03.09.2026 В1).
    item: nativeItem,
    // «Покажи страницу заново» — счётчик тапов по уже открытой строке (К16.1 П1).
    reopen,
    // «Покажи поля заново» — счётчик отказов «Готово» (К22.2 этап 1 п.1): страница
    // остаётся на месте и получает патч с подсказкой-ошибкой.
    itemPatch: warnTick,
    onItemBack: (done, pop) => {
      // Страница «Варианты ответа» (ТЗ USERTEST-1014 п.7): правки уже в ответах — «Готово» и «×» просто закрывают.
      if (linesAt) {
        setLinesAt(null);
        return;
      }
      // СТРЕЛКА «НАЗАД» ВНУТРИ ШТОРКИ ПОТОКА (К20.1 п.1, владелец 07.09.2026: «стрелка
      // возвращает на шаг назад»): страница настроек ушла, а шторка осталась — под нами
      // список ботов или меню видов. Черновик выбрасываем (человек передумал), но ни
      // `addAt`, ни `rowsAt` не трогаем и «открыть заново» не бьём: их страницы на экране.
      if (pop) {
        setDraftAdd(null);
        return;
      }
      // ЧЕРНОВИК НОВОГО ТРИГГЕРА (П10 п.6): «Готово» (или сборка без ключа `done`) —
      // триггер уезжает в ответы; закрыли иначе — ничего не добавилось. В обоих случаях
      // уходим к списку «Что запускает»: список ботов под шторкой закрывается тоже.
      const draft = draftRef.read();
      if (draft) {
        // «ГОТОВО» НЕ ПУСКАЕТ, ПОКА СТРАНИЦА С ОШИБКОЙ (К19.1 п.6 — одинаковый хвост,
        // К21.1 п.5 а — пустое кодовое слово, занятый хвост): черновик не применяется, а
        // страница ОСТАЁТСЯ НА МЕСТЕ с подсказкой-ошибкой под полем — ей уезжает патч
        // полей (`warnTick`), и ничего не закрывается и не открывается заново.
        //
        // INVARIANT(scriptera): на пути отказа `setReopen` не звать. Причина (претензия
        // владельца 07.09.2026 по сборке 164): сборка закрывала шторку по «Готово» сама,
        // веб просил показать страницу заново — она вставала отдельным листом, второе
        // «Готово» уносило и его, а «Добавить триггер» после этого молчал. Закрытие
        // теперь решает веб: применил — `wizItemClose`, отказал — `wizItemPatch`.
        // Канон: docs/MESSAGE-COMPOSER-PLAN.md К22.1.
        const dcfg = stepWithItems(draft.name)?.itemList;
        if (
          done !== false &&
          dcfg &&
          pageWarnAt(dcfg, draft.next, draft.i)(dcfg.fieldValues?.(draft.next, draft.i) ?? {})
        ) {
          // ПЕРВЫЙ ОТКАЗ ВКЛЮЧАЕТ ПОДСКАЗКИ (К24 п.1): до него страница чистая, после —
          // они живут до ухода с неё и пересчитываются по живым значениям.
          setDraftTried(true);
          setWarnTick((n) => n + 1);
          return;
        }
        if (done !== false) applyAnswers(draft.next);
        setDraftAdd(null);
        setAddAt(null);
        setRowsAt(null);
        setRowAt(null);
        return;
      }
      // Уровни снимаются по одному: сперва строка письма, потом страница триггера
      // (она тоже приезжает нативу страницей элемента).
      if (rowAt !== null) {
        setRowAt(null);
        return;
      }
      if (rowsAt === null) return;
      const rcfg = stepWithItems(rowsAt.name)?.itemList;
      if (!rcfg?.fields) return;
      // ПРАВКА УЖЕ ДОБАВЛЕННОГО ТРИГГЕРА — ТОТ ЖЕ ОТКАЗ, ЧТО У ЧЕРНОВИКА (К22.2 этап 1
      // п.1): ошибка на странице — «Готово» ничего не закрывает, уезжает патч полей.
      // Значения берём из ЖИВЫХ ответов (`liveValues`): правка и «Готово» приходят одним
      // тиком, и состояние React к этой секунде ещё старое.
      const vals = liveValues.current;
      if (
        done !== false &&
        pageWarnAt(rcfg, vals, rowsAt.i)(rcfg.fieldValues?.(vals, rowsAt.i) ?? {})
      ) {
        // Признак страницы правки — свой (К24 п.1), включается тем же первым отказом.
        setRowTried(true);
        setWarnTick((n) => n + 1);
        return;
      }
      setRowsAt(null);
    },
    // ── ШАГ-СПИСОК ОБЪЕКТОВ: письма цепочки (ТЗ В2) ──────────────────────────────
    itemRows: nativeItemRows,
    onItemListTap: (name, key) => {
      const cfg = stepWithItems(name)?.itemList;
      if (!cfg) return;
      const i = Number(key);
      if (!Number.isFinite(i) || i < 1 || i > cfg.count(formValues)) return;
      // Строка без страницы («Команда /start») — тап не открывает ничего (К16.0 п.3):
      // старая сборка шеврона не читает и событие всё равно шлёт.
      if (cfg.line(formValues, i, itemLive).chevron === false) return;
      // Повторный тап по уже открытой строке = «покажи заново» (К16.1 П1).
      if (rowsAt && rowsAt.name === name && rowsAt.i === i && rowAt === null) {
        setReopen((n) => n + 1);
      }
      // ПРАВКА ПИСЬМА — ТОЖЕ ЧЕРЕЗ ЧЕРНОВИК (ТЗ раздел 6 п.4): письмо в анкете уже стоит
      // (`seed: null`), а «Когда» и «Условия» ждут «Готово» страницы.
      // ЧЕРНОВИКА ПИСЬМА БОЛЬШЕ НЕТ (П130, владелец 24.09.2026: «каждое поле сохраняет
      // само»): правки идут прямо в ответы, как у входов.
      setLetterDraft(null);
      // Открыли уже стоящий элемент — он не «добавлен этим открытием» (П132 п.3); признак
      // отбитого «Готово» живёт только у своего элемента.
      itemFresh.current = null;
      if (!itemTried || itemTried.name !== name || itemTried.i !== i) setItemTried(null);
      setRowAt(null);
      setRowsAt({ name, i });
    },
    onItemListAdd: (name, key) => {
      const cfg = stepWithItems(name)?.itemList;
      if (!cfg || cfg.count(formValues) >= cfg.max) return;
      // ЕСТЬ ВИДЫ (входы цепочки, К5.1). Новая сборка называет выбранный вид сама, старая
      // шлёт голое намерение — тогда меню видов веб рисует страницей списка. Вид с
      // выбором («Прошёл бота») ведёт на список ботов, остальные добавляются сразу.
      if (cfg.addKinds) {
        if (!key) {
          setRowAt(null);
          setRowsAt(null);
          setAddAt({ name });
          return;
        }
        const kind = itemAddKinds(cfg, formValues).find((k) => k.key === key);
        // НЕДОСТУПНЫЙ ВИД НЕ ОТКРЫВАЕТ НИЧЕГО (К18.3 этап 1 п.1): старая сборка про ключ
        // `disabled` не знает, рисует пункт живым и событие всё равно шлёт.
        if (!kind || kind.disabled) return;
        if (kind.pick === 'bots') {
          setRowAt(null);
          setRowsAt(null);
          setAddAt({ name, kind: key });
          return;
        }
        addNativeItem(name, key);
        return;
      }
      const next = cfg.add(formValues);
      const at = cfg.count(next);
      if (at === cfg.count(formValues)) return;
      // НОВОЕ ПИСЬМО ВСТАЁТ В СПИСОК СРАЗУ (П130, владелец 24.09.2026; отменяет ТЗ
      // 21.09 раздел 6): без черновика и без подтверждения «Готово».
      applyAnswers(next);
      // «ПРАВИЛО ЗАМЕТОК» (П132 п.3): строка стоит сразу, но ушли, ничего не тронув, —
      // сообщение убирается молча (`dropFreshItem` в `onItemRowsBack`).
      itemFresh.current = { name, i: at };
      setItemTried(null);
      setRowAt(null);
      setRowsAt({ name, i: at });
    },
    onItemListRemove: (name, key) => {
      const cfg = stepWithItems(name)?.itemList;
      if (!cfg) return;
      // Со строки списка ключ — номер («3»); со СТРАНИЦЫ элемента (кнопка «Удалить», если
      // веб её прислал) — адрес страницы «шаг:номер»: берём номер. У черновика номера нет.
      const i = Number(key.includes(':') ? (key.split(':')[1] ?? '') : key);
      if (!Number.isFinite(i)) return;
      // «Удалить письмо» у НЕ подтверждённого письма — это отказ от черновика: удалять в
      // анкете нечего, там его ещё нет (ТЗ раздел 6 п.3).
      const draft = letterAt(name, i);
      if (draft?.seed) {
        setLetterDraft(null);
        setRowsAt((cur) => (cur && cur.name === name ? null : cur));
        setRowAt(null);
        return;
      }
      const doRemove = (): void => {
        if (draft) setLetterDraft(null);
        itemFresh.current = null;
        setItemTried(null);
        liveApply.current(cfg.remove(liveValues.current, i));
        setRowsAt((cur) => (cur && cur.name === name ? null : cur));
        setRowAt(null);
        // Удаление сообщения — запись (ТЗ П188): явное действие, «Далее» не нужно.
        if (edit) void saveEdit(true, 'itemRemove');
      };
      // «УДАЛИТЬ» СО СТРАНИЦЫ ЭЛЕМЕНТА — С ВОПРОСОМ (П132 п.5). Натив шлёт тот же
      // `wizItemListRemove` и со страницы, и со смахивания в списке; со страницы — когда
      // открыта именно она (список под ней недоступен). Смахивание — без вопроса: жест
      // намеренный. Нетронутое новое сообщение (п.3) убирается без вопроса. Сборка без
      // `wizAsk` — удаление сразу, как было (запрет №8).
      const fromPage = !!rowsAt && rowsAt.name === name && rowsAt.i === i && rowAt === null;
      const fresh = itemFresh.current;
      const freshUntouched =
        !!fresh && fresh.name === name && fresh.i === i && !!cfg.untouched?.(liveValues.current, i);
      const ask = fromPage && !freshUntouched ? cfg.removeAsk?.(liveValues.current, i) : undefined;
      if (ask && nativeMethod('wizAsk')) {
        itemAskDel.current = (key) => {
          if (key === 'del') doRemove();
        };
        void nativeBridge()?.wizAsk?.({
          title: ask.title,
          text: ask.text,
          actions: [{ key: 'del', label: ask.ok, tone: 'danger' }],
          cancel: tc('cancel'),
          theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
        });
        return;
      }
      doRemove();
    },
    onItemListMove: (name, key, dir) => {
      const cfg = stepWithItems(name)?.itemList;
      if (!cfg?.move) return;
      const from = Number(key);
      const to = from + dir;
      if (!Number.isFinite(from) || dir === 0) return;
      if (to < 1 || to > cfg.count(formValues)) return;
      // Номера поехали — признаки «добавлен этим открытием» и «Готово отбито» больше
      // не про то место (П132 п.2–3).
      itemFresh.current = null;
      setItemTried(null);
      applyAnswers(cfg.move(formValues, from, to));
      // Перестановка сообщения — запись (ТЗ П188).
      if (edit) void saveEdit(true, 'itemMove');
    },
    onItemRowTap: (key) => openNativeRow(key),
    onItemRowsBack: (pop, done) => {
      // СТРЕЛКА «НАЗАД» СО СПИСКА БОТОВ К МЕНЮ ВИДОВ (К20.1 п.1): меню рисует сам натив
      // страницей той же шторки — вебу остаётся забыть выбранный вид.
      if (pop) {
        setAddAt(null);
        return;
      }
      // ЧЕРНОВИК ПИСЬМА (ТЗ 21.09.2026 раздел 6): «Готово» страницы письма уносит в
      // анкету «Когда» и «Условия», а уход «‹» их выбрасывает — и НОВОГО письма в
      // списке не появляется вовсе.
      //
      // Старая сборка ключа `done` не шлёт (мост заморожен, запрет №8): ей уход со
      // страницы значит «Готово» — ровно то же правило, что у `onItemBack` (П10 п.6), и
      // ровно прежнее поведение, при котором письмо сохранялось само.
      // Черновика письма нет (П130): уход со страницы письма ничего не применяет и не
      // выбрасывает — всё уже в ответах.
      if (letterRef.read()) setLetterDraft(null);
      // Страница выбора бота уходит НАЗАД к меню видов, а не к списку целиком (стопка).
      if (addAt?.kind) {
        setAddAt({ name: addAt.name });
        return;
      }
      if (addAt) {
        setAddAt(null);
        return;
      }
      if (rowsAt) {
        // УХОД СО СТРАНИЦЫ ЭЛЕМЕНТА — ОДНО МЕСТО НА ВСЕ ПУТИ («Готово», «×», свайп):
        // нетронутое новое сообщение убирается молча (П132 п.3).
        if (!dropFreshItem(rowsAt.name, rowsAt.i) && done === true) {
          // «ГОТОВО» ПРИ НЕГОДНОМ СООБЩЕНИИ (П132 п.2): шторку письма натив уводит САМ,
          // до этого события (`wizFlowClose`), — отказать, не закрывая, веб здесь не
          // может, а открыть её заново владелец запретил (К22.2: «закрылась, потом сама
          // открылась»). Поэтому: вибрация-предупреждение, строка «Сообщение» с этой
          // секунды подсвечена (`invalid`), причина стоит её значением и в строке списка,
          // а «Дальше» шага такое сообщение не пускает (stepBlocked анкеты).
          const rcfg = stepWithItems(rowsAt.name)?.itemList;
          const bad = rcfg?.rows?.(liveValues.current, rowsAt.i).some((r) => r.invalid) ?? false;
          if (bad) {
            setItemTried({ name: rowsAt.name, i: rowsAt.i });
            if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
          }
        }
      }
      setRowsAt(null);
      setRowAt(null);
    },
    // «ГОТОВО» СТРАНИЦЫ СООБЩЕНИЯ ПРИ `askDone` (П132 п.2): натив страницу не закрыл и
    // ждёт. Нетронутое новое сообщение — убрать и закрыть; негодное — отбить (строка
    // «Сообщение» с подсветкой, вибро), страница стоит; годное — закрыть. Натив по нашей
    // просьбе `wizItemRowsClose` события `wizItemRowsBack` не шлёт — состояние снимаем сами.
    onItemRowsDone: (name, key) => {
      if (!rowsAt || rowsAt.name !== name || String(rowsAt.i) !== key) return;
      const close = (): void => {
        void nativeBridge()?.wizItemRowsClose?.();
        setRowsAt(null);
        setRowAt(null);
      };
      if (dropFreshItem(rowsAt.name, rowsAt.i)) {
        close();
        return;
      }
      const rcfg = stepWithItems(rowsAt.name)?.itemList;
      const rows = rcfg?.rows?.(liveValues.current, rowsAt.i) ?? [];
      const bad = rows.some((r) => r.invalid);
      const at = { name: rowsAt.name, i: rowsAt.i };
      const refuse = (): void => {
        setItemTried(at);
        if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
      };
      // ЧИНИТСЯ ОТВЕТОМ (ТЗ П163 п.4) — родной низкий лист: «Убрать медиа» и закрыть,
      // «Отмена» — страница стоит, строка «Сообщение» подсвечена, как раньше.
      const clash = bad ? itemMsgConflict(rows) : null;
      if (clash && nativeMethod('wizAsk')) {
        const q = conflictAsk(clash.c, conflictWords);
        journalLog('msg.conflictAsk', { kind: clash.c.kind, at: 'item' });
        itemAskDel.current = (key) => {
          const answer = q.actions.find((a) => a.key === key)?.key ?? null;
          if (answer === null) {
            refuse();
            return;
          }
          fixItemMsg(clash, answer);
          close();
          // «ГОТОВО» СООБЩЕНИЯ — ЗАПИСЬ (ТЗ П188, владелец 27.09.2026): без «Далее».
          if (edit) void saveEdit(true, 'itemDone');
        };
        // Заголовок, текст, герой-значок и слово отмены — из `conflictAsk` (П151 §11р п.3).
        void nativeBridge()?.wizAsk?.({
          title: q.title,
          text: q.text,
          actions: q.actions,
          cancel: q.cancel ?? tc('cancel'),
          theme: document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light',
          hero: { icon: q.icon, name: '', meta: '' },
        });
        return;
      }
      if (bad) {
        refuse();
        return;
      }
      close();
      // «ГОТОВО» СООБЩЕНИЯ — ЗАПИСЬ (ТЗ П188, владелец 27.09.2026): каждое сообщение
      // уезжает на сервер по своему «Готово», без «Далее» — иначе крестик после правок
      // терял всё (журнал 27.09: три сессии с `wizCancel` без единого PUT).
      if (edit) void saveEdit(true, 'itemDone');
    },
  });

  /** Добавить элемент выбранного вида в приложении и открыть его страницу (К5.1). */
  function addNativeItem(name: string, kind: string, value?: string): void {
    const cfg = stepWithItems(name)?.itemList;
    if (!cfg) return;
    // ССЫЛКА ИЗ ДРУГОГО БОТА (п.29) — сразу «Переход по ссылке» с её меткой, без страницы.
    if (kind.startsWith(WIZ_ADD_BOTLINK_PFX)) {
      const next = wizAddBotLinkEntry(cfg, liveValues.current, kind.slice(WIZ_ADD_BOTLINK_PFX.length));
      setAddAt(null);
      if (next) liveApply.current(next);
      setRowAt(null);
      setRowsAt(null);
      return;
    }
    const before = cfg.count(liveValues.current);
    // ВИД БЕЗ НАСТРОЕК — БЕЗ СТРАНИЦЫ (К16.0 п.4). Смотрим меню ДО добавления: после
    // него добавленный вид из меню уходит, и спросить его было бы уже не у кого.
    const opt = itemAddKinds(cfg, liveValues.current).find((k) => k.key === kind);
    if (opt?.disabled) return;
    const direct = !!opt?.direct;
    const next = cfg.add(liveValues.current, kind, value);
    if (cfg.count(next) === before) {
      setAddAt(null);
      return;
    }
    // ВИД БЕЗ НАСТРОЕК добавляется сразу, человек возвращается к списку «Что запускает»
    // — как в родных списках iOS (будильники, конфигурации VPN).
    if (direct) {
      setAddAt(null);
      liveApply.current(next);
      setRowAt(null);
      setRowsAt(null);
      return;
    }
    // ВИД С НАСТРОЙКАМИ — ЧЕРНОВИК (владелец 06.09.2026, П10 п.5–7): страница настроек
    // встаёт полушторкой поверх списка ботов (список остаётся открытым), а в ответы
    // триггер попадает только по «Готово» (onItemBack).
    setRowAt(null);
    setDraftAdd({
      name,
      kind,
      ...(value !== undefined ? { value } : {}),
      next,
      i: cfg.count(next),
    });
  }

  /**
   * ТАП ПО СТРОКЕ-ПЕРЕХОДУ В ПРИЛОЖЕНИИ — со страницы сообщения (`wizItemRowTap`) или с
   * самого шага (`wizPick steprow:<ключ>`, см. openNativePick). Одна дорога на оба входа.
   */
  function openNativeRow(key: string): void {
    // ОТКРЫТО «ДОБАВИТЬ …» (К5.1): тап — это выбор вида либо выбор бота, а не строка
    // страницы. Дорожка та же, что в браузере, событий моста новых нет.
    if (addAt) {
      const cfg = stepWithItems(addAt.name)?.itemList;
      if (!cfg?.addKinds) return;
      if (!addAt.kind) {
        const kind = itemAddKinds(cfg, formValues).find((k) => k.key === key);
        // За недоступным видом страницы нет (К18.3 этап 1 п.1).
        if (!kind || kind.disabled) return;
        if (kind.pick === 'bots') setAddAt({ name: addAt.name, kind: key });
        else addNativeItem(addAt.name, key);
        return;
      }
      if (!key.startsWith(ITEM_PICK_PFX)) return;
      addNativeItem(addAt.name, addAt.kind, key.slice(ITEM_PICK_PFX.length));
      return;
    }
    // СТРОКИ САМОГО ШАГА (событие запуска цепочки, И10 п.10) стоят на экране шага, а не
    // на странице элемента: страницы письма сейчас нет (`rowsAt` пуст) — значит тап
    // пришёл со строки шага. Второго события для них не заводим (мост заморожен, №8).
    // ЧЕРНОВИК ПИСЬМА (ТЗ раздел 6): строки открытого письма считаются по черновику —
    // у НОВОГО письма в ответах анкеты его ещё нет вовсе.
    const rowDraft = rowsAt ? letterAt(rowsAt.name, rowsAt.i) : null;
    const rowVals = wizLetterValues(rowDraft, liveValues.current);
    const row = rowsAt
      ? stepWithItems(rowsAt.name)
          ?.itemList?.rows?.(rowVals, rowsAt.i)
          .find((r) => r.key === key)
      : nativeStepParts(step).rows.find((r) => r.key === key);
    if (!row) return;
    // «КОМУ» — общий экран «Кто получит» (И3). Сборка с родным экраном (Г11) поднимает
    // его мозг (audAt), старая — прежнюю веб-шторку: развилка живёт одним местом,
    // внутри openAudienceScreen.
    if ((row.kind === 'audience' || row.kind === 'conditions') && row.param) {
      const param = row.param;
      openAudienceScreen({
        panel,
        native: setAudAt,
        title: row.title,
        value: audienceOfRaw(rowVals[param]),
        inherited: row.inherited ?? [],
        // Бот-отправитель из ответа анкеты (К27.2 п.5).
        ...(typeof formValues.accountId === 'string' && formValues.accountId
          ? { cred: formValues.accountId }
          : {}),
        footer: row.footer ?? [],
        // УСЛОВИЯ ПИСЬМА — СТРАНИЦА ТОЙ ЖЕ ШТОРКИ (К23.1 п.3): открыли со страницы
        // письма цепочки — натив кладёт лист условий внутрь (push, «Сбросить» справа
        // в строке шапки). Со строки самого шага — прежним листом.
        // ПОЛУШТОРКОЙ ПОВЕРХ КАРТОЧКИ (П130 п.13): `stack` — новой сборке, `flow` — старой.
        ...(rowsAt ? { flow: true, stack: true } : {}),
        ...(row.kind === 'conditions'
          ? {
              conditions: true,
              runningNote: !!edit?.draftId,
              ...(row.only ? { only: row.only } : {}),
            }
          : {}),
        ...(edit?.draftId && row.setKey ? { set: `chain:${edit.draftId}:${row.setKey}` } : {}),
        // УСЛОВИЯ ПИСЬМА ЖДУТ «ГОТОВО» (ТЗ 21.09.2026 раздел 6 п.2, владелец: «открыл
        // „Условия“, настроил, вышел „‹“ без „Готово“ — НЕ сохранилось»): правка
        // ложится в черновик письма, а не в ответы анкеты. Со строки САМОГО шага
        // (черновика письма нет) — прежней дорогой, сразу в ответы.
        onChange: (v) =>
          letterRef.read()
            ? letterPatch({ [param]: JSON.stringify(v) })
            : liveApply.current({ ...liveValues.current, [param]: JSON.stringify(v) }),
      });
      return;
    }
    // СООБЩЕНИЕ — общее окно письма (стопка поверх стопки, Д2); остальные строки —
    // страница полей той же механикой, что у входов.
    if (row.kind === 'message' && row.param) {
      setMsgAt({
        param: row.param,
        title: row.title,
        subtitle: nativeItemRows?.title ?? '',
        // ОКНО ПИСЬМА ЦЕПОЧКИ (ТЗ 21.09.2026 раздел 6 п.1): его содержимое кладёт в
        // письмо СВОЯ круглая галочка — значит окну нужен черновик письма, а не ответы.
        ...(rowDraft ? { letter: true as const } : {}),
        // ОКНО ПИСЬМА — СТРАНИЦА ТОЙ ЖЕ ШТОРКИ (К23.1 п.3): открыли его со страницы
        // письма цепочки, значит натив кладёт композер внутрь (push), а не поверх.
        // Со строки самого шага (`rowsAt` пуст) окно по-прежнему открывается листом.
        ...(rowsAt ? { flow: true } : {}),
      });
      return;
    }
    if ((row.fields ?? []).length === 0) return;
    // Повторный тап по уже открытой строке = «покажи заново» (К16.1 П1).
    if (rowAt === key) setReopen((n) => n + 1);
    setRowAt(key);
  }

  /**
   * РОДНОЕ ОКНО «СООБЩЕНИЕ» В ПРИЛОЖЕНИИ (ТЗ Б3, Д2): мозг окна стоит скрытым куском
   * веба, а человек видит родной экран чата. Своей веб-шторки тут не открываем — она
   * легла бы ПОД системным листом анкеты и человек её бы не увидел.
   */
  const nativeMsgHost = msgAt ? (
    <MessageNativeHost
      key={msgAt.param}
      {...(() => {
        // Шаг-сообщение знает про ПРЕЖНИЕ поля («текст / картинка / кнопки») — ими окно
        // наполняется в первый раз (адаптер Е10); у строки письма прежних полей нет.
        const cfg = steps.flatMap((st) => st.messages ?? []).find((c) => c.param === msgAt.param);
        // ЧЕРНОВИК ПИСЬМА (ТЗ раздел 6): окно открывается тем, что человек уже набрал на
        // этой странице письма, — даже если галочку ещё не нажимали.
        const mvals = wizLetterValues(msgAt.letter ? letterRef.read() : null, liveValues.current);
        const one = cfg
          ? wizMessageDraft(cfg, mvals)
          : (() => {
              const raw = mvals[msgAt.param];
              if (typeof raw !== 'string' || raw === '') return null;
              try {
                return toTgDraft(JSON.parse(raw));
              } catch {
                return null;
              }
            })();
        return one ? { value: one } : {};
      })()}
      title={msgAt.title}
      subtitle={msgAt.subtitle}
      {...(msgAt.flow ? { flow: true, stack: true } : {})}
      {...(msgAt.clockAt ? { clockAt: msgAt.clockAt } : {})}
      // ПИСЬМО ЦЕПОЧКИ (ТЗ М2.1, ошибка 13.09): у кнопки-метки есть тумблер «и завершить
      // эту цепочку» — в приложении письмо открывает ЭТОТ мозг, а не веб-страница, и без
      // ключа тумблера там не было вовсе (претензия владельца 16.09).
      {...(activeDef.id === CHAIN_WIZARD_ID || activeDef.quickStart === 'sales' ? { chainButtons: true } : {})}
      // ПИСЬМО СОХРАНЯЕТСЯ ПРИ ЛЮБОМ УХОДЕ (ТЗ М2.2): черновик ложится в ответы анкеты на
      // КАЖДУЮ правку — уход «‹», свайпом или возвратом со страницы письма
      // (`wizItemRowsBack`) больше ничего не теряет.
      //
      // У ПИСЬМА ЦЕПОЧКИ (`letter`) правка ложится в ЧЕРНОВИК ПИСЬМА, а в анкету его
      // кладёт круглая галочка окна (ТЗ 21.09.2026 раздел 6 п.1: «нажал её — содержимое в
      // письме, даже если „Готово“ страницы письма ещё не нажимали»). Набранное при этом
      // не теряется и без галочки: черновик жив, пока открыта страница письма, и уезжает
      // в анкету её «Готово». Прочие окна (карточка шага, рассылка) — как были.
      onChange={(draft) => {
        const json = JSON.stringify(draft);
        if (msgAt.letter) {
          letterPatch({ [msgAt.param]: json });
          return;
        }
        liveApply.current({ ...liveValues.current, [msgAt.param]: json });
      }}
      onSave={(draft) => {
        const json = JSON.stringify(draft);
        if (msgAt.letter) {
          letterPatch({ [msgAt.param]: json });
          letterApply([msgAt.param]);
          setMsgAt(null);
          return;
        }
        liveApply.current({ ...liveValues.current, [msgAt.param]: json });
        setMsgAt(null);
      }}
      onCancel={() => setMsgAt(null)}
    />
  ) : null;

  /**
   * РОДНОЙ ЭКРАН «КТО ПОЛУЧИТ» В ПРИЛОЖЕНИИ (И4, Г11): мозг экрана стоит скрытым куском
   * веба, а человек видит родную стопку со списком живых людей. Значение уезжает в ответ
   * анкеты на КАЖДУЮ правку (как и в браузере), поэтому закрытие экрана ничего не теряет.
   */
  const nativeAudHost = audAt ? (
    <AudienceNativeHost
      key={`${audAt.title}|${audAt.set ?? ''}`}
      {...audAt}
      onClose={() => setAudAt(null)}
    />
  ) : null;

  // Страница-повтор (письмо цепочки): её номер (0 — страница не про повтор) и сколько
  // повторов сейчас в ответах. Кнопки «Добавить/Удалить» живут только на таких
  // страницах; у анкет без повторов repeatIdx всегда 0 и блок не рисуется.
  const repeatIdx = activeDef.repeatPages ? activeDef.repeatPages.ordOfStep(step.key) : 0;
  const repeatCount = repeatIdx > 0 ? activeDef.repeatPages!.count(formValues) : 0;

  // «ШАГ N ИЗ M» — БЕЗ СЦЕНЫ (П86 + владелец 04.10.2026): «Знакомство» — не шаг ни при
  // создании, ни в правке; на самой сцене счётчика нет (n = 0 → пусто).
  const stepNo = wizStepOf(steps, idx);
  const stepOf = stepNo.n > 0 ? ta('wizStepOf', stepNo) : '';
  // Таймера у красной строки НЕТ (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128):
  // причина стоит первой строкой тела шага, пока её не устранили, и гаснет сама.
  // Шапку панели ведёт хост (режим создания, владелец 05.08 №3): серый счётчик шагов
  // под заголовком и живая кнопка-телефон (ref — превью строится из текущих ответов).
  // Кольцо-прогресс у счётчика (ТЗ WEB-PARITY-3 М3, кадр iOS W11/W32): доля пройденных шагов.
  const stepProgress = stepNo.m > 0 ? stepNo.n / stepNo.m : 0;
  useEffect(() => {
    // Один экран рассылки (bcastOne): счётчика шагов и кольца нет — шагов нет.
    header?.update(bcastOne ? { subtitle: '' } : { subtitle: stepOf, progress: stepProgress });
  }, [header, stepOf, stepProgress, bcastOne]);
  // Телефон — В ПРАВОМ ВЕРХУ шапки (владелец 2026-08-06, второй заход: из планки убрать).
  // НА ШАГЕ «ЧТО ЗАПУСКАЕТ» ЕГО НЕТ ВОВСЕ (К19.1 п.9): вместо руки уезжает null, и хозяин
  // шапки убирает кнопку — две кнопки в шапке лишние.
  useEffect(() => {
    header?.setPreview(stepNoPreview ? null : openPreview);
  });
  // «ИЗМ./ГОТОВО» ШАГА-СПИСКА — В ШАПКЕ ЛИСТА (ТЗ WEB-PARITY-3 М33/М41, кадры iOS W23/W33):
  // пилюля справа, где на других шагах телефон-превью. Уходим с шага — слот отдаём хозяину
  // шапки: его `setPreview` ставит телефон обратно только по смене состояния, поэтому
  // дёргаем его «нет → есть» нарочно. Только телефон в браузере (на широком — кнопка над
  // списком, как была).
  const listPill = wizPhoneWeb && !!itemCfg && itemCfg.max > 1;
  const listEditOn = listEdit && itemLines.length > 0;
  const listEmpty = itemLines.length === 0;
  useEffect(() => {
    if (!header || !listPill) return;
    header.update({
      headerRight: (
        // Пустой список — пилюля на месте, но бледная и не нажимается (кадр iOS W33-tg-step4:
        // WizardScreen гасит «Изм.» до 0,45, пока строк нет).
        // Вид — общая пилюля набора KPill (пакет Д2, Закон 5 п.8): свой вид мастера (15/600, поля 12)
        // снят по слову владельца «одна пилюля везде».
        <KPill disabled={listEmpty} onClick={() => setListEdit((v) => !v)}>
          {listEditOn ? tc('done') : ta('wizListEdit')}
        </KPill>
      ),
    });
    return () => {
      header.update({ headerRight: null });
      if (!stepNoPreview) {
        header.setPreview(null);
        header.setPreview(openPreview);
      }
    };
    // openPreview — новая рука каждый рендер; слот перерисовывается по смыслу (режим, пустота).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header, listPill, listEditOn, listEmpty, stepNoPreview, ta, tc]);

  // key=анкета+шаг+кусок: между шагами и при смене сети форма перемонтируется
  // (другой набор params). Рисует куски ОБЩИЙ WizSegs — тот же, которым живёт страница
  // элемента шага-списка (см. инвариант у WizSegs).
  const renderSegs = (segs: WizSeg[], segParams: CatalogParam[], pfx: string) => (
    <WizSegs
      segs={segs}
      params={segParams}
      keyPfx={`${activeDef.id}:${step.key}:${pfx}`}
      values={formValues}
      {...(warnings ? { warnings } : {})}
      dict={dict}
      {...(activeDef.accountChannel ? { channel: activeDef.accountChannel } : {})}
      botHandle={accCred?.handle?.replace(/^@/, '') ?? null}
      {...(activeDef.id === CHAIN_WIZARD_ID ? { chain: true as const } : {})}
      wiz={activeDef.id}
      stepKey={step.key}
      tgBot={{ handle: giftTgHandle, addLabel: ta('wizAddTgBot'), onAdd: connFlow.openWebAlt }}
      {...(activeDef.giftChannel ? { giftChannel: activeDef.giftChannel } : {})}
      onChange={applyAnswers}
      onSet={(name, value) => setAnswers({ ...formValues, [name]: value })}
    />
  );

  /**
   * СТРАНИЦА ОДНОГО ПИСЬМА (шаг-список объектов, ТЗ В2): вертикальная шторка поверх
   * анкеты, внутри — три строки, каждая со своей страницей. Перестановка и удаление
   * закрывают страницу: в списке письмо уже стоит на новом месте.
   */
  const openItemPage = (
    i: number,
    values: Record<string, unknown>,
    /** Элемент добавлен ЭТИМ открытием (П132 п.3): нетронутый при уходе убирается. */
    fresh?: boolean,
  ) => {
    const cfg = step.itemList;
    if (!cfg) return;
    const title = wizItemTitle(cfg, values, i, itemLive);
    // СТРАНИЦА ВХОДА — ФОРМА, А НЕ СПИСОК ПЕРЕХОДОВ (К3): «Как прошёл» из трёх или метки
    // галочками прямо здесь, внизу «Удалить вход». Значения этих полей живут ВНУТРИ
    // ответа-списка, поэтому их подкладывает `fieldValues`, а правку складывает обратно
    // `applyFields` — плоских ответов у входа нет.
    if (cfg.fields) {
      // ПРИЗНАК «ПЫТАЛИСЬ ОТПРАВИТЬ» — СВОЙ У КАЖДОГО ОТКРЫТИЯ (К24 п.1, п.2): пока
      // человек не нажал «Готово», страница чистая; закрыл и открыл заново — снова.
      const tried = wizTriedSignal();
      // ЖИВЫЕ ОТВЕТЫ ЭТОЙ СТРАНИЦЫ: тело шторки собирается ОДИН раз, а «Готово» живёт в
      // её шапке — проверять оно обязано последнюю правку, а не снимок открытия (у
      // черновика ту же роль играет `draft.next`).
      const live = { vals: values };
      // ТА ЖЕ РАЗВИЛКА, ЧТО У РОДНОЙ СТРАНИЦЫ (К22 механика, К24 п.2): ошибка —
      // «Готово» не закрывает страницу, а взводит признак, и под полем встаёт
      // подсказка; значения берём из ЖИВЫХ ответов, как в `onItemBack`.
      const doneEntry = () => {
        if (pageWarnAt(cfg, live.vals, i)(cfg.fieldValues?.(live.vals, i) ?? {})) {
          tried.fire();
          return;
        }
        panel.closeFrom(WIZ_FLOW);
      };
      panel.open({
        title,
        sheet: true,
        winKey: WIZ_ITEM,
        // ТЕЛЕФОН В БРАУЗЕРЕ: «Готово» — широкой кнопкой внизу страницы (М35), галочки в
        // шапке нет; широкий экран — галочка в шапке, как была.
        ...(wizPhoneWeb ? {} : { actionLabel: tc('done'), onAction: doneEntry }),
        body: (
          <WizEntryItem
            {...(wizPhoneWeb ? { foot: { label: tc('done'), onClick: doneEntry } } : {})}
            {...(cfg.pageNoFocus?.(values, i) ? { noFocus: true } : {})}
            params={cfg.fields(values, i, itemLive)}
            initial={{ ...values, ...(cfg.fieldValues?.(values, i) ?? {}) }}
            dict={dict}
            {...(activeDef.accountChannel ? { channel: activeDef.accountChannel } : {})}
            botHandle={accCred?.handle?.replace(/^@/, '') ?? null}
            {...(activeDef.id === CHAIN_WIZARD_ID ? { chain: true as const } : {})}
            warnFor={pageWarnAt(cfg, values, i)}
            tried={tried}
            onPatch={(patch) => {
              const nextVals = cfg.applyFields
                ? cfg.applyFields(liveValues.current, i, patch)
                : { ...liveValues.current, ...patch };
              live.vals = nextVals;
              liveApply.current(nextVals);
            }}
          />
        ),
      });
      return;
    }
    // СТРАНИЦА СООБЩЕНИЯ В БРАУЗЕРЕ — МОДЕЛЬ А (П132 п.2, владелец 25.09.2026; та же, что
    // у родной страницы с П130): правка «Названия», «Когда», «Условий» и окна «Сообщение»
    // ложится в ответы сразу, «Готово» только закрывает. Прежний черновик письма (ТЗ
    // 21.09.2026 раздел 6: правки копились до «Готово», «×» их выбрасывал) снят.
    //
    // `page.fresh` — элемент добавлен ЭТИМ открытием (П132 п.3, «правило Заметок»): при
    // уходе со страницы любым путём нетронутый убирается молча. Путь один —
    // `onLeave` (страница снята: «Готово», «×», «‹», клик мимо); удаление и перестановка
    // признак снимают сами.
    const page = { fresh: !!fresh };
    const tried = wizTriedSignal();
    const freshUntouched = (): boolean => page.fresh && !!cfg.untouched?.(liveValues.current, i);
    const msgPage: DrawerPayload = {
      title,
      sheet: true,
      winKey: WIZ_ITEM,
      // Телефон в браузере — «Готово» широкой кнопкой внизу (М35), галочки в шапке нет;
      // шапки у страницы сообщения нет вовсе — крупный заголовок в теле (М107, кадр W56).
      ...(wizPhoneWeb ? { headless: true } : { actionLabel: tc('done') }),
      onAction: () => {
        // «ГОТОВО» НЕ ВЫПУСКАЕТ НЕГОДНОЕ СООБЩЕНИЕ (П132 п.2, К24 п.1–2): страница
        // остаётся, строка «Сообщение» подсвечивается. Нетронутое новое — не «негодное»,
        // а пустая заметка: закрываем, и `onLeave` его уберёт.
        const rows = cfg.rows?.(liveValues.current, i) ?? [];
        const bad = rows.some((r) => r.invalid);
        if (bad && !freshUntouched()) {
          // ЧИНИТСЯ ОТВЕТОМ (ТЗ П163 п.4): лист вопроса поверх страницы — «Убрать медиа»
          // закрывает страницу, «Отмена» возвращает на неё с подсветкой строки.
          const clash = itemMsgConflict(rows);
          if (clash) {
            const q = conflictAsk(clash.c, conflictWords);
            journalLog('msg.conflictAsk', { kind: clash.c.kind, at: 'item' });
            panel.open({
              title: q.title,
              sheet: true,
              winKey: 'message-conflict',
              body: (
                <MediaAskSheet
                  text={q.text}
                  cancel={q.cancel ?? tc('cancel')}
                  onCancel={() => {
                    panel.back();
                    tried.fire();
                  }}
                  actions={q.actions.map((a) => ({
                    key: a.key,
                    label: a.label,
                    onPick: () => {
                      fixItemMsg(clash, a.key);
                      // Лист вопроса — и страница сообщения под ним (П163 п.4: ответ
                      // закрывает страницу). В вебе это уровни потока — снимаются все разом.
                      panel.closeFrom(WIZ_FLOW);
                      if (edit) void saveEdit(true, 'itemDone');
                    },
                  }))}
                />
              ),
            });
            return;
          }
          tried.fire();
          return;
        }
        panel.closeFrom(WIZ_FLOW);
        // «ГОТОВО» СООБЩЕНИЯ В БРАУЗЕРЕ — ЗАПИСЬ (ТЗ П188), как в приложении.
        if (edit) void saveEdit(true, 'itemDone');
      },
      body: (
        <WizItemPage
          list={cfg}
          index={i}
          pageTitle={title}
          initial={values}
          dict={dict}
          {...(activeDef.accountChannel ? { channel: activeDef.accountChannel } : {})}
          botHandle={accCred?.handle?.replace(/^@/, '') ?? null}
          {...(edit?.draftId ? { editDraftId: edit.draftId } : {})}
          {...(activeDef.id === CHAIN_WIZARD_ID || activeDef.quickStart === 'sales' ? { chainButtons: true } : {})}
          moveUpLabel={ta('itemMoveUp')}
          moveDownLabel={ta('itemMoveDown')}
          tried={tried}
          onPatch={(patch) => {
            liveApply.current({ ...liveValues.current, ...patch });
          }}
          // ВОПРОС ПЕРЕД «УДАЛИТЬ» (П132 п.5); нетронутое новое сообщение — без вопроса.
          removeAsk={() =>
            freshUntouched() ? null : (cfg.removeAsk?.(liveValues.current, i) ?? null)
          }
          onRemove={() => {
            page.fresh = false;
            liveApply.current(cfg.remove(liveValues.current, i));
            panel.closeFrom(WIZ_FLOW);
            if (edit) void saveEdit(true, 'itemRemove');
          }}
          onMove={(dir) => {
            page.fresh = false;
            liveApply.current(cfg.move?.(liveValues.current, i, i + dir) ?? liveValues.current);
            panel.closeFrom(WIZ_FLOW);
            if (edit) void saveEdit(true, 'itemMove');
          }}
          onLeave={() => {
            if (!freshUntouched()) return;
            page.fresh = false;
            liveApply.current(cfg.remove(liveValues.current, i));
          }}
          {...(wizPhoneWeb
            ? {
                foot: { label: tc('done'), onClick: () => msgPage.onAction?.() },
                phoneWebTitle: true,
              }
            : {})}
        />
      ),
    };
    panel.open(msgPage);
  };

  /** Добавить элемент выбранного вида и ТУТ ЖЕ открыть его страницу (уровнем поверх меню). */
  const addItemOfKind = (kind: string, value?: string) => {
    const cfg = step.itemList;
    if (!cfg) return;
    // ССЫЛКА ИЗ ДРУГОГО БОТА (п.29) — сразу «Переход по ссылке» с её меткой, без страницы.
    if (kind.startsWith(WIZ_ADD_BOTLINK_PFX)) {
      const next = wizAddBotLinkEntry(cfg, liveValues.current, kind.slice(WIZ_ADD_BOTLINK_PFX.length));
      if (next) liveApply.current(next);
      panel.closeFrom(WIZ_FLOW);
      return;
    }
    const before = cfg.count(liveValues.current);
    const opt = itemAddKinds(cfg, liveValues.current).find((k) => k.key === kind);
    if (opt?.disabled) return;
    const direct = !!opt?.direct;
    const next = cfg.add(liveValues.current, kind, value);
    if (cfg.count(next) === before) {
      panel.closeFrom(WIZ_FLOW);
      return;
    }
    if (direct) {
      liveApply.current(next);
      panel.closeFrom(WIZ_FLOW);
      return;
    }
    // ЧЕРНОВИК (владелец 06.09.2026, П10 п.6): страница настроек нового триггера, а в
    // ответы он попадает только по «Готово»; закрыли крестиком — ничего не добавилось.
    const i = cfg.count(next);
    const draft = { next };
    const tried = wizTriedSignal();
    if (!cfg.fields) {
      liveApply.current(next);
      openItemPage(i, next, true);
      return;
    }
    // «ГОТОВО» НЕ ПУСКАЕТ ПРИ ОДИНАКОВОМ ХВОСТЕ (К19.1 п.6, владелец 06.09.2026):
    // черновик не применяется и страница не закрывается — под полем стоит подсказка,
    // человек правит подпись. Пустой черновик закрывается как закрывался.
    const doneDraft = () => {
      // ПОДСКАЗКА ПОЯВЛЯЕТСЯ ТОЛЬКО ЗДЕСЬ (К24 п.1–2): до первой попытки «Готово»
      // страница чистая, после — подсказки живут до её закрытия.
      if (pageWarnAt(cfg, draft.next, i)(cfg.fieldValues?.(draft.next, i) ?? {})) {
        tried.fire();
        return;
      }
      liveApply.current(draft.next);
      panel.closeFrom(WIZ_FLOW);
    };
    panel.open({
      title: wizItemTitle(cfg, next, i, itemLive),
      sheet: true,
      winKey: WIZ_ITEM,
      // ТЕЛЕФОН В БРАУЗЕРЕ — СТРАНИЦА ТРИГГЕРА ТЕМ ЖЕ ПОЛУЛИСТОМ, что меню и список ботов
      // (кадры iOS W53-chain-trigger-passed-result, W53-chain-trigger-tag: кромка на 421, а не
      // лист во весь экран). После выбора бота слева «×» (лист-стопка, кадр -passed-result),
      // вид без выбора («Получил метку») — «‹» (кадр -tag). «Готово» — широкой кнопкой внизу
      // (М35), галочки в шапке нет.
      // ВЫСОТА — ТЕМ ЖЕ ПРАВИЛОМ, ЧТО У РОДНОЙ ШТОРКИ ПОТОКА (`pageFull` анкеты / `full` вида,
      // см. черновик для натива выше): события Инстаграма с настройками («Сообщение в директ»,
      // комментарии, «Ответ на историю») и «Переход по ссылке» растят лист до полного (кадры
      // iOS W23-iggift-trigger-dm / -comment / -story-reply: кромка листа под статус-баром),
      // остальные — полулистом 0,52.
      ...(wizPhoneWeb
        ? {
                        ...((cfg.pageFull ? cfg.pageFull(next, i) : !!opt?.full)
              ? {}
              : { sheetDetent: WIZ_HALF_SHEET }),
          }
        : { actionLabel: tc('done'), onAction: doneDraft }),
      body: (
        <WizEntryItem
          {...(wizPhoneWeb ? { foot: { label: tc('done'), onClick: doneDraft } } : {})}
          {...(cfg.pageNoFocus?.(next, i) ? { noFocus: true } : {})}
          params={cfg.fields(next, i, itemLive)}
          initial={{ ...next, ...(cfg.fieldValues?.(next, i) ?? {}) }}
          dict={dict}
          {...(activeDef.accountChannel ? { channel: activeDef.accountChannel } : {})}
          botHandle={accCred?.handle?.replace(/^@/, '') ?? null}
          {...(activeDef.id === CHAIN_WIZARD_ID ? { chain: true as const } : {})}
          warnFor={pageWarnAt(cfg, next, i)}
          tried={tried}
          onPatch={(patch) => {
            draft.next = cfg.applyFields
              ? cfg.applyFields(draft.next, i, patch)
              : { ...draft.next, ...patch };
          }}
        />
      ),
    });
  };

  /**
   * Список ботов страницей: тап по боту ДОБАВЛЯЕТ триггер и открывает его страницу.
   * ПУСТОЙ СТРАНИЦЫ ЗДЕСЬ НЕ БЫВАЕТ (К18.3 этап 1 п.1, отменяет прежнюю «Ботов пока
   * нет»): свободных ботов нет — вид гаснет прямо в меню и сюда не ведёт.
   */
  const openItemPick = (kindKey: string, kind: WizardItemAddKind) => {
    const cfg = step.itemList;
    if (!cfg) return;
    const bots = itemPickBots(cfg, liveValues.current);
    if (bots.length === 0) return;
    // СПИСОК БОТОВ — ПОЛУЛИСТ-СТОПКА С «‹» И ШИРОКОЙ «ГОТОВО» ВНИЗУ (ТЗ WEB-PARITY-3 М100,
    // кадр iOS W53-chain-trigger-passed-pick); на широком экране — лист как был.
    panel.open({
      title: kind.title,
      sheet: true,
      winKey: WIZ_PICK,
      // Верх полулиста — 0,52 экрана (кадры iOS W53-chain-*: кромка листа на 421 из 874).
      ...(wizPhoneWeb ? { sheetDetent: WIZ_HALF_SHEET } : {}),
      body: wizPhoneWeb ? (
        <FPane className="wiz-pick">
          <KListGroup>
            {bots.map((b) => (
              <KListRow
                key={b.value}
                title={b.label}
                chevron
                onClick={() => addItemOfKind(kindKey, b.value)}
              />
            ))}
          </KListGroup>
          <WizPageFootBar foot={{ label: tc('done'), onClick: () => panel.back() }} />
        </FPane>
      ) : (
        <FPane>
          <FNotifyList
            items={bots.map((b) => ({
              key: b.value,
              label: b.label,
              onOpen: () => addItemOfKind(kindKey, b.value),
            }))}
          />
        </FPane>
      ),
    });
  };

  /**
   * «ДОБАВИТЬ ТРИГГЕР» У ШАГА-СПИСКА ОБЪЕКТОВ (К3): меню видов (имя + описание) — ровно
   * то же, что у триггеров обычного бота. Вид с выбором («Прошёл бота») ведёт на список
   * ботов, остальные добавляются сразу и открывают свою страницу.
   *
   * НЕДОСТУПНЫЙ ВИД ОСТАЁТСЯ НА СВОЁМ МЕСТЕ (К18.1 п.3): серый, без шеврона и без руки, а
   * причина стоит второй строкой вместо описания. Исчезнувший пункт человек ищет и не
   * находит — это и была претензия владельца.
   */
  /** Лист «Добавить сообщение» по группам (D17b): строки — название и подстрока, уже добавленные — серым «в серии». */
  const openItemPickSheet = () => {
    const cfg = step.itemList;
    if (!cfg?.addPick) return;
    const pick = cfg.addPick(liveValues.current);
    panel.open({
      title: pick.title,
      sheet: true,
      deck: true,
      lead: 'close',
      winKey: WIZ_KINDS,
      body: (
        <FPane>
          {pick.groups.map((g) => (
            <KListSection key={g.title} label={g.title}>
              <KListGroup className="wiz-kinds">
                {g.items.map((it) => (
                  <KListRow
                    key={it.key}
                    className="wiz-kind"
                    title={it.title}
                    hint={it.disabled ? `${it.sub} · ${it.disabledNote ?? ''}` : it.sub}
                    {...(it.disabled
                      ? { disabled: true }
                      : {
                          onClick: () => {
                            liveApply.current(cfg.add(liveValues.current, it.key));
                            panel.close();
                          },
                        })}
                  />
                ))}
              </KListGroup>
            </KListSection>
          ))}
        </FPane>
      ),
    });
  };

  const openItemAdd = () => {
    const cfg = step.itemList;
    if (!cfg?.addKinds) return;
    const kinds = itemAddKinds(cfg, liveValues.current);
    // МЕНЮ ВИДОВ — ЛИСТ-СТОПКА С ПОЛНЫМИ ОПИСАНИЯМИ (ТЗ WEB-PARITY-3 М43/М53/М79, кадр iOS
    // W33-tg-trigger-menu): строки «название + описание» без шеврона; занятый вид серый, с
    // причиной второй строкой и серой галочкой справа. Строки — из набора (KListRow), своей
    // разметки нет.
    panel.open({
      title: cfg.itemAdd,
      sheet: true,
      deck: true,
            // Полулист (кадры W33-tg-trigger-menu, W53-chain-trigger-menu: кромка на 421 из 874 —
      // 52 % экрана; было 0,56 = 385); длинное меню прокручивается.
      sheetDetent: WIZ_HALF_SHEET,
      // «×» — полулист, представленный поверх (кадр iOS W23-iggift-trigger-menu, W33-tg-trigger-menu; ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2).
      lead: 'close',
      winKey: WIZ_KINDS,
      body: (
        <FPane>
          {/* «ССЫЛКИ ИЗ ВАШИХ БОТОВ» — СВОИМ ПУЗЫРЁМ НАД ВИДАМИ (п.29): тап — сразу «Переход по ссылке» с меткой. */}
          {(() => {
            const sec = kinds.filter((k) => k.section);
            return sec.length > 0 ? (
              <KListSection label={sec[0]!.section!} hint={sec[0]!.sectionHint ?? ''}>
                <KListGroup className="wiz-kinds">
                  {sec.map((k) => (
                    <KListRow key={k.key} className="wiz-kind" title={k.title} hint={k.description ?? ''} onClick={() => addItemOfKind(k.key)} />
                  ))}
                </KListGroup>
              </KListSection>
            ) : null;
          })()}
          <KListGroup className="wiz-kinds">
            {kinds.filter((k) => !k.section).map((k) => (
              <KListRow
                key={k.key}
                className="wiz-kind"
                title={k.title}
                hint={k.disabled ? (k.disabledHint ?? '') : (k.description ?? '')}
                {...(k.disabled
                  ? {
                      disabled: true,
                      value: (
                        <span className="wiz-kind-taken">
                          <KListCheck />
                        </span>
                      ),
                    }
                  : {
                      onClick: () => {
                        if (k.pick === 'bots') openItemPick(k.key, k);
                        else addItemOfKind(k.key);
                      },
                    })}
              />
            ))}
          </KListGroup>
        </FPane>
      ),
    });
  };

  // РОДНАЯ ФОРМА РАССЫЛКИ (волна 2): рисует её натив, а этот кусок — её мозг (список
  // ботов отбора, живой счётчик «получат N», родные окна дня и часа). Своей разметки у
  // него нет вовсе — он ничего не выводит. Собирается ДО ранней ветки «анкету ведёт
  // натив» ниже: у рассылки родной мастер тоже «готов» (nativeOn), и прежний ранний
  // return выбрасывал форму из дерева — тап «Создать рассылку» в приложении молчал
  // (владелец 03.09.2026, разбор по журналу клиента: wiz.host on:1, а bcastOpen нет).
  const bcastForm = bcastOn ? (
    <BcastNativeForm
      steps={bcastSteps}
      values={formValues}
      accOptions={accOptions}
      warnings={bcastWarn}
      busy={busy || photoBusy}
      error={formErr ?? ''}
      onAnswers={(next) => {
        setBcastWarn({});
        applyAnswers(next);
      }}
      onSubmit={bcastSubmit}
      onCancel={onDone}
      onPhoto={(name, data) => void uploadNativePhoto(name, data)}
      onAudience={setAudAt}
      onMessage={(param, runAt) => {
        const st = steps.find((x) => (x.messages ?? []).some((c) => c.param === param));
        const card = (st?.messages ?? []).find((c) => c.param === param);
        setMsgAt({
          param,
          title: card?.title ?? st?.title ?? '',
          // ОТКУДА ПРИШЛИ — сама рассылка, а не имя шага (И11.8): «Сообщение /
          // Сообщение» человек читал как поломку.
          subtitle: activeDef.lead(loc).title,
          // Назначен час отправки — он и стоит в углу пузыря (И11.7).
          ...(runAt ? { clockAt: runAt } : {}),
        });
      }}
    />
  ) : null;

  // СПИСОК ОБЪЕКТОВ ШАГА одним куском: место у него РАЗНОЕ — обычно под полями шага,
  // а у шага «Ответы в комментариях» (`first`, этап J п.1) НАД ними. Порядок один на
  // браузер и приложение (wizard-native собирает поля в том же порядке).
  const itemBlock = itemCfg ? (
    /* ШАГ-СПИСОК ОБЪЕКТОВ (ТЗ В2): письма цепочки — строками с «Добавить письмо».
           Одно письмо настраивается своей страницей из трёх строк (стопка окон, Д2), а
           страниц-повторов у анкеты больше нет: счётчик шагов от числа писем не зависит. */
    <>
      {/* СПИСОК ИЗ ОДНОГО ЭЛЕМЕНТА — НЕ СПИСОК (Л5.2.2 п.1): у строки «На какие
              комментарии» потолок `max: 1`, добавлять и удалять нечего — «Править» такому
              списку не нужна (пунктирная «Добавить» гаснет ниже тем же потолком). */}
      {/* На телефоне в браузере «Изм./Готово» стоит В ШАПКЕ листа (М33/М41) — см.
              эффект у `header.update({ headerRight })`; над списком кнопки нет. */}
      {itemCfg.max > 1 && !wizPhoneWeb && (
        <WizListEdit
          on={listEdit && itemLines.length > 0}
          onToggle={() => setListEdit((v) => !v)}
          editLabel={ta('wizListEdit')}
          doneLabel={tc('done')}
          disabled={itemLines.length === 0}
        />
      )}
      {itemLines.length === 0 ? (
        <FHint className="wiz-item-empty">{itemCfg.itemEmpty}</FHint>
      ) : (
        <FNotifyList
          editing={listEdit}
          removeLabel={ta('cardDelete')}
          items={itemLines.map((it) => ({
            key: `item-${it.i}`,
            label: it.title,
            // Мёртвый вход (бот удалён, П203) — подстрока ролью красного, как «Сообщение» не готово.
            summary: it.letter ? (
              // СТРОКА ПИСЬМА ЦЕПОЧКИ (QUICK-START §11.34 п.12а): текст сообщения, ниже серым — значок вида и «текст ·
              // кнопка «…»». Слов паузы здесь нет — пауза пилюлей справа (`pause`).
              <span className="qs-rich">
                {it.letter.preview ? <span className="qs-rich-prev">{it.letter.preview}</span> : null}
                <span className={it.invalid ? 'qs-rich-media wiz-item-bad' : 'qs-rich-media'}>{it.letter.meta}</span>
              </span>
            ) : it.invalid ? (
              <span className="wiz-item-bad">{it.sub}</span>
            ) : it.preview ? (
              // БОГАТАЯ СТРОКА СЕРИИ (быстрый запуск D17, §2.15 п.6): пауза, «медиа · кнопка», начало текста.
              <span className="qs-rich">
                <span>{it.sub}</span>
                {it.sub2 ? <span className="qs-rich-media">{it.sub2}</span> : null}
                <span className="qs-rich-prev">{it.preview}</span>
              </span>
            ) : (
              it.sub
            ),
            // Вход без настроек — строка без шеврона и без страницы (П5).
            chevron: it.chevron !== false,
            ...(it.chevron === false
              ? {}
              : {
                  onOpen: () =>
                    // QUICK-START §11.16 п.2: «Цепочка сообщений» вне режима «Изм.» — тап сразу в окно «Сообщение»
                    // (без страницы «название / когда / условия»); в режиме правки — страница письма (пауза).
                    // §11.34 п.12в: строка письма цепочки в «Изм.» не нажимается — паузу открывает пилюля.
                    it.letter && listEdit ? undefined : openItemPage(it.i, formValues),
                }),
            // §11.34 п.12б–в: строка письма цепочки — пилюля паузы справа всегда; в «Изм.» — тап открывает «Когда» письма.
            ...(it.letter
              ? {
                  pause: {
                    code: it.letter.code,
                    icon: <Clock size={13} strokeWidth={2} aria-hidden />,
                    onClick: () => setLetterWaitAt(it.i),
                  },
                }
              : {}),
            // Последний элемент не убирается — это правило живёт в самой анкете
            // (`remove` вернёт прежние ответы), второго его списка тут не заводим.
            onRemove: () => {
              applyAnswers(itemCfg.remove(formValues, it.i));
              if (edit) void saveEdit(true, 'itemRemove');
            },
          }))}
        />
      )}
      {letterWaitAt !== null
        ? (() => {
            // Ряд «Когда» письма (`chainLetterRows` → `waitFields`: число и единица «минутах/часах/днях») — тем же колесом,
            // что на странице письма (KWheelDialog, кадр iOS W56-chain-msg-when).
            const row = itemCfg.rows?.(formValues, letterWaitAt).find((r) => r.key === 'wait');
            const amount = row?.fields?.[0];
            const unit = row?.fields?.[1];
            if (!row || !amount || !unit) return null;
            const max = Number((amount.typeOptions as { maxValue?: unknown } | undefined)?.maxValue ?? 60);
            return (
              <KWheelDialog
                open
                sheet={pausePhone}
                closeLabel={tc('close')}
                title={row.title}
                doneLabel={tc('done')}
                columns={[
                  {
                    key: 'n',
                    value: String(formValues[amount.name] ?? amount.default ?? 0),
                    items: Array.from({ length: Math.min(max, 365) + 1 }, (_, n) => ({ value: String(n), label: String(n) })),
                  },
                  {
                    key: 'u',
                    value: String(formValues[unit.name] ?? unit.default ?? ''),
                    items: ((unit.options as CatalogParamOption[] | undefined) ?? []).map((o) => ({ value: String(o.value), label: o.name })),
                  },
                ]}
                onDone={([n, u]) => {
                  const patch: Record<string, unknown> = {};
                  if (n !== undefined) patch[amount.name] = Number(n);
                  if (u !== undefined) patch[unit.name] = u;
                  setLetterWaitAt(null);
                  applyAnswers({ ...formValues, ...patch });
                  if (edit) void saveEdit(true, 'pause');
                }}
                onClose={() => setLetterWaitAt(null)}
              />
            );
          })()
        : null}
      {(!listEdit || itemLines.length === 0) && itemLines.length < itemCfg.max && (
        <FAddBubble
          label={itemCfg.itemAdd}
          // ВСЕ ВИДЫ ЗАНЯТЫ — ПУЗЫРЬ НЕАКТИВЕН (К20.1 п.15, владелец 07.09.2026):
          // то же правило, что нативу уезжает ключом `addDisabled`, — одно условие
          // на браузер и приложение. Видов у списка нет вовсе (письма) — пузырь
          // живой, как был.
          disabled={
            itemCfg.addKinds
              ? (() => {
                  const kk = itemAddKinds(itemCfg, formValues);
                  return kk.length > 0 && kk.every((k) => k.disabled);
                })()
              : false
          }
          onClick={() => {
            // ЛИСТ «ДОБАВИТЬ СООБЩЕНИЕ» ПО ГРУППАМ (быстрый запуск D17b, §2.15 п.8): тап — в конец списка, лист
            // закрывается.
            if (itemCfg.addPick) {
              openItemPickSheet();
              return;
            }
            // ЕСТЬ ВИДЫ — «Добавить» открывает МЕНЮ (входы цепочки, К3); нет —
            // добавляется один и тот же элемент (письма).
            if (itemCfg.addKinds) {
              openItemAdd();
              return;
            }
            const next = itemCfg.add(formValues);
            const at = itemCfg.count(next);
            if (at === itemCfg.count(formValues)) return;
            // НОВОЕ СООБЩЕНИЕ ВСТАЁТ В СПИСОК СРАЗУ (П130; П132 п.3 — как в
            // приложении): нетронутое при уходе со страницы убирается молча.
            applyAnswers(next);
            openItemPage(at, next, true);
          }}
        />
      )}
    </>
  ) : null;

  // Анкету ведёт натив — своей формы веб не рисует (кроме листа «такой бот уже есть»,
  // он живёт слоем поверх, и родной формы рассылки — её мозг обязан жить в дереве).
  // Старая сборка без методов wiz* сюда не попадает.
  if (nativeOn)
    return (
      <>
        {dupSheet}
        {saveDup.sheet}
        {bcastForm}
        {nativeMsgHost}
        {nativeAudHost}
      </>
    );

  /** ОТКРЫТЬ ОКНО «СООБЩЕНИЕ» СТРОКИ-КАРТОЧКИ ШАГА — одна рука на страницу шага и на
   *  один экран рассылки (bcastOne): правила приколотых кнопок и часа отправки общие. */
  const openMsgCard = (cfg: WizardStepMessage, st: WizardStep): void => {
    const draft = wizMessageDraft(cfg, formValues);
    const pins = cfg.pinned?.(formValues) ?? [];
    openStepMessage({
      ...(draft ? { value: draft } : {}),
      title: cfg.title,
      // ОТКУДА ПРИШЛИ (И11.8): имя шага; совпало с заголовком — окно его не
      // рисует (двух «Сообщений» подряд в шапке не бывает). У одного экрана рассылки
      // (bcastOne) — сама рассылка, как у родного окна (`onMessage` выше; кадр iOS M01
      // «Сообщение / Рассылка», круг 3 П8).
      subtitle: bcastOne ? activeDef.lead(loc).title : st.title,
      // Назначен час отправки (рассылка «в назначенное время») — он стоит в
      // углу пузыря вместо «сейчас» (И11.7).
      ...(wizClockAt(formValues) ? { clockAt: wizClockAt(formValues) } : {}),
      // «ОТПРАВИТЬ СЕБЕ» (ТЗ SEND-SELF З-5): только у рассылки (Р5) — черновик (новая — `new`) и поле письма.
      ...(activeDef.id === BROADCAST_WIZARD_ID
        ? { testSend: { draftId: edit?.draftId ?? 'new', param: cfg.param, area: () => liveValues.current } }
        : {}),
      ...(pins.length > 0
        ? {
            pinned: pins.map((one) => ({ key: one.key, text: one.text })),
            // В сообщении СЦЕНАРИЯ у свободных кнопок работают только ссылка и
            // мини-апп: приставка кнопок сценария (buttonPrefix) перебивает
            // собственное действие метки и «не пишите мне», а трогать отправитель
            // и `callback_data` нельзя (замок Г3). Мёртвых кнопок не предлагаем.
            buttonKinds: ['url' as const, 'app' as const],
            onPinned: (key: string, text: string) => {
              const own = pins.find((one) => one.key === key);
              if (!own) return;
              liveApply.current({ ...liveValues.current, [own.param]: text });
            },
          }
        : {}),
      // ОДИН ЭКРАН РАССЫЛКИ НА ТЕЛЕФОНЕ — МОДЕЛЬ А, как родное окно (`nativeMsgHost`:
      // черновик ложится в ответы на КАЖДУЮ правку; П132 п.2): галочки в шапке нет (кадры
      // iOS M01/M10 — «‹ · Сообщение / Рассылка» без «✓»), «‹», свайп и «Готово» снизу
      // дают один результат — набранное остаётся в форме (круг 3 П8).
      ...(bcastOne
        ? {
            onChange: (next: TgDraft) =>
              liveApply.current({ ...liveValues.current, [cfg.param]: JSON.stringify(next) }),
          }
        : {}),
      onSave: (next) =>
        liveApply.current({
          ...liveValues.current,
          [cfg.param]: JSON.stringify(next),
        }),
    });
  };

  // ТЕЛО ОДНОГО ЭКРАНА РАССЫЛКИ (bcastOne, Р2): группы по шагам анкеты.
  const bcastOneBody = bcastOne ? (
    <>
      {windowErr ? <FFormError>{windowErr}</FFormError> : null}
      {steps
        .filter((s) => s.key !== WIZ_INTRO_KEY)
        .map((s) => {
          const cards = wizMessageCards(s, formValues);
          const whenP = s.params.find(
            (p) => (p.typeOptions as { wizControl?: unknown } | undefined)?.wizControl === 'when',
          );
          const audP = s.params.find((p) => p.name === 'audience');
          const hasAcc = s.params.some((p) => p.name === 'accountId');
          const warn = { ...(warnings ?? {}), ...bcastWarn };
          return (
            <Fragment key={`bo-${s.key}`}>
              {hasAcc || audP ? (
                <WizBcastWho
                  label={s.title}
                  botLabel={
                    s.params.find((p) => p.name === 'accountId')?.displayName ?? ta('wizAccount')
                  }
                  botValue={accOptions.find((o) => o.value === formValues.accountId)?.label ?? ''}
                  accNone={accNone}
                  credsReady={credsReady}
                  noAccHint={
                    merged
                      ? ta('wizNoAccounts')
                      : ts(
                          def.accountChannel === 'telegram'
                            ? 'chatStartNoBot'
                            : 'chatStartNoIgAccount',
                        )
                  }
                  addLabel={ta('wizAddAccount')}
                  onAdd={connFlow.openWeb}
                  onBot={() =>
                    // Имя листа — подпись строки («Бот»), как у родного выбора (кадр iOS
                    // R02-broadcast-bot-pick; было общее «Аккаунт»). Строки «Добавить» у листа
                    // рассылки нет.
                    openAccountPick(s.params.find((p) => p.name === 'accountId')?.displayName ?? ta('wizAccount'), false)
                  }
                  {...(audP
                    ? {
                        aud: {
                          name: audP.name,
                          label: audP.displayName ?? '',
                          value:
                            typeof formValues[audP.name] === 'string'
                              ? (formValues[audP.name] as string)
                              : '{}',
                          cred:
                            typeof formValues.accountId === 'string' ? formValues.accountId : '',
                          onChange: (v: string) =>
                            liveApply.current({ ...liveValues.current, [audP.name]: v }),
                        },
                      }
                    : {})}
                  bad={!!warn.accountId || !!warn[audP?.name ?? '']}
                />
              ) : null}
              {cards.length > 0 ? (
                <FNotifyList
                  label={s.title}
                  items={cards.map((cfg) => ({
                    key: `wiz-msg-${cfg.param}`,
                    label: cfg.title,
                    value: cardValue(cfg, formValues) || cfg.empty,
                    onOpen: () => openMsgCard(cfg, s),
                  }))}
                />
              ) : null}
              {cards.map((cfg) =>
                cfg.hint ? <FHint key={`wiz-msg-hint-${cfg.param}`}>{cfg.hint}</FHint> : null,
              )}
              {whenP ? (
                <WizWhen
                  caption={s.title}
                  valueRight
                  label={whenP.displayName ?? ''}
                  mode={formValues.startMode === 'date' ? 'date' : 'manual'}
                  runAt={typeof formValues.runAt === 'string' ? formValues.runAt : ''}
                  options={((whenP.options as CatalogParamOption[] | undefined) ?? []).map((o) => ({
                    value: String(o.value),
                    label: o.name,
                    ...(o.description ? { hint: o.description } : {}),
                  }))}
                  bad={!!warn.startMode || !!warn.runAt}
                  onManual={() => applyAnswers({ ...formValues, startMode: 'manual' })}
                  onDate={(runAt, tz) =>
                    applyAnswers({ ...formValues, startMode: 'date', runAt, tz })
                  }
                />
              ) : null}
            </Fragment>
          );
        })}
    </>
  ) : null;

  return (
    // INVARIANT(scriptera): корень — FPane wiz (класс wiz-pane + iosf, тот же, что у
    // настроек «Старта»): вид и отступы мастера задаёт ОДИН набор правил (cabinet.css,
    // блок «Формы строками»). Своих правил вида у мастера нет. Причина: две копии
    // правил разъехались, и форма мастера перестала совпадать с эталоном.
    // Канон: docs/WIZARD-FIX-2-PLAN.md Э1.
    //
    // ШАГ «ЗНАКОМСТВО» ПОМЕЧЕН КЛАССОМ (владелец 02.09.2026, Н12 п.1
    // docs/NATIVE-DESIGN-FIXLIST.md: «на первом шаге фон мокапа-примера должен быть
    // БЕЛЫЙ, как в родной версии»). В родном мастере цвет листа зависит от шага
    // (WizardScreen.swift, sheetColor: kind == "intro" → WizSkinColors.introBg белый,
    // остальные шаги — sheetBg); вебу об этом сообщает класс на корне — лист над ним
    // перекрашивается ролью словаря (cabinet.css, `:has(.wiz-intro)`). Своего цвета
    // мастер не пишет: белая бумага — та же роль --surf, что у страницы сцены
    // (wizard-scene.css, --wsc-paper), иначе вуаль телефона не растворится в лист.
    <FPane wiz className={bcastOne ? 'wiz-bo' : onIntro ? 'wiz-intro' : undefined}>
      {/* Правка на странице сценария (шапки-хоста нет): счётчик шагов + телефон своей
          строкой сверху; при создании их ведёт шапка панели (header). */}
      {!header && (
        <div className="wiz-head">
          <div className="meta wiz-step">{stepOf}</div>
          <div className="wiz-btns">
            {/* «⋯» хозяина (правая панель) — В СТРОКУ с телефоном (владелец 05.08). */}
            {menu}
            {/* ТЕЛЕФОНЧИКА НЕТ НА ШАГЕ «ЧТО ЗАПУСКАЕТ» (К19.1 п.9, владелец 06.09.2026:
                «перевёрнутый телефон на шаге триггера будем убирать»). Решает ШАГ
                (`noPreview` анкеты), а не страница: то же правило уезжает нативу. */}
            {stepNoPreview ? null : (
              <KBareButton
                className="dr-act wiz-sec"
                aria-label={ta('wizPreviewTitle')}
                title={ta('wizPreviewTitle')}
                onClick={openPreview}
              >
                <WizPhoneIcon />
              </KBareButton>
            )}
          </div>
        </div>
      )}
      {bcastOne ? (
        bcastOneBody
      ) : (
        <>
          {/* Подводка шага — ГЕРОЙ, а не мелкая серая сноска (владелец 2026-08-10:
          «текст какой-то мелкий и неприятный; сделай hero, как на страницах настроек»):
          предложение по центру тем же кеглем, что значения полей. Подводка есть у
          КАЖДОГО шага (владелец 2026-08-20, CABINET-UX-FIXLIST п.7). Крупного значка
          сети над ней НЕТ (владелец 02.09.2026, Н12): место первого экрана отдано
          анимированному примеру. */}
          {/* Заголовок шага — в теле, едет с содержимым (6.0 п.5; на знакомстве — имя
          шаблона, дальше — имя шага, как в iOS). */}
          {/* Серая строка над заголовком («О чём рассказать · 1 из 8», быстрый запуск §2.7). */}
          {step.above ? <p className="wiz-above">{step.above}</p> : null}
          {(step.intro || step.title) && (
            <WizHero
              title={onIntro ? step.title || title : step.title}
              text={step.intro}
              intro={onIntro}
              // §11.31 п.1: «Цепочка рассылок» работает только на Pro — наклейка у названия мастера (тап — полушторка).
              {...(onIntro && activeDef.id === CHAIN_WIZARD_ID ? { mark: <ProBadge mark tappable /> } : {})}
            />
          )}
          {/* ОШИБКА ВСЕГО ШАГА — ПЕРВОЙ СТРОКОЙ ТЕЛА, СРАЗУ ПОД ВСТУПЛЕНИЕМ
          (канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128); в приложении заголовок
          рисует натив, писать в него нельзя). Появляется только после попытки уйти
          дальше и стоит, пока причина не устранена. */}
          {windowErr ? <FFormError>{windowErr}</FFormError> : null}
          {/* «НАЗВАНИЕ» РАССЫЛКИ — ПЕРВЫМ ПОЛЕМ ПЕРВОГО ШАГА ФОРМЫ (APP-CLAIMS-1020 ред. 8, Р17; у приложения — первая
          секция формы `broadcast/<id>`): подпись над пузырём, «Видно только вам» под ним, подсказка пустого поля —
          «Сообщение N», которое поставит сервер. */}
          {isBcastWiz && !onIntro && step.key === bcastFirstStep ? (
            <FField cap className="wiz-bcast-name" label={tBc('nameLabel')} hint={tBc('nameHint')}>
              <KInput
                value={bcastName}
                maxLength={120}
                aria-label={tBc('nameLabel')}
                {...(bcastNamePh ? { placeholder: bcastNamePh } : {})}
                onChange={(e) => {
                  bcastNameRef.current = e.target.value;
                  setBcastName(e.target.value);
                }}
              />
            </FField>
          ) : null}
          {/* ЗНАКОМСТВО — ПЕРВЫЙ ЭКРАН АНКЕТЫ (ТЗ-2 З1, владелец 2026-08-30: «хочу шаг перед
          первым шагом… там текст описания, который сейчас под заголовком аккаунта»).
          Полей тут нет: при создании человек читает, что делает бот, и видит готовую
          переписку; при правке — сводку «как будет работать» с тем, что уже заполнено.
          Разметка — набор form-kit, свои пузыри и подписи запрещены. */}
          {onIntro && (
            <>
              {/* СЦЕНА С ПРИМЕРОВ РАБОТ (ТЗ-3 Т1): «дальше на оставшееся место — мой чат,
              который находится ещё до регистрации в примерах работ». Тот же компонент,
              что на продающей странице и в мини-туре; своих кнопок «Дальше»/«Заново» он
              тут не показывает (проп still), а высоту ему считает wiz-stage. */}
              {showScene && demoScene && (
                <>
                  {/* Подпись «Пример переписки» снята (владелец 29.09.2026): место отдано
                  телефону. */}
                  <div
                    className="wiz-stage"
                    ref={stageRef}
                    style={{ height: stageH ? `${stageH}px` : undefined }}
                  >
                    <Scene def={demoScene} on active still replay />
                    {/* Пометка «Доступно только в Pro» в углу телефона — как на сцене приложения (12.10.2026). */}
                    {proLock && PRO_ONLY_WIZARDS.includes(activeDef.id) ? (
                      <ScenePhoneBadge className="wiz-stage-pro" label={tBill('proSceneBadge')} />
                    ) : null}
                  </div>
                </>
              )}
            </>
          )}
          {/* Абзаца-предупреждения о пересборке при правке ЗДЕСЬ НЕТ (владелец 2026-08-06,
          IMG_1386): страница правки и страница создания обязаны быть ИДЕНТИЧНЫ, а он был
          только у правки — да ещё и лез под плавающую шапку. */}
          {/* АККАУНТА ЕЩЁ НЕТ (владелец 11.09.2026): прежней плашки с уводом в «Подключения»
          больше нет — те же слова стоят пояснением, а подключение открывается ЗДЕСЬ ЖЕ,
          стопкой поверх мастера, и возвращает на этот же шаг. */}
          {accNone && !onIntro && activeDef.id === BROADCAST_WIZARD_ID && (
            // РАССЫЛКА БЕЗ АККАУНТА — ОДИН В ОДИН С iOS (владелец 12.10.2026): подпись «Аккаунт», строка «Добавить
            // аккаунт ›», под ней факт тарифа (у Pro — без строки).
            <FBlock label={ta('wizAccount')} {...(proLock ? { hint: tBill('proAppBcastNote') } : {})}>
              <KListGroup>
                <KListRow title={ta('wizAddAccount')} chevron onClick={connFlow.openWeb} />
              </KListGroup>
            </FBlock>
          )}
          {accNone && !onIntro && activeDef.id !== BROADCAST_WIZARD_ID && (
            <>
              <FHint>
                {merged
                  ? ta('wizNoAccounts')
                  : ts(
                      def.accountChannel === 'telegram' ? 'chatStartNoBot' : 'chatStartNoIgAccount',
                    )}
              </FHint>
              <FAddBubble label={ta('wizAddAccount')} onClick={connFlow.openWeb} />
            </>
          )}
          {/* Форма — «пузырями», как страница добавления подключения (владелец 05.08,
          IMG_1280): аккаунт — первым пузырём, остальные поля шага — вторым. */}
          {accParam && !accNone && credsReady && wizPhoneWeb && (
            // СТРОКА АККАУНТА «@логин ›» И ЛИСТ ВЫБОРА (кадры iOS W12-igkw-step3-account / W22 /
            // W32, ТЗ WEB-PARITY-3 М7/М63): тап открывает лист-стопку со списком аккаунтов (галочка
            // у текущего) и строкой «Добавить аккаунт» внизу — пунктирной кнопки на шаге нет.
            // Подпись «Аккаунт» серым над карточкой — как у всех полей (кадр W32): пузырь из набора.
            // Подписи НЕТ, когда она слово в слово повторяет заголовок шага («Аккаунт» под
            // «Аккаунтом») — так решает родной мастер (WizardScreen.card, `same`; кадр
            // W12-igkw-step3-account: строка «@логин ›» сразу под подводкой).
            <>
            {/* §11.11: подзаголовок группы («Instagram» шага «Аккаунты» «Продажи») — тем же видом, что подпись блока. */}
            {typeof step.groupBefore?.accountId === 'string' ? (
              <div className="wiz-div">
                <span>{step.groupBefore.accountId}</span>
              </div>
            ) : null}
            <FBlock
              label={
                // QUICK-START §11.34 п.6: анкета выбрала подпись сама («Аккаунт Instagram», ownLabel) — она и стоит;
                // иначе общее «Аккаунт» каркаса (и ничего, если так назван сам шаг).
                accParamRaw && wizAccountOwnLabel(accParamRaw) && accParam.displayName
                  ? accParam.displayName
                  : ta('wizAccount').toLowerCase() === (step.title ?? '').toLowerCase()
                    ? null
                    : ta('wizAccount')
              }
              className="wiz-acc-row"
              bad={!!warnings?.accountId}
              key={`acc-${shake}`}
              // Под аккаунтом рассылки без Pro — факт тарифа, как в iOS-приложении (12.10.2026).
              {...(proLock && activeDef.id === BROADCAST_WIZARD_ID ? { hint: tBill('proAppBcastNote') } : {})}
              // §11.11: описание аккаунта под ЭТОТ мастер (что он делает в воронке).
              {...(activeDef.quickStart && typeof accParam?.description === 'string' && accParam.description ? { hint: accParam.description } : {})}
            >
              <KListGroup>
                <KListRow
                  title={
                    accOptions.find((o) => o.value === formValues.accountId)?.label ??
                    ta('wizAccount')
                  }
                  chevron
                  {...(warnings?.accountId
                    ? { hint: warnings.accountId, tone: 'danger' as const }
                    : {})}
                  onClick={() => openAccountPick(ta('wizAccount'), true)}
                />
              </KListGroup>
            </FBlock>
            </>
          )}
          {accParam && !accNone && credsReady && !wizPhoneWeb && (
            <>
              <FCard className="wiz-card" key={`acc-${shake}`}>
                <ParamsForm
                  params={subParam ? [accParam, subParam] : [accParam]}
                  values={formValues}
                  dict={dict}
                  keepOrder
                  toggleCaps
                  dynamicOptions={{ accountId: accOptions }}
                  fieldWarnings={warnings}
                  onChange={applyAnswers}
                />
              </FCard>
              {/* «ДОБАВИТЬ АККАУНТ» ПОД ПУЗЫРЁМ — И У ТЕХ, У КОГО АККАУНТ УЖЕ ЕСТЬ (владелец
              11.09.2026): открывает ту же стопку «Подключить», что в «Настройках». */}
              <FAddBubble label={ta('wizAddAccount')} onClick={connFlow.openWeb} />
            </>
          )}
          {/* ШАГ-СПИСОК ОБЪЕКТОВ — ОДИН МЕХАНИЗМ НА ВСЕХ (К18.1 п.9–10): триггеры ботов и
          цепочки, письма цепочки. «Много одинаковых кусков» — не лента полей, а строки с
          «Добавить»; настройка одного элемента живёт своей страницей (вертикальная шторка
          поверх анкеты; в приложении — стопка). Прежняя вторая ветка (`WizardStep.list`,
          галочки) снесена целиком — копипасты не осталось. */}
          {/* ПОЛЯ ШАГА СТОЯТ ПЕРЕД СПИСКОМ, а не вместо него (Л5.2.2 п.1): так шаг уезжает и
          нативу (wizard-native: сначала `params`, потом одно поле `itemList`), и порядок
          прихода полей и есть порядок на экране. До 09.09 у шага со списком поля на
          странице не рисовались вовсе — ни одного такого шага в анкетах не было.
          ИСКЛЮЧЕНИЕ — `first` (этап J п.1, владелец 09.09.2026): у шага «Ответы в
          комментариях» строка «Отвечать в комментариях» стоит НАД пузырём «Варианты
          ответа». Решает анкета, а не экран; в приложении порядок тот же. */}
          {itemCfg?.first ? itemBlock : null}
          {restParams.length > 0 && (
            <>
              {/* Серой строки-заголовка над пузырём НЕТ (владелец 2026-08-06: «эти подзаголовки
              пузырей — „Настройка сообщений“, „Дополнительные настройки“ — мне не нравится,
              убрать»). Название раздела человек и так видит в шапке шага. */}
              <FCard className="wiz-card" key={`main-${shake}`}>
                {renderSegs(segments.main, segments.mainParams, 'm')}
                {/* «Проверить подписку» со своими полями — последним пунктом, залит фоном. */}
                {segments.sub.length > 0 && (
                  <div className="wiz-sub">{renderSegs(segments.sub, segments.subParams, 's')}</div>
                )}
              </FCard>
            </>
          )}
          {/* «ПРИМЕР ИСПОЛЬЗОВАНИЯ» (быстрый запуск §11.34 п.2): тексты ботов из текущих ответов под полями шага. */}
          {step.usage ? <WizUsageCard usage={step.usage} values={formValues} /> : null}
          {/* «НАПИСАТЬ СВОИМИ СЛОВАМИ» / «ВЕРНУТЬСЯ К КОНСТРУКТОРУ» (быстрый запуск D8, §2.6): ручной текст хранится
          отдельно и имеет приоритет; возврат его не теряет. */}
          {(() => {
            const op = step.params.find((p) => p.type === 'offer');
            if (!op) return null;
            const manual = formValues.offerManual === '1';
            const to = (op.typeOptions ?? {}) as { own?: string; back?: string };
            return (
              <KListGroup>
                <KListRow
                  title={(manual ? to.back : to.own) ?? ''}
                  chevron
                  onClick={() =>
                    applyAnswers({ ...formValues, offerManual: manual ? '' : '1' })
                  }
                />
              </KListGroup>
            );
          })()}
          {itemCfg?.first ? null : itemBlock}
          {/* СТРОКИ-КАРТОЧКИ «СООБЩЕНИЕ» (ТЗ Б, В2, З7): вместо трёх полей «текст / кнопка /
          картинка» — строка с превью, тап открывает ОБЩЕЕ окно «Сообщение». Прежние поля
          остаются в ответах: ими наполняется письмо в первый раз (адаптер Е10) и ими же
          живёт родная форма рассылки, пока у неё нет родного окна (Г8). Кнопки, по которым
          ветвится сценарий, уезжают в окно ПРИКОЛОТЫМИ: их подписи — по-прежнему свои
          ответы анкеты, и `callback_data` от этого не меняется.
          Карточки стоят ПОД полями шага: у «Дополнительных настроек» сообщение появляется
          ровно там, где человек только что включил тумблер. */}
          {msgCards.length > 0 && (
            <FNotifyList
              items={msgCards.map((cfg) => ({
                key: `wiz-msg-${cfg.param}`,
                label: cfg.title,
                summary: cardValue(cfg, formValues) || cfg.empty,
                onOpen: () => openMsgCard(cfg, step),
              }))}
            />
          )}
          {/* СТРОКИ-ПЕРЕХОДЫ САМОГО ШАГА (владелец 04.09.2026, И10 п.10): вопрос, за которым
          стоит СВОЙ экран, а не поле. Так на шаге «С чего начинается» живёт событие
          запуска цепочки: «Запускается, когда человек…» → лист условий без списка людей.
          Открывает их ТОТ ЖЕ код, что строки страницы письма, — второго вида
          строки-перехода в анкетах нет. */}
          {(step.rows ?? []).length > 0 && (
            <FNotifyList
              items={(step.rows ?? []).map((row) => ({
                key: `wiz-row-${row.key}`,
                label: row.title,
                // Сводка «Готово» быстрого запуска: аккаунт и бот — подписью подключения по id из ответов.
                summary: QS_SUMMARY_ACCOUNT_ROWS[row.key]
                  ? (row.key === 'sum-tgbot'
                      ? tgOptions.find((o) => o.value === formValues[QS_SUMMARY_ACCOUNT_ROWS[row.key]!])?.label
                      : accOptions.find((o) => o.value === formValues[QS_SUMMARY_ACCOUNT_ROWS[row.key]!])?.label) ?? ''
                  : row.value,
                onOpen: () => openStepRow(row),
              }))}
            />
          )}
          {/* ПОДВАЛ ГРУППЫ ПОД КАРТОЧКАМИ (И10 п.3): пояснение «Напишите, что придёт
          человеку…» живёт ПОД капсулой, а не подсказкой внутри строки — так же, как в
          приложении под тем же пузырём. */}
          {msgCards.map((cfg) =>
            cfg.hint ? <FHint key={`wiz-msg-hint-${cfg.param}`}>{cfg.hint}</FHint> : null,
          )}
          {/* ПОДПИСЬ ПРО ТЕЛЕФОНЧИК (ТЗ-9 Е2, владелец 2026-08-30: «на шаге „Аккаунт“ снизу
          пусто — написать: в любой момент нажмите телефончик…»). Стоит там же, где в
          приложении, — под полями шага; только на шаге «Аккаунт» и только когда кнопка
          предпросмотра вообще есть, иначе строка обещала бы несуществующее. */}
          {/* Подпись про телефончик — footNote родного мастера (14 вторичным, через 22 от
          карточки; кадр iOS W22-iggift-step3-account): класс ради этих чисел на телефоне. */}
          {step.key === WIZ_PHONE_HINT_STEP && hasWizPreview && (
            <FHint className="wiz-phone-hint">{ta('wizPhoneHint')}</FHint>
          )}
          {/* СТРАНИЦЫ-ПОВТОРЫ: у цепочки рассылок каждое письмо — своя страница, и
          распоряжаться письмами можно только с неё. «Добавить письмо» заводит следующую
          страницу и сразу на неё уходит; «Удалить письмо» убирает текущую и возвращает
          на предыдущую. Последнее письмо не удаляется: цепочка без письма не цепочка.
          Кнопки — из набора (KButton), своей разметки тут нет. */}
          {repeatIdx > 0 && (
            <div className="wiz-reps-acts">
              {repeatCount < (activeDef.repeatPages?.max ?? 0) && (
                <KButton
                  size="sm"
                  onClick={() => {
                    setAnswers(activeDef.repeatPages!.add(formValues));
                    setIdx(idx + 1);
                  }}
                >
                  {ta('wizLetterAdd')}
                </KButton>
              )}
              {repeatCount > 1 && (
                <KButton
                  size="sm"
                  onClick={() => {
                    setAnswers(activeDef.repeatPages!.remove(formValues, repeatIdx));
                    setIdx(Math.max(0, idx - 1));
                  }}
                >
                  {ta('wizLetterRemove')}
                </KButton>
              )}
            </div>
          )}
          {/* Жёлтых плашек у мастера НЕТ (владелец 05.08, IMG_1290): пустые поля уже
          подчёркнуты красным с подписью «Заполните это поле» — сводка не нужна.
          Инвариант шага и ошибка сервера стоят ОДНОЙ строкой наверху тела
          (канон: UI-TEMPLATE-CANON «Ошибка всего окна», П128), а не внизу над планкой. */}
          {/* «Назад»/«Дальше» — планкой, прибитой к низу (владелец 05.08 №3): sticky-бар
          с заливкой, скролл-контент сквозь него не просвечивает. */}
          {/* ВИД ПЛАНКИ — РОДНОГО ОКНА (владелец 02.09.2026, Н13): круглая «Назад» и широкая
          кнопка со словом. Счётчик «Шаг N из M» из планки убран — он стоит серой строкой
          под заголовком шапки (header.update ниже), ровно как наверху родного окна. */}
        </>
      )}
      <WizFoot
        busy={busy}
        backLabel={ta('wizBack')}
        // Первый экран зовёт не «Дальше», а к делу: «Настроить» при создании и
        // «Редактировать» в правке (ТЗ-7 Ш2) — те же слова, что на родном экране.
        nextLabel={
          bcastOne
            ? edit
              ? ta('wizSave')
              : ta('wizCreate')
            : onIntro
              ? edit
                ? ta('wizEdit')
                : ta('wizSetup')
              : last
                ? edit
                  ? ta('wizSave')
                  : (activeDef.finishLabel?.(loc) ?? ta('wizCreate'))
                : ta('wizNext')
        }
        onBack={
          // Один экран рассылки: «Назад» нет — закрывает крестик шапки.
          bcastOne
            ? undefined
            : // П86: в правке шага «Знакомство» нет — «Назад» с первого шага закрывает мастер.
              skipScene && idx <= 1
              ? () => onDone()
              : idx > 0
                ? () => {
                    setTried(false);
                    setIdx(idx - 1);
                  }
                : undefined
        }
        onNext={bcastOne ? bcastSubmit : goNext}
        // ПОД КНОПКОЙ — ФАКТ ТАРИФА (владелец 12.10.2026): у цепочки и разовой рассылки без Pro отправка закрыта.
        // QUICK-START §11.34 п.5: та же строка, что у приложения (`wizard.footNote`, словарь замка `proGate.note`), на каждом
        // шаге мастеров с замком Pro — и у «Быстрой настройки → Продажа»; только бесплатному тарифу.
        note={
          proLock && (activeDef.id === BROADCAST_WIZARD_ID || activeDef.id === CHAIN_WIZARD_ID || activeDef.quickStart === 'sales')
            ? tBill('proGate.note')
            : undefined
        }
      />
      {bcastForm}
      {nativeMsgHost}
      {/* «Такой запуск уже занят» (запрет одинаковых, раздел Л правило 4): нижний лист
          уводит настраивать бота-хозяина слова. Живёт в useCreateGo (создание) и
          useBotExistsAsk (пересохранение анкеты), здесь только рисуется. */}
      {dupSheet}
      {saveDup.sheet}
    </FPane>
  );
}

/**
 * СТРОКА «КТО ПОЛУЧИТ» в анкете (И3). Компонент общий (audience-screen.tsx) — тот же,
 * что у формы старта «вручную» и у письма цепочки; здесь только перевод значения: ответ
 * анкеты хранится СТРОКОЙ JSON, ровно как поле `audience` узла «Начало рассылки», —
 * второй формы условий отбора в проекте нет.
 */
function WizAudience({
  name,
  label,
  value,
  cred,
  onChange,
}: {
  name: string;
  label: string;
  value: string;
  /** Бот-отправитель из ответа анкеты `accountId` (К27.2 п.5): счёт и список — только по
   *  своим людям ЭТОГО бота, а не по всем карточкам кабинета. */
  cred: string;
  onChange: (v: string) => void;
}) {
  const t = useTranslations('cabinet.audience');
  const parsed = useMemo(() => audienceOfRaw(value), [value]);
  // Число нужно и ПОДВАЛУ («Пока никто не получит — измените условия»), и самой строке —
  // счёт один на обоих: считает его общая рука экрана «Кто получит».
  const { n } = useAudienceCount(audienceQuery(parsed), { key: name, ...(cred ? { cred } : {}) });
  return (
    <>
      <AudienceRow
        label={label || t('whoTitle')}
        value={parsed}
        onChange={(v) => onChange(JSON.stringify(v))}
        emptyWords={t('allPeople')}
        {...(cred ? { cred } : {})}
        countKey={name}
        // СПРАВА — ТОЛЬКО ЧИСЛО (владелец 04.09.2026, И10 п.3): «1 человек», «1 234
        // человека», «Никто». Сводка условий словами в строку не пишется — она длинная
        // и превращала строку в абзац; условия человек читает на самом экране.
        short
      />
      {/* ПОДВАЛ ГРУППЫ: кому мы не пишем никогда и — при нуле — что делать дальше.
          Пояснений ВНУТРИ строки не бывает (канон родных списков iOS). */}
      <FHint>
        {t('stoppedNote')}
        {n === 0 ? ` ${t('nobodyHint')}` : ''}
      </FHint>
    </>
  );
}

/**
 * ГРУППА «ПОЛУЧАТЕЛИ» ОДНОГО ЭКРАНА РАССЫЛКИ (ТЗ WEB-PARITY-3 Р2, кадр iOS R02): строки
 * «Бот · @логин ›» и «Кто получит · Никто ›» в одном пузыре с подписью, под пузырём —
 * подвал про тех, кому не пишем. Счёт людей — той же рукой, что у строки шага (WizAudience).
 */
function WizBcastWho({
  label,
  botLabel,
  botValue,
  accNone,
  credsReady,
  noAccHint,
  addLabel,
  onAdd,
  onBot,
  aud,
  bad,
}: {
  label: string;
  botLabel: string;
  botValue: string;
  accNone: boolean;
  credsReady: boolean;
  noAccHint: string;
  addLabel: string;
  onAdd: () => void;
  onBot: () => void;
  aud?: { name: string; label: string; value: string; cred: string; onChange: (v: string) => void };
  bad?: boolean;
}) {
  const t = useTranslations('cabinet.audience');
  const panel = useSlidePanel();
  const parsed = useMemo(() => audienceOfRaw(aud?.value ?? '{}'), [aud?.value]);
  const { n } = useAudienceCount(audienceQuery(parsed), {
    key: aud?.name ?? 'row',
    ...(aud?.cred ? { cred: aud.cred } : {}),
  });
  const onChangeRef = useRef(aud?.onChange);
  onChangeRef.current = aud?.onChange;
  const items: FNotifyItem[] = [];
  if (!accNone && credsReady)
    items.push({ key: 'bot', label: botLabel, value: botValue, onOpen: onBot });
  if (aud) {
    items.push({
      key: 'audience',
      label: aud.label || t('whoTitle'),
      value: (
        <AudienceSummary
          value={parsed}
          emptyWords={t('allPeople')}
          {...(aud.cred ? { cred: aud.cred } : {})}
          countKey={aud.name}
          short
        />
      ),
      onOpen: () =>
        openAudienceScreen({
          panel,
          title: t('title'),
          value: parsed,
          onChange: (v) => onChangeRef.current?.(JSON.stringify(v)),
          inherited: [],
          ...(aud.cred ? { cred: aud.cred } : {}),
          footer: [],
          phoneSheet: true,
        }),
    });
  }
  return (
    <>
      {accNone ? (
        <>
          <FHint>{noAccHint}</FHint>
          <FAddBubble label={addLabel} onClick={onAdd} />
        </>
      ) : null}
      {items.length > 0 ? (
        <FNotifyList label={label} className={bad ? 'wiz-bad' : undefined} items={items} />
      ) : null}
      {aud ? (
        <FHint>
          {t('stoppedNote')}
          {n === 0 ? ` ${t('nobodyHint')}` : ''}
        </FHint>
      ) : null}
    </>
  );
}

/**
 * ЧАСОВОЙ ПОЯС СЛОВАМИ — «Europe/Lisbon, UTC+1» (И10 п.6, владелец 04.09.2026). Имя
 * пояса и смещение берутся у самого браузера: рассылка уходит по часам ЧЕЛОВЕКА, и он
 * должен видеть, по каким именно. Чистая функция — её же зовёт родная форма рассылки.
 */
export function wizTzWords(now: Date = new Date()): { tz: string; offset: string } {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  // getTimezoneOffset даёт минуты, на которые местное время ОТСТАЁТ от UTC, — знак у него
  // обратный привычному «UTC+1», поэтому переворачиваем.
  const min = -now.getTimezoneOffset();
  const sign = min < 0 ? '-' : '+';
  const abs = Math.abs(min);
  const hh = Math.floor(abs / 60);
  const mm = abs % 60;
  return { tz, offset: `${sign}${hh}${mm === 0 ? '' : `:${String(mm).padStart(2, '0')}`}` };
}

/**
 * «ОТПРАВИТЬ» — ОДНА СТРОКА ВМЕСТО ДВУХ (владелец 04.09.2026, И10 п.6
 * docs/MESSAGE-COMPOSER-PLAN.md). Прежде рядом стояли «Когда отправлять» (два варианта
 * раскрытым списком) и появлявшаяся следом строка «День и час» с календарём — форма
 * прыгала, а зависимая строка вклинивалась в ту же группу (не канон iOS).
 *
 * Теперь: строка со значением «Вручную» либо «5 сент., 12:00»; тап открывает лист с двумя
 * вариантами галочкой, а выбор «В назначенное время» СРАЗУ поднимает календарь (в
 * приложении — родные окна дня и часа, их зовёт `bcast-native`). Отменил окно — значение
 * остаётся «Вручную»: режим меняет не выбор варианта, а ВЫБРАННЫЙ момент.
 *
 * Время в прошлом показывается красным, а «Создать» не пускает словами (И11.9 п.7) —
 * замок живёт в самой анкете (`stepBlocked` рассылки), здесь только цвет.
 */
function WizWhen({
  label,
  mode,
  runAt,
  options,
  bad,
  onManual,
  onDate,
  caption,
  valueRight,
}: {
  /** Подпись над пузырём («Когда») — на одном экране рассылки (Р2). */
  caption?: string;
  /** Значение справа в строке, а не второй строкой (кадр iOS R02). */
  valueRight?: boolean;
  label: string;
  mode: 'manual' | 'date';
  runAt: string;
  options: readonly { value: string; label: string; hint?: string }[];
  bad?: boolean;
  onManual: () => void;
  onDate: (runAt: string, tz: string) => void;
}) {
  const t = useTranslations('cabinet.broadcasts');
  const locale = useLocale();
  const panel = useSlidePanel();
  const two = (n: number): string => String(n).padStart(2, '0');
  const at = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(runAt) ? new Date(runAt) : null;
  const date = at && !Number.isNaN(at.getTime()) ? at : null;
  // ДАТА СЛОВАМИ — КАК НА ТЕЛЕФОНЕ (APP-CLAIMS-1020 ред. 11, Д2): день + короткий месяц, запятая, ЧЧ:ММ — тот же вид, что
  // `runAtWords` страницы сервера (broadcasts.ts) и DateFormatter приложения («15 нояб., 12:00»), не «15.11.2026».
  const dateWords = date
    ? `${new Intl.DateTimeFormat(locale === 'en' ? 'en' : 'ru', { day: 'numeric', month: 'short' }).format(date)}, ${two(date.getHours())}:${two(date.getMinutes())}`
    : '';
  const past = mode === 'date' && whenIsPast(runAt);
  const value =
    mode === 'date' && dateWords !== ''
      ? dateWords
      : (options.find((o) => o.value === 'manual')?.label ?? '');
  const { tz, offset } = wizTzWords();
  const manualHint = options.find((o) => o.value === 'manual')?.hint ?? '';

  // Лист уезжает в стопку СНИМКОМ, а ответы живут дальше своей жизнью: руки отдаём
  // свежими ссылками — иначе вторая правка подряд легла бы поверх старого снимка.
  const onManualRef = useRef(onManual);
  onManualRef.current = onManual;
  const onDateRef = useRef(onDate);
  onDateRef.current = onDate;
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 Р8/Р10, кадры iOS R04-broadcast-when /
  // -date-picker / -time-picker): лист «Отправить» — стопка с «×», а «В назначенное время»
  // закрывает лист и поднимает окно даты ПО ЦЕНТРУ над формой, за ним — барабан времени.
  // Окно живёт у самой строки (здесь), а не в листе: лист к этому моменту уже закрыт.
  const openCal = useRef<(() => void) | null>(null);
  const applyDate = (d: Date): void =>
    onDateRef.current(
      `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    );

  const open = (): void =>
    panel.open({
      title: label,
      sheet: true,
      // Телефон: полулист 52 % над формой, как выбор бота (кадр iOS R04-broadcast-when —
      // форма видна над листом; было во всю высоту).
      ...(valueRight ? { sheetDetent: 0.52 } : {}),
      // «×» — полулист, представленный поверх (кадр iOS R04-broadcast-when; ТЗ WEB-CAUSES М83, доработка ФИНАЛ-2).
      lead: 'close',
      winKey: 'wiz-when',
      body: (
        <WizWhenSheet
          label={label}
          mode={mode}
          value={date}
          options={options}
          {...(valueRight
            ? {
                phone: true,
                onDateAsk: () => {
                  panel.back();
                  window.setTimeout(() => openCal.current?.(), 80);
                },
              }
            : {})}
          onManual={() => {
            onManualRef.current();
            panel.back();
          }}
          onDate={(d) => {
            applyDate(d);
            panel.back();
          }}
        />
      ),
    });

  return (
    <>
      <FNotifyList
        {...(caption ? { label: caption } : {})}
        items={[
          {
            key: 'when',
            label,
            // Прошедшее время — красным: значение врёт само по себе, и человек должен
            // видеть это на строке, а не только упершись в «Создать».
            [valueRight ? 'value' : 'summary']: past ? (
              <span className="wiz-row-past">{value}</span>
            ) : (
              value
            ),
            onOpen: open,
          },
        ]}
        // ПОДВАЛ ГРУППЫ (И10 п.6): пояснений внутри строки не бывает. ПОДПИСЬ ПОД КАРТОЧКОЙ — КАК НА ТЕЛЕФОНЕ
        // (APP-CLAIMS-1020 ред. 11, Д2/10.7; сервер broadcasts.ts `footerBy`): серым абзацем снаружи пузыря, не пузырём;
        // «Вручную» — подпись П16 (подсказка варианта `manual`) и второй строкой часовой пояс, дата — только пояс.
        // Меняется на месте тем же кадром, что строка: выбор живёт в ответах формы, сервер видит их при «Создать».
        foot={
          mode === 'manual' && manualHint ? (
            <>
              {manualHint}
              <br />
              {t('whenTzNote', { tz, offset })}
            </>
          ) : (
            t('whenTzNote', { tz, offset })
          )
        }
      />
      {bad ? <FError>{t('whenBad')}</FError> : null}
      {valueRight ? (
        <DateTimePick
          bare
          dialog
          openRef={openCal}
          value={date}
          clearable={false}
          onChange={(d) => d && applyDate(d)}
        />
      ) : null}
    </>
  );
}

/**
 * ЛИСТ «ОТПРАВИТЬ» — два варианта галочкой. Выбор «В назначенное время» НЕ меняет режим
 * сам по себе: он поднимает календарь, и режим переключает уже ВЫБРАННЫЙ момент
 * (отменил окно — остаётся «Вручную», И10 п.6).
 */
function WizWhenSheet({
  label,
  mode,
  value,
  options,
  onManual,
  onDate,
  phone,
  onDateAsk,
}: {
  label: string;
  mode: 'manual' | 'date';
  value: Date | null;
  options: readonly { value: string; label: string; hint?: string }[];
  onManual: () => void;
  onDate: (d: Date) => void;
  /** Телефон (кадр iOS R04-broadcast-when): без подписи над пузырём — имя уже в шапке. */
  phone?: boolean;
  /** «В назначенное время» на телефоне: окно даты поднимает ХОЗЯИН строки (лист к этому
   *  моменту закрыт), своего календаря у листа нет. */
  onDateAsk?: () => void;
}) {
  // Календарь стоит БЕЗ своей кнопки: строку уже назвали снаружи, а второй даты на
  // экране быть не должно — окно поднимает рука.
  const openCal = useRef<(() => void) | null>(null);
  return (
    // Телефон: лист мастера — строки выбора по мере родного списка мастера (wiz-pane:
    // слово 17, пояснение 13/16, поля 20, галочка 28; кадр iOS R04-broadcast-when — у веба
    // было 15/18, строки 81 против 75).
    <FPane wiz={phone}>
      <FChoiceBlock
        label={phone ? null : label}
        value={mode}
        options={options.map((o) => ({
          value: o.value,
          label: o.label,
          ...(o.hint ? { hint: o.hint } : {}),
        }))}
        onChange={(v) =>
          v === 'date' ? (onDateAsk ? onDateAsk() : openCal.current?.()) : onManual()
        }
      />
      {onDateAsk ? null : (
        <DateTimePick
          bare
          openRef={openCal}
          value={value}
          clearable={false}
          onChange={(d) => {
            if (d) onDate(d);
          }}
        />
      )}
    </FPane>
  );
}

/**
 * ДЕНЬ И ЧАС отправки (ТЗ 16а, вид старта «в дату»). Календарь — общий DateTimePick, как
 * везде в кабинете (нативных date-инпутов у нас нет). Значение хранится «стенными
 * часами» — «2026-09-01T10:00» без пояса, а сам пояс уезжает вторым ответом (`tz`): узел
 * «Начало рассылки» держит их именно такой парой, и движок будит рассылку по поясу
 * клиента, а не по поясу сервера.
 */
function WizRunAt({
  label,
  value,
  bad,
  onChange,
}: {
  label: string;
  value: string;
  bad?: boolean;
  onChange: (value: string, tz: string) => void;
}) {
  const two = (n: number): string => String(n).padStart(2, '0');
  const at = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) ? new Date(value) : null;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  return (
    <FField cap label={label} bad={bad}>
      <DateTimePick
        value={at && !Number.isNaN(at.getTime()) ? at : null}
        clearable={false}
        onChange={(d) =>
          onChange(
            d
              ? `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}T${two(d.getHours())}:${two(d.getMinutes())}`
              : '',
            tz,
          )
        }
      />
    </FField>
  );
}

/** Служебный пункт «Другой канал…» (не адрес). */
const WIZ_OTHER = '__other__';

/** Тело листа выбора публикаций мастера: своё состояние, наружу — id через запятую
 *  (ответ анкеты mediaIds). Тот же вид, что лист «Старта». */
function WizPostsSheetBody({
  credId,
  initial,
  onPick,
  onOk,
  ios,
}: {
  credId: string | null;
  initial: string[];
  onPick: (ids: string[]) => void;
  onOk?: () => void;
  /** Лист как в iOS (М8, кадр W13-igkw-posts-sheet): крупный заголовок слева в теле,
   *  сетка без поиска и кружков, широкая «ОК» у низа. */
  ios?: boolean;
}) {
  const t = useTranslations('cabinet.scenario');
  const [ids, setIds] = useState<string[]>(initial);
  return (
    <div className={`ig-pick-sheet${ios ? ' ig-pick-sheet--ios' : ''}`}>
      {ios ? <h2 className="wiz-h1 ig-pick-title">{t('chatStartPosts')}</h2> : null}
      <IgMediaPicker
        credId={credId}
        selected={ids}
        onChange={(next) => {
          setIds(next);
          onPick(next);
        }}
        {...(ios ? { plain: true } : {})}
      />
      {onOk ? (
        ios ? (
          <WizPageFootBar foot={{ label: t('chatStartPostsOk'), onClick: onOk }} />
        ) : (
          <KButton tone="primary" className="ig-pick-ok" onClick={onOk}>
            {t('chatStartPostsOk')}
          </KButton>
        )
      ) : null}
    </div>
  );
}

/** Выбор публикаций в IG-анкете (владелец 05.08): превью выбранных + «Изменить» одной
 *  строкой; сам выбор — вертикальным листом поверх (натив: panel sheet, веб: BottomSheet).
 *  Значение ответа — id через запятую (см. mediaIds в keyword-bot-ig). */
function WizIgPosts({
  credId,
  value,
  onChange,
}: {
  credId: string | null;
  value: string;
  onChange: (v: string) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const panel = useSlidePanel();
  const [webOpen, setWebOpen] = useState(false);
  // Телефон в браузере — лист-стопка без шапки, как родной лист публикаций (М8/М10/М15).
  const phoneWeb = usePhoneLook();
  const ids = value ? value.split(',').filter(Boolean) : [];
  const openSheet = () => {
    if (!panel.available && phoneWeb) {
      panel.open({
        title: t('chatStartPosts'),
        sheet: true,
        deck: true,
                headless: true,
        winKey: 'wiz-posts',
        body: (
          <WizPostsSheetBody
            credId={credId}
            initial={ids}
            ios
            onPick={(next) => onChange(next.join(','))}
            onOk={() => panel.back()}
          />
        ),
      });
      return;
    }
    if (panel.available) {
      panel.open({
        title: t('chatStartPosts'),
        sheet: true,
        actionLabel: t('chatStartPostsOk'),
        actionIcon: 'checkmark',
        onAction: () => panel.close(),
        body: (
          <WizPostsSheetBody
            credId={credId}
            initial={ids}
            onPick={(next) => onChange(next.join(','))}
          />
        ),
      });
    } else {
      setWebOpen(true);
    }
  };
  // Переключил «Только под выбранными» — лист выбора открывается СРАЗУ (владелец
  // 2026-08-06: «убираю только выбранными — у меня сразу же должна открываться модалка,
  // где я могу выбрать»). Контрол рождается ровно в этот момент (его показывают
  // displayOptions), поэтому открываем на монтировании и ровно один раз: закрыл лист
  // ничего не выбрав — второй раз он сам не выскочит.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current || ids.length > 0) return;
    autoOpened.current = true;
    openSheet();
    // Открытие — разовое действие монтирования; перезапуск по смене ответов не нужен.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      {/* Свой контрол — тоже полем НАБОРА (form-kit): иначе он не попадает под общий
          набор правил и стоит со своими отступами (владелец 2026-08-06, Э1). */}
      {phoneWeb ? (
        // СТРОКА ВЫБРАННЫХ ПУБЛИКАЦИЙ — как родной ряд iOS (кадр W13-igkw-trigger-final,
        // WizardScreen.swift `postsRow`): снимки 46×46 подряд (до пяти) и «›», без слов;
        // стоит В ТОЙ ЖЕ карточке, что «Под всеми / Только под выбранными», под волоском
        // (стык карточек — cabinet.css `.igm-pick-row`). Пусто — «Выбрать публикации ›».
        // Тап открывает лист.
        <KListGroup className="igm-pick-row">
          <KListRow
            title={
              ids.length > 0 ? (
                <IgMediaChosen credId={credId} ids={ids} compact />
              ) : (
                t('chatStartPostsChoose')
              )
            }
            chevron
            onClick={openSheet}
          />
        </KListGroup>
      ) : (
        <FField>
          <div className="igm-row">
            <IgMediaChosen credId={credId} ids={ids} />
            <KButton tone="link" onClick={openSheet}>
              {ids.length > 0 ? t('chatStartPostsEdit') : t('chatStartPostsChoose')}
            </KButton>
          </div>
        </FField>
      )}
      {/* Оговорка про соавторство/чужие посты снята (владелец 05.08, IMG_1330:
          «лишний текст, который только путает»). */}
      {webOpen ? (
        <BottomSheet title={t('chatStartPosts')} onClose={() => setWebOpen(false)}>
          {(close) => (
            <WizPostsSheetBody
              credId={credId}
              initial={ids}
              onPick={(next) => onChange(next.join(','))}
              onOk={close}
            />
          )}
        </BottomSheet>
      ) : null}
    </>
  );
}

/**
 * Варианты публичного ответа под комментарием — СПИСКОМ ПОЛЕЙ (владелец 2026-08-06:
 * «подряд идут пять инпутов, где можно вбить разные ответы… можно удалить или добавить
 * шестой, то есть сделать их любое количество»). Наружу уходит прежний формат — одна
 * строка с переводами строк, поэтому сборка канона не менялась.
 * Пустые строки не выкидываем на лету: человек чистит поле, чтобы вписать другое —
 * исчезающая строка выдёргивала бы фокус. Пустые отсеиваются при сборке (readAnswers).
 */
function WizReplies({
  label,
  hint,
  value,
  onChange,
  sheet = false,
  editing = false,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  /** В ШТОРКЕ «Варианты ответа» (ТЗ USERTEST-1014 п.7): без своей подписи и пояснения (они в шапке и сверху
   *  шторки), удаление — смахиванием влево на любом сенсорном экране и режимом «Изм.», крестиков нет. */
  sheet?: boolean;
  /** Режим «Изм.»: у каждой строки слева красный «минус», тап по нему открывает «Удалить» справа. */
  editing?: boolean;
}) {
  const ta = useTranslations('cabinet.automations');
  const list = value.split('\n');
  const put = (next: string[]): void => onChange(next.join('\n'));
  // ТЕЛЕФОН В БРАУЗЕРЕ (ТЗ WEB-PARITY-3 М25, кадр W17-igkw-step8-comments): крестиков у строк
  // нет — удаление смахиванием влево (решение владельца: «Изм.»/смахивание, без крестиков);
  // «Добавить вариант» — строкой с зелёным кружком «+». Смахивание — своё, лёгкое: строка
  // уезжает за палец, за ней красная «Удалить» 88, как у списков FNotifyRows.
  const phoneWeb = usePhoneLook();
  const [swiped, setSwiped] = useState<number | null>(null);
  const touch = useRef<{ i: number; x: number; y: number; dx: number } | null>(null);
  const SWIPE_W = 88;
  const onTouchStart = (i: number) => (e: React.TouchEvent) => {
    const t0 = e.touches[0];
    if (!t0) return;
    touch.current = { i, x: t0.clientX, y: t0.clientY, dx: 0 };
  };
  const onTouchMove = (i: number) => (e: React.TouchEvent) => {
    const t0 = e.touches[0];
    const st = touch.current;
    if (!t0 || !st || st.i !== i) return;
    const dx = t0.clientX - st.x;
    const dy = t0.clientY - st.y;
    if (Math.abs(dy) > Math.abs(dx)) return;
    st.dx = dx;
    const el = (e.currentTarget as HTMLElement).querySelector<HTMLElement>('.wiz-rep-in');
    if (el)
      el.style.transform = `translateX(${Math.max(-SWIPE_W, Math.min(0, (swiped === i ? -SWIPE_W : 0) + dx))}px)`;
  };
  const onTouchEnd = (i: number) => (e: React.TouchEvent) => {
    const st = touch.current;
    touch.current = null;
    if (!st || st.i !== i) return;
    const el = (e.currentTarget as HTMLElement).querySelector<HTMLElement>('.wiz-rep-in');
    if (el) el.style.transform = '';
    const open = (swiped === i ? -SWIPE_W : 0) + st.dx < -SWIPE_W / 2;
    setSwiped(open ? i : null);
  };
  const swipe = phoneWeb || sheet;
  return (
    <FField
      cap={!sheet}
      label={sheet ? undefined : label}
      className={`wiz-reps${swipe ? ' wiz-reps--ios' : ''}${sheet ? ' wiz-reps--sheet' : ''}`}
      hint={sheet ? undefined : hint}
    >
      {list.map((v, i) => (
        // Ключ по НОМЕРУ строки: тексты повторяются и меняются на лету, по ним ключ
        // нестабилен — поле теряло бы фокус на каждой букве.
        <div
          className={`wiz-rep${swiped === i ? ' is-swiped' : ''}`}
          key={i}
          {...(swipe && list.length > 1
            ? {
                onTouchStart: onTouchStart(i),
                onTouchMove: onTouchMove(i),
                onTouchEnd: onTouchEnd(i),
              }
            : {})}
        >
          {swipe && list.length > 1 ? (
            <KBareButton
              className="wiz-rep-swipe"
              aria-label={ta('wizReplyRemove')}
              onClick={() => {
                setSwiped(null);
                put(list.filter((_, j) => j !== i));
              }}
            >
              {ta('cardDelete')}
            </KBareButton>
          ) : null}
          <div className="wiz-rep-in">
            {/* РЕЖИМ «ИЗМ.» (ТЗ USERTEST-1014 п.7): красный «минус» слева открывает «Удалить» справа, как родной
                режим правки iOS. Последнюю строку удалить нельзя — у единственной минуса нет. */}
            {editing && list.length > 1 ? (
              <KBareButton
                className="fk-nrow-minus"
                aria-label={ta('wizReplyRemove')}
                onClick={() => setSwiped(swiped === i ? null : i)}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" aria-hidden>
                  <path d="M5 12h14" />
                </svg>
              </KBareButton>
            ) : null}
            {/* Поле РАСТЁТ до двух строк, дальше скроллится внутри себя (владелец
              2026-08-06, Т4): ответы бывают длиннее строки, а трёх строк заранее
              резервировать нельзя — это та же дыра, что чинили в сообщениях. */}
            <AutoGrowTextarea
              value={v}
              max={2}
              maxLength={200}
              ariaLabel={`${label} ${i + 1}`}
              placeholder={ta('wizReplyPh')}
              onValue={(next) => put(list.map((x, j) => (j === i ? next : x)))}
            />
            {list.length > 1 && !swipe ? (
              <KBareButton
                aria-label={ta('wizReplyRemove')}
                title={ta('wizReplyRemove')}
                onClick={() => put(list.filter((_, j) => j !== i))}
              >
                <svg
                  viewBox="0 0 24 24"
                  width={14}
                  height={14}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  aria-hidden
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </KBareButton>
            ) : null}
          </div>
        </div>
      ))}
      {swipe ? (
        <KBareButton className="wiz-rep-add-row" onClick={() => put([...list, ''])}>
          <span className="wiz-rep-add-ic" aria-hidden>
            <PlusGlyph size={18} />
          </span>
          {ta('wizReplyAdd')}
        </KBareButton>
      ) : (
        <KButton tone="link" className="wiz-rep-add" onClick={() => put([...list, ''])}>
          {ta('wizReplyAdd')}
        </KButton>
      )}
    </FField>
  );
}

/**
 * «ВАРИАНТЫ ОТВЕТА» НА ШАГЕ — ОДНОЙ СТРОКОЙ СО СЧЁТОМ (ТЗ USERTEST-1014 п.7, владелец 14.10.2026: пользователь не понял,
 * что фразы — варианты, из которых бот берёт один случайно). Подпись над строкой, в строке «5 вариантов» и шеврон,
 * пояснение под строкой видно всегда. Тап — полушторка стопкой (на широком экране — панель справа) со списком строк.
 */
function WizRepliesRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const ta = useTranslations('cabinet.automations');
  const panel = useSlidePanel();
  // Шторка живёт своим состоянием, а наружу пишет СВЕЖЕЙ рукой: тело шторки собрано в момент открытия.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const n = value.split('\n').filter((x) => x.trim() !== '').length;
  const open = (): void =>
    panel.open({
      title: label,
      sheet: true,
      deck: true,
      lead: 'close',
      winKey: 'wiz-replies',
      body: (
        <WizRepliesSheet
          hint={hint}
          label={label}
          initial={value}
          onChange={(v) => onChangeRef.current(v)}
          onDone={() => panel.close()}
        />
      ),
    });
  return (
    <FBlock label={label} hint={hint}>
      <KListGroup>
        <KListRow title={ta('wizReplyCount', { n })} chevron onClick={open} />
      </KListGroup>
    </FBlock>
  );
}

/** Тело шторки «Варианты ответа»: пояснение сверху, список строк (правка на месте, «Добавить вариант», смахивание
 *  и режим «Изм.» — пилюля справа в шапке), «Готово» внизу закрывает. Последний вариант не удаляется. */
function WizRepliesSheet({
  hint,
  label,
  initial,
  onChange,
  onDone,
}: {
  hint: string;
  label: string;
  initial: string;
  onChange: (v: string) => void;
  onDone: () => void;
}) {
  const ta = useTranslations('cabinet.automations');
  const tc = useTranslations('cabinet.common');
  const panel = useSlidePanel();
  const [value, setValue] = useState(initial);
  const [editing, setEditing] = useState(false);
  const many = value.split('\n').length > 1;
  const editOn = editing && many;
  // «ИЗМ.» / «ГОТОВО» В ШАПКЕ ШТОРКИ — та же стеклянная пилюля, что у списка триггеров (KPill).
  useEffect(() => {
    panel.update({
      headerRight: (
        <KPill disabled={!many} onClick={() => setEditing((v) => !v)}>
          {editOn ? tc('done') : ta('wizListEdit')}
        </KPill>
      ),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- рука панели новая каждый рендер; слот — по смыслу
  }, [editOn, many, ta, tc]);
  return (
    // `wiz-entry` — та же раскладка строк «как iOS», что у страниц мастера (linesCard), на телефонной ширине.
    <FPane className="wiz-entry">
      {hint ? <FHint>{hint}</FHint> : null}
      <WizReplies
        sheet
        editing={editOn}
        label={label}
        hint=""
        value={value}
        onChange={(v) => {
          setValue(v);
          onChange(v);
        }}
      />
      <KButton tone="primary" action block onClick={onDone}>
        {tc('done')}
      </KButton>
    </FPane>
  );
}

/** Байты файла в мегабайты для слов человеку (мегабайт — минимум один). */
const wizMb = (bytes: number): number => Math.max(1, Math.round(bytes / (1024 * 1024)));

/** Что известно о ЗАЛИТОМ файле: числа считает ручка заливки, интерфейс их только читает. */
interface WizUploaded {
  /** id в хранилище — по нему раннер ужимает ролик. */
  id?: string;
  url: string;
  name?: string;
  bytes?: number;
  /** Предел КАНАЛА на этот вид (ответ ручки) — число одно у сервера и у экрана. */
  maxBytes?: number;
  /** Настоящий тип файла (по байтам) — им судят о формате, а не подписью браузера. */
  mime?: string;
}

/** Вопрос про тяжёлый файл ГОТОВЫМИ словами: как его показать — решает вызывающий. */
interface WizFileAsk {
  title: string;
  text: string;
  actions: Array<{ key: string; label: string }>;
  cancel: string;
}

/**
 * ЧЕМ РАННЕР ПЕРЕВОДИТ ФОРМАТ ЭТОГО ВИДА (ТЗ 20.09.2026 V.3): картинка — 'photo'
 * (heic → jpeg), ролик — 'video' (mov → mp4), звук — 'audio' (wav → m4a). Документ не
 * переводится ничем: «файл» и есть файл.
 */
function wizConvertOp(kind: ChatAttachKind): 'photo' | 'video' | 'audio' | null {
  if (kind === 'image') return 'photo';
  if (kind === 'video') return 'video';
  if (kind === 'audio') return 'audio';
  return null;
}

/** Куда положить готовый файл и чем ответить, если положить его не вышло. */
interface WizFileTarget {
  channel: BotAccountChannel;
  kind: ChatAttachKind;
  /** Поле получает адрес файла. */
  apply: (url: string) => void;
  /** «Выбрать другой файл» — открыть системный выбор заново. */
  again: () => void;
  /** Честная строка человеку: сжать нечем или не получилось. */
  fail: (msg: string) => void;
  /** СВОЁ сжатие вместо раннера (ТЗ 20.09.2026 V5): картинку в браузере ужимает сам веб
   *  (canvas), в приложении — устройство. Есть — капсула «Сжать» показывается и там, где
   *  раннер бессилен. */
  squeeze?: () => void;
}

/**
 * ФАЙЛ ТЯЖЕЛЕЕ ПРЕДЕЛА — ВОПРОС ЛИСТОМ, А НЕ КРАСНАЯ СТРОКА (ТЗ 20.09.2026 F3, владелец:
 * «Видео весит 73 МБ, максимум 50» с действиями «Сжать» и «Выбрать другой файл»).
 *
 * Механика ТА ЖЕ, что в окне «Сообщение» (И9.7): лист MediaAskSheet и подготовка файла
 * раннером (useMediaConvert, операция `fit-size`). Второй такой механики не заводим.
 *
 * INVARIANT(scriptera): решение «влезает / не влезает / можно сжать» считает свод правил
 * каналов (chatAttachFit), а вес и предел приезжают ОТВЕТОМ РУЧКИ. Причина: числа в
 * интерфейсе разъезжались с сервером (подсказка «до 20 МБ», ошибка «8 МБ» — претензия
 * владельца 20.09.2026). Канон: packages/ui/src/scenario/channels.ts.
 */
function useWizFileFix(askNative?: (q: WizFileAsk) => boolean): {
  /** Файл залит: влезает — кладём в поле, нет — спрашиваем. */
  settle: (up: WizUploaded, target: WizFileTarget) => void;
  /** Файл ещё НЕ залит (приложение, ТЗ V.4): вес и тип известны с диска — спрашиваем ДО
   *  сети, решение приносит вызвавший (одна чистая функция свода правил). */
  askBefore: (
    file: { kind: ChatAttachKind; bytes: number; mime?: string },
    fit: ChatAttachFit,
    acts: { squeeze?: () => void; convert?: () => void; again: () => void; drop?: () => void },
  ) => void;
  /** Ответ на заданный вопрос: ключ действия; пустой ключ — «Отмена». */
  pick: (key: string) => void;
  /** Раннер сейчас готовит файл — поле показывает «Сжимаем…» или «Обрабатываем…». */
  busy: boolean;
  /** Ключ слова под текущее дело (ТЗ V.2): сжатие и перевод формата — разные слова. */
  busyWord: 'attachCompressing' | 'attachProcessing';
} {
  const t = useTranslations('cabinet.scenario');
  const tc = useTranslations('cabinet');
  const panel = useSlidePanel();
  /** Заданный вопрос: файл, его место и что с ним делают. Ответ приходит один. */
  const asked = useRef<{
    up: WizUploaded;
    target: WizFileTarget;
    maxBytes: number;
    targetMb: number;
  } | null>(null);
  /** Вопрос о ЕЩЁ НЕ ЗАЛИТОМ файле (ТЗ V2): сжимает не раннер, а тот, кто спросил. */
  const askedLocal = useRef<{
    squeeze?: () => void;
    convert?: () => void;
    again: () => void;
    drop?: () => void;
  } | null>(null);
  /** Какое дело идёт сейчас — от него зависит слово в поле («Сжимаем…» / «Обрабатываем…»). */
  const [work, setWork] = useState<'compress' | 'convert' | null>(null);

  const conv = useMediaConvert({
    onDone: (_key, op, out) => {
      const job = asked.current;
      asked.current = null;
      setWork(null);
      if (!job) return;
      // Сжатие и перевод формата — два разных дела, и слова у них свои (ТЗ V.2).
      const failWord = op === 'fit-size' ? 'attachCompressFail' : 'attachProcessFail';
      if (!out.id) {
        job.target.fail(t(failWord));
        return;
      }
      // Подготовили, а всё равно не влезает (сжатие не всесильно) — говорим честно, а не
      // кладём в поле файл, который площадка отобьёт уже у живого бота.
      if (job.maxBytes > 0 && (out.bytes ?? 0) > job.maxBytes) {
        job.target.fail(t('attachStillTooBig'));
        return;
      }
      // Адрес собираем по id: ручка подготовки отдаёт наш же файл хранилища, а имя
      // кладём параметром (сборка деплоя перепишет его в публичный путь /f/<id>/<имя>).
      // Хвост имени — по НАСТОЯЩЕМУ типу готового файла: площадки судят о виде по нему.
      const base = (job.up.name ?? '').replace(/\.[^.]+$/, '') || 'file';
      const name = withExtension(base, out.mime ?? 'video/mp4');
      job.target.apply(`/api/form/file/${out.id}?name=${encodeURIComponent(name)}`);
    },
    onFail: (_key, op) => {
      const job = asked.current;
      asked.current = null;
      setWork(null);
      job?.target.fail(t(op === 'fit-size' ? 'attachCompressFail' : 'attachProcessFail'));
    },
  });

  function pick(key: string): void {
    // Вопрос был задан ДО заливки (приложение, картинка в браузере) — раннер тут ни при
    // чём: «Сжать» делает тот, кто спросил, и только по этому слову человека.
    const local = askedLocal.current;
    if (local) {
      askedLocal.current = null;
      if (key === 'compress' && local.squeeze) {
        local.squeeze();
        return;
      }
      if (key === 'convert' && local.convert) {
        local.convert();
        return;
      }
      if (key === 'again') {
        local.again();
        return;
      }
      local.drop?.();
      return;
    }
    const job = asked.current;
    if (!job) return;
    if (key === 'compress' && job.target.squeeze) {
      // Своё сжатие (картинка в браузере): файл уже залит, но ужимает его не раннер.
      asked.current = null;
      job.target.squeeze();
      return;
    }
    if (key === 'compress' && job.up.id) {
      // Задание раннеру: цель НИЖЕ предела канала (у Инстаграма он уже общей цели 48 МБ).
      setWork('compress');
      conv.start(job.up.id, job.up.id, 'fit-size', { targetMb: job.targetMb });
      return;
    }
    // «Обработать» — перевод формата (wav → m4a, heic → jpeg, mov → mp4): это работа
    // раннера, и человеку про форматы знать незачем (ТЗ V.3).
    const convOp = wizConvertOp(job.target.kind);
    if (key === 'convert' && job.up.id && convOp) {
      setWork('convert');
      conv.start(job.up.id, job.up.id, convOp);
      return;
    }
    asked.current = null;
    // «Отмена» — поле остаётся пустым: файл, который не уедет, мастеру не нужен.
    if (key === 'again') job.target.again();
  }

  /**
   * ОДИН ВОПРОС НА ВСЕ ПРИЧИНЫ (ТЗ 20.09.2026 V.2): «весит 73 МБ, максимум 50» /
   * «в формате wav — канал принимает mp3, m4a» / «фото слишком вытянутое». Слова — общие
   * ключи словаря (cabinet.scenario), числа — из решения свода правил (chatAttachFit), а
   * не из кода. Сам лист один: на сайте свой нижний, в приложении — родной (F3/F4).
   */
  const askFile = (
    kind: ChatAttachKind,
    file: { bytes: number; mime?: string },
    fit: ChatAttachFit,
    can: { compress?: boolean; convert?: boolean },
  ): void => {
    const maxMb = wizMb(fit.maxBytes);
    const hasMb =
      wizMb(file.bytes) > maxMb ? wizMb(file.bytes) : Math.ceil(file.bytes / (1024 * 1024));
    const list = mimeWords(fit.accept);
    // ФОРМАТ И ВЕС НАЗЫВАЮТСЯ ВМЕСТЕ (владелец 20.09.2026: «говоришь, что не подходит
    // формат, но ничего не говоришь про размер»): поправив одно, человек упёрся бы во
    // второе. Обе беды сразу — `alsoBig` свода правил (chatAttachFit), и тогда вопрос
    // несёт и вес, и предел канала.
    const text =
      fit.reason === 'dims'
        ? t('attachBadDims')
        : fit.reason === 'format'
          ? fit.alsoBig
            ? t(can.convert || can.compress ? 'attachBadFormatBigAsk' : 'attachBadFormatBigOnly', {
                ext: mimeWord(file.mime ?? ''),
                list,
                has: hasMb,
                max: maxMb,
              })
            : t(can.convert ? 'attachBadFormatAsk' : 'attachBadFormatOnly', {
                ext: mimeWord(file.mime ?? ''),
                list,
              })
          : t(can.compress ? 'attachTooBigAsk' : 'attachTooBigOnly', {
              kind: t(chatContentNameKey(kind) as 'chatContentImage'),
              has: hasMb,
              max: maxMb,
            });
    const q: WizFileAsk = {
      title: t('chatMsgAttach'),
      text,
      actions: [
        // Действие — только там, где оно спасёт: ролик и фото ужимает устройство или
        // раннер, формат переводит раннер, документ чужого вида не чинится ничем, и
        // мёртвая капсула хуже её отсутствия.
        ...(can.compress ? [{ key: 'compress', label: t('attachCompress') }] : []),
        ...(can.convert ? [{ key: 'convert', label: t('attachProcess') }] : []),
        { key: 'again', label: t('attachPickAnother') },
      ],
      cancel: tc('cancel'),
    };
    // В приложении вопрос показывает НАТИВ (веб-лист лёг бы ПОД системной шторкой
    // мастера — канон §11); не вышло или мы в браузере — свой нижний лист.
    if (askNative?.(q)) return;
    panel.open({
      title: q.title,
      sheet: true,
      winKey: 'wiz-file-toobig',
      body: (
        <MediaAskSheet
          text={q.text}
          cancel={q.cancel}
          onCancel={() => {
            panel.back();
            pick('');
          }}
          actions={q.actions.map((a) => ({
            key: a.key,
            label: a.label,
            onPick: () => {
              panel.back();
              pick(a.key);
            },
          }))}
        />
      ),
    });
  };

  /**
   * ФАЙЛ ЕЩЁ НЕ ЗАЛИТ, И НЕ БУДЕТ, ПОКА ЧЕЛОВЕК НЕ ОТВЕТИТ (ТЗ 20.09.2026 V2, владелец:
   * тяжёлое видео «гнать по сети нельзя»). Так приходит тяжёлый файл из приложения: вес
   * оболочка знает с диска, и вопрос задаётся ДО сети. «Сжать» тут делает не раннер, а
   * само устройство (`squeeze`), «Выбрать другой файл» — новый заход, «Отмена» — `drop`.
   */
  const askBefore = (
    file: { kind: ChatAttachKind; bytes: number; mime?: string },
    fit: ChatAttachFit,
    acts: { squeeze?: () => void; convert?: () => void; again: () => void; drop?: () => void },
  ): void => {
    asked.current = null;
    askedLocal.current = acts;
    askFile(file.kind, file, fit, {
      compress: Boolean(acts.squeeze),
      convert: Boolean(acts.convert),
    });
  };

  const settle = (up: WizUploaded, target: WizFileTarget): void => {
    const bytes = up.bytes ?? 0;
    const base = chatAttachFit(
      target.channel,
      target.kind,
      bytes,
      up.mime !== undefined ? { mime: up.mime } : undefined,
    );
    const maxBytes = up.maxBytes ?? base.maxBytes;
    const fit = { ...base, maxBytes };
    // Вес неизвестен (старый ответ ручки) или файл ПОДХОДИТ каналу — прежняя дорога.
    // Ничего не готовим: подходящий файл не трогаем никогда (владелец 20.09.2026).
    if (bytes <= 0 || maxBytes <= 0 || fit.fits) {
      target.apply(up.url);
      return;
    }
    askedLocal.current = null;
    asked.current = { up, target, maxBytes, targetMb: fit.targetMb };
    // Действие предлагаем там, где оно спасёт: раннеру нужен залитый файл, а своим делом
    // (картинка в браузере) занимается `squeeze` вызвавшего.
    askFile(target.kind, { bytes, ...(up.mime ? { mime: up.mime } : {}) }, fit, {
      compress:
        (fit.fix === 'compress' && fit.compress && Boolean(up.id)) || Boolean(target.squeeze),
      convert: fit.fix === 'convert' && Boolean(up.id) && Boolean(wizConvertOp(target.kind)),
    });
  };

  return {
    settle,
    askBefore,
    pick,
    busy: conv.anyBusy,
    busyWord: work === 'convert' ? 'attachProcessing' : 'attachCompressing',
  };
}

/**
 * Картинка сообщения — ЗАГРУЗКОЙ файла (владелец 2026-08-06: «ссылка на картинку — это
 * ублюдышество какое-то, естественно, её надо загружать»). Наружу уходит тот же ответ,
 * что и раньше, — адрес: файл ложится в публичный бакет (POST /api/cabinet/images), в
 * ответ анкеты пишется его адрес. Сборка канона от этого не меняется.
 * Картинка уже выбрана — показываем её саму и «Убрать»: человек видит, что приложил.
 */
function WizImage({
  channel = 'telegram',
  label,
  hint,
  value,
  onChange,
}: {
  /** Канал бота — точные форматы и предел картинки по ссылке (владелец 19.09.2026). */
  channel?: 'telegram' | 'instagram';
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const ta = useTranslations('cabinet.automations');
  const ts = useTranslations('cabinet.scenario');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Тяжёлая картинка — тот же вопрос листом, что у файла подарка (F3), и по «Сжать» её
  // ужимает САМ БРАУЗЕР (ТЗ 20.09.2026 V5): раннер тут не нужен, файл и так у нас в руках.
  const fix = useWizFileFix();

  const upload = async (file: File, squeezed = false): Promise<void> => {
    const limit = chatAttachLimit(channel, 'image');
    // ВЛЕЗАЕТ — НИЧЕГО НЕ ЖМЁМ (владелец 20.09.2026). Не влезает по весу или по стороне —
    // сначала ВОПРОС человеку, и только по «Сжать» перерисовываем картинку.
    const side = limit && !squeezed ? await imageSide(file) : 0;
    if (limit && !squeezed && imageNeedsSqueeze(file.size, limit.maxBytes, side)) {
      setErr(null);
      fix.settle(
        { url: '', bytes: file.size, maxBytes: limit.maxBytes, mime: file.type },
        {
          channel,
          kind: 'image',
          apply: () => {},
          again: () => fileRef.current?.click(),
          fail: (msg) => setErr(msg),
          squeeze: () => {
            void (async () => {
              setBusy(true);
              const small = await squeezeImage(file, limit.maxBytes);
              setBusy(false);
              // Ужали, а всё равно не влезает — говорим честно, а не кладём в анкету
              // картинку, которую площадка отобьёт уже у живого бота.
              if (!small || small.size > limit.maxBytes) {
                setErr(ts('attachStillTooBig'));
                return;
              }
              await upload(small, true);
            })();
          },
        },
      );
      return;
    }
    setBusy(true);
    setErr(null);
    const body = new FormData();
    body.append('file', file);
    // Тем же путём, что медиа сообщений (/api/cabinet/media → наш адрес /api/form/file/<id>):
    // билд перепишет его на /f/<id>/<имя> за Cloudflare, Telegram скачает. Публичный S3
    // Telegram не достаёт — картинка приветствия роняла бота (владелец 19.09.2026).
    // `kind=image`: настоящий тип по байтам сверяется с видом (владелец 19.09.2026).
    const r = await fetch(`/api/cabinet/media?kind=image&channel=${channel}`, {
      method: 'POST',
      body,
    })
      .then(async (res) => ({
        status: res.status,
        j: (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          url?: string;
          name?: string;
          error?: string;
        },
      }))
      .catch(() => null);
    setBusy(false);
    if (r?.j.ok && r.j.url) {
      onChange(r.j.name ? `${r.j.url}?name=${encodeURIComponent(r.j.name)}` : r.j.url);
      return;
    }
    // Честная причина: слишком большой файл, «не картинка» и панорама человек может исправить сам.
    setErr(
      r?.status === 413
        ? ta('wizImageTooBig')
        : r?.status === 415
          ? r.j.error === 'bad_dims'
            ? ts('attachBadDims')
            : ta('wizImageBadType')
          : ta('wizImageFail'),
    );
  };

  return (
    <FField cap label={label} className="wiz-img" hint={hint} error={err}>
      {/* СТРОКА КАК В iOS (кадр W14-igkw-step5-picture, ТЗ WEB-PARITY-3 М18–М19): слева
          миниатюра 46 (или розовый квадрат «+», пока картинки нет), справа серое слово
          «Заменить картинку» / «Загрузить картинку». Убрать — крестик в углу миниатюры
          (FThumb, решение владельца по К3.60). */}
      <div className="wiz-img-row">
        {value ? (
          <FThumb
            src={value}
            onRemove={() => onChange('')}
            removeLabel={ta('wizImageRemove')}
            onTap={() => fileRef.current?.click()}
            tapLabel={ta('wizImageReplace')}
          />
        ) : (
          <KBareButton
            type="button"
            className="wiz-img-add"
            disabled={busy}
            aria-label={ta('wizImageAdd')}
            onClick={() => fileRef.current?.click()}
          >
            <PlusGlyph size={22} />
          </KBareButton>
        )}
        <KBareButton
          type="button"
          className="wiz-img-word"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
        >
          {busy ? ta('wizImageBusy') : value ? ta('wizImageReplace') : ta('wizImageAdd')}
        </KBareButton>
      </div>
      <KInput
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          // Значение обнуляем сразу: выбрать ТОТ ЖЕ файл второй раз иначе не выйдет —
          // change не сработает, если значение не изменилось.
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
      {/* Подсказка поля видима всегда (владелец: под (i) не прятать) — её и ошибку
          печатает само поле набора (FField), своих абзацев тут нет. */}
    </FField>
  );
}

/**
 * ФАЙЛ «ВКУСНЯШКИ» (картинка, видео, аудио, документ) — ТОТ ЖЕ путь, что у редактора
 * схемы: multipart в /api/cabinet/scenarios/files?kind&channel, обратно ссылка нашего
 * домена, её и записываем ответом анкеты. Своего второго пути заливки у мастера нет —
 * иначе собранный бот и правка в схеме разъехались бы форматом.
 *
 * Пределы, форматы и подпись «png, jpg; до 8 МБ» — из свода правил КАНАЛА (одна правда,
 * channels.ts): человек не зальёт то, чего площадка не примет, и узнает об этом сразу,
 * а не живым прогоном.
 */
function WizFile({
  channel,
  kind,
  value,
  onChange,
  warn,
}: {
  channel: BotAccountChannel;
  kind: ChatAttachKind;
  value: string;
  onChange: (v: string) => void;
  warn?: string;
}) {
  const t = useTranslations('cabinet.scenario');
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const limit = chatAttachLimit(channel, kind);
  const mb = limit ? Math.round(limit.maxBytes / (1024 * 1024)) : 0;
  // Маски окна выбора — ШИРЕ списка отправки: ролик mov/webm/avi канал как есть не берёт,
  // но выбрать его человек может — в mp4 его переведёт раннер по вопросу «Обработать».
  const picks = chatAttachPick(channel, kind);
  // Подпись поля — «Файл» у любого вида (кадры iOS W35-tg-step6-kind-*, ТЗ WEB-PARITY-3 М84):
  // какой именно файл, говорят подсказка форматов снизу и слово в строке.
  const label = t('chatContentFile');
  const ta = useTranslations('cabinet.automations');
  const name = attachFileRef(value).name;
  // Тяжёлый файл — вопрос листом «Сжать / Выбрать другой файл» (F3), а не красная строка.
  const fix = useWizFileFix();

  const upload = async (file: File, squeezed = false): Promise<void> => {
    // КАРТИНКУ ГОТОВИТ БРАУЗЕР, А НЕ СЕРВЕР (ТЗ 20.09.2026 V.3/V.5): тяжёлое или слишком
    // широкое фото на сервер не едет — сперва вопрос человеку, и по «Сжать» canvas
    // перерисовывает его на месте. Подходящую картинку не трогаем.
    if (kind === 'image' && !squeezed && limit) {
      const side = await imageSide(file);
      if (imageNeedsSqueeze(file.size, limit.maxBytes, side)) {
        setErr(null);
        fix.settle(
          { url: '', bytes: file.size, maxBytes: limit.maxBytes, mime: file.type },
          {
            channel,
            kind,
            apply: () => {},
            again: () => fileRef.current?.click(),
            fail: (msg) => setErr(msg),
            squeeze: () => {
              void (async () => {
                setBusy(true);
                const small = await squeezeImage(file, limit.maxBytes);
                setBusy(false);
                if (!small || small.size > limit.maxBytes) {
                  setErr(t('attachStillTooBig'));
                  return;
                }
                await upload(small, true);
              })();
            },
          },
        );
        return;
      }
    }
    setBusy(true);
    setErr(null);
    const body = new FormData();
    body.append('file', file);
    // Ручка принимает ШИРЕ предела канала (ролик, звук, документ): тяжёлый файл надо
    // взять, чтобы предложить починку. Влезает он или нет — решает ответ ручки.
    const r = await fetch(`/api/cabinet/scenarios/files?kind=${kind}&channel=${channel}`, {
      method: 'POST',
      body,
    })
      .then(async (res) => ({
        status: res.status,
        j: (await res.json().catch(() => ({}))) as {
          ok?: boolean;
          id?: string;
          url?: string;
          name?: string;
          mime?: string;
          bytes?: number;
          maxBytes?: number;
          error?: string;
        },
      }))
      .catch(() => null);
    setBusy(false);
    if (r?.j.ok && r.j.url) {
      fix.settle(
        {
          url: r.j.url,
          ...(r.j.id ? { id: r.j.id } : {}),
          ...(r.j.name ? { name: r.j.name } : {}),
          // Настоящий тип по байтам: по нему свод правил судит о формате (ТЗ V.1).
          ...(r.j.mime ? { mime: r.j.mime } : {}),
          ...(r.j.bytes !== undefined ? { bytes: r.j.bytes } : {}),
          ...(r.j.maxBytes !== undefined ? { maxBytes: r.j.maxBytes } : {}),
        },
        {
          channel,
          kind,
          apply: (url) => onChange(url),
          again: () => fileRef.current?.click(),
          fail: (msg) => setErr(msg),
        },
      );
      return;
    }
    // ФОРМАТ, КОТОРЫЙ НЕ ПЕРЕВЕСТИ НИ ВО ЧТО (документ чужого вида, неизвестный тип) —
    // не красная строка, а тот же лист с одним действием «Выбрать другой файл» (ТЗ V.2).
    if (r?.status === 415 && r.j.error !== 'bad_dims') {
      setErr(null);
      fix.askBefore(
        { kind, bytes: file.size, mime: file.type },
        chatAttachFit(channel, kind, file.size, { mime: file.type }),
        { again: () => fileRef.current?.click() },
      );
      return;
    }
    setErr(
      r?.status === 413
        ? t('attachTooLarge', { mb })
        : r?.status === 415
          ? t('attachBadDims')
          : t('attachUploadError'),
    );
  };

  // Поле занято: заливаем, ужимаем ролик или переводим формат — слово своё у каждого дела.
  const work = busy || fix.busy;
  const workWord = busy ? t('attachUploading') : fix.busy ? t(fix.busyWord) : null;

  return (
    // Трясётся само поле, а не пузырь (владелец 2026-08-07) — метка «я красное».
    // Подпись — над пузырём (владелец 11.08.2026), подсказка о форматах и пределе
    // канала — видимой строкой снизу (правило владельца): всё это несёт FField cap.
    <FField
      cap
      // Вид «Картинка» — карточка 13 сверху и снизу (imageRow), остальные виды (видео, аудио,
      // файл) — 16, как однострочное поле (fileRow в default у capsules, padV 16): кадры iOS
      // W35-tg-step6-kind-video/-audio/-file — карточка 78, у «Картинки» 72.
      className={kind === 'image' ? 'wiz-img' : 'wiz-img wiz-img--file'}
      label={label}
      bad={Boolean(warn) && !value}
      hint={limitHint(channel, kind, t)}
      error={err ?? (warn && !value ? warn : null)}
    >
      {value ? (
        <>
          {/* Картинку показываем саму — человек видит, что приложил (как в редакторе). */}
          {kind === 'image' ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img decoding="async" loading="lazy" src={value} alt="" className="wiz-img-prev" />
          ) : null}
          <div className="wiz-img-row">
            <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
              {name ?? t('attachFileNoName')}
            </span>
            <KButton tone="link" disabled={work} onClick={() => fileRef.current?.click()}>
              {workWord ?? t('attachReplace')}
            </KButton>
            <KButton tone="link" disabled={work} onClick={() => onChange('')}>
              {t('attachRemove')}
            </KButton>
          </div>
        </>
      ) : (
        // СТРОКА «+ ВЫБРАТЬ ФАЙЛ» / «+ ЗАГРУЗИТЬ КАРТИНКУ» (М84, кадр W35): розовый квадрат
        // со знаком «+» и серое слово — тот же рецепт, что у картинки приветствия (WizImage).
        <div className="wiz-img-row">
          <KBareButton
            type="button"
            className="wiz-img-add"
            disabled={work}
            aria-label={kind === 'image' ? ta('wizImageAdd') : t('attachPickFile')}
            onClick={() => fileRef.current?.click()}
          >
            <PlusGlyph size={22} />
          </KBareButton>
          <KBareButton
            type="button"
            className="wiz-img-word"
            disabled={work}
            onClick={() => fileRef.current?.click()}
          >
            {workWord ?? (kind === 'image' ? ta('wizImageAdd') : t('attachPickFile'))}
          </KBareButton>
        </div>
      )}
      <KInput
        ref={fileRef}
        type="file"
        {...(picks.length > 0 ? { accept: picks.join(',') } : {})}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          // Тот же файл вторым заходом иначе не выбрать — change не сработает.
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
    </FField>
  );
}

/**
 * Пикер канала в TG-анкете (владелец 05.08): выбор из каналов, где бот замечен
 * (реестр bot_channels копится из событий Телеграма — бота добавили админом, пост
 * в канале), или логин руками. В ОБОИХ случаях — живая проверка «бот действительно
 * администратор?» через POST /api/cabinet/bot-channels (getChat + getChatMember).
 * Каналы без публичного логина не предлагаем: кнопке «Перейти в канал» нужна ссылка.
 *
 * СОСТОЯНИЕ ПРОВЕРКИ — ОБЩЕЕ С ПРИЛОЖЕНИЕМ (ТЗ 19.09.2026 Р6, W2): склад один
 * (wizard-channel-check), поэтому «Проверяем…», отказ площадки и замок «Дальше» в
 * браузере и в родном мастере говорят ровно одно и то же. Своего fetch тут больше нет.
 */
function TgChannelPicker({
  credId,
  value,
  onChange,
  warn,
  wiz,
  stepKey,
  hint,
}: {
  credId: string;
  value: string;
  onChange: (v: string) => void;
  warn?: string;
  /** Где мы — для журнала клиента (Р9). */
  wiz: string;
  stepKey: string;
  /** Подсказка поля анкеты (`description`) — серым ПОД карточкой на телефоне (М67). */
  hint?: string;
}) {
  const t = useTranslations('cabinet.scenario');
  // Телефон в браузере — строка «Добавить канал ›» и лист (кадры iOS W32-tg-step3-channel*);
  // широкий экран — прежний селект с полем.
  const phoneWeb = usePhoneLook();
  const panel = useSlidePanel();
  // Автопроверки ведёт ХОЗЯИН мастера (`auto: true` там), здесь только действия человека:
  // второй сторож слал бы второй запрос на тот же адрес.
  const chan = useWizChannelCheck({ credId, value, active: true, wiz, step: stepKey });
  const { items } = chan;
  const botName = chan.botName || t('chatSubYourBot');
  const [manual, setManual] = useState('');
  const [other, setOther] = useState(false);

  const state = chan.state;
  const busy = state?.phase === 'checking';
  const fail =
    state?.phase === 'fail'
      ? t(chanFailKey(state.reason) as 'chatSubErrNetwork', { bot: botName })
      : null;

  const chosen = items.find((i) => chanKey(i.address) === chanKey(value)) ?? null;
  const unknown = value.trim() !== '' && !chosen;
  const otherMode = other || unknown;
  // Известных каналов НЕТ — селект показывать не из чего (владелец 2026-08-06, Ч12:
  // «у меня нет каналов, и я не знаю, что делать»): сразу поле «назовите канал» и
  // кнопка «Добавить». Появился хоть один — впереди привычный выбор из списка.
  const noneKnown = credId !== '' && chan.listReady && items.length === 0;
  const addMode = noneKnown || otherMode;

  /** Спросить площадку и записать ответ в анкету. Негодный канал в ответ всё равно
   *  пишем: человек видит, ЧТО ввёл и почему это пока не работает. */
  const check = (address: string, src: 'list' | 'form'): void => {
    const addr = address.trim();
    if (!credId || busy || addr === '') return;
    chan.note({ pick: 1, src });
    void chan.check(addr, src).then((res) => {
      if (res.phase !== 'ok') {
        onChange(addr);
        return;
      }
      onChange(res.address);
      setOther(false);
    });
  };

  const options: SmartOption[] = [
    ...chanOptionsOf(items, {
      isAdmin: t('chatSubIsAdmin'),
      notAdmin: t('chatSubNotAdmin'),
      closed: t('chatSubClosed'),
    }).map(
      (o) => ({ value: o.value, label: o.label, desc: o.hint }),
    ),
    { value: WIZ_OTHER, label: t('chatSubOther') },
  ];

  const verified =
    state?.phase === 'ok' ? chanKey(state.address) === chanKey(value) : !!chosen?.canCheck;

  if (phoneWeb) {
    // СТРОКА «ДОБАВИТЬ КАНАЛ ›» → ЛИСТ-СТОПКА «Добавить канал» (ТЗ WEB-PARITY-3 М61/М62/М67,
    // кадры iOS W32-tg-step3-channel / -channel-error): поле-строка «Логин канала», широкая
    // «Проверить и добавить», отказ площадки — красным под полем с подсветкой и вибрацией.
    // Выбранный канал стоит в строке словом, второй строкой — «бот — администратор».
    const has = value.trim() !== '';
    const rowTitle = !has
      ? t('chatSubAddTitle')
      : chosen?.title || (state?.phase === 'ok' ? state.title : '') || value;
    const rowSub = !has
      ? ''
      : busy
        ? t('chatSubChecking')
        : verified
          ? t('chatSubIsAdmin')
          : chosen
            ? t('chatSubNotAdmin')
            : '';
    const err = !credId
      ? t('chatSubErrNoBot')
      : fail
        ? fail
        : !busy && chosen && !chosen.canCheck
          ? t('chatSubErrNotAdmin', { bot: botName })
          : warn || '';
    return (
      <FBlock
        label={t('chatSubChannel')}
        className="wiz-chan"
        bad={!!(warn || fail)}
        hint={hint || undefined}
        error={err || undefined}
      >
        <KListGroup>
          <KListRow
            title={rowTitle}
            hint={rowSub}
            chevron
            onClick={() =>
              panel.open({
                title: t('chatSubAddTitle'),
                sheet: true,
                // ПОЛУШТОРКА ПО СОДЕРЖИМОМУ (UT 14.10 п.30): стартовая высота, точную ставит замер FPane fit.
                ...webOnly({ sheetDetent: WIZ_HALF_SHEET }),
                winKey: 'wiz-chan-add',
                body: (
                  <WizChanAddSheet
                    credId={credId}
                    items={items}
                    value={value}
                    wiz={wiz}
                    stepKey={stepKey}
                    onPick={(addr) => {
                      onChange(addr);
                      setOther(false);
                    }}
                  />
                ),
              })
            }
          />
        </KListGroup>
      </FBlock>
    );
  }

  return (
    <div className={`fld wiz-chan${warn ? ' wiz-bad' : ''}`}>
      <label>{t('chatSubChannel')}</label>
      {/* Красное ЗДЕСЬ — всегда про сам канал, значит ошибка ПОЛЯ, FError
          (канон: UI-TEMPLATE-CANON «Ошибка всего окна» (П128): строка окна живёт первой
          строкой тела, внутри поля её не бывает). Своих <p> с классом ошибки нет. */}
      {!credId ? (
        <FError>{t('chatSubErrNoBot')}</FError>
      ) : (
        <>
          {!noneKnown && (
            <SmartSelect
              options={options}
              value={otherMode ? WIZ_OTHER : (chosen?.address ?? '')}
              onChange={(v) => {
                if (v === WIZ_OTHER) {
                  setOther(true);
                  setManual(value);
                  return;
                }
                setOther(false);
                onChange(v);
                // Живой пересчёт прав и по выбору из списка (владелец 05.08: «в обоих
                // случаях отправить запрос и проверить»).
                check(v, 'list');
              }}
              placeholder={t('chatSubPick')}
              ariaLabel={t('chatSubChannel')}
              className="selbox"
            />
          )}
          {addMode ? (
            <div className="scn-sub-add">
              <KInput
                value={manual || (unknown ? value : '')}
                aria-label={t('chatSubChannel')}
                onChange={(e) => setManual(e.target.value)}
                placeholder={t('chatSubChannelPh')}
              />
              <KButton
                size="sm"
                tone="primary"
                disabled={busy || !(manual || value).trim()}
                onClick={() => check((manual || value).trim(), 'form')}
              >
                {busy ? t('chatSubChecking') : t('chatSubAdd')}
              </KButton>
              <p className="scn-help-field">{t('chatSubManual')}</p>
            </div>
          ) : null}
          {/* «КАК ДОБАВИТЬ» (ТЗ CHAN-ADD 16.10.2026): канала в списке нет — шаги и строка бота; список выше обновится сам. */}
          {addMode ? (
            <WizChanSteps bot={chan.botName.replace(/^@/, '')} offline={!chan.live} onRefresh={chan.reload} />
          ) : null}
          {/* «Проверяем…» строкой под полем (Р6): пока площадка не ответила, человек
              видит, что работа идёт, — и «Дальше» на это время заперт. */}
          {busy ? <p className="scn-help-field">{t('chatSubChecking')}</p> : null}
          {fail ? <FError>{fail}</FError> : null}
          {!busy && !fail && value && verified ? (
            <p className="scn-help-field">
              {t('chatSubOk', {
                title: (state?.phase === 'ok' && state.title) || chosen?.title || value,
              })}
            </p>
          ) : null}
          {!busy && !fail && chosen && !chosen.canCheck ? (
            <FError>{t('chatSubErrNotAdmin', { bot: botName })}</FError>
          ) : null}
        </>
      )}
      {warn ? <FError>{warn}</FError> : null}
    </div>
  );
}

/** «Как добавить» в браузере (ТЗ CHAN-ADD 16.10.2026) — тот же раздел, что у родной страницы (`wizChanPage`): строка бота
 *  (ссылка в Telegram и копирование), под ней шаги с номерами; «Обновить» — только когда живой канал недоступен. */
function WizChanSteps({ bot, offline, onRefresh }: { bot: string; offline: boolean; onRefresh: () => void }) {
  const t = useTranslations('cabinet.scenario');
  // Сырой текст + подстановка (п.27): t() без значения {bot} отдавал ключ.
  const steps = chanStepsText({ steps: String(t.raw('chatSubHowSteps')) }, bot);
  return (
    <>
      <FBlock label={t('chatSubHowHead')} hint={<span className="wiz-chan-steps">{steps}</span>}>
        {bot ? (
          <KListGroup>
            <KListRow
              title={
                <a href={tgHandleUrl(bot)} target="_blank" rel="noreferrer">
                  @{bot}
                </a>
              }
              value={<CopyBtn value={`@${bot}`} label={t('shareCopy')} />}
            />
          </KListGroup>
        ) : null}
      </FBlock>
      {offline ? (
        <FBlock label={null}>
          <KListGroup>
            <KListRow title={t('chatSubRefresh')} onClick={onRefresh} />
          </KListGroup>
        </FBlock>
      ) : null}
    </>
  );
}

/**
 * ЛИСТ «ДОБАВИТЬ КАНАЛ» (телефон, браузер; ТЗ WEB-PARITY-3 М61, кадры iOS W32-tg-step3-channel*):
 * известные каналы бота строками с галочкой у выбранного, ниже поле-строка «Логин канала ·
 * @канал» и широкая «Проверить и добавить». Площадка отказала — красная строка под полем,
 * поле трясётся и телефон вибрирует (правило ошибок владельца 26.09.2026). Прошло — адрес
 * уходит в анкету, лист закрывается. Состояние проверки — тот же общий склад
 * (wizard-channel-check), что у шага и у родного мастера.
 */
function WizChanAddSheet({
  credId,
  items,
  value,
  wiz,
  stepKey,
  onPick,
}: {
  credId: string;
  items: readonly WizChannelItem[];
  value: string;
  wiz: string;
  stepKey: string;
  onPick: (address: string) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const panel = useSlidePanel();
  const chan = useWizChannelCheck({ credId, value, active: true, wiz, step: stepKey });
  const [addr, setAddr] = useState('');
  // Счётчик отказов: до первой попытки лист чистый; каждая новая — перемонтирует поле
  // (key), и тряска проигрывается заново.
  const [tried, setTried] = useState(0);
  const [fail, setFail] = useState('');
  const busy = chan.state?.phase === 'checking';
  const botName = chan.botName || t('chatSubYourBot');
  const submit = () => {
    const a = addr.trim();
    if (!credId || !a || busy) return;
    chan.note({ pick: 1, src: 'form' });
    void chan.check(a, 'form').then((res) => {
      if (res.phase === 'ok') {
        onPick(res.address);
        panel.back();
        return;
      }
      if (res.phase === 'fail')
        setFail(t(chanFailKey(res.reason) as 'chatSubErrNetwork', { bot: botName }));
      setTried((n) => n + 1);
      if (nativeMethod('haptic')) void nativeBridge()?.haptic?.({ kind: 'warning' });
      else if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate?.(30);
    });
  };
  return (
    <FPane fit>
      {items.length > 0 ? (
        <FBlock label={null}>
        <KListGroup>
          {items.map((i) => (
            <KListRow
              key={i.address}
              title={i.title || i.address}
              hint={chanItemHint(i, {
                isAdmin: t('chatSubIsAdmin'),
                notAdmin: t('chatSubNotAdmin'),
                closed: t('chatSubClosed'),
              })}
              {...(chanKey(i.address) === chanKey(value) ? { value: <KListCheck /> } : {})}
              onClick={() => {
                chan.note({ pick: 1, src: 'list' });
                onPick(i.address);
                void chan.check(i.address, 'list');
                panel.back();
              }}
            />
          ))}
        </KListGroup>
        </FBlock>
      ) : null}
      {/* «КАК ДОБАВИТЬ» (ТЗ CHAN-ADD 16.10.2026): строка бота и шаги. Список выше обновляется сам по поводу живого
          канала — бот стал администратором, и канал встаёт строкой; «Обновить» — только без живого канала. */}
      <WizChanSteps bot={chan.botName.replace(/^@/, '')} offline={!chan.live} onRefresh={chan.reload} />
      <FBlock
        label={null}
        className="wiz-chan-add"
        bad={!!fail}
        error={fail || undefined}
        hint={fail ? undefined : t('chatSubManual')}
        key={`chan-try-${tried}`}
      >
        <KListGroup>
          <KListRow
            className="wiz-chan-field"
            title={t('chatSubChannelLogin')}
            control
            value={
              <KInput
                value={addr}
                placeholder={t('chatSubChannelLoginPh')}
                aria-label={t('chatSubChannelLogin')}
                autoFocus
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => {
                  setAddr(e.target.value);
                  if (fail) setFail('');
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
              />
            }
          />
        </KListGroup>
      </FBlock>
      <KButton
        action
        tone="primary"
        // Один тон главной кнопки — розовая с чёрным словом (владелец 11.10.2026; прежняя белая капсула с
        // розовым словом по кадру W32 снята).
        className="wiz-chan-go"
        disabled={busy || !addr.trim() || !credId}
        onClick={submit}
      >
        {busy ? t('chatSubChecking') : t('chatSubCheckAdd')}
      </KButton>
    </FPane>
  );
}

/**
 * «Подпись источника» + живая «Готовая ссылка» (владелец 05.08, скрин ручного
 * создания): хвост — транслитом из подписи, ссылка t.me/<бот>?start=<хвост> с кнопкой
 * «Скопировать». Саму ссылку в реестре заведёт «Создать»/«Сохранить» (как раньше).
 */
/**
 * Ссылка-приглашение при правке мастером: если у сценария в реестре ещё нет ссылки С
 * ТАКИМ ХВОСТОМ — завести (переключение анкеты на запуск «по ссылке» иначе оставит запуск
 * без единого входа).
 *
 * РАННИЙ ВЫХОД — ПО КОДУ, А НЕ «у черновика уже есть ручная ссылка» (К18.3 этап 1 п.2 в):
 * ссылок у черновика бывает несколько, и прежняя проверка заводила только первую.
 * Удалённый триггер ссылку в реестре не трогает — по ней осталась статистика.
 *
 * Возвращает 'taken', когда ТОТ ЖЕ хвост уже занят ДРУГОЙ ссылкой этого бота (409
 * `code_taken`): это вина клиента, и страница показывает подсказку под полем «Откуда
 * придут люди» (п.7 CLAUDE.md). Остальное — best-effort: не вышло, человек доберёт в
 * карточке сценария.
 */
/** Превью чата: пузыри «что увидит клиент» при текущих ответах анкеты. Живёт в нижнем
 *  листе по кнопке-телефону (владелец 05.08); в теле формы больше не рисуется. */

/** Лист выбора аккаунта шага мастера (телефон, браузер): строки с галочкой и «Добавить
 *  аккаунт» акцентом — как родной лист выбора iOS (W12/W32). */
function WizAccountPick({
  options,
  value,
  addLabel,
  onPick,
  onAdd,
}: {
  options: readonly { value: string; label: string }[];
  value: string;
  /** Нет — строки «Добавить аккаунт» в листе нет (лист «Бот» рассылки, кадр iOS
   *  R02-broadcast-bot-pick: родной выбор показывает только аккаунты с галочкой). */
  addLabel?: string;
  onPick: (v: string) => void;
  onAdd?: () => void;
}) {
  return (
    <div className="bw-page">
      <KListGroup>
        {options.map((o) => (
          <KListRow
            key={o.value}
            title={o.label}
            value={o.value === value ? <KListCheck /> : ''}
            onClick={() => onPick(o.value)}
          />
        ))}
        {addLabel && onAdd ? <KListRow title={addLabel} tone="accent" onClick={onAdd} /> : null}
      </KListGroup>
    </div>
  );
}
